import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { trackMetaEvent } from '../utils/metaPixel';
import MetaConversionsPanel from '../components/MetaConversionsPanel';
import DismissReasonModal from '../components/DismissReasonModal';
import EdgeScroll from '../components/EdgeScroll';
import { ArrowsClockwise, X, ArrowSquareOut, ArrowsOutSimple, ArrowsInSimple, ToggleLeft, ToggleRight, Plus, CircleNotch, WarningCircle, Warning, Trash, ShieldCheck, ShieldWarning, Shield, ArrowCounterClockwise, CheckCircle, PencilSimple, FloppyDisk, Pause, Play, CaretDown, CaretRight, CaretUp, Sparkle, FileText, Globe, Check, Funnel, ChartBar, Heart, Rows, Key, TextAa, Lightbulb, CalendarBlank, Lock, Minus, Robot, MagnifyingGlass, Target, Gear, GoogleLogo, MetaLogo, ShoppingCart, Circle, DotsSixVertical, UsersThree, Upload, Columns } from '@phosphor-icons/react';
import {
  Box, Title, Group, Button, Badge, Paper, Text, Table, Loader, Flex, Modal, Drawer, Anchor, Menu,
  TextInput, NumberInput, Select, Tabs, SimpleGrid, Stack, ActionIcon, Tooltip, Alert, ThemeIcon, Switch, SegmentedControl,
  Popover, Checkbox, Divider, Textarea, Chip, Portal,
} from '@mantine/core';
import { DateTimePicker, DateInput } from '@mantine/dates';
import 'dayjs/locale/pl';
import { ResponsiveContainer, ComposedChart, Line, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, AreaChart, Area, ReferenceDot } from 'recharts';
import api, { apiUrl } from '../utils/api';
import { formatMoney, currencySymbol } from '../utils/money';
import { getGoogleAccounts } from '../utils/googleAccounts';
import StickyHeader from '../components/StickyHeader';
import { useAuth } from '../hooks/useAuth';
import { useNotifications } from '../hooks/useNotifications';
import SEO from '../components/SEO';
import AnimatedLogo from '../components/AnimatedLogo';
import CreateCampaignWizard from '../components/CreateCampaignWizard';
import CampaignDraftReview from '../components/CampaignDraftReview';
import CampaignReorder from '../components/CampaignReorder';
import ExportMenu from '../components/ExportMenu';
import ConversionSetup from '../components/ConversionSetup';
import ConversionsTab from '../components/ConversionsTab';
import CampaignLocationEditor from '../components/CampaignLocationEditor';
import ShoppingDashboard from '../components/ShoppingDashboard';

// Mini-CRM: statusy leadów Meta (etykieta + kolor Mantine).
// Czytelny numer telefonu: +48XXXXXXXXX -> +48 XXX XXX XXX (Polska i ogólny
// przypadek +CC + 9 cyfr; gołe 9 cyfr traktujemy jak numer PL). Inne formaty
// zostawiamy bez zmian.
function formatLeadPhone(raw) {
  if (!raw) return raw;
  const s = String(raw).replace(/\s+/g, '');
  let m = s.match(/^(\+\d{1,3})(\d{9})$/);
  if (m) return `${m[1]} ${m[2].slice(0, 3)} ${m[2].slice(3, 6)} ${m[2].slice(6)}`;
  m = s.match(/^(\d{9})$/);
  if (m) return `${m[1].slice(0, 3)} ${m[1].slice(3, 6)} ${m[1].slice(6)}`;
  return raw;
}

const LEAD_STATUS = {
  new: { labelKey: 'campaigns.leadStatus.new', color: 'blue' },
  contacted: { labelKey: 'campaigns.leadStatus.contacted', color: 'yellow' },
  qualified: { labelKey: 'campaigns.leadStatus.qualified', color: 'indigo' },
  won: { labelKey: 'campaigns.leadStatus.won', color: 'green' },
  lost: { labelKey: 'campaigns.leadStatus.lost', color: 'gray' },
};

// LeadsTab component for Meta Ads lead management (mini-CRM)
function LeadsTab({ campaignId }) {
  const { t } = useTranslation();
  const LEAD_STATUS_OPTS = Object.entries(LEAD_STATUS).map(([value, v]) => ({ value, label: t(v.labelKey) }));
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [expandedId, setExpandedId] = useState(null);
  // Podgląd żywego formularza lead gen.
  const [formOpen, setFormOpen] = useState(false);
  const [formData, setFormData] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState('');

  const openFormPreview = async () => {
    setFormOpen(true);
    if (formData) return;
    setFormLoading(true); setFormError('');
    try {
      const res = await api.get(`/ads/campaigns/${campaignId}/leads/form/`);
      setFormData(res.data);
    } catch (err) {
      setFormError(err.response?.data?.error || t('campaigns.leads.formFetchError'));
    } finally { setFormLoading(false); }
  };
  // Automatyczne przekazywanie leadów (email/webhook).
  const [cfgOpen, setCfgOpen] = useState(false);
  const [cfg, setCfg] = useState({ enabled: false, email_to: '', webhook_url: '' });
  const [cfgSaving, setCfgSaving] = useState(false);
  const [cfgTesting, setCfgTesting] = useState(false);
  const [cfgMsg, setCfgMsg] = useState(null);

  useEffect(() => {
    api.get(`/ads/campaigns/${campaignId}/leads/delivery-config/`)
      .then(res => setCfg(res.data))
      .catch(() => {});
  }, [campaignId]);

  const saveCfg = async (test = false) => {
    setCfgMsg(null);
    if (test) setCfgTesting(true); else setCfgSaving(true);
    try {
      const res = await api.post(`/ads/campaigns/${campaignId}/leads/delivery-config/`, { ...cfg, test });
      if (test) {
        const r = res.data.results || {};
        const parts = [];
        if (r.email) parts.push(`email: ${r.email === 'ok' ? t('campaigns.leads.testSent') : r.email}`);
        if (r.webhook) parts.push(`webhook: ${r.webhook === 'ok' ? 'OK' : r.webhook}`);
        setCfgMsg({ type: 'info', text: t('campaigns.leads.testResult', { result: parts.join(', ') || t('campaigns.leads.testNoChannels') }) });
      } else {
        setCfgMsg({ type: 'success', text: t('campaigns.leads.saved') });
        setTimeout(() => setCfgOpen(false), 700);
      }
    } catch (err) {
      setCfgMsg({ type: 'error', text: err.response?.data?.error || t('campaigns.leads.saveError') });
    } finally {
      setCfgSaving(false); setCfgTesting(false);
    }
  };

  useEffect(() => {
    api.get(`/ads/campaigns/${campaignId}/leads/`)
      .then(res => setLeads(res.data.leads || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [campaignId]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const res = await api.post(`/ads/campaigns/${campaignId}/leads/sync/`);
      if (res.data.new_leads > 0) {
        const updated = await api.get(`/ads/campaigns/${campaignId}/leads/`);
        setLeads(updated.data.leads || []);
      }
    } catch {}
    setSyncing(false);
  };

  // Optymistyczny PATCH pól mini-CRM (status / termin kontaktu / notatka).
  const patchLead = async (lead, patch) => {
    setLeads(prev => prev.map(l => l.id === lead.id ? { ...l, ...patch } : l));
    try {
      const res = await api.patch(`/ads/campaigns/${campaignId}/leads/${lead.id}/`, patch);
      // Backend może domknąć status/is_contacted - przyjmij jego wersję.
      const srv = res.data || {};
      setLeads(prev => prev.map(l => l.id === lead.id ? {
        ...l,
        ...(srv.lead_status ? { status: srv.lead_status } : {}),
        ...(typeof srv.is_contacted === 'boolean' ? { is_contacted: srv.is_contacted } : {}),
        ...('follow_up_date' in srv ? { follow_up_date: srv.follow_up_date } : {}),
      } : l));
    } catch {}
  };

  // Pobranie CSV z nagłówkiem autoryzacji (zwykły link nie wysyła tokenu -> 401).
  const downloadCsv = async () => {
    try {
      const res = await api.get(`/ads/campaigns/${campaignId}/leads/export/`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `leady_${campaignId}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {}
  };

  const filtered = leads.filter(l =>
    (filterStatus === 'all' || (l.status || 'new') === filterStatus) &&
    (!search || [l.full_name, l.email, l.phone, l.company_name].some(v => (v || '').toLowerCase().includes(search.toLowerCase())))
  );

  if (loading) return <div className="flex justify-center py-8"><Loader size="sm" color="brand" /></div>;

  return (
    <div className="px-4 py-3">
      <Group justify="space-between" mb="sm">
        <Group gap="xs">
          <TextInput placeholder={t('campaigns.leads.searchPlaceholder')} size="xs" value={search} onChange={e => setSearch(e.target.value)} style={{ width: 180 }} />
          <Select
            size="xs" style={{ width: 150 }} value={filterStatus}
            onChange={(v) => setFilterStatus(v || 'all')}
            data={[{ value: 'all', label: t('campaigns.leads.allStatuses') }, ...LEAD_STATUS_OPTS]}
          />
          <Badge size="sm" variant="light">{t('campaigns.leads.count', { count: filtered.length })}</Badge>
        </Group>
        <Group gap="xs">
          <Button size="compact-xs" variant="light" onClick={openFormPreview}>{t('campaigns.leads.previewForm')}</Button>
          <Button size="compact-xs" variant={cfg.enabled ? 'filled' : 'light'} color={cfg.enabled ? 'teal' : undefined} onClick={() => { setCfgMsg(null); setCfgOpen(true); }}>
            {cfg.enabled ? t('campaigns.leads.autoSendOn') : t('campaigns.leads.autoSendOff')}
          </Button>
          <Button size="compact-xs" variant="light" onClick={handleSync} loading={syncing}>{t('campaigns.leads.sync')}</Button>
          <Button size="compact-xs" variant="light" onClick={downloadCsv}>{t('campaigns.leads.exportCsv')}</Button>
        </Group>
      </Group>

      <Modal opened={formOpen} onClose={() => setFormOpen(false)} title={t('campaigns.leads.formPreviewTitle')} size="lg" overlayProps={{ backgroundOpacity: 0.3, blur: 4 }}>
        <Text size="sm" c="dimmed" mb="md">{t('campaigns.leads.formPreviewDesc')}</Text>
        {formLoading ? (
          <Flex align="center" justify="center" py="lg"><Loader size="sm" color="brand" /></Flex>
        ) : formError ? (
          <Text size="sm" c="red">{formError}</Text>
        ) : formData && formData.questions ? (
          <Stack gap="sm">
            {formData.questions.map((q, i) => (
              <Paper key={q.key || i} withBorder p="sm" radius="sm">
                <Group gap={8} wrap="nowrap" align="baseline">
                  <Text size="xs" c="dimmed" fw={600} style={{ minWidth: 18 }}>{i + 1}.</Text>
                  <div style={{ flex: 1 }}>
                    <Text size="sm" fw={500}>{q.label}</Text>
                    {q.options && q.options.length > 0 ? (
                      <Group gap={6} mt={4}>
                        {q.options.map((o, j) => (
                          <Badge key={j} size="sm" variant="light" color="gray" styles={{ root: { textTransform: 'none' } }}>{o}</Badge>
                        ))}
                      </Group>
                    ) : (
                      <Text size="xs" c="dimmed" mt={2}>{t('campaigns.leads.textField')}</Text>
                    )}
                  </div>
                </Group>
              </Paper>
            ))}
            <Text size="10px" c="dimmed">{t('campaigns.leads.formEditHint')}</Text>
          </Stack>
        ) : (
          <Text size="sm" c="dimmed">{t('campaigns.leads.formNoData')}</Text>
        )}
      </Modal>

      <Modal opened={cfgOpen} onClose={() => setCfgOpen(false)} title={t('campaigns.leads.autoSendTitle')} size="lg" overlayProps={{ backgroundOpacity: 0.3, blur: 4 }}>
        <Text size="sm" c="dimmed" mb="md">
          {t('campaigns.leads.autoSendDesc')}
        </Text>
        <Switch
          checked={cfg.enabled}
          onChange={e => setCfg(c => ({ ...c, enabled: e.currentTarget.checked }))}
          label={t('campaigns.leads.autoSendEnable')}
          mb="md"
        />
        <TextInput
          label={t('campaigns.leads.emailLabel')}
          description={t('campaigns.leads.emailDesc')}
          placeholder={t('campaigns.leads.emailPlaceholder')}
          value={cfg.email_to}
          onChange={e => setCfg(c => ({ ...c, email_to: e.target.value }))}
          mb="md"
        />
        <TextInput
          label={t('campaigns.leads.webhookLabel')}
          description={t('campaigns.leads.webhookDesc')}
          placeholder="https://twojcrm.pl/webhook/leady"
          value={cfg.webhook_url}
          onChange={e => setCfg(c => ({ ...c, webhook_url: e.target.value }))}
          mb="xs"
        />
        <Paper withBorder radius="sm" p="xs" mb="md" bg="gray.0">
          <Text size="xs" fw={600} mb={4} c="dimmed">{t('campaigns.leads.webhookFormat')}</Text>
          <Box component="pre" style={{ margin: 0, fontSize: 11, lineHeight: 1.5, fontFamily: 'monospace', whiteSpace: 'pre-wrap', overflowX: 'auto' }}>
{t('campaigns.leads.webhookExample', { campaignId })}
          </Box>
        </Paper>
        {cfgMsg && (
          <Text size="sm" mb="sm" c={cfgMsg.type === 'error' ? 'red' : cfgMsg.type === 'success' ? 'teal' : 'blue'}>
            {cfgMsg.text}
          </Text>
        )}
        <Group justify="space-between" mt="md">
          <Button size="xs" variant="default" onClick={() => saveCfg(true)} loading={cfgTesting} disabled={cfgSaving}>
            {t('campaigns.leads.sendTestLead')}
          </Button>
          <Group gap="xs">
            <Button size="xs" variant="subtle" color="gray" onClick={() => setCfgOpen(false)}>{t('campaigns.common.cancel')}</Button>
            <Button size="xs" onClick={() => saveCfg(false)} loading={cfgSaving} disabled={cfgTesting}>{t('campaigns.common.save')}</Button>
          </Group>
        </Group>
      </Modal>

      {filtered.length === 0 ? (
        <Paper withBorder p="lg" radius="md" ta="center">
          <Text size="sm" fw={500} mb={4}>{t('campaigns.leads.emptyTitle')}</Text>
          <Text size="xs" c="dimmed">{t('campaigns.leads.emptyDesc')}</Text>
          <Button size="xs" variant="light" mt="sm" onClick={handleSync} loading={syncing}>{t('campaigns.leads.syncNow')}</Button>
        </Paper>
      ) : (
        <Paper withBorder radius="md" style={{ overflow: 'hidden' }}>
          <Table verticalSpacing="xs" horizontalSpacing="sm" style={{ fontSize: 13 }}>
            <Table.Thead>
              <Table.Tr>
                <Table.Th style={{ width: 28 }}></Table.Th>
                <Table.Th>{t('campaigns.leads.colFullName')}</Table.Th>
                <Table.Th>{t('campaigns.leads.colContact')}</Table.Th>
                <Table.Th>{t('campaigns.leads.colCompany')}</Table.Th>
                <Table.Th>{t('campaigns.leads.colDate')}</Table.Th>
                <Table.Th style={{ width: 150 }}>{t('campaigns.leads.colStatus')}</Table.Th>
                <Table.Th style={{ width: 140 }}>{t('campaigns.leads.colContactBy')}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {filtered.map(lead => {
                const st = LEAD_STATUS[lead.status || 'new'] || LEAD_STATUS.new;
                const isOpen = expandedId === lead.id;
                // Czytelne odpowiedzi z pytań formularza (etykieta + wartość zamiast q1/q0_opt2).
                const answers = (lead.field_data && lead.field_data._answers) || [];
                const answeredKeys = new Set(answers.map(a => a.key));
                const extra = Object.entries(lead.field_data || {}).filter(([k, v]) =>
                  !['id', 'created_time', 'ad_name', 'full_name', 'email', 'phone', 'phone_number',
                    'company_name', 'city', 'job_title'].includes(k) && !String(k).startsWith('_') &&
                    !answeredKeys.has(k) && v && typeof v !== 'object')
                  .map(([k, v]) => [k, v]);
                const answerRows = answers.map(a => [a.label, a.value]);
                return (
                  <React.Fragment key={lead.id}>
                    <Table.Tr style={{ cursor: 'pointer' }} onClick={() => setExpandedId(isOpen ? null : lead.id)}>
                      <Table.Td>
                        {isOpen ? <CaretDown size={13} className="text-gray-400" /> : <CaretRight size={13} className="text-gray-400" />}
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" fw={500}>{lead.full_name || '-'}</Text>
                        {lead.job_title && <Text size="11px" c="dimmed">{lead.job_title}</Text>}
                      </Table.Td>
                      <Table.Td>
                        {lead.email && <Text size="sm">{lead.email}</Text>}
                        {lead.phone && <Text size="xs" c="dimmed">{formatLeadPhone(lead.phone)}</Text>}
                        {!lead.email && !lead.phone && <Text size="sm" c="dimmed">-</Text>}
                      </Table.Td>
                      <Table.Td><Text size="sm" c="dimmed">{lead.company_name || '-'}</Text></Table.Td>
                      <Table.Td><Text size="xs" c="dimmed">{new Date(lead.created_at).toLocaleDateString('pl-PL')}</Text></Table.Td>
                      <Table.Td onClick={(e) => e.stopPropagation()}>
                        <Select
                          size="xs" variant="unstyled" allowDeselect={false}
                          value={lead.status || 'new'}
                          onChange={(v) => v && patchLead(lead, { status: v })}
                          data={LEAD_STATUS_OPTS}
                          leftSection={<Box style={{ width: 8, height: 8, borderRadius: '50%', background: `var(--mantine-color-${st.color}-5)` }} />}
                          styles={{ input: { fontWeight: 500, minHeight: 26, color: `var(--mantine-color-${st.color}-7)` } }}
                          comboboxProps={{ width: 160 }}
                        />
                      </Table.Td>
                      <Table.Td onClick={(e) => e.stopPropagation()}>
                        <DateInput
                          size="xs" variant="unstyled" valueFormat="DD.MM.YYYY" clearable
                          placeholder={t('campaigns.leads.setDate')}
                          value={lead.follow_up_date ? new Date(lead.follow_up_date) : null}
                          onChange={(d) => patchLead(lead, { follow_up_date: d ? new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10) : null })}
                        />
                      </Table.Td>
                    </Table.Tr>
                    {isOpen && (
                      <Table.Tr>
                        <Table.Td colSpan={7} style={{ background: 'var(--mantine-color-gray-0)' }}>
                          <Box p="xs">
                            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="xs" mb="sm">
                              {[[t('campaigns.leads.detailFullName'), lead.full_name], [t('campaigns.leads.detailEmail'), lead.email], [t('campaigns.leads.detailPhone'), formatLeadPhone(lead.phone)],
                                [t('campaigns.leads.detailCompany'), lead.company_name], [t('campaigns.leads.detailCity'), lead.city], [t('campaigns.leads.detailJobTitle'), lead.job_title],
                                [t('campaigns.leads.detailAd'), lead.ad_name], ...answerRows, ...extra].filter(([, v]) => v).map(([k, v]) => (
                                <Group key={k} gap={6} wrap="nowrap">
                                  <Text size="xs" c="dimmed" style={{ minWidth: 110 }}>{k}:</Text>
                                  <Text size="xs" fw={500}>{String(v)}</Text>
                                </Group>
                              ))}
                            </SimpleGrid>
                            <Textarea
                              label={t('campaigns.leads.noteLabel')} size="xs" autosize minRows={2}
                              placeholder={t('campaigns.leads.notePlaceholder')}
                              defaultValue={lead.notes || ''}
                              onBlur={(e) => { if (e.target.value !== (lead.notes || '')) patchLead(lead, { notes: e.target.value }); }}
                            />
                            <Text size="10px" c="dimmed" mt={4}>{t('campaigns.leads.noteHint')}</Text>
                          </Box>
                        </Table.Td>
                      </Table.Tr>
                    )}
                  </React.Fragment>
                );
              })}
            </Table.Tbody>
          </Table>
        </Paper>
      )}
    </div>
  );
}

// Modals

function ModalWrapper({ children, onClose, size = "md" }) {
  return (
    <Modal opened onClose={onClose} centered withCloseButton={false} size={size} overlayProps={{ backgroundOpacity: 0.3, blur: 4 }}>
      {children}
    </Modal>
  );
}

function ConnectGoogleAdsModal({ onClose }) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);

  const handleConnect = async () => {
    setLoading(true);
    try {
      const res = await api.get('/accounts/google/auth/');
      window.location.href = res.data.auth_url;
    } catch {
      setLoading(false);
    }
  };

  return (
    <ModalWrapper onClose={onClose}>
      <Title order={4} mb="xs">{t('campaigns.connectModal.title')}</Title>
      <Text size="sm" c="dimmed" mb="md">
        {t('campaigns.connectModal.desc')}
      </Text>
      <Stack gap={6} mb="lg">
        <Text size="xs" c="dimmed">{t('campaigns.connectModal.bullet1')}</Text>
        <Text size="xs" c="dimmed">{t('campaigns.connectModal.bullet2')}</Text>
        <Text size="xs" c="dimmed">{t('campaigns.connectModal.bullet3')}</Text>
      </Stack>
      <Group grow>
        <Button variant="default" onClick={onClose}>{t('campaigns.common.cancel')}</Button>
        <Button color="brand" onClick={handleConnect} loading={loading}>
          {loading ? t('campaigns.connectModal.connecting') : t('campaigns.connectModal.connect')}
        </Button>
      </Group>
    </ModalWrapper>
  );
}

function SelectAccountModal({ onClose, onSelected, onRenamed }) {
  const { t } = useTranslation();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selecting, setSelecting] = useState(null);
  const [currentId, setCurrentId] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [savingName, setSavingName] = useState(false);

  useEffect(() => {
    getGoogleAccounts()
      .then(res => {
        const accts = (res.data.accounts || []).sort((a, b) => (b.campaign_count || 0) - (a.campaign_count || 0));
        setAccounts(accts);
        setCurrentId(res.data.current_customer_id || '');
      })
      .catch(() => {
        setError(t('campaigns.selectAccount.fetchError'));
      })
      .finally(() => setLoading(false));
  }, []);

  const handleSelect = async (account) => {
    if (editingId) return;
    setSelecting(account.id);
    try {
      const res = await api.post('/accounts/connect-google-ads/', {
        customer_id: account.id,
        refresh_token: 'keep',
      });
      onSelected(account, res.data.syncing);
    } catch {
      setError(t('campaigns.selectAccount.selectError'));
      setSelecting(null);
    }
  };

  const displayName = (account) => account.custom_name || account.name || t('campaigns.selectAccount.accountName', { id: account.id });

  const handleStartRename = (e, account) => {
    e.stopPropagation();
    setEditingId(account.id);
    setEditName(account.custom_name || account.name || '');
  };

  const handleSaveRename = async (accountId) => {
    if (!editName.trim()) { setEditingId(null); return; }
    setSavingName(true);
    try {
      await api.post('/accounts/rename-google-ads-account/', { account_id: accountId, name: editName.trim() });
      setAccounts(prev => prev.map(a => a.id === accountId ? { ...a, custom_name: editName.trim() } : a));
      if (onRenamed) onRenamed(accountId, editName.trim());
      setEditingId(null);
    } catch {}
    setSavingName(false);
  };

  return (
    <ModalWrapper onClose={onClose}>
      <h2 className="text-sm font-semibold text-gray-900 mb-1">{t('campaigns.selectAccount.title')}</h2>
      <p className="text-xs text-gray-500 mb-4">
        {t('campaigns.selectAccount.desc')}
      </p>

      {loading && (
        <div className="flex items-center justify-center py-8">
          <CircleNotch className="w-5 h-5 animate-spin text-indigo-600" />
          <span className="ml-2 text-sm text-gray-500">{t('campaigns.selectAccount.loadingAccounts')}</span>
        </div>
      )}

      {error && !loading && (
        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-md mb-3">
          <WarningCircle className="w-4 h-4 text-red-500 shrink-0" />
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {!loading && !error && accounts.length === 0 && (
        <div className="text-center py-6">
          <p className="text-sm text-gray-700 mb-1">{t('campaigns.selectAccount.emptyTitle')}</p>
          <p className="text-xs text-gray-500">
            {t('campaigns.selectAccount.emptyDesc')}
          </p>
        </div>
      )}

      {!loading && accounts.length > 0 && (
        <div className="space-y-1.5 max-h-64 overflow-y-auto">
          {accounts.map(account => {
            const isCurrent = account.id === currentId;
            const isEditing = editingId === account.id;
            return (
              <div
                key={account.id}
                onClick={() => !isEditing && handleSelect(account)}
                className={`w-full text-left px-3 py-2.5 rounded-md border transition ${
                  isCurrent
                    ? 'border-indigo-300 bg-indigo-50'
                    : 'border-gray-200 hover:border-indigo-200 hover:bg-indigo-50/50'
                } ${selecting ? 'opacity-50 pointer-events-none' : 'cursor-pointer'}`}
              >
                <div className="flex items-center justify-between">
                  <div className="min-w-0 flex-1">
                    {isEditing ? (
                      <div className="flex items-center gap-1.5" onClick={e => e.stopPropagation()}>
                        <input
                          type="text"
                          value={editName}
                          onChange={e => setEditName(e.target.value)}
                          onKeyDown={e => { if (e.key === 'Enter') handleSaveRename(account.id); if (e.key === 'Escape') setEditingId(null); }}
                          autoFocus
                          className="flex-1 text-sm px-2 py-1 border border-indigo-300 rounded focus:outline-none focus:ring-1 focus:ring-indigo-400"
                          placeholder={t('campaigns.selectAccount.accountNamePlaceholder')}
                        />
                        <button onClick={() => handleSaveRename(account.id)} disabled={savingName} className="p-1 text-indigo-600 hover:text-indigo-800">
                          {savingName ? <CircleNotch className="w-3.5 h-3.5 animate-spin" /> : <FloppyDisk className="w-3.5 h-3.5" />}
                        </button>
                        <button onClick={() => setEditingId(null)} className="p-1 text-gray-400 hover:text-gray-600">
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {displayName(account)}
                        </p>
                        {isCurrent && (
                          <CheckCircle className="w-3.5 h-3.5 text-indigo-600 shrink-0" />
                        )}
                        <button onClick={(e) => handleStartRename(e, account)} className="p-0.5 text-gray-300 hover:text-indigo-500 shrink-0" title={t('campaigns.selectAccount.rename')}>
                          <PencilSimple className="w-3 h-3" />
                        </button>
                      </div>
                    )}
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-xs font-mono text-gray-500">{account.id}</span>
                      {account.campaign_count > 0 ? (
                        <span className="text-xs text-green-600 font-medium">{t('campaigns.selectAccount.campaignCount', { count: account.campaign_count })}</span>
                      ) : (
                        <span className="text-xs text-orange-500">{t('campaigns.selectAccount.noCampaigns')}</span>
                      )}
                    </div>
                  </div>
                  {selecting === account.id && (
                    <CircleNotch className="w-4 h-4 animate-spin text-indigo-600 shrink-0" />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="mt-4 pt-3 border-t border-gray-100">
        <button
          onClick={onClose}
          className="w-full px-3 py-1.5 rounded-md border border-gray-200 text-sm text-gray-700 hover:bg-gray-50 transition"
        >
          {t('campaigns.common.cancel')}
        </button>
      </div>
    </ModalWrapper>
  );
}

// Najmniejszy pakiet mieszczący tę kampanię (budżet dzienny + liczba kampanii),
// lustro backendowego activation_plan_for. Dzięki temu karta aktywacji pokazuje
// realną cenę (np. Micro 79 zł dla kampanii <= 50 zł/dzień), nie zawsze 299.
function planForCampaign(campaign, ps) {
  const plans = ps?.plans || [];
  if (!plans.length) return null;
  const daily = Number(campaign?.budget_amount || 0);
  const need = (ps?.managed_campaigns || 0) + 1;
  const fit = plans.find(p =>
    (p.campaigns == null || need <= p.campaigns) &&
    (!p.budget_max_daily || daily <= p.budget_max_daily)
  );
  return fit || plans[plans.length - 1];
}

function ActivateTrialModal({ onClose, onConfirm, loading, trialUsed, isFree, onSubscribe, error, activationPlan, currency = 'PLN', transferDays = null, planChoices = 0 }) {
  const { t } = useTranslation();
  const actPriceNum = activationPlan?.prices?.[currency] != null ? activationPlan.prices[currency] : (activationPlan?.price ?? 299);
  const actPrice = formatMoney(actPriceNum, currency);
  const actLabel = activationPlan?.label || 'Start';
  if (isFree) {
    return (
      <ModalWrapper onClose={onClose}>
        <Title order={4} mb="xs">{t('campaigns.activateTrial.freeTitle')}</Title>
        <Text size="sm" c="dimmed" mb="md">
          {t('campaigns.activateTrial.freeDesc')}
        </Text>
        <Paper bg="green.0" withBorder p="sm" mb="md" style={{ borderColor: 'var(--mantine-color-green-3)' }}>
          <Text size="sm" fw={600} c="green.8">{t('campaigns.activateTrial.freePlanTitle')}</Text>
          <Text size="xs" c="green.6">{t('campaigns.activateTrial.freePlanDesc')}</Text>
        </Paper>
        {error && (
          <Alert color="red" variant="light" mb="sm">
            <Text size="sm">{error}</Text>
          </Alert>
        )}
        <Group grow>
          <Button variant="default" onClick={onClose}>{t('campaigns.common.cancel')}</Button>
          <Button color="brand" onClick={onConfirm} loading={loading}>
            {t('campaigns.common.activate')}
          </Button>
        </Group>
      </ModalWrapper>
    );
  }

  if (trialUsed) {
    return (
      <ModalWrapper onClose={onClose}>
        <Title order={4} mb="xs">{t('campaigns.activateTrial.usedTitle')}</Title>
        <Text size="sm" c="dimmed" mb="md">
          {t('campaigns.activateTrial.usedDesc')}
        </Text>
        <Paper bg="gray.0" withBorder p="sm" mb="md">
          <Text size="sm" fw={600}>{t('campaigns.activateTrial.planPrice', { label: actLabel, price: actPrice })}</Text>
          <Text size="xs" c="dimmed">{t('campaigns.activateTrial.noCommitment')}</Text>
        </Paper>
        <Group grow>
          <Button variant="default" onClick={onClose}>{t('campaigns.common.cancel')}</Button>
          <Button color="brand" onClick={onSubscribe} loading={loading}>
            {t('campaigns.activateTrial.activateSubscription')}
          </Button>
        </Group>
      </ModalWrapper>
    );
  }

  return (
    <ModalWrapper onClose={onClose}>
      <Title order={4} mb="xs">{t('campaigns.activateTrial.title')}</Title>
      <Text size="sm" c="dimmed" mb="md">
        {t('campaigns.activateTrial.chooseMethod')}
      </Text>
      <Stack gap="sm" mb="md">
        <Paper withBorder p="md" radius="md" style={{ cursor: 'pointer' }} onClick={!loading ? onConfirm : undefined}>
          <Group justify="space-between">
            <div>
              {/* Przeniesienie resztki triala to NIE nowe 7 dni - nie obiecujemy ich. */}
              <Text size="sm" fw={600}>
                {transferDays != null
                  ? t('campaigns.activateTrial.transferTitle', { count: transferDays })
                  : t('campaigns.activateTrial.trialTitle')}
              </Text>
              <Text size="xs" c="dimmed">
                {transferDays != null
                  ? t('campaigns.activateTrial.transferDesc')
                  : t('campaigns.activateTrial.trialDesc')}
              </Text>
            </div>
            <Button size="compact-sm" color="brand" variant="light" loading={loading} onClick={(e) => { e.stopPropagation(); onConfirm(); }}>
              {t('campaigns.common.activate')}
            </Button>
          </Group>
        </Paper>
        <Paper withBorder p="md" radius="md" style={{ cursor: 'pointer' }} onClick={!loading ? onSubscribe : undefined}>
          <Group justify="space-between">
            <div>
              {/* Pelny wybor pakietu jest w SubscribeModal (nastepny krok). Wczesniej
                  ten kafelek pokazywal tylko rekomendowany pakiet i wygladal na
                  jedyna dostepna opcje. */}
              <Text size="sm" fw={600}>
                {planChoices > 1
                  ? t('campaigns.activateTrial.subscriptionChooseTitle')
                  : t('campaigns.activateTrial.subscriptionTitle', { label: actLabel })}
              </Text>
              <Text size="xs" c="dimmed">
                {planChoices > 1
                  ? t('campaigns.activateTrial.priceFrom', { price: actPrice })
                  : t('campaigns.activateTrial.priceMonthly', { price: actPrice })}
              </Text>
            </div>
            <Button size="compact-sm" color="brand" onClick={(e) => { e.stopPropagation(); onSubscribe(); }}>
              {t('campaigns.activateTrial.choose')}
            </Button>
          </Group>
        </Paper>
      </Stack>
      {error && (
        <Alert color="red" mb="sm" icon={<WarningCircle size={16} />} radius="md">
          <Text size="sm" fw={500} mb={2}>{t('campaigns.activateTrial.errorTitle')}</Text>
          <Text size="xs">{error}</Text>
          <Text size="xs" c="dimmed" mt={4}>{t('campaigns.activateTrial.errorHint')}</Text>
        </Alert>
      )}
      <Button variant="subtle" color="gray" fullWidth onClick={onClose}>{t('campaigns.common.cancel')}</Button>
    </ModalWrapper>
  );
}

function SubscribeModal({ onClose, onConfirm, loading, isReactivation, addonsIncluded, planStatus, campaign }) {
  const { t } = useTranslation();
  const [includeScheduler, setIncludeScheduler] = React.useState(addonsIncluded || false);
  const [selectedKey, setSelectedKey] = React.useState(null);
  const ps = planStatus || {};
  const selfPlans = ps.plans || [];
  // Waluta rozliczeniowa subskrypcji (PLN/EUR/USD). Domyślnie z planStatus.currency
  // (auto z konta reklamowego), z przełącznikiem gdy dostępna więcej niż jedna.
  const currencies = ps.currencies || ['PLN'];
  const [currency, setCurrency] = React.useState(() => ps.currency || 'PLN');
  React.useEffect(() => { if (ps.currency) setCurrency(ps.currency); }, [ps.currency]);
  const priceOf = (p) => (p?.prices?.[currency] != null ? p.prices[currency] : p?.price);
  // Kurs dodatku Harmonogram (100 zł) w wybranej walucie: proporcja z cen pakietów.
  const curRatio = (() => {
    const base = selfPlans.find(p => p.price && p.prices?.[currency] != null);
    return base ? base.prices[currency] / base.price : 1;
  })();
  const schedulerAddon = currency === 'PLN' ? 100 : Math.round(100 * curRatio * 100) / 100;
  // Budżet WYŚWIETLAMY w walucie konta (campaign.currency, np. TRY), ale limity
  // pakietów (budget_max_daily) są w PLN, więc do PORÓWNANIA używamy budżetu
  // znormalizowanego do PLN (budget_amount_pln z backendu). Bez tego konto tureckie
  // porównywało budżet w TRY z limitem 50 PLN (błędna kwalifikacja Micro).
  const acctCurrency = campaign?.currency || 'PLN';
  const dailyBudget = Number(campaign?.budget_amount || 0);
  const dailyBudgetPln = Number(campaign?.budget_amount_pln ?? campaign?.budget_amount ?? 0);
  // Kurs konto->PLN wyliczony z pary (budżet w walucie konta / budżet w PLN). Pozwala
  // pokazać limit budżetu pakietu (w PLN) w walucie konta, spójnie z budżetem kampanii.
  const acctPerPln = (dailyBudget > 0 && dailyBudgetPln > 0) ? dailyBudget / dailyBudgetPln : 1;
  const budgetLimitInAcct = (pln) => acctCurrency === 'PLN' ? pln : Math.round(pln * acctPerPln * 100) / 100;
  const need = (ps.managed_campaigns ?? 0) + 1;
  // Czy pakiet pomieści TĘ kampanię: liczba kampanii + (dla Micro) budżet dzienny (w PLN).
  const planFits = (p) =>
    (p.campaigns == null || need <= p.campaigns) &&
    (!p.budget_max_daily || dailyBudgetPln <= p.budget_max_daily);
  const recommended = selfPlans.find(planFits) || selfPlans[0];
  const selectedPlan = selfPlans.find(p => p.key === selectedKey) || recommended;
  const planLabel = selectedPlan?.label || ps.plan_label || 'Start';
  const planPrice = priceOf(selectedPlan) ?? 299;
  const microPlan = selfPlans.find(p => p.key === 'micro');
  const microEligible = !!(microPlan && planFits(microPlan));
  const withinPackage = ps.subscription_active && (ps.campaign_limit == null || (ps.managed_campaigns ?? 0) < ps.campaign_limit);
  const slotsLeft = ps.campaign_limit == null ? null : Math.max(0, ps.campaign_limit - (ps.managed_campaigns ?? 0));
  // Czy ta akcja faktycznie obciąży klienta (brak darmowego slotu i nie konto darmowe).
  // Wtedy pokazujemy WYBÓR pakietu (kampania po wygaśnięciu trialu wymaga zakupu).
  const willCharge = !withinPackage && !ps.is_free;
  return (
    <ModalWrapper onClose={onClose} size="lg">
      <Title order={4} mb="xs">
        {isReactivation ? t('campaigns.subscribe.reactivateTitle') : t('campaigns.activateTrial.title')}
      </Title>
      <Text size="sm" c="dimmed" mb="sm">
        {willCharge
          ? t('campaigns.subscribe.descCharge')
          : (isReactivation
            ? t('campaigns.subscribe.descReactivation')
            : t('campaigns.subscribe.descDefault'))}
      </Text>
      {willCharge && microEligible && (
        <Text size="xs" c="green.7" fw={500} mb="sm">
          {t('campaigns.subscribe.microEligible', {
            budget: formatMoney(dailyBudget, acctCurrency),
            price: formatMoney(priceOf(microPlan), currency),
          })}
        </Text>
      )}
      {willCharge && currencies.length > 1 && (
        <Group justify="space-between" align="center" mb="sm">
          <Text size="xs" c="dimmed">{t('campaigns.subscribe.currencyLabel')}</Text>
          <SegmentedControl
            size="xs"
            value={currency}
            onChange={setCurrency}
            data={currencies.map(c => ({ value: c, label: c }))}
          />
        </Group>
      )}
      {willCharge ? (
        <Stack gap="xs" mb="md">
          {selfPlans.map((p) => {
            const fits = planFits(p);
            const isSel = selectedPlan?.key === p.key;
            return (
              <Paper key={p.key} withBorder p="sm" radius="md"
                style={{
                  cursor: fits ? 'pointer' : 'not-allowed',
                  opacity: fits ? 1 : 0.55,
                  borderColor: isSel ? 'var(--mantine-color-brand-5)' : undefined,
                  borderWidth: isSel ? 2 : 1,
                  backgroundColor: isSel ? 'var(--mantine-color-brand-0)' : undefined,
                }}
                onClick={fits ? () => setSelectedKey(p.key) : undefined}>
                <Group justify="space-between" wrap="nowrap" align="flex-start">
                  <Box>
                    <Group gap="xs" mb={2}>
                      <Text size="sm" fw={600}>{p.label}</Text>
                      {p.key === 'micro' && fits && <Badge size="xs" color="green" variant="light">{t('campaigns.subscribe.qualifies')}</Badge>}
                      {!fits && <Badge size="xs" color="gray" variant="light">{t('campaigns.subscribe.unavailable')}</Badge>}
                    </Group>
                    <Text size="xs" c="dimmed">
                      {p.budget_max_daily
                        ? t('campaigns.subscribe.planDescBudget', { budget: formatMoney(budgetLimitInAcct(p.budget_max_daily), acctCurrency, { decimals: 0 }) })
                        : t('campaigns.subscribe.planDescCampaigns', { campaigns: p.campaigns, accounts: p.accounts })}
                      {!fits && p.budget_max_daily && dailyBudgetPln > p.budget_max_daily
                        ? t('campaigns.subscribe.budgetExceeds', { budget: formatMoney(dailyBudget, acctCurrency) }) : ''}
                    </Text>
                  </Box>
                  <Text size="sm" fw={700} style={{ whiteSpace: 'nowrap' }}>
                    {formatMoney(priceOf(p), currency)}<Text span size="xs" c="dimmed">{t('campaigns.subscribe.perMonthShort')}</Text>
                  </Text>
                </Group>
              </Paper>
            );
          })}
        </Stack>
      ) : !isReactivation && (
        <Paper bg="gray.0" withBorder p="sm" mb="sm">
          {ps.is_free ? (
            <>
              <Text size="sm" fw={600}>{t('campaigns.subscribe.freeAccount')}</Text>
              <Text size="xs" c="dimmed">{t('campaigns.subscribe.freeAccountDesc')}</Text>
            </>
          ) : (
            <>
              <Text size="sm" fw={600}>{t('campaigns.subscribe.withinPackage', { label: planLabel })}</Text>
              <Text size="xs" c="dimmed">
                {t('campaigns.subscribe.noExtraCharge')}{slotsLeft != null ? t('campaigns.subscribe.freeSlots', { slots: slotsLeft }) : ''}
              </Text>
            </>
          )}
        </Paper>
      )}
      {(willCharge || (!isReactivation && !ps.is_free)) && (
        <>
          <Paper withBorder p="sm" mb="md" style={{ borderColor: includeScheduler ? 'var(--mantine-color-brand-4)' : undefined, cursor: 'pointer' }} onClick={() => setIncludeScheduler(!includeScheduler)}>
            <Group justify="space-between">
              <Box>
                <Group gap="xs" mb={2}>
                  <Text size="sm" fw={500}>{t('campaigns.subscribe.schedulerModule')}</Text>
                  <Badge size="xs" variant="light" color="brand">{t('campaigns.subscribe.optionBadge')}</Badge>
                </Group>
                <Text size="xs" c="dimmed">{t('campaigns.subscribe.schedulerDesc')}</Text>
              </Box>
              {!addonsIncluded && (
                <Box ta="right">
                  <Text size="sm" fw={600}>+{formatMoney(schedulerAddon, currency)}</Text>
                  <Text size="xs" c="dimmed">{t('campaigns.subscribe.netPerMonth')}</Text>
                </Box>
              )}
              {addonsIncluded && (
                <Badge size="xs" variant="light" color="green">{t('campaigns.subscribe.included')}</Badge>
              )}
            </Group>
            <div onClick={(e) => e.stopPropagation()}>
              <Switch
                checked={includeScheduler}
                onChange={() => setIncludeScheduler(!includeScheduler)}
                color="brand"
                size="sm"
                mt="xs"
                label={includeScheduler ? t('campaigns.subscribe.added') : t('campaigns.subscribe.addModule')}
                styles={{ track: { cursor: 'pointer' }, label: { cursor: 'pointer', fontSize: 12 } }}
              />
            </div>
          </Paper>
          {includeScheduler && !addonsIncluded && willCharge && (
            <Text size="xs" c="brand.6" fw={500} mb="sm" ta="center">
              {t('campaigns.subscribe.total', { price: formatMoney(planPrice + schedulerAddon, currency) })}
            </Text>
          )}
        </>
      )}
      {willCharge && (
        <Text size="xs" c="dimmed" mb="md" ta="center">
          {t('campaigns.subscribe.needMore')} <Anchor href="/app/settings" size="xs" c="brand">{t('campaigns.subscribe.chooseOtherPlan')}</Anchor>
        </Text>
      )}
      <Group grow>
        <Button variant="default" onClick={onClose}>{t('campaigns.common.cancel')}</Button>
        <Button color="brand" onClick={() => onConfirm(includeScheduler, willCharge ? selectedPlan?.key : null, currency)} loading={loading} styles={{ label: { whiteSpace: 'nowrap' } }}>
          {loading
            ? t('campaigns.subscribe.activating')
            : willCharge
              ? t('campaigns.subscribe.goToPayment', { price: formatMoney(includeScheduler && !addonsIncluded ? planPrice + schedulerAddon : planPrice, currency) })
              : (isReactivation ? t('campaigns.subscribe.reactivate') : t('campaigns.subscribe.activateManagement'))}
        </Button>
      </Group>
    </ModalWrapper>
  );
}

function PlanUpgradeModal({ info, onClose, onUpgrade, loading }) {
  const { t } = useTranslation();
  if (!info) return null;
  const isAgency = info.suggested_plan === 'agency';
  let reasonText;
  if (info.reason === 'accounts') {
    reasonText = t('campaigns.planUpgrade.reasonAccounts', { plan: info.current_plan_label, limit: info.account_limit, used: info.managed_accounts });
  } else if (info.reason === 'budget') {
    reasonText = t('campaigns.planUpgrade.reasonBudget', { plan: info.current_plan_label, limit: info.budget_limit_daily, budget: info.campaign_budget_daily });
  } else {
    reasonText = t('campaigns.planUpgrade.reasonCampaigns', { plan: info.current_plan_label, limit: info.campaign_limit, used: info.managed_campaigns });
  }
  const titleText = info.reason === 'budget'
    ? t('campaigns.planUpgrade.titleBudget', { plan: info.current_plan_label })
    : t('campaigns.planUpgrade.titleLimit', { plan: info.current_plan_label });
  const priceText = info.suggested_plan_price
    ? t('campaigns.planUpgrade.priceMonthly', { price: info.suggested_plan_price })
    : t('campaigns.planUpgrade.customPricing');
  return (
    <ModalWrapper onClose={onClose}>
      <Title order={4} mb="xs">{titleText}</Title>
      <Text size="sm" c="dimmed" mb="sm">
        {reasonText} {info.reason === 'budget'
          ? t('campaigns.planUpgrade.ctaBudget')
          : t('campaigns.planUpgrade.ctaCampaigns')}
      </Text>
      <Paper bg="gray.0" withBorder p="sm" mb="md">
        <Group justify="space-between" mb={4}>
          <Text size="sm" fw={600}>{t('campaigns.planUpgrade.suggestedPlan', { plan: info.suggested_plan_label })}</Text>
          <Text size="sm" fw={700} c="brand.6">{priceText}</Text>
        </Group>
        <Text size="xs" c="dimmed">
          {isAgency
            ? t('campaigns.planUpgrade.agencyDesc')
            : t('campaigns.planUpgrade.upgradeDesc')}
        </Text>
      </Paper>
      <Group grow>
        <Button variant="default" onClick={onClose}>{t('campaigns.common.cancel')}</Button>
        {isAgency ? (
          <Button component="a" href="/kontakt" color="brand">{t('campaigns.planUpgrade.contact')}</Button>
        ) : (
          <Button color="brand" onClick={onUpgrade} loading={loading}>
            {loading ? t('campaigns.planUpgrade.processing') : t('campaigns.planUpgrade.upgradeTo', { plan: info.suggested_plan_label })}
          </Button>
        )}
      </Group>
    </ModalWrapper>
  );
}

function ConfirmDeactivateModal({ onClose, onConfirm, loading }) {
  const { t } = useTranslation();
  return (
    <ModalWrapper onClose={onClose}>
      <Title order={4} mb="xs">{t('campaigns.deactivate.title')}</Title>
      <Text size="sm" c="dimmed" mb="md">
        {t('campaigns.deactivate.desc')}
      </Text>
      <Group grow>
        <Button variant="default" onClick={onClose}>{t('campaigns.common.cancel')}</Button>
        <Button color="red" onClick={onConfirm} loading={loading}>
          {loading ? t('campaigns.deactivate.deactivating') : t('campaigns.deactivate.confirm')}
        </Button>
      </Group>
    </ModalWrapper>
  );
}

// Campaign Detail Drawer

function CharCounter({ value, max }) {
  const len = (value || '').length;
  return (
    <span className={`text-[10px] ${len > max ? 'text-red-500 font-medium' : 'text-gray-400'}`}>
      {len}/{max}
    </span>
  );
}

function AdForm({ adGroups, campaignId, onSaved, onCancel, initialData, initialAdGroupId }) {
  const { t } = useTranslation();
  const { addNotification } = useNotifications();
  const [selectedAgId, setSelectedAgId] = useState(initialAdGroupId || (adGroups[0]?.id || ''));
  const [headlines, setHeadlines] = useState(initialData?.headlines || ['', '', '']);
  const [descriptions, setDescriptions] = useState(initialData?.descriptions || ['', '']);
  const [finalUrl, setFinalUrl] = useState(initialData?.final_url || '');
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  // Limit Google 3 włączonych RSA: lista reklam do wyboru, którą zastąpić.
  const [limitAds, setLimitAds] = useState(null);

  const updateHeadline = (idx, val) => {
    const h = [...headlines];
    h[idx] = val;
    setHeadlines(h);
  };
  const updateDescription = (idx, val) => {
    const d = [...descriptions];
    d[idx] = val;
    setDescriptions(d);
  };

  const handleGenerateAI = async () => {
    setGenerating(true);
    try {
      const res = await api.post(`/ads/campaigns/${campaignId}/generate-ad-copy/`);
      const h = res.data.headlines || [];
      const d = res.data.descriptions || [];
      setHeadlines(h.length > 0 ? h : headlines);
      setDescriptions(d.length > 0 ? d : descriptions);
      addNotification(t('campaigns.adForm.aiGenerated'), 'success');
    } catch (err) {
      addNotification(err.response?.data?.error || t('campaigns.adForm.aiError'), 'error');
    } finally {
      setGenerating(false);
    }
  };

  const handleSave = async (replaceAdId = null) => {
    const filteredH = headlines.filter(h => h.trim());
    const filteredD = descriptions.filter(d => d.trim());
    if (filteredH.length < 3) {
      addNotification(t('campaigns.adForm.minHeadlines'), 'error');
      return;
    }
    if (filteredD.length < 2) {
      addNotification(t('campaigns.adForm.minDescriptions'), 'error');
      return;
    }
    if (!finalUrl.trim()) {
      addNotification(t('campaigns.adForm.urlRequired'), 'error');
      return;
    }
    setSaving(true);
    try {
      await api.post(`/ads/campaigns/${campaignId}/ad-groups/${selectedAgId}/ads/create/`, {
        headlines: filteredH,
        descriptions: filteredD,
        final_url: finalUrl,
        ...(replaceAdId ? { replace_ad_id: replaceAdId } : {}),
      });
      setLimitAds(null);
      addNotification(t('campaigns.adForm.saved'), 'success');
      if (onSaved) onSaved();
    } catch (err) {
      if (err.response?.data?.limit) setLimitAds(err.response.data.enabled_ads || []);
      addNotification(err.response?.data?.error || t('campaigns.adForm.saveError'), 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-4 bg-gray-50 border border-gray-200 rounded-md space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-xs font-medium text-gray-700">{initialData ? t('campaigns.adForm.editTitle') : t('campaigns.adForm.newTitle')}</p>
        <button
          onClick={handleGenerateAI}
          disabled={generating}
          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-indigo-50 border border-indigo-200 text-xs text-indigo-700 hover:bg-indigo-100 disabled:opacity-50 transition"
        >
          {generating ? <CircleNotch className="w-3 h-3 animate-spin" /> : <Sparkle className="w-3 h-3" />}
          {generating ? t('campaigns.adForm.generating') : t('campaigns.adForm.generateAI')}
        </button>
      </div>

      {!initialAdGroupId && (
        <div>
          <label className="block text-xs text-gray-500 mb-1">{t('campaigns.adForm.adGroup')}</label>
          <select
            value={selectedAgId}
            onChange={e => setSelectedAgId(e.target.value)}
            className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 bg-white"
          >
            {adGroups.map(ag => (
              <option key={ag.id} value={ag.id}>{ag.name}</option>
            ))}
          </select>
        </div>
      )}

      <div>
        <div className="flex items-center justify-between mb-1">
          <label className="text-xs text-gray-500">{t('campaigns.adForm.headlinesLabel')}</label>
          {headlines.length < 15 && (
            <button onClick={() => setHeadlines([...headlines, ''])} className="text-[10px] text-indigo-600 hover:text-indigo-700">
              {t('campaigns.adForm.add')}
            </button>
          )}
        </div>
        <div className="space-y-1.5">
          {headlines.map((h, i) => (
            <div key={i} className="flex items-center gap-2">
              <input
                type="text"
                value={h}
                onChange={e => updateHeadline(i, e.target.value)}
                maxLength={30}
                placeholder={t('campaigns.adForm.headlinePlaceholder', { n: i + 1 })}
                className={`flex-1 px-2.5 py-1 text-sm border rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 ${h.length > 30 ? 'border-red-300' : 'border-gray-200'}`}
              />
              <CharCounter value={h} max={30} />
              {headlines.length > 3 && (
                <button onClick={() => setHeadlines(headlines.filter((_, j) => j !== i))} className="text-gray-400 hover:text-red-500 transition">
                  <X className="w-3 h-3" />
                </button>
              )}
            </div>
          ))}
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-1">
          <label className="text-xs text-gray-500">{t('campaigns.adForm.descriptionsLabel')}</label>
          {descriptions.length < 4 && (
            <button onClick={() => setDescriptions([...descriptions, ''])} className="text-[10px] text-indigo-600 hover:text-indigo-700">
              {t('campaigns.adForm.add')}
            </button>
          )}
        </div>
        <div className="space-y-1.5">
          {descriptions.map((d, i) => (
            <div key={i} className="flex items-center gap-2">
              <input
                type="text"
                value={d}
                onChange={e => updateDescription(i, e.target.value)}
                maxLength={90}
                placeholder={t('campaigns.adForm.descriptionPlaceholder', { n: i + 1 })}
                className={`flex-1 px-2.5 py-1 text-sm border rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 ${d.length > 90 ? 'border-red-300' : 'border-gray-200'}`}
              />
              <CharCounter value={d} max={90} />
              {descriptions.length > 2 && (
                <button onClick={() => setDescriptions(descriptions.filter((_, j) => j !== i))} className="text-gray-400 hover:text-red-500 transition">
                  <X className="w-3 h-3" />
                </button>
              )}
            </div>
          ))}
        </div>
      </div>

      <div>
        <label className="block text-xs text-gray-500 mb-1">{t('campaigns.adForm.finalUrl')}</label>
        <div className="flex items-center gap-2">
          <Globe className="w-3.5 h-3.5 text-gray-400 shrink-0" />
          <input
            type="url"
            value={finalUrl}
            onChange={e => setFinalUrl(e.target.value)}
            placeholder="https://example.com"
            className="flex-1 px-2.5 py-1 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
          />
        </div>
      </div>

      {limitAds && limitAds.length > 0 && (
        <div className="p-3 border border-amber-200 bg-amber-50 rounded-md space-y-2">
          <p className="text-xs text-amber-800">{t('campaigns.adForm.limitChoose')}</p>
          {limitAds.map(ad => (
            <div key={ad.ad_id} className="flex items-center justify-between gap-3">
              <p className="text-xs text-gray-700 truncate">{(ad.headlines || []).join(' | ') || ad.ad_id}</p>
              <button
                onClick={() => handleSave(ad.ad_id)}
                disabled={saving}
                className="shrink-0 px-2.5 py-1 rounded-md border border-amber-300 bg-white text-xs text-amber-800 hover:bg-amber-100 disabled:opacity-50 transition"
              >
                {t('campaigns.adForm.replaceThis')}
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="flex items-center gap-2 pt-1">
        <button
          onClick={() => handleSave()}
          disabled={saving}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-[#1D4ED8] text-xs text-white hover:bg-[#1e40af] disabled:opacity-50 transition"
        >
          {saving ? <><CircleNotch className="w-3 h-3 animate-spin" /> {t('campaigns.common.saving')}</> : <><FloppyDisk className="w-3 h-3" /> {t('campaigns.common.save')}</>}
        </button>
        {onCancel && (
          <button onClick={onCancel} className="px-3 py-1.5 rounded-md border border-gray-200 text-xs text-gray-700 hover:bg-gray-50 transition">
            {t('campaigns.common.cancel')}
          </button>
        )}
      </div>
    </div>
  );
}

function KeywordCpcEditor({ keyword, currentBid, bidTooLow, campaignId, onUpdated, addNotification, inherited, currency = 'PLN' }) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);

  const handleStartEdit = () => {
    const numBid = parseFloat(currentBid) || 0;
    setValue(numBid > 0 ? numBid.toFixed(2) : '');
    setEditing(true);
  };

  const handleSave = async () => {
    const newBid = parseFloat(value);
    if (!newBid || newBid <= 0) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      await api.post(`/ads/campaigns/${campaignId}/keywords/${keyword.id}/`, {
        action: 'update_bid',
        bid: newBid.toFixed(2),
      });
      addNotification(t('campaigns.keywordCpc.changed', { text: keyword.text, bid: newBid.toFixed(2), cur: currencySymbol(currency) }), 'success');
      setEditing(false);
      if (onUpdated) onUpdated();
    } catch (err) {
      addNotification(err.response?.data?.error || t('campaigns.keywordCpc.changeError'), 'error');
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    return (
      <div className="flex items-center gap-1 justify-end">
        <input
          type="number"
          step="0.01"
          min="0.01"
          value={value}
          onChange={e => setValue(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') handleSave(); if (e.key === 'Escape') setEditing(false); }}
          className="w-16 text-right text-xs border border-indigo-300 rounded px-1.5 py-0.5 focus:outline-none focus:ring-1 focus:ring-indigo-500"
          autoFocus
          disabled={saving}
        />
        <span className="text-[10px] text-gray-400">{currencySymbol(currency)}</span>
        {saving ? (
          <CircleNotch className="w-3 h-3 animate-spin text-indigo-500" />
        ) : (
          <button onClick={handleSave} className="text-indigo-600 hover:text-indigo-700"><Check className="w-3 h-3" /></button>
        )}
        <button onClick={() => setEditing(false)} className="text-gray-400 hover:text-gray-600"><X className="w-3 h-3" /></button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1 justify-end">
      <button
        onClick={handleStartEdit}
        className={`text-sm hover:underline ${bidTooLow ? 'text-red-600 font-medium' : inherited ? 'text-gray-400 italic' : 'text-gray-700'}`}
        title={inherited ? t('campaigns.keywordCpc.inheritedTitle') : t('campaigns.keywordCpc.editTitle')}
      >
        {currentBid}
        {bidTooLow && <span className="text-[10px] text-red-500 ml-1">!</span>}
      </button>
      {inherited && <Tooltip label={t('campaigns.keywordCpc.inheritedTooltip')} position="left" withArrow><span className="text-[9px] text-gray-400 bg-gray-100 rounded px-1">AG</span></Tooltip>}
    </div>
  );
}

// Shared keyword-detail modal, used by both the Keywords tab and the Search
// Terms tab (via the loupe next to each triggering keyword). Fetches its own
// metrics so callers only pass the keyword object.
function KeywordDetailModal({ keyword, campaignId, onClose, addNotification, currency = 'PLN' }) {
  const { t } = useTranslation();
  const [detailData, setDetailData] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailPeriod, setDetailPeriod] = useState('30');
  const [exportLoading, setExportLoading] = useState(null);
  const matchTypeLabels = { BROAD: t('campaigns.matchType.broad'), PHRASE: t('campaigns.matchType.phrase'), EXACT: t('campaigns.matchType.exact') };

  useEffect(() => { setDetailPeriod('30'); }, [keyword?.id]);

  useEffect(() => {
    if (!keyword) { setDetailData(null); return; }
    if (!keyword.google_criterion_id) { setDetailData({ status: 'error', message: t('campaigns.keywordDetail.noGoogleData') }); return; }
    setDetailLoading(true);
    api.get(`/ads/campaigns/${campaignId}/keywords/${keyword.id}/metrics/?days=${detailPeriod}`)
      .then(res => setDetailData(res.data))
      .catch(() => setDetailData({ status: 'error', message: t('campaigns.keywordDetail.fetchError') }))
      .finally(() => setDetailLoading(false));
  }, [keyword?.id, keyword, detailPeriod, campaignId]);

  const handleExportKeyword = async (format) => {
    if (!keyword || detailData?.status !== 'success') return;
    setExportLoading(format);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(
        apiUrl(`/ads/campaigns/${campaignId}/keywords/${keyword.id}/export/?days=${detailPeriod}&export_format=${format}`),
        { headers: { Authorization: `Token ${token}` } }
      );
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `${keyword.text.replace(/\s+/g, '_')}_${detailPeriod}d.${format === 'pdf' ? 'pdf' : 'xlsx'}`;
      a.click();
    } catch {
      addNotification && addNotification(t('campaigns.keywordDetail.reportError'), 'error');
    } finally {
      setExportLoading(null);
    }
  };

  return (
    <Modal opened={!!keyword} onClose={onClose} title={keyword ? `${keyword.text}` : ''} centered size="lg">
      {keyword && (
        <Stack gap="sm">
          <Group justify="space-between">
            <Group gap="xs">
              <Badge size="sm" variant="light" color={keyword.status === 'active' ? 'green' : 'yellow'}>
                {keyword.status === 'active' ? t('campaigns.keywordDetail.active') : t('campaigns.keywordDetail.paused')}
              </Badge>
              <Badge size="sm" variant="light" color="gray">
                {matchTypeLabels[keyword.match_type] || keyword.match_type}
              </Badge>
              {keyword.quality_score > 0 && (
                <Badge size="sm" variant="light" color="blue">QS: {keyword.quality_score}/10</Badge>
              )}
            </Group>
            <Select
              value={detailPeriod}
              onChange={(v) => setDetailPeriod(v)}
              data={[
                { value: '7', label: t('campaigns.period.last7') },
                { value: '14', label: t('campaigns.period.last14') },
                { value: '30', label: t('campaigns.period.last30') },
                { value: '90', label: t('campaigns.period.last90') },
              ]}
              size="xs"
              w={160}
            />
          </Group>

          {detailLoading ? (
            <Flex align="center" justify="center" py="xl" gap="xs">
              <CircleNotch className="w-4 h-4 animate-spin text-gray-400" />
              <Text size="sm" c="dimmed">{t('campaigns.keywordDetail.fetching')}</Text>
            </Flex>
          ) : detailData?.status === 'error' ? (
            <Alert color="orange" variant="light">
              <Text size="xs">{detailData.message}</Text>
            </Alert>
          ) : detailData?.status === 'success' ? (
            <>
              <SimpleGrid cols={3} spacing="xs">
                {[
                  { label: t('campaigns.metrics.cost'), value: formatMoney(detailData.totals.cost, currency) },
                  { label: t('campaigns.metrics.clicks'), value: detailData.totals.clicks.toLocaleString('pl-PL') },
                  { label: t('campaigns.metrics.impressions'), value: detailData.totals.impressions.toLocaleString('pl-PL') },
                  { label: t('campaigns.metrics.conversions'), value: Math.round(detailData.totals.conversions) },
                  { label: t('campaigns.metrics.ctr'), value: `${detailData.totals.ctr}%` },
                  { label: t('campaigns.metrics.avgCpc'), value: formatMoney(detailData.totals.cpc, currency) },
                ].map(s => (
                  <Paper key={s.label} p="xs" bg="gray.0" radius="sm" ta="center">
                    <Text size="xs" c="dimmed">{s.label}</Text>
                    <Text size="sm" fw={600} ff="monospace">{s.value}</Text>
                  </Paper>
                ))}
              </SimpleGrid>

              {detailData.daily.length > 1 && (
                <Paper p="sm" bg="gray.0" radius="sm">
                  <Text size="xs" fw={500} c="dimmed" mb="xs">{t('campaigns.keywordDetail.dailyChartTitle')}</Text>
                  <div style={{ width: '100%', height: 180 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <ComposedChart data={detailData.daily} margin={{ top: 4, right: 4, bottom: 4, left: 4 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={d => d.slice(5)} />
                        <YAxis yAxisId="cost" orientation="left" tick={{ fontSize: 10 }} tickFormatter={v => `${v} ${currencySymbol(currency)}`} />
                        <YAxis yAxisId="clicks" orientation="right" tick={{ fontSize: 10 }} />
                        <RTooltip
                          contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e5e7eb' }}
                          formatter={(v, name) => [name === t('campaigns.metrics.cost') ? formatMoney(v, currency) : v, name]}
                        />
                        <Bar yAxisId="clicks" dataKey="clicks" name={t('campaigns.metrics.clicks')} fill="#1D4ED8" radius={[3,3,0,0]} opacity={0.2} />
                        <Bar yAxisId="clicks" dataKey="conversions" name={t('campaigns.metrics.conversions')} fill="#10b981" radius={[3,3,0,0]} opacity={0.6} />
                        <Line yAxisId="cost" type="monotone" dataKey="cost" name={t('campaigns.metrics.cost')} stroke="#1D4ED8" strokeWidth={2} dot={false} />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </div>
                </Paper>
              )}

              {detailData.daily.length > 0 && (
                <Paper p="sm" bg="gray.0" radius="sm" style={{ maxHeight: 200, overflowY: 'auto' }}>
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left text-[10px] font-medium text-gray-500 py-1 pr-2">{t('campaigns.metrics.date')}</th>
                        <th className="text-right text-[10px] font-medium text-gray-500 py-1 px-1">{t('campaigns.metrics.clicksShort')}</th>
                        <th className="text-right text-[10px] font-medium text-gray-500 py-1 px-1">{t('campaigns.metrics.impressionsShort')}</th>
                        <th className="text-right text-[10px] font-medium text-gray-500 py-1 px-1">{t('campaigns.metrics.cost')}</th>
                        <th className="text-right text-[10px] font-medium text-gray-500 py-1 px-1">{t('campaigns.metrics.cpc')}</th>
                        <th className="text-right text-[10px] font-medium text-gray-500 py-1 px-1">{t('campaigns.metrics.ctr')}</th>
                        <th className="text-right text-[10px] font-medium text-gray-500 py-1 pl-1">{t('campaigns.metrics.conversionsShort')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...detailData.daily].reverse().map(d => (
                        <tr key={d.date} className="border-b border-gray-100">
                          <td className="text-[11px] text-gray-700 py-1 pr-2">{d.date}</td>
                          <td className="text-right text-[11px] text-gray-700 py-1 px-1">{d.clicks}</td>
                          <td className="text-right text-[11px] text-gray-700 py-1 px-1">{d.impressions}</td>
                          <td className="text-right text-[11px] text-gray-700 py-1 px-1">{formatMoney(d.cost, currency)}</td>
                          <td className="text-right text-[11px] text-gray-500 py-1 px-1">{d.cpc > 0 ? `${d.cpc.toFixed(2)}` : '-'}</td>
                          <td className="text-right text-[11px] text-gray-500 py-1 px-1">{d.ctr > 0 ? `${d.ctr}%` : '-'}</td>
                          <td className="text-right text-[11px] text-gray-700 py-1 pl-1">{d.conversions > 0 ? Math.round(d.conversions) : '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Paper>
              )}

              {detailData.conversion_breakdown && detailData.conversion_breakdown.length > 0 && (
                <Paper p="sm" bg="gray.0" radius="sm">
                  <Text size="xs" fw={500} c="dimmed" mb="xs">{t('campaigns.keywordDetail.conversionBreakdown')}</Text>
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left text-[10px] font-medium text-gray-500 py-1 pr-2">{t('campaigns.keywordDetail.conversionType')}</th>
                        <th className="text-right text-[10px] font-medium text-gray-500 py-1 px-1">{t('campaigns.keywordDetail.conversionCount')}</th>
                        <th className="text-right text-[10px] font-medium text-gray-500 py-1 pl-1">{t('campaigns.keywordDetail.conversionValue')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detailData.conversion_breakdown.map((cb, i) => (
                        <tr key={i} className="border-b border-gray-100">
                          <td className="text-[11px] text-gray-700 py-1.5 pr-2">{cb.name}</td>
                          <td className="text-right text-[11px] text-gray-700 py-1.5 px-1 font-medium">{cb.conversions}</td>
                          <td className="text-right text-[11px] text-gray-500 py-1.5 pl-1">{cb.value > 0 ? formatMoney(cb.value, currency) : '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Paper>
              )}

              {detailData.daily.length === 0 && (!detailData.conversion_breakdown || detailData.conversion_breakdown.length === 0) && (
                <Text size="sm" c="dimmed" ta="center" py="md">{t('campaigns.keywordDetail.noDataPeriod')}</Text>
              )}

              {detailData.daily.length > 0 && (
                <Group gap="xs">
                  <Button variant="light" color="brand" size="xs" leftSection={<FileText size={12} />}
                    loading={exportLoading === 'pdf'} onClick={() => handleExportKeyword('pdf')}>
                    {t('campaigns.keywordDetail.exportPdf')}
                  </Button>
                  <Button variant="light" color="brand" size="xs" leftSection={<FileText size={12} />}
                    loading={exportLoading === 'xlsx'} onClick={() => handleExportKeyword('xlsx')}>
                    {t('campaigns.keywordDetail.exportExcel')}
                  </Button>
                </Group>
              )}
            </>
          ) : null}
        </Stack>
      )}
    </Modal>
  );
}

function SearchTermsTab({ campaignId, googleCampaignId, addNotification, onExcluded, keywords = [], currency = 'PLN' }) {
  const { t: tr } = useTranslation();
  const [terms, setTerms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [excluding, setExcluding] = useState(null);
  const [sortCol, setSortCol] = useState('cost');
  const [sortDir, setSortDir] = useState('desc');
  const [selected, setSelected] = useState(new Set());
  const [bulkExcluding, setBulkExcluding] = useState(false);
  const [kwFilter, setKwFilter] = useState('');
  const [detailKeyword, setDetailKeyword] = useState(null);
  const lastClickedSt = React.useRef(null);

  // Resolve a triggering keyword to the full keyword object (id,
  // google_criterion_id, status...) so the loupe can open the detail modal.
  // Prefer the criterion id: the same keyword text can exist as several keywords
  // (e.g. BROAD + PHRASE) with different metrics, so matching on text alone can
  // open the wrong sibling.
  const { kwByText, kwByCrit } = React.useMemo(() => {
    const byText = {}, byCrit = {};
    (keywords || []).forEach(k => {
      if (k.text) byText[k.text.toLowerCase()] = k;
      if (k.google_criterion_id) byCrit[String(k.google_criterion_id)] = k;
    });
    return { kwByText: byText, kwByCrit: byCrit };
  }, [keywords]);
  const resolveKw = (t) =>
    (t.keyword_criterion_id && kwByCrit[String(t.keyword_criterion_id)])
    || (t.keyword && kwByText[t.keyword.toLowerCase()])
    || null;

  // Distinct triggering keywords across all terms, for the filter dropdown.
  const allKeywords = React.useMemo(() => {
    const s = new Set();
    terms.forEach(t => (t.keywords && t.keywords.length ? t.keywords : (t.keyword ? [t.keyword] : [])).forEach(k => s.add(k)));
    return [...s].sort((a, b) => a.localeCompare(b, 'pl'));
  }, [terms]);

  const visibleTerms = kwFilter
    ? terms.filter(t => (t.keywords && t.keywords.length ? t.keywords : (t.keyword ? [t.keyword] : [])).includes(kwFilter))
    : terms;

  const excludableTerms = visibleTerms.filter(t => !t.excluded);
  const toggleSelect = (term, e) => {
    const allTerms = excludableTerms.map(t => t.search_term);
    if (e?.shiftKey && lastClickedSt.current !== null) {
      const lastIdx = allTerms.indexOf(lastClickedSt.current);
      const curIdx = allTerms.indexOf(term);
      if (lastIdx !== -1 && curIdx !== -1) {
        const [from, to] = [Math.min(lastIdx, curIdx), Math.max(lastIdx, curIdx)];
        setSelected(prev => {
          const next = new Set(prev);
          for (let i = from; i <= to; i++) next.add(allTerms[i]);
          return next;
        });
        lastClickedSt.current = term;
        return;
      }
    }
    lastClickedSt.current = term;
    setSelected(prev => {
      const next = new Set(prev);
      next.has(term) ? next.delete(term) : next.add(term);
      return next;
    });
  };
  const toggleAll = () => {
    if (selected.size === excludableTerms.length) setSelected(new Set());
    else setSelected(new Set(excludableTerms.map(t => t.search_term)));
  };

  const handleBulkExclude = async () => {
    if (selected.size === 0) return;
    setBulkExcluding(true);
    let success = 0;
    for (const term of selected) {
      try {
        await api.post(`/ads/campaigns/${campaignId}/negative-keywords/add/`, { text: term, match_type: 'EXACT' });
        success++;
      } catch {}
    }
    setTerms(prev => prev.map(t => selected.has(t.search_term) ? { ...t, excluded: true } : t));
    setSelected(new Set());
    setBulkExcluding(false);
    addNotification(tr('campaigns.searchTerms.bulkExcluded', { count: success }), 'success');
    if (onExcluded) onExcluded();
  };

  useEffect(() => {
    if (!googleCampaignId) { setLoading(false); return; }
    setLoading(true);
    api.get(`/ads/campaigns/${campaignId}/search-terms/`)
      .then(res => setTerms(res.data.search_terms || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [campaignId, googleCampaignId]);

  const handleExclude = async (term) => {
    setExcluding(term);
    try {
      const res = await api.post(`/ads/campaigns/${campaignId}/negative-keywords/add/`, {
        text: term, match_type: 'EXACT',
      });
      setTerms(prev => prev.map(t => t.search_term === term
        ? { ...t, excluded: true, negative_keyword_id: res.data?.id } : t));
      addNotification(tr('campaigns.searchTerms.excluded', { term }), 'success');
      if (onExcluded) onExcluded();
    } catch {
      addNotification(tr('campaigns.searchTerms.excludeError'), 'error');
    } finally {
      setExcluding(null);
    }
  };

  const handleUnexclude = async (term, nkId) => {
    if (!nkId) { addNotification(tr('campaigns.searchTerms.noExclusionId'), 'error'); return; }
    setExcluding(term);
    try {
      await api.delete(`/ads/campaigns/${campaignId}/negative-keywords/${nkId}/remove/`);
      setTerms(prev => prev.map(t => t.search_term === term
        ? { ...t, excluded: false, negative_keyword_id: null } : t));
      addNotification(tr('campaigns.searchTerms.restored', { term }), 'success');
      if (onExcluded) onExcluded();
    } catch {
      addNotification(tr('campaigns.searchTerms.restoreError'), 'error');
    } finally {
      setExcluding(null);
    }
  };

  const sorted = [...visibleTerms].sort((a, b) => {
    const dir = sortDir === 'asc' ? 1 : -1;
    if (sortCol === 'search_term') return dir * a.search_term.localeCompare(b.search_term, 'pl');
    if (sortCol === 'keyword') return dir * (a.keyword || '').localeCompare(b.keyword || '', 'pl');
    return dir * ((a[sortCol] || 0) - (b[sortCol] || 0));
  });

  const handleSort = (col) => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir(col === 'search_term' ? 'asc' : 'desc'); }
  };

  if (!googleCampaignId) {
    return (
      <div className="text-center py-12">
        <Text size="sm" c="dimmed">{tr('campaigns.searchTerms.notConnected')}</Text>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 gap-2">
        <CircleNotch className="w-4 h-4 animate-spin text-gray-400" />
        <Text size="sm" c="dimmed">{tr('campaigns.searchTerms.loadingReport')}</Text>
      </div>
    );
  }

  if (terms.length === 0) {
    return (
      <div className="text-center py-12">
        <MagnifyingGlass className="w-8 h-8 text-gray-300 mx-auto mb-2" />
        <Text size="sm" c="dimmed">{tr('campaigns.searchTerms.noData')}</Text>
        <Text size="xs" c="dimmed">{tr('campaigns.searchTerms.noDataHint')}</Text>
      </div>
    );
  }

  const totalCost = visibleTerms.reduce((s, t) => s + (t.cost || 0), 0);
  const wastedTerms = terms.filter(t => t.clicks > 0 && t.conversions === 0 && !t.excluded);
  const wastedCost = wastedTerms.reduce((s, t) => s + (t.cost || 0), 0);

  const cols = [
    { key: 'search_term', label: tr('campaigns.searchTerms.colTerm'), align: 'left' },
    { key: 'keyword', label: tr('campaigns.searchTerms.colTriggeredBy'), align: 'left' },
    { key: 'clicks', label: tr('campaigns.metrics.clicksShort') },
    { key: 'impressions', label: tr('campaigns.metrics.impressionsShort') },
    { key: 'ctr', label: tr('campaigns.metrics.ctr') },
    { key: 'cost', label: tr('campaigns.metrics.cost') },
    { key: 'conversions', label: tr('campaigns.metrics.conversionsShort') },
  ];
  const matchLabel = { BROAD: tr('campaigns.searchTerms.matchBroad'), PHRASE: tr('campaigns.searchTerms.matchPhrase'), EXACT: tr('campaigns.searchTerms.matchExact') };

  return (
    <div>
      {wastedCost > 0 && (
        <Alert color="orange" mb="sm" icon={<WarningCircle size={16} />}>
          <Text size="xs">
            <Text span fw={600}>{tr('campaigns.searchTerms.wastedCount', { count: wastedTerms.length })}</Text> {tr('campaigns.searchTerms.wastedMid')} <Text span fw={600}>{formatMoney(wastedCost, currency)}</Text> {tr('campaigns.searchTerms.wastedTail')}
          </Text>
        </Alert>
      )}

      {/* Filter by triggering keyword */}
      {allKeywords.length > 0 && (
        <Group gap="xs" mb="sm" align="center">
          <Text size="xs" c="dimmed">{tr('campaigns.searchTerms.filterByKeyword')}</Text>
          <Select
            size="xs" w={260} clearable searchable
            placeholder={tr('campaigns.searchTerms.allKeywords')}
            data={allKeywords.map(k => ({ value: k, label: k }))}
            value={kwFilter || null}
            onChange={(v) => setKwFilter(v || '')}
            comboboxProps={{ withinPortal: true }}
          />
          {kwFilter && <Text size="xs" c="dimmed">{tr('campaigns.searchTerms.termsTriggeredBy', { count: visibleTerms.length, keyword: kwFilter })}</Text>}
        </Group>
      )}

      {/* Bulk action bar */}
      {selected.size > 0 && (
        <div className="flex items-center gap-3 py-2 px-3 bg-red-50 border border-red-200 rounded-lg mb-2">
          <Text size="xs" fw={600} c="red.7">{tr('campaigns.searchTerms.selectedCount', { count: selected.size })}</Text>
          <div className="flex-1" />
          <Button size="xs" color="red" variant="filled" leftSection={<Minus size={12} weight="bold" />}
            onClick={handleBulkExclude} loading={bulkExcluding}>
            {tr('campaigns.searchTerms.excludeN', { count: selected.size })}
          </Button>
          <button onClick={() => setSelected(new Set())} className="text-xs text-gray-400 hover:text-gray-600 ml-1">
            <X size={14} />
          </button>
        </div>
      )}

      <EdgeScroll>
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-200">
              <th className="py-2 px-1 w-8">
                <input
                  type="checkbox"
                  checked={excludableTerms.length > 0 && selected.size === excludableTerms.length}
                  onChange={toggleAll}
                  className="rounded border-gray-300 text-red-500 focus:ring-red-500 w-3.5 h-3.5 cursor-pointer"
                />
              </th>
              {cols.map(col => (
                <th
                  key={col.key}
                  className={`text-${col.align || 'right'} text-xs font-medium text-gray-500 py-2 ${col.align === 'left' ? 'pr-4' : 'px-2'} cursor-pointer select-none hover:text-gray-700 transition`}
                  onClick={() => handleSort(col.key)}
                >
                  <span className="inline-flex items-center gap-0.5">
                    {col.label}
                    {sortCol === col.key && (sortDir === 'asc' ? <CaretUp className="w-3 h-3" /> : <CaretDown className="w-3 h-3" />)}
                  </span>
                </th>
              ))}
              <th className="w-16"></th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((t, i) => {
              const isWasted = t.clicks > 0 && t.conversions === 0 && !t.excluded;
              return (
                <tr key={i} className={`border-b border-gray-100 ${t.excluded ? 'bg-gray-50/60 text-gray-400' : ''} ${selected.has(t.search_term) ? 'bg-red-50/50' : ''}`}>
                  <td className="py-2 px-1 w-8">
                    {!t.excluded && (
                      <input
                        type="checkbox"
                        checked={selected.has(t.search_term)}
                        onChange={(e) => { e.stopPropagation(); toggleSelect(t.search_term, e.nativeEvent); }}
                        className="rounded border-gray-300 text-red-500 focus:ring-red-500 w-3.5 h-3.5 cursor-pointer"
                      />
                    )}
                  </td>
                  <td className="py-2 pr-4">
                    <span className={`text-sm ${isWasted ? 'text-orange-700' : 'text-gray-900'}`}>{t.search_term}</span>
                    {t.excluded && <span className="ml-1.5 text-[10px] text-red-500 bg-red-50 rounded px-1">{tr('campaigns.searchTerms.excludedBadge')}</span>}
                  </td>
                  <td className="py-2 pr-4">
                    {t.keyword ? (
                      <span className="inline-flex items-center gap-1">
                        <button
                          onClick={() => setKwFilter(t.keyword)}
                          title={tr('campaigns.searchTerms.showAllTriggered')}
                          className="text-xs text-indigo-600 hover:underline">
                          {t.keyword}
                        </button>
                        {resolveKw(t) && (
                          <button
                            onClick={() => setDetailKeyword(resolveKw(t))}
                            title={tr('campaigns.searchTerms.keywordDetails')}
                            className="text-gray-400 hover:text-indigo-600 transition">
                            <MagnifyingGlass className="w-3.5 h-3.5" />
                          </button>
                        )}
                        {t.match_type && <span className="text-[10px] text-gray-400 bg-gray-100 rounded px-1">{matchLabel[t.match_type] || t.match_type.toLowerCase()}</span>}
                        {t.keywords && t.keywords.length > 1 && (
                          <span className="text-[10px] text-gray-400" title={t.keywords.join(', ')}>+{t.keywords.length - 1}</span>
                        )}
                      </span>
                    ) : <span className="text-xs text-gray-300">-</span>}
                  </td>
                  <td className="text-right py-2 px-2 text-sm text-gray-700">{t.clicks}</td>
                  <td className="text-right py-2 px-2 text-sm text-gray-700">{t.impressions.toLocaleString('pl-PL')}</td>
                  <td className="text-right py-2 px-2 text-sm text-gray-500">{t.ctr.toFixed(1)}%</td>
                  <td className="text-right py-2 px-2 text-sm text-gray-700">{formatMoney(t.cost, currency)}</td>
                  <td className="text-right py-2 px-2 text-sm text-gray-700">{t.conversions > 0 ? t.conversions : '-'}</td>
                  <td className="text-right py-2 px-1">
                    {!t.excluded ? (
                      <button
                        onClick={() => handleExclude(t.search_term)}
                        disabled={excluding === t.search_term}
                        className="inline-flex items-center gap-1 text-[11px] text-red-500 hover:text-white hover:bg-red-500 border border-red-200 hover:border-red-500 px-2 py-0.5 rounded-md transition disabled:opacity-50"
                      >
                        {excluding === t.search_term ? <CircleNotch className="w-3 h-3 animate-spin" /> : <Minus className="w-3 h-3" weight="bold" />}
                        {tr('campaigns.searchTerms.exclude')}
                      </button>
                    ) : (
                      <button
                        onClick={() => handleUnexclude(t.search_term, t.negative_keyword_id)}
                        disabled={excluding === t.search_term}
                        title={tr('campaigns.searchTerms.removeExclusionTitle')}
                        className="inline-flex items-center gap-1 text-[11px] text-gray-500 hover:text-white hover:bg-gray-600 border border-gray-200 hover:border-gray-600 px-2 py-0.5 rounded-md transition disabled:opacity-50"
                      >
                        {excluding === t.search_term ? <CircleNotch className="w-3 h-3 animate-spin" /> : <ArrowCounterClockwise className="w-3 h-3" weight="bold" />}
                        {tr('campaigns.searchTerms.restore')}
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </EdgeScroll>
      <Text size="xs" c="dimmed" mt="sm">{tr('campaigns.searchTerms.summary', { cost: totalCost.toFixed(2), count: visibleTerms.length, cur: currencySymbol(currency) })}{kwFilter ? tr('campaigns.searchTerms.summaryFilter', { keyword: kwFilter }) : ''}</Text>
      <Group gap={4} mt={4} align="center" wrap="nowrap">
        <WarningCircle size={13} className="text-gray-400" style={{ flexShrink: 0 }} />
        <Text size="xs" c="dimmed">
          {tr('campaigns.searchTerms.rareNote')}
        </Text>
      </Group>

      <KeywordDetailModal keyword={detailKeyword} campaignId={campaignId}
        onClose={() => setDetailKeyword(null)} addNotification={addNotification} currency={currency} />
    </div>
  );
}

function NegativeKeywordsSection({ negativeKeywords: localNegatives, campaignId, onChanged }) {
  const { t } = useTranslation();
  const [newText, setNewText] = useState('');
  const [newMatchType, setNewMatchType] = useState('BROAD');
  const [adding, setAdding] = useState(false);
  const [removing, setRemoving] = useState(null);
  // Lista z Google Ads (kampania + grupy reklam + listy wykluczeń); do czasu
  // jej pobrania pokazujemy lokalne wykluczenia dodane w Droidzie.
  const [liveNegatives, setLiveNegatives] = useState(null);
  const [liveError, setLiveError] = useState('');
  const [loadingLive, setLoadingLive] = useState(false);
  const [filter, setFilter] = useState('');

  const fetchNegatives = useCallback(() => {
    setLoadingLive(true);
    api.get(`/ads/campaigns/${campaignId}/negative-keywords/`)
      .then(res => {
        setLiveNegatives(res.data.negative_keywords || []);
        setLiveError(res.data.live === false && res.data.error ? res.data.error : '');
      })
      .catch(() => setLiveNegatives(null))
      .finally(() => setLoadingLive(false));
  }, [campaignId]);

  useEffect(() => { fetchNegatives(); }, [fetchNegatives]);

  const negativeKeywords = liveNegatives
    || localNegatives.map(nk => ({ ...nk, level: 'campaign', removable: true }));
  const q = filter.trim().toLowerCase();
  const visible = q
    ? negativeKeywords.filter(nk => nk.text.toLowerCase().includes(q)
        || (nk.ad_group_name || '').toLowerCase().includes(q)
        || (nk.list_name || '').toLowerCase().includes(q))
    : negativeKeywords;

  const refresh = () => { fetchNegatives(); onChanged(); };

  const handleAdd = async () => {
    if (!newText.trim()) return;
    setAdding(true);
    try {
      await api.post(`/ads/campaigns/${campaignId}/negative-keywords/add/`, {
        text: newText.trim(), match_type: newMatchType,
      });
      setNewText('');
      refresh();
    } catch (err) {
      alert(err.response?.data?.error || t('campaigns.common.error'));
    } finally {
      setAdding(false);
    }
  };

  const handleRemove = async (nkId) => {
    setRemoving(nkId);
    try {
      await api.delete(`/ads/campaigns/${campaignId}/negative-keywords/${nkId}/remove/`);
      refresh();
    } catch {
    } finally {
      setRemoving(null);
    }
  };

  const levelLabel = (nk) => {
    if (nk.level === 'ad_group') return t('campaigns.negatives.levelAdGroup', { name: nk.ad_group_name });
    if (nk.level === 'shared') return t('campaigns.negatives.levelShared', { name: nk.list_name });
    return t('campaigns.negatives.levelCampaign');
  };
  const sourceLabel = (nk) => {
    if (nk.source === 'agent' || nk.source === 'agent_intent') return t('campaigns.negatives.sourceAgent');
    if (nk.source === 'google') return t('campaigns.negatives.sourceGoogle');
    return t('campaigns.negatives.sourceManual');
  };

  return (
    <div className="mt-6">
      <div className="flex items-center gap-2 mb-2">
        <Minus size={14} weight="bold" className="text-red-500" />
        <p className="text-sm font-semibold text-gray-700">{t('campaigns.negatives.title', { count: negativeKeywords.length })}</p>
        {loadingLive && <Loader size={12} />}
        {negativeKeywords.length > 10 && (
          <TextInput
            placeholder={t('campaigns.negatives.filterPlaceholder')}
            size="xs"
            value={filter}
            onChange={(e) => setFilter(e.currentTarget.value)}
            className="ml-auto"
            style={{ width: 220 }}
          />
        )}
      </div>
      {liveError && (
        <Text size="xs" c="orange.7" mb={6}>{t('campaigns.negatives.liveError')}</Text>
      )}

      {negativeKeywords.length > 0 && (
        <div className="border rounded-lg overflow-auto mb-3" style={{ maxHeight: 420 }}>
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-gray-50">
              <tr className="border-b bg-gray-50">
                <th className="text-left text-xs font-medium text-gray-500 py-2 px-3">{t('campaigns.negatives.colPhrase')}</th>
                <th className="text-left text-xs font-medium text-gray-500 py-2 px-3">{t('campaigns.negatives.colMatch')}</th>
                <th className="text-left text-xs font-medium text-gray-500 py-2 px-3">{t('campaigns.negatives.colLevel')}</th>
                <th className="text-left text-xs font-medium text-gray-500 py-2 px-3">{t('campaigns.negatives.colSource')}</th>
                <th className="text-right text-xs font-medium text-gray-500 py-2 px-3"></th>
              </tr>
            </thead>
            <tbody>
              {visible.map(nk => (
                <tr key={`${nk.level}-${nk.id}-${nk.ad_group_name || nk.list_name || ''}`} className="border-b border-gray-100">
                  <td className="py-2 px-3">
                    <Text size="sm" fw={500} c="red.7">-{nk.text}</Text>
                  </td>
                  <td className="py-2 px-3">
                    <Badge size="xs" variant="light" color="gray" styles={{ root: { textTransform: 'none' } }}>
                      {nk.match_type === 'EXACT' ? t('campaigns.matchType.exact') : nk.match_type === 'PHRASE' ? t('campaigns.matchType.phrase') : t('campaigns.matchType.broad')}
                    </Badge>
                  </td>
                  <td className="py-2 px-3">
                    <Text size="xs" c="dimmed">{levelLabel(nk)}</Text>
                  </td>
                  <td className="py-2 px-3">
                    <Text size="xs" c="dimmed">{sourceLabel(nk)}</Text>
                  </td>
                  <td className="py-2 px-3 text-right">
                    {nk.removable ? (
                      <Tooltip label={t('campaigns.negatives.removeExclusion')}>
                        <ActionIcon
                          variant="subtle" color="gray" size="sm"
                          onClick={() => handleRemove(nk.id)}
                          loading={removing === nk.id}
                        >
                          <Trash size={14} />
                        </ActionIcon>
                      </Tooltip>
                    ) : (
                      <Tooltip label={t('campaigns.negatives.removeInGoogle')} multiline w={220}>
                        <Text size="xs" c="dimmed">Google Ads</Text>
                      </Tooltip>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add negative keyword form */}
      <div className="flex items-center gap-2">
        <TextInput
          placeholder={t('campaigns.negatives.placeholder')}
          size="xs"
          value={newText}
          onChange={(e) => setNewText(e.currentTarget.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
          style={{ flex: 1 }}
        />
        <Select
          data={[
            { value: 'BROAD', label: t('campaigns.matchType.broad') },
            { value: 'PHRASE', label: t('campaigns.matchType.phrase') },
            { value: 'EXACT', label: t('campaigns.matchType.exact') },
          ]}
          value={newMatchType}
          onChange={setNewMatchType}
          size="xs"
          style={{ width: 130 }}
        />
        <Button size="xs" color="red" variant="light" onClick={handleAdd} loading={adding} disabled={!newText.trim()}>
          {t('campaigns.negatives.exclude')}
        </Button>
      </div>
    </div>
  );
}

function KeywordsTab({ keywords, negativeKeywords = [], adGroups, campaignId, keywordActionLoading, handleKeywordAction, addNotification, suggestedKeywords, onSuggestedKeywordsChange, onKeywordsChanged, currency = 'PLN' }) {
  const { t } = useTranslation();
  const [volumes, setVolumes] = useState({});
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);

  // Sort state (persisted in localStorage)
  const [sortCol, setSortColRaw] = useState(() => localStorage.getItem('kw_sort_col') || 'impressions');
  const [sortDir, setSortDirRaw] = useState(() => localStorage.getItem('kw_sort_dir') || 'desc');
  const setSortCol = (v) => { setSortColRaw(v); localStorage.setItem('kw_sort_col', v); };
  const setSortDir = (v) => {
    const newVal = typeof v === 'function' ? v(sortDir) : v;
    setSortDirRaw(newVal);
    localStorage.setItem('kw_sort_dir', newVal);
  };

  // Add keyword form state
  const [newKeywordText, setNewKeywordText] = useState('');
  const [newKeywordMatchType, setNewKeywordMatchType] = useState('PHRASE');
  const [addingKeyword, setAddingKeyword] = useState(false);

  // Batch add state
  const [addingBatch, setAddingBatch] = useState(false);

  // Keyword Planner + AI long-tail ideas
  const [ideas, setIdeas] = useState(null);          // null = not generated yet
  const [ideasLoading, setIdeasLoading] = useState(false);
  const [ideasMaxCpc, setIdeasMaxCpc] = useState(null);
  const [ideasInfo, setIdeasInfo] = useState(null);  // { fits_count, cheapest_low_cpc, ai_used }
  const [ideasSelected, setIdeasSelected] = useState(new Set());

  // Bulk selection state
  const [selected, setSelected] = useState(new Set());
  const [bulkModal, setBulkModal] = useState(null); // { action: 'pause'|'enable'|'delete' }
  const [bulkLoading, setBulkLoading] = useState(false);
  const lastClickedKw = React.useRef(null);

  const toggleSelect = (id, e) => {
    const ids = keywords.map(k => k.id);
    if (e?.shiftKey && lastClickedKw.current !== null) {
      const lastIdx = ids.indexOf(lastClickedKw.current);
      const curIdx = ids.indexOf(id);
      if (lastIdx !== -1 && curIdx !== -1) {
        const [from, to] = [Math.min(lastIdx, curIdx), Math.max(lastIdx, curIdx)];
        setSelected(prev => {
          const next = new Set(prev);
          for (let i = from; i <= to; i++) next.add(ids[i]);
          return next;
        });
        lastClickedKw.current = id;
        return;
      }
    }
    lastClickedKw.current = id;
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    if (selected.size === keywords.length) setSelected(new Set());
    else setSelected(new Set(keywords.map(k => k.id)));
  };
  const selectedKeywords = keywords.filter(k => selected.has(k.id));

  // Keyword detail modal (rendered via the shared KeywordDetailModal, which
  // fetches its own metrics; we only track which keyword is open + the URL sync).
  const [detailKeyword, setDetailKeyword] = useState(null);

  // Restore keyword detail from URL on mount
  const initialKwId = useRef(new URLSearchParams(window.location.search).get('keyword'));
  useEffect(() => {
    if (initialKwId.current && keywords.length > 0 && !detailKeyword) {
      const kw = keywords.find(k => k.id === initialKwId.current);
      if (kw) setDetailKeyword(kw);
      initialKwId.current = null;
    }
  }, [keywords]);

  const openKeywordDetail = (kw) => {
    setDetailKeyword(kw);
    const params = new URLSearchParams(window.location.search);
    params.set('keyword', kw.id);
    window.history.replaceState({}, '', `${window.location.pathname}?${params}`);
  };

  const closeKeywordDetail = () => {
    setDetailKeyword(null);
    const params = new URLSearchParams(window.location.search);
    params.delete('keyword');
    window.history.replaceState({}, '', `${window.location.pathname}?${params}`);
  };

  const handleBulkAction = async () => {
    if (!bulkModal || selectedKeywords.length === 0) return;
    setBulkLoading(true);
    let success = 0;
    for (const kw of selectedKeywords) {
      try {
        await api.post(`/ads/campaigns/${campaignId}/keywords/${kw.id}/`, { action: bulkModal.action });
        success++;
      } catch {}
    }
    setBulkLoading(false);
    setBulkModal(null);
    setSelected(new Set());
    const labels = { pause: t('campaigns.keywords.bulkPaused'), enable: t('campaigns.keywords.bulkEnabled'), delete: t('campaigns.keywords.bulkDeleted') };
    addNotification(t('campaigns.keywords.bulkResult', { label: labels[bulkModal.action], count: success }), 'success');
    if (onKeywordsChanged) onKeywordsChanged();
  };

  // Auto-load volumes on mount
  useEffect(() => {
    if (keywords.length > 0 && !loaded) {
      setLoading(true);
      api.post('/ads/keyword-planner/', { keywords: keywords.map(k => k.text) })
        .then(res => {
          const v = {};
          (res.data.results || []).forEach(r => { v[r.text.toLowerCase()] = r; });
          setVolumes(v);
          setLoaded(true);
        })
        .catch(() => {})
        .finally(() => setLoading(false));
    }
  }, [keywords, loaded]);

  // Get keyword max CPC bid, with source info (individual vs ad group)
  const getKeywordBidInfo = (kw) => {
    if (parseFloat(kw.cpc_bid) > 0) return { value: formatMoney(kw.cpc_bid, currency), inherited: false };
    const ag = adGroups.find(g => (g.keywords || []).some(k => k.id === kw.id))
      || (adGroups.length === 1 ? adGroups[0] : null);
    if (ag && parseFloat(ag.cpc_bid) > 0) return { value: formatMoney(ag.cpc_bid, currency), inherited: true };
    return { value: '-', inherited: false };
  };
  const getKeywordBid = (kw) => getKeywordBidInfo(kw).value;

  const handleAddKeyword = async () => {
    const text = newKeywordText.trim();
    if (!text) return;
    setAddingKeyword(true);
    try {
      await api.post(`/ads/campaigns/${campaignId}/keywords/add/`, {
        text,
        match_type: newKeywordMatchType,
      });
      addNotification(t('campaigns.keywords.added', { text }), 'success');
      setNewKeywordText('');
      if (onKeywordsChanged) onKeywordsChanged();
    } catch (err) {
      addNotification(err.response?.data?.error || t('campaigns.keywords.addError'), 'error');
    } finally {
      setAddingKeyword(false);
    }
  };

  const handleAddSuggested = async (kw) => {
    setAddingKeyword(true);
    try {
      await api.post(`/ads/campaigns/${campaignId}/keywords/add/`, {
        text: kw.text,
        match_type: kw.match_type || 'PHRASE',
      });
      addNotification(t('campaigns.keywords.added', { text: kw.text }), 'success');
      if (onSuggestedKeywordsChange) {
        onSuggestedKeywordsChange(prev => prev.filter(k => k.text !== kw.text));
      }
      if (onKeywordsChanged) onKeywordsChanged();
    } catch (err) {
      addNotification(err.response?.data?.error || t('campaigns.keywords.addError'), 'error');
    } finally {
      setAddingKeyword(false);
    }
  };

  const handleAddAllSuggested = async () => {
    if (!suggestedKeywords || suggestedKeywords.length === 0) return;
    setAddingBatch(true);
    try {
      const kwList = suggestedKeywords.map(kw => ({
        text: kw.text,
        match_type: kw.match_type || 'PHRASE',
      }));
      await api.post(`/ads/campaigns/${campaignId}/keywords/add-batch/`, {
        keywords: kwList,
      });
      addNotification(t('campaigns.keywords.addedN', { count: kwList.length }), 'success');
      if (onSuggestedKeywordsChange) onSuggestedKeywordsChange([]);
      if (onKeywordsChanged) onKeywordsChanged();
    } catch (err) {
      addNotification(err.response?.data?.error || t('campaigns.keywords.addErrorN'), 'error');
    } finally {
      setAddingBatch(false);
    }
  };

  const handleGenerateIdeas = async () => {
    setIdeasLoading(true);
    try {
      const res = await api.post(`/ads/campaigns/${campaignId}/keyword-ideas/`, {});
      const cands = res.data?.candidates || [];
      setIdeas(cands);
      setIdeasMaxCpc(res.data?.max_cpc_limit ?? null);
      setIdeasInfo({
        fits_count: res.data?.fits_count ?? 0,
        cheapest_low_cpc: res.data?.cheapest_low_cpc ?? null,
        ai_used: res.data?.ai_used ?? false,
      });
      // Preselect the ones that fit the campaign's CPC cap (the "cheap" ones).
      setIdeasSelected(new Set(cands.filter(c => c.within_max_cpc).map(c => c.text)));
      if (cands.length === 0) {
        addNotification(t('campaigns.keywords.noIdeas'), 'info');
      }
    } catch (err) {
      addNotification(err.response?.data?.error || t('campaigns.keywords.ideasError'), 'error');
    } finally {
      setIdeasLoading(false);
    }
  };

  const toggleIdea = (text) => {
    setIdeasSelected(prev => {
      const next = new Set(prev);
      next.has(text) ? next.delete(text) : next.add(text);
      return next;
    });
  };

  const handleAddSelectedIdeas = async () => {
    if (!ideas || ideasSelected.size === 0) return;
    const chosen = ideas.filter(c => ideasSelected.has(c.text));
    setAddingBatch(true);
    try {
      await api.post(`/ads/campaigns/${campaignId}/keywords/add-batch/`, {
        keywords: chosen.map(c => ({ text: c.text, match_type: c.match_type || 'PHRASE' })),
      });
      addNotification(t('campaigns.keywords.addedLongTail', { count: chosen.length }), 'success');
      setIdeas(prev => prev.filter(c => !ideasSelected.has(c.text)));
      setIdeasSelected(new Set());
      if (onKeywordsChanged) onKeywordsChanged();
    } catch (err) {
      addNotification(err.response?.data?.error || t('campaigns.keywords.addErrorN'), 'error');
    } finally {
      setAddingBatch(false);
    }
  };

  const handleDeleteKeyword = async (kw) => {
    handleKeywordAction(kw, 'delete');
  };

  const matchTypeLabels = { BROAD: t('campaigns.matchType.broad'), PHRASE: t('campaigns.matchType.phrase'), EXACT: t('campaigns.matchType.exact') };

  return (
    <div>
      {/* Suggested keywords section */}
      {suggestedKeywords && suggestedKeywords.length > 0 && (
        <div className="border border-indigo-200 bg-indigo-50/50 rounded-lg p-3 mb-4">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-medium text-gray-900">{t('campaigns.keywords.suggestedTitle')}</p>
            <button
              onClick={handleAddAllSuggested}
              disabled={addingBatch}
              className="inline-flex items-center gap-1 text-xs px-2.5 py-1 bg-[#1D4ED8] text-white rounded-md hover:bg-[#1e40af] transition disabled:opacity-50"
            >
              {addingBatch ? (
                <><CircleNotch className="w-3 h-3 animate-spin" /> {t('campaigns.keywords.adding')}</>
              ) : (
                <><Plus className="w-3 h-3" /> {t('campaigns.keywords.addAll')}</>
              )}
            </button>
          </div>
          <div className="space-y-1">
            {suggestedKeywords.map((kw, i) => (
              <div key={i} className="flex items-center justify-between py-1.5 border-b border-indigo-100 last:border-0">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-sm text-gray-900">{kw.text}</span>
                  <span className="text-[10px] text-gray-400">{matchTypeLabels[kw.match_type] || kw.match_type}</span>
                  {kw.estimated_cpc && (
                    <span className="text-[10px] text-gray-400">~{kw.estimated_cpc} {currencySymbol(currency)}</span>
                  )}
                </div>
                <button
                  onClick={() => handleAddSuggested(kw)}
                  disabled={addingKeyword}
                  className="text-xs text-indigo-600 hover:text-indigo-700 transition disabled:opacity-50 px-2 py-0.5"
                >
                  {t('campaigns.keywords.add')}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Keywords table */}
      {keywords.length === 0 && (!suggestedKeywords || suggestedKeywords.length === 0) ? (
        <div className="text-center py-12">
          <p className="text-sm text-gray-700 mb-1">{t('campaigns.keywords.emptyTitle')}</p>
          <p className="text-xs text-gray-400">{t('campaigns.keywords.emptyDesc')}</p>
        </div>
      ) : keywords.length > 0 ? (
        <>
        <Text size="xs" c="dimmed" mb="xs">{t('campaigns.keywords.activePausedCount', { active: keywords.filter(k => k.status === 'active').length, paused: keywords.filter(k => k.status !== 'active').length })}</Text>
        <EdgeScroll>
          {loading && (
            <div className="flex items-center gap-2 mb-2 text-xs text-gray-400">
              <CircleNotch className="w-3 h-3 animate-spin" />
              {t('campaigns.keywords.loadingPlanner')}
            </div>
          )}
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="py-2 px-1 w-8">
                  <input
                    type="checkbox"
                    checked={keywords.length > 0 && selected.size === keywords.length}
                    onChange={toggleAll}
                    className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 w-3.5 h-3.5 cursor-pointer"
                  />
                </th>
                {[
                  { key: 'text', label: t('campaigns.keywords.colKeyword'), align: 'left' },
                  { key: 'status', label: t('campaigns.keywords.colStatus'), align: 'center' },
                  { key: 'cpc_bid', label: t('campaigns.keywords.colMaxCpc'), align: 'right' },
                  { key: 'cpc', label: t('campaigns.metrics.avgCpc'), align: 'right' },
                  { key: 'volume', label: t('campaigns.keywords.colVolume'), align: 'right' },
                  { key: 'sug_cpc', label: t('campaigns.keywords.colSugCpc'), align: 'right' },
                  { key: 'clicks', label: t('campaigns.metrics.clicksShort'), align: 'right' },
                  { key: 'impressions', label: t('campaigns.metrics.impressionsShort'), align: 'right' },
                  { key: 'conversions', label: t('campaigns.metrics.conversionsShort'), align: 'right' },
                ].map(col => (
                  <th
                    key={col.key}
                    className={`text-${col.align} text-xs font-medium text-gray-500 py-2 ${col.align === 'left' ? 'pr-4' : 'px-2'} cursor-pointer select-none hover:text-gray-700 transition`}
                    onClick={() => {
                      if (sortCol === col.key) {
                        setSortDir(d => d === 'asc' ? 'desc' : 'asc');
                      } else {
                        setSortCol(col.key);
                        setSortDir(col.key === 'text' || col.key === 'status' ? 'asc' : 'desc');
                      }
                    }}
                  >
                    <span className="inline-flex items-center gap-0.5">
                      {col.label}
                      {sortCol === col.key && (
                        sortDir === 'asc' ? <CaretUp className="w-3 h-3" /> : <CaretDown className="w-3 h-3" />
                      )}
                    </span>
                  </th>
                ))}
                <th className="text-center text-xs font-medium text-gray-500 py-2 pl-2"></th>
              </tr>
            </thead>
            <tbody>
              {[...keywords].sort((a, b) => {
                if (!sortCol) return 0;
                const dir = sortDir === 'asc' ? 1 : -1;
                let va, vb;
                if (sortCol === 'text') {
                  return dir * a.text.localeCompare(b.text, 'pl');
                } else if (sortCol === 'status') {
                  return dir * a.status.localeCompare(b.status);
                } else if (sortCol === 'cpc_bid') {
                  va = parseFloat(a.cpc_bid) || parseFloat(adGroups[0]?.cpc_bid) || 0;
                  vb = parseFloat(b.cpc_bid) || parseFloat(adGroups[0]?.cpc_bid) || 0;
                } else if (sortCol === 'volume') {
                  va = volumes[a.text.toLowerCase()]?.avg_monthly_searches || 0;
                  vb = volumes[b.text.toLowerCase()]?.avg_monthly_searches || 0;
                } else if (sortCol === 'sug_cpc') {
                  va = volumes[a.text.toLowerCase()]?.low_cpc || 0;
                  vb = volumes[b.text.toLowerCase()]?.low_cpc || 0;
                } else {
                  va = parseFloat(a[sortCol]) || 0;
                  vb = parseFloat(b[sortCol]) || 0;
                }
                return dir * (va - vb);
              }).map((kw) => {
                const vol = volumes[kw.text.toLowerCase()];
                const bidInfo = getKeywordBidInfo(kw);
                const bidStr = bidInfo.value;
                const hasVolCpc = vol && vol.low_cpc != null && vol.high_cpc != null && vol.low_cpc > 0;
                const sugCpc = hasVolCpc ? `${vol.low_cpc.toFixed(2)}-${vol.high_cpc.toFixed(2)}` : '';
                const bidTooLow = hasVolCpc && parseFloat(bidStr) > 0 && parseFloat(bidStr) < vol.low_cpc;

                return (
                  <tr key={kw.id} className={`border-b border-gray-100 cursor-pointer transition-colors ${kw.status === 'paused' ? 'opacity-60' : ''} ${selected.has(kw.id) ? 'bg-indigo-50/50' : 'hover:bg-gray-50'}`} onClick={(e) => { if (e.target.tagName === 'INPUT' || e.target.closest('button')) return; openKeywordDetail(kw); }}>
                    <td className="py-2 px-1 w-8">
                      <input
                        type="checkbox"
                        checked={selected.has(kw.id)}
                        onChange={(e) => { e.stopPropagation(); toggleSelect(kw.id, e.nativeEvent); }}
                        className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 w-3.5 h-3.5 cursor-pointer"
                      />
                    </td>
                    <td className="py-2 pr-4">
                      <Tooltip label={t('campaigns.keywords.clickForDetails')} position="top-start" withArrow openDelay={400}>
                        <div onClick={() => openKeywordDetail(kw)} className="cursor-pointer">
                          <p className="text-sm text-gray-900">{kw.text}</p>
                          <p className="text-[11px] text-gray-400">{matchTypeLabels[kw.match_type] || kw.match_type}</p>
                        </div>
                      </Tooltip>
                    </td>
                    <td className="text-center py-2 px-2">
                      <span className="inline-flex items-center gap-1 text-xs">
                        <span className={`w-1.5 h-1.5 rounded-full ${kw.status === 'active' ? 'bg-green-500' : 'bg-yellow-500'}`} />
                      </span>
                    </td>
                    <td className="text-right py-2 px-2">
                      <KeywordCpcEditor
                        keyword={kw}
                        currentBid={bidStr}
                        bidTooLow={bidTooLow}
                        campaignId={campaignId}
                        onUpdated={onKeywordsChanged}
                        addNotification={addNotification}
                        inherited={bidInfo.inherited}
                        currency={currency}
                      />
                    </td>
                    <td className="text-right py-2 px-2 text-sm text-gray-500">
                      {parseFloat(kw.cpc) > 0 ? formatMoney(kw.cpc, currency) : '-'}
                    </td>
                    <td className="text-right py-2 px-2 text-sm text-gray-700">
                      {vol ? (
                        vol.avg_monthly_searches != null && vol.avg_monthly_searches > 0
                          ? vol.avg_monthly_searches.toLocaleString('pl-PL')
                          : <span className="text-gray-300">{'<10'}</span>
                      ) : <span className="text-gray-300">-</span>}
                    </td>
                    <td className="text-right py-2 px-2 text-xs text-gray-500">
                      {hasVolCpc ? `${sugCpc} ${currencySymbol(currency)}` : '-'}
                    </td>
                    <td className="text-right py-2 px-2 text-sm text-gray-700">{kw.clicks || 0}</td>
                    <td className="text-right py-2 px-2 text-sm text-gray-700">{(kw.impressions || 0).toLocaleString('pl-PL')}</td>
                    <td className="text-right py-2 px-2 text-sm text-gray-700">{parseFloat(kw.conversions) > 0 ? Math.round(parseFloat(kw.conversions)) : '-'}</td>
                    <td className="text-center py-2 pl-2">
                      <div className="flex items-center gap-1 justify-center">
                        {keywordActionLoading === kw.id ? (
                          <CircleNotch className="w-3 h-3 animate-spin text-gray-400" />
                        ) : (
                          <>
                            <button
                              onClick={() => handleKeywordAction(kw, kw.status === 'active' ? 'pause' : 'enable')}
                              className="text-xs text-gray-500 hover:text-gray-700 transition"
                              title={kw.status === 'active' ? t('campaigns.keywords.pause') : t('campaigns.keywords.resume')}
                            >
                              {kw.status === 'active' ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
                            </button>
                            <button
                              onClick={() => handleDeleteKeyword(kw)}
                              className="text-xs text-gray-400 hover:text-red-600 transition"
                              title={t('campaigns.keywords.delete')}
                            >
                              <Trash className="w-3.5 h-3.5" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </EdgeScroll>
        </>
      ) : null}

      {/* Bulk action bar */}
      {selected.size > 0 && (
        <div className="flex items-center gap-3 py-2 px-3 bg-indigo-50 border border-indigo-200 rounded-lg mt-2">
          <Text size="xs" fw={600} c="indigo.7">{t('campaigns.keywords.selectedCount', { count: selected.size })}</Text>
          <div className="flex-1" />
          <Button size="xs" variant="light" color="yellow" leftSection={<Pause size={12} />}
            onClick={() => setBulkModal({ action: 'pause' })}>
            {t('campaigns.keywords.pauseBtn')}
          </Button>
          <Button size="xs" variant="light" color="green" leftSection={<Play size={12} />}
            onClick={() => setBulkModal({ action: 'enable' })}>
            {t('campaigns.keywords.resumeBtn')}
          </Button>
          <Button size="xs" variant="light" color="red" leftSection={<Trash size={12} />}
            onClick={() => setBulkModal({ action: 'delete' })}>
            {t('campaigns.keywords.deleteBtn')}
          </Button>
          <button onClick={() => setSelected(new Set())} className="text-xs text-gray-400 hover:text-gray-600 ml-1">
            <X size={14} />
          </button>
        </div>
      )}

      {/* Bulk action confirmation modal */}
      <Modal
        opened={!!bulkModal}
        onClose={() => !bulkLoading && setBulkModal(null)}
        title={
          bulkModal?.action === 'delete' ? t('campaigns.keywords.bulkDeleteTitle') :
          bulkModal?.action === 'pause' ? t('campaigns.keywords.bulkPauseTitle') :
          t('campaigns.keywords.bulkEnableTitle')
        }
        centered
        size="sm"
      >
        {bulkModal && (
          <Stack gap="md">
            <Text size="sm" c="dimmed">
              {bulkModal.action === 'delete'
                ? t('campaigns.keywords.bulkDeleteConfirm', { count: selectedKeywords.length })
                : bulkModal.action === 'pause'
                ? t('campaigns.keywords.bulkPauseConfirm', { count: selectedKeywords.length })
                : t('campaigns.keywords.bulkEnableConfirm', { count: selectedKeywords.length })
              }
            </Text>
            <Paper p="xs" bg="gray.0" radius="sm" style={{ maxHeight: 200, overflowY: 'auto' }}>
              {selectedKeywords.map(kw => (
                <Text key={kw.id} size="xs" c="dark" py={2}>
                  {kw.text} <Text span c="dimmed">({matchTypeLabels[kw.match_type] || kw.match_type})</Text>
                </Text>
              ))}
            </Paper>
            <Group justify="flex-end" gap="sm">
              <Button variant="default" size="xs" onClick={() => setBulkModal(null)} disabled={bulkLoading}>
                {t('campaigns.common.cancel')}
              </Button>
              <Button
                size="xs"
                color={bulkModal.action === 'delete' ? 'red' : bulkModal.action === 'pause' ? 'yellow' : 'green'}
                onClick={handleBulkAction}
                loading={bulkLoading}
                leftSection={bulkModal.action === 'delete' ? <Trash size={12} /> : bulkModal.action === 'pause' ? <Pause size={12} /> : <Play size={12} />}
              >
                {bulkModal.action === 'delete' ? t('campaigns.keywords.bulkDeleteBtn', { count: selectedKeywords.length }) :
                 bulkModal.action === 'pause' ? t('campaigns.keywords.bulkPauseBtn', { count: selectedKeywords.length }) :
                 t('campaigns.keywords.bulkEnableBtn', { count: selectedKeywords.length })}
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>

      {/* Keyword detail modal (shared component, used here and in Search Terms) */}
      <KeywordDetailModal keyword={detailKeyword} campaignId={campaignId}
        onClose={closeKeywordDetail} addNotification={addNotification} />

      {/* Add keyword form - fixed at bottom */}
      <div className="border-t border-gray-200 pt-3 mt-3">
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={newKeywordText}
            onChange={e => setNewKeywordText(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && !addingKeyword) handleAddKeyword(); }}
            placeholder={t('campaigns.keywords.newPlaceholder')}
            className="flex-1 text-sm px-3 py-1.5 border border-gray-200 rounded-md focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
          />
          <select
            value={newKeywordMatchType}
            onChange={e => setNewKeywordMatchType(e.target.value)}
            className="text-xs px-2 py-1.5 border border-gray-200 rounded-md focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 outline-none bg-white"
          >
            <option value="EXACT">{t('campaigns.matchType.exact')}</option>
            <option value="PHRASE">{t('campaigns.matchType.phrase')}</option>
            <option value="BROAD">{t('campaigns.matchType.broad')}</option>
          </select>
          <button
            onClick={handleAddKeyword}
            disabled={addingKeyword || !newKeywordText.trim()}
            className="inline-flex items-center gap-1 text-xs px-3 py-1.5 bg-[#1D4ED8] text-white rounded-md hover:bg-[#1e40af] transition disabled:opacity-50"
          >
            {addingKeyword ? (
              <CircleNotch className="w-3 h-3 animate-spin" />
            ) : (
              <Plus className="w-3 h-3" />
            )}
            {t('campaigns.keywords.add')}
          </button>
        </div>
        <Text size="xs" c="dimmed" mt={6}>
          {t('campaigns.keywords.matchTypeHelp')} <Anchor href="/poradniki/keyword-match-types" target="_blank" size="xs" c="brand">{t('campaigns.keywords.matchTypeGuide')}</Anchor>
        </Text>

        {/* Long-tail ideas from Google Keyword Planner */}
        <div className="mt-3">
          <button
            onClick={handleGenerateIdeas}
            disabled={ideasLoading}
            className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 border border-[#1D4ED8] text-[#1D4ED8] rounded-md hover:bg-indigo-50 transition disabled:opacity-50"
          >
            {ideasLoading ? <CircleNotch className="w-3.5 h-3.5 animate-spin" /> : <MagnifyingGlass className="w-3.5 h-3.5" />}
            {t('campaigns.keywords.generateLongTail')}
          </button>

          {ideas && ideas.length > 0 && (
            <div className="border border-gray-200 rounded-lg p-3 mt-3">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-medium text-gray-900">
                  {t('campaigns.keywords.longTailCount', { count: ideas.length })} {ideasInfo?.ai_used ? t('campaigns.keywords.sourceAiPlanner') : t('campaigns.keywords.sourcePlanner')}
                </p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setIdeasSelected(new Set(ideas.filter(c => c.within_max_cpc).map(c => c.text)))}
                    className="text-xs text-[#1D4ED8] hover:underline"
                  >
                    {t('campaigns.keywords.selectWithinLimit')}
                  </button>
                  <button
                    onClick={handleAddSelectedIdeas}
                    disabled={addingBatch || ideasSelected.size === 0}
                    className="inline-flex items-center gap-1 text-xs px-2.5 py-1 bg-[#1D4ED8] text-white rounded-md hover:bg-[#1e40af] transition disabled:opacity-50"
                  >
                    {addingBatch ? <CircleNotch className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
                    {t('campaigns.keywords.addSelected', { count: ideasSelected.size })}
                  </button>
                </div>
              </div>
              {ideasMaxCpc != null && (
                <Text size="xs" c={ideasInfo?.fits_count ? 'dimmed' : 'red'} mb={6}>
                  {t('campaigns.keywords.cpcLimit', { limit: ideasMaxCpc.toFixed(2), cur: currencySymbol(currency) })}{' '}
                  {ideasInfo?.fits_count
                    ? t('campaigns.keywords.cpcFits', { count: ideasInfo.fits_count })
                    : t('campaigns.keywords.cpcNone', { cheapest: ideasInfo?.cheapest_low_cpc ? t('campaigns.keywords.cheapestFragment', { cheapest: ideasInfo.cheapest_low_cpc.toFixed(2), cur: currencySymbol(currency) }) : '' })}
                </Text>
              )}
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-gray-400 text-[10px] uppercase">
                    <th className="text-left font-medium py-1 w-6"></th>
                    <th className="text-left font-medium py-1">{t('campaigns.keywords.colKeyword')}</th>
                    <th className="text-left font-medium py-1 pl-2">{t('campaigns.keywords.colSource')}</th>
                    <th className="text-right font-medium py-1">{t('campaigns.keywords.colAvgSearches')}</th>
                    <th className="text-left font-medium py-1 pl-3">{t('campaigns.keywords.colCompetition')}</th>
                    <th className="text-right font-medium py-1">{t('campaigns.keywords.colEstCpc')}</th>
                  </tr>
                </thead>
                <tbody>
                  {ideas.map((c) => {
                    const compLabel = { LOW: t('campaigns.keywords.compLow'), MEDIUM: t('campaigns.keywords.compMedium'), HIGH: t('campaigns.keywords.compHigh') }[c.competition] || '-';
                    const srcLabel = { ai: 'AI', planner: 'Planner', both: 'AI+Planner' }[c.source] || c.source;
                    return (
                      <tr key={c.text} className="border-t border-gray-100">
                        <td className="py-1.5">
                          <input type="checkbox" checked={ideasSelected.has(c.text)}
                            onChange={() => toggleIdea(c.text)}
                            className="accent-[#1D4ED8]" />
                        </td>
                        <td className="py-1.5">
                          <span className={c.within_max_cpc ? 'text-green-700' : 'text-gray-900'}>{c.text}</span>
                        </td>
                        <td className="py-1.5 pl-2 text-[10px] text-gray-400">{srcLabel}</td>
                        <td className="py-1.5 text-right tabular-nums text-gray-700">
                          {c.cpc_known ? (c.avg_monthly_searches || 0).toLocaleString('pl-PL') : '-'}
                        </td>
                        <td className="py-1.5 pl-3 text-gray-500">{c.cpc_known ? compLabel : '-'}</td>
                        <td className="py-1.5 text-right tabular-nums text-gray-700">
                          {c.cpc_known ? `${c.low_cpc.toFixed(2)} - ${formatMoney(c.high_cpc, currency)}` : t('campaigns.keywords.noDataShort')}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Negative keywords */}
      <NegativeKeywordsSection negativeKeywords={negativeKeywords} campaignId={campaignId} onChanged={onKeywordsChanged} />
    </div>
  );
}

function SuggestionsTab({ suggestions, campaign, keywords, adGroups, suggestionActionLoading, handleSuggestionApply, handleSuggestionDismiss, onOpenTab }) {
  const { t } = useTranslation();
  const { addNotification } = useNotifications();
  const [applying, setApplying] = useState(null);
  const [dismissed, setDismissed] = useState([]);
  const [fetchData, setFetchData] = useState(null);

  const agBid = adGroups.length > 0 ? parseFloat(adGroups[0].cpc_bid || 0) : 0;
  const agId = adGroups.length > 0 ? adGroups[0].id : null;
  const budget = parseFloat(campaign.budget_amount || 0);
  const cur = currencySymbol(campaign.currency);
  const matchTypes = new Set(keywords.map(k => k.match_type));

  // Proposed new CPC: 5x current or minimum 5 zł
  const proposedCpc = Math.max(agBid * 2.5, 5).toFixed(2);
  const proposedBudget = Math.max(budget * 2, 20).toFixed(0);

  const handleApplyAction = async (id, action) => {
    setApplying(id);
    try {
      if (action.type === 'update_bid' && agId) {
        await api.post(`/ads/campaigns/${campaign.id}/ad-groups/${agId}/`, { cpc_bid: action.value });
        addNotification(t('campaigns.suggestions.cpcChanged', { value: action.value, cur }), 'success');
      } else if (action.type === 'update_budget') {
        await api.post(`/ads/campaigns/${campaign.id}/settings/`, { budget_amount: action.value });
        addNotification(t('campaigns.suggestions.budgetChanged', { value: action.value, cur }), 'success');
      } else if (action.type === 'enable_campaign') {
        await api.post(`/ads/campaigns/${campaign.id}/settings/`, { status: 'active' });
        addNotification(t('campaigns.suggestions.campaignStarted'), 'success');
      } else if (action.type === 'add_broad_keywords' && action.keywords) {
        const batch = action.keywords.map(text => ({ text, match_type: 'BROAD' }));
        await api.post(`/ads/campaigns/${campaign.id}/keywords/add-batch/`, { keywords: batch });
        addNotification(t('campaigns.suggestions.addedBroad', { count: batch.length }), 'success');
      } else if (action.type === 'expand_keywords_ai') {
        const currentKws = keywords.map(k => k.text);
        const sugRes = await api.post('/ai/suggest-keywords/', {
          description: `${campaign.name} ${campaign.source_url || ''}`,
          current_keywords: currentKws,
        });
        const newKws = (sugRes.data.keywords || [])
          .filter(k => !currentKws.includes(k.text.toLowerCase()))
          .slice(0, 10);
        if (newKws.length > 0) {
          const batch = newKws.map(k => ({ text: k.text, match_type: k.match_type || 'PHRASE' }));
          await api.post(`/ads/campaigns/${campaign.id}/keywords/add-batch/`, { keywords: batch });
          addNotification(t('campaigns.suggestions.aiAdded', { count: batch.length }), 'success');
        } else {
          addNotification(t('campaigns.suggestions.noNewKeywords'), 'info');
        }
      }
      setDismissed(prev => [...prev, id]);
    } catch (err) {
      addNotification(err.response?.data?.error || t('campaigns.suggestions.applyError'), 'error');
    } finally {
      setApplying(null);
    }
  };

  // Build actionable suggestions with concrete proposed values
  const proactive = [];

  // The ad-group CPC suggestion only applies to Search campaigns. Shopping/PMax
  // bid on product groups (managed in the Shopping dashboard, ROAS-based), so the
  // ad-group default CPC here is the wrong lever and would show a stale value
  // (e.g. suggesting 1.00 -> 5.00 after the product-group bid was already raised).
  // Przy strategiach automatycznych (Maximize Clicks/Conversions, tCPA, tROAS) stawki
  // grupy/słów to placeholdery Google, nie realne ustawienie, więc „podnieś CPC"
  // nie ma sensu (ten sam błąd był w analizie AI, patrz ai-analysis-cpc-semantics).
  const automatedBidding = ['maximize_clicks', 'maximize_conversions', 'maximize_conversion_value',
    'target_cpa', 'target_roas', 'target_spend'].includes(campaign.bidding_strategy);
  const cpcRelevant = campaign.platform !== 'meta_ads' && !automatedBidding
    && !['shopping', 'performance_max', 'pmax', 'display', 'video'].includes(campaign.campaign_type);
  // Only show CPC suggestion if both ad group AND keyword bids are low
  const maxKwBid = keywords.length > 0 ? Math.max(...keywords.map(k => parseFloat(k.cpc_bid) || 0)) : 0;
  const effectiveBid = Math.max(agBid, maxKwBid);
  if (cpcRelevant && agBid > 0 && effectiveBid < 3) {
    proactive.push({
      id: 'pro-bid', priority: 'high',
      title: t('campaigns.suggestions.bidTitle', { current: agBid.toFixed(2), proposed: proposedCpc, cur }),
      description: t('campaigns.suggestions.bidDesc', { current: agBid.toFixed(2), proposed: proposedCpc, cur }),
      impact: t('campaigns.suggestions.bidImpact'),
      action: { type: 'update_bid', value: proposedCpc },
      buttonLabel: t('campaigns.suggestions.bidButton', { proposed: proposedCpc, cur }),
    });
  }

  if (budget > 0 && budget < 10) {
    proactive.push({
      id: 'pro-budget', priority: 'medium',
      title: t('campaigns.suggestions.budgetTitle', { current: budget, proposed: proposedBudget, cur }),
      description: t('campaigns.suggestions.budgetDesc', { current: budget, proposed: proposedBudget, cur }),
      impact: t('campaigns.suggestions.budgetImpact', { clicks: Math.floor(proposedBudget / Math.max(agBid, 3)) }),
      action: { type: 'update_budget', value: proposedBudget },
      buttonLabel: t('campaigns.suggestions.budgetButton', { proposed: proposedBudget, cur }),
    });
  }

  if (campaign.status === 'paused') {
    proactive.push({
      id: 'pro-enable', priority: 'high',
      title: t('campaigns.suggestions.enableTitle'),
      description: t('campaigns.suggestions.enableDesc'),
      impact: t('campaigns.suggestions.enableImpact'),
      action: { type: 'enable_campaign' },
      buttonLabel: t('campaigns.suggestions.enableButton'),
    });
  }

  if (keywords.length > 0 && matchTypes.size === 1 && matchTypes.has('PHRASE')) {
    const topKws = keywords.slice(0, 5).map(k => k.text);
    proactive.push({
      id: 'pro-match', priority: 'low',
      title: t('campaigns.suggestions.matchTitle'),
      description: t('campaigns.suggestions.matchDesc', { total: keywords.length, top: topKws.length }),
      impact: t('campaigns.suggestions.matchImpact'),
      action: { type: 'add_broad_keywords', keywords: topKws },
      buttonLabel: t('campaigns.suggestions.matchButton', { count: topKws.length }),
    });
  }

  if (keywords.length < 10 && keywords.length > 0) {
    proactive.push({
      id: 'pro-kw', priority: 'medium',
      title: t('campaigns.suggestions.moreKwTitle', { count: keywords.length }),
      description: t('campaigns.suggestions.moreKwDesc'),
      impact: t('campaigns.suggestions.moreKwImpact'),
      action: { type: 'expand_keywords_ai' },
      buttonLabel: t('campaigns.suggestions.moreKwButton'),
    });
  }

  // Filter out stale suggestions where proposed value = current value
  const filteredSuggestions = suggestions.filter(s => {
    if (s.type === 'bid_adjustment' && s.title) {
      // Waluta w tytule zależy od konta (zł/₺/€/$...), więc symbol jest opcjonalny.
      const match = s.title.match(/(\d+[\.,]\d+)\s*\S{0,3}.*→\s*(\d+[\.,]\d+)\s*\S{0,3}/);
      if (match && match[1].replace(',', '.') === match[2].replace(',', '.')) return false;
    }
    return true;
  });

  const allItems = [
    ...proactive.filter(p => !dismissed.includes(p.id)),
    ...filteredSuggestions,
  ];

  const priorityColors = { high: 'bg-red-500', medium: 'bg-yellow-500', low: 'bg-blue-500' };

  if (allItems.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-sm text-gray-700 mb-1">{t('campaigns.suggestions.allGoodTitle')}</p>
        <p className="text-xs text-gray-400">{t('campaigns.suggestions.allGoodDesc')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {allItems.map((rec) => (
        <div key={rec.id} className="p-3 border border-gray-100 rounded-md">
          <div className="flex items-start gap-2">
            <span className={`w-1.5 h-1.5 rounded-full mt-1.5 shrink-0 ${priorityColors[rec.priority] || 'bg-indigo-500'}`} />
            <div className="flex-1 min-w-0">
              <p className="text-sm text-gray-900 font-medium">{rec.title}</p>
              {rec.safety_warning && (
                <span className="inline-flex items-center gap-1 text-[10px] font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5 mt-1" title={rec.safety_warning}>
                  <ShieldWarning className="w-3 h-3" /> {t('campaigns.suggestions.safetyWarning')}
                </span>
              )}
              <p className="text-xs text-gray-500 mt-0.5">{rec.description}</p>
              {(rec.impact || rec.expected_impact) && (
                <p className="text-xs text-indigo-600 mt-1">{rec.impact || rec.expected_impact}</p>
              )}
              <div className="flex items-center gap-2 mt-2">
                {rec.action ? (
                  applying === rec.id ? (
                    <span className="inline-flex items-center gap-1 text-xs text-gray-400"><CircleNotch className="w-3 h-3 animate-spin" /> {t('campaigns.suggestions.applying')}</span>
                  ) : (
                    <>
                      <button
                        onClick={() => handleApplyAction(rec.id, rec.action)}
                        className="inline-flex items-center gap-1 text-xs bg-[#1D4ED8] text-white px-2.5 py-1 rounded-md hover:bg-[#1e40af] transition"
                      >
                        <Check className="w-3 h-3" /> {rec.buttonLabel}
                      </button>
                      <button
                        onClick={() => setDismissed(prev => [...prev, rec.id])}
                        className="text-xs text-gray-400 hover:text-gray-600 px-2 py-1 rounded hover:bg-gray-50 transition"
                      >
                        {t('campaigns.suggestions.dismiss')}
                      </button>
                    </>
                  )
                ) : (rec.planned_changes?._advisory || rec.type === 'conversion_tracking') ? (
                  // Sugestia informacyjna (np. niedziałający tag konwersji): nie ma czego
                  // „stosować" - zamiast przycisku Zastosuj prowadzimy do zakładki Konwersje.
                  <>
                    <span className="text-xs text-gray-500 italic">{t('campaigns.suggestions.advisoryText')}</span>
                    {rec.type === 'conversion_tracking' && onOpenTab && (
                      <button onClick={() => onOpenTab('conversions')} className="inline-flex items-center gap-1 text-xs border border-[#1D4ED8] text-[#1D4ED8] px-2.5 py-1 rounded-md hover:bg-indigo-50 transition">{t('campaigns.suggestions.checkInstallation')}</button>
                    )}
                    <button onClick={() => handleSuggestionDismiss(rec)} className="text-xs text-gray-400 hover:text-gray-600 px-2 py-1 rounded hover:bg-gray-50 transition">{t('campaigns.suggestions.dismiss')}</button>
                  </>
                ) : rec.type !== 'config' && !rec.buttonLabel ? (
                  <>
                    {suggestionActionLoading === rec.id ? (
                      <CircleNotch className="w-3 h-3 animate-spin text-gray-400" />
                    ) : (
                      <>
                        <button onClick={() => handleSuggestionApply(rec)} className="text-xs bg-[#1D4ED8] text-white px-2.5 py-1 rounded-md hover:bg-[#1e40af] transition"><Check className="w-3 h-3 inline mr-1" />{t('campaigns.suggestions.apply')}</button>
                        <button onClick={() => handleSuggestionDismiss(rec)} className="text-xs text-gray-400 hover:text-gray-600 px-2 py-1 rounded hover:bg-gray-50 transition">{t('campaigns.suggestions.dismiss')}</button>
                      </>
                    )}
                  </>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function campaignTypeLabel(c, t) {
  if (!c) return '';
  if (c.platform === 'meta_ads') return c.campaign_goal === 'leads' ? t('campaigns.campaignType.metaLeads') : t('campaigns.campaignType.metaTraffic');
  if (c.platform === 'microsoft_ads') return t('campaigns.campaignType.microsoftSearch');
  const ct = c.campaign_type;
  if (ct === 'shopping') return t('campaigns.campaignType.googleShopping');
  if (ct === 'performance_max' || ct === 'pmax') return t('campaigns.campaignType.googlePmax');
  if (ct === 'display') return t('campaigns.campaignType.googleDisplay');
  if (ct === 'video') return t('campaigns.campaignType.googleVideo');
  return t('campaigns.campaignType.googleSearch');
}

function FacebookPageControl({ campaign, addNotification, onChanged }) {
  const { t } = useTranslation();
  const [currentName, setCurrentName] = React.useState(campaign.ai_draft_data?.page_name || '');
  const [editing, setEditing] = React.useState(false);
  const [pages, setPages] = React.useState([]);
  const [selectedId, setSelectedId] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState(null);

  const openEdit = async () => {
    setEditing(true); setLoading(true); setError(null);
    try {
      const res = await api.get('/ads/meta/pages/');
      setPages(res.data.pages || []);
      setSelectedId(res.data.selected_page_id || '');
    } catch { /* ignore */ } finally { setLoading(false); }
  };

  const save = async () => {
    const page = pages.find(p => p.id === selectedId);
    if (!page) return;
    setSaving(true); setError(null);
    try {
      await api.post(`/ads/campaigns/${campaign.id}/change-page/`, { page_id: page.id, page_name: page.name });
      addNotification(t('campaigns.facebookPage.changed', { name: page.name }), 'success');
      setCurrentName(page.name);
      setEditing(false);
      if (onChanged) onChanged();
    } catch (e) {
      const msg = e.response?.data?.error || t('campaigns.facebookPage.changeError');
      const url = (msg.match(/https?:\/\/\S+/) || [])[0] || null;
      setError({ msg: url ? msg.replace(url, '').replace(/Otwórz\s*$/, '').trim() : msg, url });
    } finally { setSaving(false); }
  };

  return (
    <Paper withBorder p="sm" radius="md">
      <Group justify="space-between" wrap="nowrap" align="flex-start">
        <div style={{ minWidth: 0 }}>
          <Text size="xs" c="dimmed">{t('campaigns.facebookPage.label')}</Text>
          <Text size="sm" fw={500} truncate>{currentName || t('campaigns.facebookPage.defaultPage')}</Text>
        </div>
        {!editing && <Button size="xs" variant="light" onClick={openEdit} style={{ flexShrink: 0 }}>{t('campaigns.facebookPage.changePage')}</Button>}
      </Group>
      {editing && (
        <Stack gap="xs" mt="sm">
          {loading ? <Loader size="xs" /> : (
            <Select size="xs" data={pages.map(p => ({ value: p.id, label: p.name }))}
              value={selectedId} onChange={setSelectedId} placeholder={t('campaigns.facebookPage.selectPage')} searchable />
          )}
          <Text size="10px" c="dimmed">{campaign.campaign_goal === 'leads' ? t('campaigns.facebookPage.recreateNoteLeads') : t('campaigns.facebookPage.recreateNote')}</Text>
          {error && (
            <Alert color="red" variant="light" p="xs" radius="md">
              <Text size="xs">{error.msg}</Text>
              {error.url && (
                <Button size="xs" variant="filled" color="red" mt="xs" component="a"
                  href={error.url} target="_blank" rel="noopener noreferrer">
                  {t('campaigns.facebookPage.openTerms')}
                </Button>
              )}
              {error.url && (
                <Button size="xs" variant="light" color="brand" mt="xs" ml="xs" onClick={save} loading={saving}>
                  {t('campaigns.facebookPage.acceptedRetry')}
                </Button>
              )}
            </Alert>
          )}
          <Group gap="xs">
            <Button size="xs" color="brand" onClick={save} loading={saving} disabled={!selectedId}>{t('campaigns.common.save')}</Button>
            <Button size="xs" variant="subtle" color="gray" onClick={() => setEditing(false)}>{t('campaigns.common.cancel')}</Button>
          </Group>
        </Stack>
      )}
    </Paper>
  );
}

function CreativePerformance({ campaignId, currency = 'PLN' }) {
  const { t } = useTranslation();
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  React.useEffect(() => {
    setLoading(true);
    api.get(`/ads/campaigns/${campaignId}/creative-performance/`)
      .then(res => setData(res.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [campaignId]);

  if (loading) return <Loader size="sm" />;
  if (!data) return <Text size="sm" c="dimmed" py="md" ta="center">{t('campaigns.creative.fetchError')}</Text>;

  if (data.platform === 'meta_ads') {
    const ads = data.ads || [];
    if (!ads.length) return <Text size="sm" c="dimmed" py="md" ta="center">{t('campaigns.creative.noAdsData')}</Text>;
    return (
      <div>
        <Text size="xs" c="dimmed" mb="sm">{t('campaigns.creative.adsPerfDesc')}</Text>
        <Table fz="xs" verticalSpacing={6} highlightOnHover>
          <Table.Thead><Table.Tr>
            <Table.Th>{t('campaigns.creative.colAd')}</Table.Th><Table.Th style={{ textAlign: 'right' }}>{t('campaigns.metrics.impressionsShort')}</Table.Th>
            <Table.Th style={{ textAlign: 'right' }}>{t('campaigns.metrics.clicksShort')}</Table.Th><Table.Th style={{ textAlign: 'right' }}>{t('campaigns.metrics.ctr')}</Table.Th>
            <Table.Th style={{ textAlign: 'right' }}>{t('campaigns.metrics.conversionsShort')}</Table.Th><Table.Th style={{ textAlign: 'right' }}>{t('campaigns.metrics.cost')}</Table.Th>
            <Table.Th style={{ textAlign: 'right' }}>{t('campaigns.creative.colCostPerResult')}</Table.Th>
          </Table.Tr></Table.Thead>
          <Table.Tbody>
            {ads.map((a, i) => (
              <Table.Tr key={a.ad_id || i} style={i === 0 && a.conversions > 0 ? { background: 'var(--mantine-color-green-0)' } : undefined}>
                <Table.Td>{a.name || a.ad_id}{i === 0 && a.conversions > 0 && <Badge size="xs" color="green" variant="light" ml={6}>{t('campaigns.creative.best')}</Badge>}</Table.Td>
                <Table.Td style={{ textAlign: 'right' }}>{(a.impressions || 0).toLocaleString('pl')}</Table.Td>
                <Table.Td style={{ textAlign: 'right' }}>{a.clicks}</Table.Td>
                <Table.Td style={{ textAlign: 'right' }}>{a.ctr}%</Table.Td>
                <Table.Td style={{ textAlign: 'right' }}>{a.conversions || '-'}</Table.Td>
                <Table.Td style={{ textAlign: 'right' }}>{formatMoney(a.cost, currency)}</Table.Td>
                <Table.Td style={{ textAlign: 'right' }}>{a.cost_per_result != null ? formatMoney(a.cost_per_result, currency) : '-'}</Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </div>
    );
  }

  const assets = data.assets || [];
  const LABELS = { BEST: { label: t('campaigns.creative.ratingBest'), color: 'green' }, GOOD: { label: t('campaigns.creative.ratingGood'), color: 'blue' }, LOW: { label: t('campaigns.creative.ratingLow'), color: 'red' }, LEARNING: { label: t('campaigns.creative.ratingLearning'), color: 'gray' }, PENDING: { label: t('campaigns.creative.ratingPending'), color: 'gray' } };
  const headlines = assets.filter(a => a.type === 'HEADLINE');
  const descriptions = assets.filter(a => a.type === 'DESCRIPTION');
  const rows = (list) => list.map((a, i) => {
    const lab = LABELS[a.label] || { label: a.label, color: 'gray' };
    return (
      <Table.Tr key={i}>
        <Table.Td><Text size="xs">{a.text}</Text></Table.Td>
        <Table.Td><Badge size="xs" color={lab.color} variant="light">{lab.label}</Badge></Table.Td>
        <Table.Td style={{ textAlign: 'right' }}>{(a.impressions || 0).toLocaleString('pl')}</Table.Td>
      </Table.Tr>
    );
  });
  return (
    <div>
      {data.note && <Alert color="blue" variant="light" p="xs" mb="sm"><Text size="xs">{data.note}</Text></Alert>}
      {!assets.length ? (
        <Text size="sm" c="dimmed" py="md" ta="center">{t('campaigns.creative.noAssetRatings')}</Text>
      ) : (
        <>
          <Text size="xs" fw={600} mb={4}>{t('campaigns.creative.headlines')}</Text>
          <Table fz="xs" verticalSpacing={4} mb="md"><Table.Thead><Table.Tr><Table.Th>{t('campaigns.creative.colText')}</Table.Th><Table.Th>{t('campaigns.creative.colRating')}</Table.Th><Table.Th style={{ textAlign: 'right' }}>{t('campaigns.metrics.impressionsShort')}</Table.Th></Table.Tr></Table.Thead><Table.Tbody>{rows(headlines)}</Table.Tbody></Table>
          <Text size="xs" fw={600} mb={4}>{t('campaigns.creative.descriptions')}</Text>
          <Table fz="xs" verticalSpacing={4}><Table.Thead><Table.Tr><Table.Th>{t('campaigns.creative.colText')}</Table.Th><Table.Th>{t('campaigns.creative.colRating')}</Table.Th><Table.Th style={{ textAlign: 'right' }}>{t('campaigns.metrics.impressionsShort')}</Table.Th></Table.Tr></Table.Thead><Table.Tbody>{rows(descriptions)}</Table.Tbody></Table>
        </>
      )}
    </div>
  );
}

export const MINI_CHART_METRICS = {
  impressions: { labelKey: 'campaigns.chartMetrics.impressions', color: '#8b5cf6' },
  clicks: { labelKey: 'campaigns.chartMetrics.clicks', color: '#3b82f6' },
  ctr: { labelKey: 'campaigns.chartMetrics.ctr', color: '#f59e0b' },
  cost: { labelKey: 'campaigns.chartMetrics.cost', color: '#1D4ED8' },
  conversions: { labelKey: 'campaigns.chartMetrics.conversions', color: '#10b981' },
  conversions_value: { labelKey: 'campaigns.chartMetrics.conversionsValue', color: '#0ea5e9' },
  roas: { labelKey: 'campaigns.chartMetrics.roas', color: '#7c3aed' },
  cpc: { labelKey: 'campaigns.chartMetrics.cpc', color: '#ef4444' },
  budget_amount: { labelKey: 'campaigns.chartMetrics.budget', color: '#3b82f6' },
};

const CONV_COLORS = ['#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16'];

// Fill missing dates in a data array so chart always shows the full range
const AD_STRENGTH_COLORS = { poor: 'red', average: 'yellow', good: 'blue', excellent: 'green', pending: 'gray' };

// Skuteczność reklam RSA (AdPerformance z synchronizacji co 6 h), tylko odczyt.
function AdPerformanceTable({ campaignId, refreshKey = 0 }) {
  const { t, i18n } = useTranslation();
  const [data, setData] = React.useState(null);
  React.useEffect(() => {
    api.get(`/ads/campaigns/${campaignId}/ad-performance/`)
      .then(res => setData(res.data))
      .catch(() => setData(null));
  }, [campaignId, refreshKey]);
  const ads = data?.ads || [];
  if (!ads.length) return null;
  const lang = i18n.language || 'pl';
  const num = (v, d = 0) => Number(v || 0).toLocaleString(lang, { minimumFractionDigits: d, maximumFractionDigits: d });
  const groups = new Set(ads.map(a => a.ad_group_id));
  return (
    <div className="border border-gray-100 rounded-md p-3">
      <div className="flex items-baseline justify-between gap-3 mb-1">
        <p className="text-sm font-medium text-gray-800">{t('campaigns.adsTab.perf.title')}</p>
        {data.synced_at && (
          <p className="text-[11px] text-gray-400">{t('campaigns.adsTab.perf.synced', { date: new Date(data.synced_at).toLocaleString(lang) })}</p>
        )}
      </div>
      <p className="text-xs text-gray-500 mb-2">{t('campaigns.adsTab.perf.desc')}</p>
      <Table fz="xs" verticalSpacing={4} highlightOnHover>
        <Table.Thead><Table.Tr>
          <Table.Th>{t('campaigns.adsTab.perf.colHeadlines')}</Table.Th>
          {groups.size > 1 && <Table.Th>{t('campaigns.adsTab.perf.colGroup')}</Table.Th>}
          <Table.Th>{t('campaigns.adsTab.perf.colStrength')}</Table.Th>
          <Table.Th style={{ textAlign: 'right' }}>{t('campaigns.adsTab.perf.colImpressions')}</Table.Th>
          <Table.Th style={{ textAlign: 'right' }}>{t('campaigns.adsTab.perf.colClicks')}</Table.Th>
          <Table.Th style={{ textAlign: 'right' }}>{t('campaigns.adsTab.perf.colCtr')}</Table.Th>
          <Table.Th style={{ textAlign: 'right' }}>{t('campaigns.adsTab.perf.colConversions')}</Table.Th>
        </Table.Tr></Table.Thead>
        <Table.Tbody>
          {ads.map(a => {
            const s = (a.ad_strength || '').toLowerCase();
            const strengthKey = AD_STRENGTH_COLORS[s] ? s : 'unknown';
            return (
              <Table.Tr key={a.id} style={a.status === 'paused' ? { opacity: 0.55 } : undefined}>
                <Table.Td style={{ maxWidth: 360 }}>
                  <span className="text-gray-800">{(a.headlines || []).slice(0, 3).join(' | ') || '-'}</span>
                  {a.status === 'paused' && <span className="text-[11px] text-gray-400 ml-1.5">({t('campaigns.adsTab.perf.paused')})</span>}
                </Table.Td>
                {groups.size > 1 && <Table.Td>{a.ad_group_name}</Table.Td>}
                <Table.Td><Badge size="xs" variant="light" color={AD_STRENGTH_COLORS[s] || 'gray'}>{t(`campaigns.adsTab.perf.strength.${strengthKey}`)}</Badge></Table.Td>
                <Table.Td style={{ textAlign: 'right' }}>{num(a.impressions)}</Table.Td>
                <Table.Td style={{ textAlign: 'right' }}>{num(a.clicks)}</Table.Td>
                <Table.Td style={{ textAlign: 'right' }}>{a.impressions ? `${num(a.ctr, 2)}%` : '-'}</Table.Td>
                <Table.Td style={{ textAlign: 'right' }}>{Number(a.conversions) ? num(a.conversions, Number.isInteger(Number(a.conversions)) ? 0 : 1) : '-'}</Table.Td>
              </Table.Tr>
            );
          })}
        </Table.Tbody>
      </Table>
    </div>
  );
}

function fillDateRange(data, days, defaults = {}) {
  const byDate = {};
  data.forEach(d => { byDate[d.date] = d; });
  const result = [];
  const today = new Date();
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(today);
    d.setDate(d.getDate() - i);
    const key = d.toISOString().slice(0, 10);
    const short = key.slice(5);
    result.push(byDate[key] ? { ...defaults, ...byDate[key], date: short } : { date: short, ...defaults });
  }
  return result;
}

// Format a metric value for tooltips/labels (waluta konta dla kwot, % dla wskaźników).
function formatMetricValue(key, v, currency = 'PLN') {
  const n = Number(v) || 0;
  if (key === 'cost' || key === 'cpc' || key === 'budget_amount') return formatMoney(n, currency, { minDecimals: key === 'cpc' ? 2 : 0, maxDecimals: 2 });
  if (key === 'ctr') return `${n.toLocaleString('pl-PL', { maximumFractionDigits: 2 })}%`;
  return n.toLocaleString('pl-PL');
}

// Tooltip for the normalized multi-metric overlay: shows each metric's real
// value (the chart itself is scaled to each metric's own maximum).
function OverlayTooltip({ active, payload, label, metrics, currency = 'PLN' }) {
  const { t } = useTranslation();
  if (!active || !payload || !payload.length) return null;
  const row = payload[0].payload || {};
  return (
    <div style={{ background: '#fff', border: '1px solid #eee', borderRadius: 8, padding: '6px 10px', fontSize: 11 }}>
      <div style={{ fontWeight: 600, marginBottom: 4 }}>{label}</div>
      {metrics.map(m => (
        <div key={m} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ width: 8, height: 8, borderRadius: 2, background: MINI_CHART_METRICS[m].color, display: 'inline-block' }} />
          <span style={{ color: '#666' }}>{t(MINI_CHART_METRICS[m].labelKey, { cur: currencySymbol(currency) })}:</span>
          <span style={{ fontWeight: 600 }}>{formatMetricValue(m, row[m], currency)}</span>
        </div>
      ))}
    </div>
  );
}

// Akcje z dziennika agenta, które traktujemy jako „zastosowana zmiana AI" i
// zaznaczamy kropką na wykresie szczegółów (reszta, np. sync/raport/alert, to szum).
const CHANGE_ACTIONS = new Set([
  'suggestion_applied', 'auto_apply', 'optimize', 'campaign_settings_update',
  'keyword_enable', 'keyword_update_bid', 'keyword_remove', 'keyword_delete',
  'keyword_add_batch', 'ad_group_update', 'negative_intent_added', 'campaign_published',
  'bid_adjustment', 'budget_change',
]);

// Kropka nad wykresem = zastosowane zmiany AI danego dnia/tygodnia. Duża i klikalna;
// po kliknięciu pokazujemy szczegóły w popoverze (Portal, wysoki z-index), bo dymek
// Recharts chował się pod mini-wykresami niżej i nie dało się go kliknąć.
function EventDot({ cx, cy, events, onClick }) {
  if (cx == null || cy == null || !events || !events.length) return null;
  return (
    <g style={{ cursor: 'pointer' }} onClick={(e) => { e.stopPropagation(); onClick && onClick(events, e); }}>
      <circle cx={cx} cy={cy} r={13} fill="#7c3aed" opacity={0.12} />
      <circle cx={cx} cy={cy} r={9} fill="#7c3aed" stroke="#fff" strokeWidth={2.5} />
      <text x={cx} y={cy + 3.5} textAnchor="middle" fontSize={10} fontWeight="700" fill="#fff">{events.length}</text>
    </g>
  );
}

// Popover ze szczegółami zmian AI po kliknięciu kropki. Renderowany przez Portal,
// pozycjonowany do kliknięcia (fixed), z wysokim z-index - zawsze nad wykresami.
function MarkerPopup({ popup, onClose }) {
  const { t } = useTranslation();
  if (!popup) return null;
  const left = Math.min(popup.x, (typeof window !== 'undefined' ? window.innerWidth : 1200) - 320);
  const top = Math.min(popup.y + 12, (typeof window !== 'undefined' ? window.innerHeight : 800) - 20 - popup.events.length * 22 - 40);
  return (
    <Portal>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 4000 }}>
        <div onClick={(e) => e.stopPropagation()} style={{
          position: 'fixed', left: Math.max(8, left), top: Math.max(8, top), zIndex: 4001,
          background: '#fff', border: '1px solid #e4e6ec', borderRadius: 10,
          boxShadow: '0 8px 28px rgba(0,0,0,.16)', padding: '10px 12px', maxWidth: 300, fontSize: 12,
        }}>
          <div style={{ fontWeight: 600, color: '#7c3aed', marginBottom: 5 }}>{t('campaigns.chart.aiChanges', { count: popup.events.length })}</div>
          {popup.events.map((ev, i) => (
            <div key={i} style={{ color: '#3a4150', marginBottom: 4, lineHeight: 1.35 }}>• {ev.description}</div>
          ))}
        </div>
      </div>
    </Portal>
  );
}

function CampaignMiniChart({ campaignId, onDataLoaded, activeMetrics = [], period, setPeriod, hideControls = false, syncId, events = [], currency = 'PLN' }) {
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [convData, setConvData] = useState(null);
  const [convLoading, setConvLoading] = useState(false);
  const [markerPopup, setMarkerPopup] = useState(null); // { events, x, y } - po kliknięciu kropki
  // Controlled by the parent's shared period when provided; otherwise self-managed.
  const [internalSel, setInternalSel] = useState('14');
  const controlled = period !== undefined;
  const sel = controlled ? period : internalSel;
  const setSel = controlled ? setPeriod : setInternalSel;
  // 'month' = calendar month-to-date (days = current day of month), like the dashboard.
  const chartDays = sel === 'month' ? new Date().getDate() : Number(sel);
  const chartDaysCount = String(chartDays);

  // Single selected metric (breakdown/area only make sense for exactly one).
  const single = activeMetrics.length === 1 ? activeMetrics[0] : null;
  const overlay = activeMetrics.length >= 2;

  useEffect(() => {
    setLoading(true);
    setConvData(null);
    api.get(`/ads/dashboard/chart/?days=${chartDaysCount}&campaign_id=${campaignId}`)
      .then(res => {
        const raw = (res.data.days || []);
        // ROAS (wartość konw./koszt) liczymy z gotowych pól dnia/tygodnia, żeby linia
        // działała dla każdego zakresu (dane historyczne = te same, co dla wart. konwersji).
        const formatted = raw.map(d => ({
          ...d,
          roas: (d.cost && d.cost > 0) ? +(((d.conversions_value || 0) / d.cost).toFixed(2)) : 0,
          _full: d.date,  // pełna data (YYYY-MM-DD) do dopasowania zdarzeń
          date: d.date.length > 7 ? d.date.slice(5) : d.date,
        }));
        setData(formatted);
        if (onDataLoaded) onDataLoaded(formatted);
      })
      .catch(() => setData([]))
      .finally(() => setLoading(false));
  }, [campaignId, chartDaysCount]);

  // Fetch conversion breakdown only when conversions is the sole selected metric
  useEffect(() => {
    if (single === 'conversions' && !convData) {
      setConvLoading(true);
      api.get(`/ads/campaigns/${campaignId}/conversion-breakdown/?days=${chartDaysCount}`)
        .then(res => setConvData(res.data))
        .catch(() => {})
        .finally(() => setConvLoading(false));
    }
  }, [single, campaignId, convData, chartDaysCount]);

  const metric = single && MINI_CHART_METRICS[single]
    ? { key: single, ...MINI_CHART_METRICS[single], label: t(MINI_CHART_METRICS[single].labelKey, { cur: currencySymbol(currency) }) }
    : null;

  const title = overlay ? t('campaigns.chart.metricComparison') : (metric ? metric.label : t('campaigns.chart.performance'));

  const placeholder = (content) => (
    <Paper withBorder p="sm" radius="md" style={{ minHeight: 190 }}>
      <Text size="sm" fw={600} c="dark" mb="xs">{title}</Text>
      <Flex justify="center" align="center" style={{ height: 140 }}>{content}</Flex>
    </Paper>
  );

  if (loading) return placeholder(<Loader size="xs" color="brand" />);
  if (data.length === 0) return placeholder(<Text size="xs" c="dimmed">{t('campaigns.chart.noData')}</Text>);

  // Conversion breakdown chart - fill all days
  const showConvBreakdown = single === 'conversions' && convData?.types?.length > 0;
  const convChartData = showConvBreakdown
    ? (() => {
        const typeDefaults = {};
        convData.types.forEach(t => { typeDefaults[t] = 0; });
        return fillDateRange(convData.daily, parseInt(chartDaysCount), { _total: 0, ...typeDefaults });
      })()
    : null;

  // For the overlay, normalise each series to its own max (0-100) so metrics
  // with very different scales are comparable; the tooltip shows real values.
  let overlayData = null;
  if (overlay) {
    const maxes = {};
    activeMetrics.forEach(m => { maxes[m] = Math.max(1, ...data.map(d => Number(d[m]) || 0)); });
    overlayData = data.map(d => {
      const row = { date: d.date };
      activeMetrics.forEach(m => {
        row[m] = Number(d[m]) || 0;
        row[`${m}__n`] = (Number(d[m]) || 0) / maxes[m] * 100;
      });
      return row;
    });
  }

  // Znaczniki zastosowanych zmian AI: dopasuj zdarzenia (z dziennika agenta) do
  // punktów wykresu po dacie (dzień lub tydzień) i pokaż jako kropki nad wykresem.
  const eventMarkers = (() => {
    if (!events || !events.length || !data.length) return [];
    const evs = events
      .filter(e => e && CHANGE_ACTIONS.has(e.action))
      .map(e => ({ day: (e.date || '').slice(0, 10), description: e.description || '' }))
      .filter(e => e.day);
    if (!evs.length) return [];
    const byPoint = {};
    evs.forEach(e => {
      let idx = -1;
      for (let i = 0; i < data.length; i++) {
        const start = data[i]._full;
        const end = i + 1 < data.length ? data[i + 1]._full : '9999-12-31';
        if (start && start <= e.day && e.day < end) { idx = i; break; }
      }
      if (idx >= 0) (byPoint[idx] = byPoint[idx] || []).push(e);
    });
    return Object.entries(byPoint).map(([idx, list]) => ({ date: data[idx].date, events: list }));
  })();

  return (
    <Paper withBorder p="sm" radius="md" style={{ minHeight: 190 }}>
      <Group justify="space-between" mb="xs" wrap="nowrap">
        <Text size="sm" fw={600} c="dark">
          {title}
          {showConvBreakdown && <Text span size="xs" c="dimmed" ml="xs">{t('campaigns.chart.byType')}</Text>}
          {overlay && <Text span size="xs" c="dimmed" ml="xs">{t('campaigns.chart.normalized')}</Text>}
          {!overlay && !metric && eventMarkers.length > 0 && (
            <Text span size="xs" c="dimmed" ml="xs">
              <span style={{ display: 'inline-block', width: 7, height: 7, borderRadius: '50%', background: '#7c3aed', marginRight: 4, verticalAlign: 'middle' }} />
              {t('campaigns.chart.aiChangesClick')}
            </Text>
          )}
        </Text>
        {!hideControls && (
          <SegmentedControl
            value={sel}
            onChange={setSel}
            data={[
              { value: '7', label: '7d' },
              { value: '14', label: '14d' },
              { value: '30', label: '30d' },
              { value: '90', label: '90d' },
              { value: 'month', label: t('campaigns.chart.thisMonth') },
            ]}
            size="xs"
          />
        )}
      </Group>
      {/* Tryb wielu metryk nakłada serie na jeden wykres z własną legendą poniżej. */}
      {convLoading && single === 'conversions' ? (
        <Flex justify="center" align="center" style={{ height: 140 }}>
          <Loader size="xs" color="brand" />
        </Flex>
      ) : overlay ? (
        // Wszystkie wybrane metryki nałożone na jeden wykres. Serie znormalizowane
        // do wspólnej skali 0-100, żeby metryki o różnych rzędach wielkości dało się
        // porównać; tooltip i legenda pokazują wartości rzeczywiste.
        <div>
          <Group gap="md" mb={4} wrap="wrap">
            {activeMetrics.map(m => (
              <Group key={m} gap={5} wrap="nowrap">
                <span style={{ width: 9, height: 9, borderRadius: 2, background: MINI_CHART_METRICS[m].color, display: 'inline-block' }} />
                <Text size="xs" c="dimmed">{t(MINI_CHART_METRICS[m].labelKey, { cur: currencySymbol(currency) })}</Text>
              </Group>
            ))}
          </Group>
          <ResponsiveContainer width="100%" height={140}>
            <ComposedChart data={overlayData} syncId={syncId} syncMethod="value" margin={{ top: 5, right: 5, left: -10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="date" tick={{ fontSize: 10 }} stroke="#ccc" />
              <YAxis tick={{ fontSize: 9 }} stroke="#ccc" width={30} domain={[0, 100]} hide />
              <RTooltip content={<OverlayTooltip metrics={activeMetrics} currency={currency} />} />
              {activeMetrics.map(m => (
                <Line key={m} type="monotone" dataKey={`${m}__n`} stroke={MINI_CHART_METRICS[m].color} strokeWidth={2} dot={false} name={t(MINI_CHART_METRICS[m].labelKey, { cur: currencySymbol(currency) })} />
              ))}
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={140}>
          {showConvBreakdown ? (
            <ComposedChart data={convChartData} syncId={syncId} syncMethod="value" margin={{ top: 5, right: 5, left: -10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="date" tick={{ fontSize: 10 }} stroke="#ccc" />
              <YAxis tick={{ fontSize: 9 }} stroke="#ccc" width={30} />
              <RTooltip contentStyle={{ fontSize: 11, borderRadius: 8, border: '1px solid #eee' }} />
              {convData.types.map((type, i) => (
                <Bar key={type} dataKey={type} stackId="conv" fill={CONV_COLORS[i % CONV_COLORS.length]} name={type} radius={i === convData.types.length - 1 ? [2,2,0,0] : [0,0,0,0]} />
              ))}
            </ComposedChart>
          ) : metric ? (
            <AreaChart data={data} syncId={syncId} syncMethod="value" margin={{ top: 5, right: 5, left: -10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="date" tick={{ fontSize: 10 }} stroke="#ccc" />
              <YAxis tick={{ fontSize: 9 }} stroke="#ccc" width={45} domain={['auto', 'auto']} />
              <RTooltip contentStyle={{ fontSize: 11, borderRadius: 8, border: '1px solid #eee' }}
                formatter={(v) => [typeof v === 'number' ? v.toLocaleString('pl-PL') : v, metric.label]} />{/* metric.label is resolved via i18n above */}
              <Area type="monotone" dataKey={metric.key} stroke={metric.color} fill={metric.color} fillOpacity={0.15} strokeWidth={2} dot={{ r: 2, fill: metric.color }} />
            </AreaChart>
          ) : (
            <ComposedChart data={data} syncId={syncId} syncMethod="value" margin={{ top: 14, right: 0, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="date" tick={{ fontSize: 10 }} stroke="#ccc" />
              <YAxis yAxisId="cost" tick={{ fontSize: 9 }} stroke="#ccc" width={40} />
              <YAxis yAxisId="clicks" orientation="right" tick={{ fontSize: 9 }} stroke="#ccc" width={35} />
              <YAxis yAxisId="conv" orientation="right" hide domain={[0, 'auto']} />
              <YAxis yAxisId="ev" domain={[0, 1]} hide />
              <RTooltip contentStyle={{ fontSize: 11, borderRadius: 8, border: '1px solid #eee' }} wrapperStyle={{ zIndex: 60 }} />
              <Bar dataKey="cost" yAxisId="cost" fill="#1D4ED8" radius={[2, 2, 0, 0]} barSize={8} opacity={0.7} name={t('campaigns.chartMetrics.cost', { cur: currencySymbol(currency) })} />
              <Line dataKey="clicks" yAxisId="clicks" stroke="#3b82f6" strokeWidth={1.5} dot={false} name={t('campaigns.chartMetrics.clicks')} />
              <Line dataKey="conversions" yAxisId="conv" stroke="#10b981" strokeWidth={1.5} dot={false} name={t('campaigns.chartMetrics.conversions')} />
              {eventMarkers.map((m, i) => (
                <ReferenceDot key={`ev-${i}`} x={m.date} y={1} yAxisId="ev" xAxisId={0} isFront ifOverflow="extendDomain"
                  shape={<EventDot events={m.events} onClick={(events, e) => setMarkerPopup({ events, x: e.clientX, y: e.clientY })} />} />
              ))}
            </ComposedChart>
          )}
        </ResponsiveContainer>
      )}
      <MarkerPopup popup={markerPopup} onClose={() => setMarkerPopup(null)} />
    </Paper>
  );
}

// Strategie stawek dopuszczane przez Google dla typu kampanii (reszta: wszystkie).
// Musi zgadzać się z UpdateBiddingStrategyView.ALLOWED_BY_TYPE w backend/ads/views.py.
const STRATEGIES_BY_TYPE = {
  performance_max: ['maximize_conversions', 'target_cpa', 'target_roas'],
  shopping: ['manual_cpc', 'maximize_clicks', 'target_roas'],
};

const STRATEGY_HINTS = {
  manual_cpc: 'campaigns.strategyHints.manualCpc',
  enhanced_cpc: 'campaigns.strategyHints.enhancedCpc',
  maximize_conversions: 'campaigns.strategyHints.maximizeConversions',
  maximize_clicks: 'campaigns.strategyHints.maximizeClicks',
  target_cpa: 'campaigns.strategyHints.targetCpa',
  target_roas: 'campaigns.strategyHints.targetRoas',
};

const PMAX_STRENGTH = {
  excellent: { c: 'green', labelKey: 'campaigns.pmaxStrength.excellent' },
  good: { c: 'teal', labelKey: 'campaigns.pmaxStrength.good' },
  average: { c: 'yellow', labelKey: 'campaigns.pmaxStrength.average' },
  poor: { c: 'red', labelKey: 'campaigns.pmaxStrength.poor' },
  pending: { c: 'gray', labelKey: 'campaigns.pmaxStrength.pending' },
  no_ads: { c: 'gray', labelKey: 'campaigns.pmaxStrength.noAds' },
  unknown: { c: 'gray', labelKey: 'campaigns.pmaxStrength.unknown' },
};

function PmaxStrengthBadge({ value }) {
  const { t } = useTranslation();
  const m = PMAX_STRENGTH[value] || PMAX_STRENGTH.unknown;
  return <Badge color={m.c} variant="light" size="sm">{t(m.labelKey)}</Badge>;
}

// Performance Max panel: read-only structure (bidding, asset groups + ad strength,
// signals) synced from Google Ads. PMax has no keywords/CPC, so no keyword/bid UI.
function PmaxPanel({ campaignId, currency = 'PLN' }) {
  const { t } = useTranslation();
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  React.useEffect(() => {
    let active = true;
    setLoading(true);
    api.get(`/ads/campaigns/${campaignId}/pmax/`)
      .then(res => { if (active) setData(res.data); })
      .catch(() => { if (active) setData(null); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [campaignId]);

  if (loading) return <Flex align="center" justify="center" py="xl" gap="xs"><Loader size="sm" color="brand" /><Text size="sm" c="dimmed">{t('campaigns.pmax.loading')}</Text></Flex>;
  if (!data || !data.campaign) return <Text size="sm" c="dimmed" ta="center" py="xl">{t('campaigns.pmax.noData')}</Text>;

  const camp = data.campaign;
  const sum = data.ad_strength_summary || {};
  const strategyLabel = { target_roas: 'Target ROAS', target_cpa: 'Target CPA', maximize_conversions: t('campaigns.pmax.maxConversions'), maximize_conversion_value: t('campaigns.pmax.maxConvValue') }[camp.bidding_strategy] || camp.bidding_strategy;

  return (
    <Stack gap="md">
      <SimpleGrid cols={3} spacing="sm">
        <Paper p="sm" withBorder bg="gray.0"><Text size="xs" c="dimmed">{t('campaigns.pmax.biddingStrategy')}</Text><Text size="sm" fw={600}>{strategyLabel}</Text></Paper>
        <Paper p="sm" withBorder bg="gray.0"><Text size="xs" c="dimmed">{camp.target_cpa ? t('campaigns.pmax.targetCpa') : t('campaigns.pmax.targetRoas')}</Text><Text size="sm" fw={600}>{camp.target_roas ? `${(camp.target_roas * 100).toFixed(0)}%` : camp.target_cpa ? formatMoney(camp.target_cpa, currency) : t('campaigns.pmax.noTarget')}</Text></Paper>
        <Paper p="sm" withBorder bg="gray.0"><Text size="xs" c="dimmed">{t('campaigns.pmax.dailyBudget')}</Text><Text size="sm" fw={600}>{formatMoney(camp.budget_amount || 0, currency, { decimals: 0 })}</Text></Paper>
      </SimpleGrid>

      <Paper p="sm" withBorder>
        <Text size="xs" c="dimmed" mb={6}>{t('campaigns.pmax.assetGroupStrength', { count: data.asset_group_count })}</Text>
        <Group gap="md">
          {['excellent', 'good', 'average', 'poor', 'pending', 'no_ads'].filter(k => sum[k]).map(k => (
            <Group key={k} gap={5}><PmaxStrengthBadge value={k} /><Text size="xs" fw={600}>{sum[k]}</Text></Group>
          ))}
        </Group>
      </Paper>

      <div>
        <Text size="xs" c="dimmed" mb={4}>{t('campaigns.pmax.assetGroups')}</Text>
        <Table striped highlightOnHover withTableBorder fz="xs">
          <Table.Thead><Table.Tr>
            <Table.Th>{t('campaigns.pmax.colName')}</Table.Th><Table.Th>{t('campaigns.pmax.colAdStrength')}</Table.Th>
            <Table.Th ta="right">{t('campaigns.chartMetrics.impressions')}</Table.Th><Table.Th ta="right">{t('campaigns.metrics.conversions')}</Table.Th>
            <Table.Th ta="right">{t('campaigns.metrics.cost')}</Table.Th><Table.Th ta="right">ROAS</Table.Th><Table.Th>{t('campaigns.pmax.colSignals')}</Table.Th>
          </Table.Tr></Table.Thead>
          <Table.Tbody>
            {data.asset_groups.map(ag => {
              const sigParts = [];
              if (ag.signals.audiences) sigParts.push(t('campaigns.pmax.audiencesCount', { count: ag.signals.audiences }));
              if (ag.signals.themes && ag.signals.themes.length) sigParts.push(t('campaigns.pmax.themesCount', { count: ag.signals.themes.length }));
              return (
                <Table.Tr key={ag.id}>
                  <Table.Td style={{ maxWidth: 220 }}><Text size="xs" truncate>{ag.name || ag.asset_group_id}</Text></Table.Td>
                  <Table.Td><PmaxStrengthBadge value={ag.ad_strength} /></Table.Td>
                  <Table.Td ta="right">{(ag.impressions || 0).toLocaleString('pl-PL')}</Table.Td>
                  <Table.Td ta="right">{ag.conversions}</Table.Td>
                  <Table.Td ta="right">{formatMoney(ag.cost || 0, currency, { decimals: 0 })}</Table.Td>
                  <Table.Td ta="right">{ag.roas != null ? `${(ag.roas * 100).toFixed(0)}%` : '-'}</Table.Td>
                  <Table.Td><Text size="xs" c="dimmed">{sigParts.join(' · ') || '-'}</Text></Table.Td>
                </Table.Tr>
              );
            })}
          </Table.Tbody>
        </Table>
      </div>
    </Stack>
  );
}

export function CampaignDrawer({ campaign, onClose, onCampaignUpdated, inComparison = false, comparisonFull = false, comparisonCount = 0, onToggleComparison, onOpenComparison }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { addNotification } = useNotifications();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') || 'overview';
  const [activeTab, setActiveTabRaw] = useState(initialTab);
  const [visible, setVisible] = useState(false);

  // Data fetching state
  const [dataLoading, setDataLoading] = useState(true);
  const [adGroups, setAdGroups] = useState([]);
  const [keywords, setKeywords] = useState([]);
  const [negativeKeywords, setNegativeKeywords] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [recentActions, setRecentActions] = useState([]);
  const [campaignData, setCampaignData] = useState(campaign);

  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [archiving, setArchiving] = useState(false);

  // Drawer expand state
  const [drawerExpanded, setDrawerExpanded] = useState(() => localStorage.getItem('drawer_expanded') === 'true');
  const toggleExpanded = () => {
    const next = !drawerExpanded;
    setDrawerExpanded(next);
    localStorage.setItem('drawer_expanded', String(next));
  };

  // Drawer resize (drag the left edge). Width persisted to localStorage.
  const DRAWER_MIN_W = 640;
  const [isDesktop, setIsDesktop] = useState(() => typeof window !== 'undefined' && window.innerWidth >= 768);
  useEffect(() => {
    const onR = () => setIsDesktop(window.innerWidth >= 768);
    onR(); // skoryguj ewentualnie nieaktualny stan początkowy przy otwarciu panelu
    window.addEventListener('resize', onR);
    return () => window.removeEventListener('resize', onR);
  }, []);
  const [drawerWidth, setDrawerWidth] = useState(() => {
    const s = parseInt(localStorage.getItem('drawer_width') || '', 10);
    return Number.isFinite(s) && s >= DRAWER_MIN_W ? s : 1000;
  });
  const [resizing, setResizing] = useState(false);
  const startResize = (e) => {
    e.preventDefault();
    setResizing(true);
    document.body.style.userSelect = 'none';
    const startX = e.clientX;
    const startW = drawerWidth;
    const maxW = () => Math.round(window.innerWidth * 0.98);
    const onMove = (ev) => {
      const delta = startX - ev.clientX; // drag left = wider (panel anchored right)
      setDrawerWidth(Math.max(DRAWER_MIN_W, Math.min(startW + delta, maxW())));
    };
    const onUp = () => {
      setResizing(false);
      document.body.style.userSelect = '';
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      setDrawerWidth((w) => { localStorage.setItem('drawer_width', String(w)); return w; });
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  // Switch the tab bar to a dropdown when the tabs don't fit the (resizable)
  // drawer width, measured directly instead of via a viewport breakpoint.
  const tabsContainerRef = useRef(null);
  const tabsListRef = useRef(null);
  const [tabsFit, setTabsFit] = useState(true);
  useEffect(() => {
    const measure = () => {
      const list = tabsListRef.current;
      const container = tabsContainerRef.current;
      if (!list || !container) return;
      setTabsFit(list.scrollWidth <= container.clientWidth + 1);
    };
    measure();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    if (ro && tabsContainerRef.current) ro.observe(tabsContainerRef.current);
    window.addEventListener('resize', measure);
    const tm = setTimeout(measure, 50);
    return () => { if (ro) ro.disconnect(); window.removeEventListener('resize', measure); clearTimeout(tm); };
  }, [campaignData, drawerWidth, drawerExpanded]);

  // Edit mode state
  const [editMode, setEditMode] = useState(false);
  const [editName, setEditName] = useState(campaign.name);
  const [editBudget, setEditBudget] = useState(campaign.budget_amount || '');
  const [editStrategy, setEditStrategy] = useState(campaign.bidding_strategy || 'manual_cpc');
  // ROAS pokazujemy/wpisujemy w % (spójnie z resztą UI); w bazie to mnożnik (14.0 = 1400%).
  const [editTargetValue, setEditTargetValue] = useState(
    campaign.bidding_strategy === 'target_roas' && campaign.bidding_target_value
      ? String(Math.round(parseFloat(campaign.bidding_target_value) * 100))
      : (campaign.bidding_target_value || '')
  );
  const [editTargetCpa, setEditTargetCpa] = useState(campaign.target_cpa || '');
  const [editMaxCpc, setEditMaxCpc] = useState(campaign.max_cpc_limit || '');
  const [editKeywordStrategy, setEditKeywordStrategy] = useState(campaign.keyword_strategy || 'standard');
  const [editOptimizationFocus, setEditOptimizationFocus] = useState(campaign.optimization_focus || 'conversions');
  const [editAutoApply, setEditAutoApply] = useState(campaign.auto_apply_override || 'inherit');
  const [editAutoApplyHR, setEditAutoApplyHR] = useState(campaign.auto_apply_high_risk_override || 'inherit');
  const [editAlertsWarn, setEditAlertsWarn] = useState(campaign.email_alerts_warning_override || 'inherit');
  const [editAlertsCrit, setEditAlertsCrit] = useState(campaign.email_alerts_critical_override || 'inherit');
  const [showConversionSetup, setShowConversionSetup] = useState(false);
  const [showReport, setShowReport] = useState(false);
  const [reportPeriod, setReportPeriod] = useState('last_month');
  const [reportLoading, setReportLoading] = useState(false);
  const [chartDays, setChartDays] = useState([]);
  // Up to 4 metrics can be selected and overlaid on the chart at once.
  const MAX_METRICS = 4;
  const initialMetrics = (searchParams.get('metric') || '')
    .split(',').map(s => s.trim()).filter(s => MINI_CHART_METRICS[s]).slice(0, MAX_METRICS);
  const [selectedMetrics, setSelectedMetricsRaw] = useState(initialMetrics);
  // Overview period selector (shared by the mini chart and the stat cards), with a
  // "Ten miesiąc" option - same behaviour as the dashboard.
  const [overviewPeriod, setOverviewPeriod] = useState('30');
  const [periodMetrics, setPeriodMetrics] = useState(null);
  useEffect(() => {
    if (!campaign?.id) { setPeriodMetrics(null); return; }
    api.get(`/ads/campaigns/period-metrics/?period=${overviewPeriod}`)
      // On success, an absent campaign means zero activity in the period (show 0),
      // not the 30-day rolling fallback. Only fall back to c.* if the fetch fails.
      .then(res => setPeriodMetrics((res.data?.metrics || {})[campaign.id]
        || { cost: 0, clicks: 0, impressions: 0, conversions: 0, conversions_value: 0 }))
      .catch(() => setPeriodMetrics(null));
  }, [campaign?.id, overviewPeriod]);
  const persistMetrics = (list) => {
    setSelectedMetricsRaw(list);
    setSearchParams(prev => {
      const params = new URLSearchParams(prev);
      params.set('campaign', campaign.id);
      params.set('tab', activeTab);
      if (list.length) params.set('metric', list.join(',')); else params.delete('metric');
      return params;
    }, { replace: true });
  };
  const toggleMetric = (m) => {
    if (!m) return;
    if (selectedMetrics.includes(m)) {
      persistMetrics(selectedMetrics.filter(x => x !== m));
    } else if (selectedMetrics.length < MAX_METRICS) {
      persistMetrics([...selectedMetrics, m]);
    } else {
      addNotification(t('campaigns.drawer.maxMetrics', { max: MAX_METRICS }), 'info');
    }
  };
  const [saving, setSaving] = useState(false);
  const [keywordActionLoading, setKeywordActionLoading] = useState(null);
  const [keywordVolumes, setKeywordVolumes] = useState({});
  const [volumeLoading, setVolumeLoading] = useState(false);
  const [suggestionActionLoading, setSuggestionActionLoading] = useState(null);
  const [suggestionSafetyModal, setSuggestionSafetyModal] = useState(null); // { suggestion, message }
  const [suggestionDismissModal, setSuggestionDismissModal] = useState(null);

  // Ad group expand/edit state
  const [expandedGroup, setExpandedGroup] = useState(null);
  const [editingGroupBid, setEditingGroupBid] = useState(null);
  const [editBidValue, setEditBidValue] = useState('');
  const [adGroupActionLoading, setAdGroupActionLoading] = useState(null);

  // Ads tab state
  const [showAdForm, setShowAdForm] = useState(false);
  const [editingAd, setEditingAd] = useState(null);
  const [adsRefreshKey, setAdsRefreshKey] = useState(0);

  // Suggested keywords from health check
  const [suggestedKeywords, setSuggestedKeywords] = useState([]);

  const setActiveTab = (tab) => {
    setActiveTabRaw(tab);
    setSearchParams(prev => {
      const params = new URLSearchParams(prev);
      params.set('campaign', campaign.id);
      params.set('tab', tab);
      return params;
    }, { replace: true });
  };

  // Fetch full campaign data on mount
  const fetchCampaignData = useCallback(() => {
    setDataLoading(true);
    api.get(`/ads/campaigns/${campaign.id}/data/`)
      .then(res => {
        const data = res.data;
        setCampaignData(data.campaign || campaign);
        setAdGroups(data.ad_groups || []);
        setKeywords(data.keywords || []);
        setNegativeKeywords(data.negative_keywords || []);
        setSuggestions(data.suggestions || []);
        setRecentActions(data.recent_actions || []);
        setEditName(data.campaign?.name || campaign.name);
        setEditBudget(data.campaign?.budget_amount || campaign.budget_amount || '');
      })
      .catch(() => {
        setCampaignData(campaign);
      })
      .finally(() => setDataLoading(false));
  }, [campaign]);

  useEffect(() => {
    fetchCampaignData();
  }, [fetchCampaignData]);

  useEffect(() => {
    requestAnimationFrame(() => setVisible(true));
  }, []);

  const handleClose = () => {
    setVisible(false);
    setTimeout(onClose, 300);
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      const payload = {};
      if (editName !== campaignData.name) payload.name = editName;
      if (String(editBudget) !== String(campaignData.budget_amount)) payload.budget_amount = editBudget;
      if (String(editTargetCpa) !== String(campaignData.target_cpa || '')) payload.target_cpa = editTargetCpa || '';
      if (String(editMaxCpc) !== String(campaignData.max_cpc_limit || '')) payload.max_cpc_limit = editMaxCpc || '';
      if (editKeywordStrategy !== (campaignData.keyword_strategy || 'standard')) payload.keyword_strategy = editKeywordStrategy;
      if (editOptimizationFocus !== (campaignData.optimization_focus || 'conversions')) payload.optimization_focus = editOptimizationFocus;
      if (editAutoApply !== (campaignData.auto_apply_override || 'inherit')) payload.auto_apply_override = editAutoApply;
      if (editAutoApplyHR !== (campaignData.auto_apply_high_risk_override || 'inherit')) payload.auto_apply_high_risk_override = editAutoApplyHR;
      if (editAlertsWarn !== (campaignData.email_alerts_warning_override || 'inherit')) payload.email_alerts_warning_override = editAlertsWarn;
      if (editAlertsCrit !== (campaignData.email_alerts_critical_override || 'inherit')) payload.email_alerts_critical_override = editAlertsCrit;

      // Strategia stawek (osobny endpoint). Zapisz też, gdy zmieniła się sama WARTOŚĆ
      // celu (np. ROAS), a strategia została ta sama - wcześniej taki przypadek był
      // po cichu pomijany ("nie zapisuje ustawień").
      const needsTarget = editStrategy === 'target_cpa' || editStrategy === 'target_roas';
      // ROAS wpisywany w % -> mnożnik dla Google (1400% -> 14.0). CPA bez zmian.
      const sendTarget = (needsTarget && editTargetValue !== '')
        ? (editStrategy === 'target_roas' ? parseFloat(editTargetValue) / 100 : parseFloat(editTargetValue))
        : null;
      const strategyChanged = editStrategy !== (campaignData.bidding_strategy || 'manual_cpc');
      const targetChanged = needsTarget && sendTarget != null && !Number.isNaN(sendTarget)
        && Math.abs(sendTarget - parseFloat(campaignData.bidding_target_value || 0)) > 1e-6;
      const biddingNeeded = strategyChanged || targetChanged;
      if (Object.keys(payload).length === 0 && !biddingNeeded) {
        setEditMode(false);
        setSaving(false);
        return;
      }

      // Najpierw pozostałe ustawienia, potem strategia osobno: odrzucona strategia
      // (np. Maximize Clicks w PMax) blokowała wcześniej zapis budżetu i reszty
      // („nie zapisuje ustawień”, RedBlond 2026-10-17).
      const res = Object.keys(payload).length > 0
        ? await api.post(`/ads/campaigns/${campaign.id}/settings/`, payload)
        : { data: { campaign: campaignData } };
      setCampaignData(res.data.campaign);

      if (biddingNeeded) {
        const stratPayload = { strategy: editStrategy };
        if (sendTarget != null && !Number.isNaN(sendTarget)) stratPayload.target_value = sendTarget;
        try {
          await api.post(`/ads/campaigns/${campaign.id}/bidding-strategy/`, stratPayload);
        } catch (err) {
          addNotification(err.response?.data?.error || t('campaigns.drawer.settingsSaveError'), 'error');
          if (onCampaignUpdated) onCampaignUpdated();
          fetchCampaignData();
          return;
        }
      }

      setEditMode(false);
      addNotification(t('campaigns.drawer.settingsSaved'), 'success');
      if (onCampaignUpdated) onCampaignUpdated();
      fetchCampaignData();
    } catch (err) {
      addNotification(err.response?.data?.error || t('campaigns.drawer.settingsSaveError'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleCampaignStatus = async () => {
    const prevStatus = campaignData.status;
    const newStatus = prevStatus === 'active' ? 'paused' : 'active';
    setSaving(true);
    // Optymistycznie odwracamy status od razu, żeby UI nie czekał na round-trip
    // (kaskada zestawów/reklam Meta dzieje się w tle po stronie backendu).
    setCampaignData(prev => ({ ...prev, status: newStatus }));
    try {
      const res = await api.post(`/ads/campaigns/${campaign.id}/settings/`, { status: newStatus });
      setCampaignData(res.data.campaign);
      addNotification(
        newStatus === 'paused' ? t('campaigns.drawer.campaignPaused') : t('campaigns.drawer.campaignResumed'),
        'success'
      );
      if (onCampaignUpdated) onCampaignUpdated();
    } catch (err) {
      setCampaignData(prev => ({ ...prev, status: prevStatus }));  // cofnij przy błędzie
      addNotification(err.response?.data?.error || t('campaigns.drawer.statusChangeError'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleKeywordAction = async (kw, action) => {
    setKeywordActionLoading(kw.id);
    try {
      await api.post(`/ads/campaigns/${campaign.id}/keywords/${kw.id}/`, { action });
      addNotification(
        action === 'pause' ? t('campaigns.drawer.keywordPaused', { text: kw.text }) :
        action === 'enable' ? t('campaigns.drawer.keywordResumed', { text: kw.text }) :
        t('campaigns.drawer.keywordActionDone', { text: kw.text }),
        'success'
      );
      fetchCampaignData();
    } catch (err) {
      addNotification(err.response?.data?.error || t('campaigns.drawer.actionError'), 'error');
    } finally {
      setKeywordActionLoading(null);
    }
  };

  const handleCheckVolumes = async () => {
    if (!keywords.length) return;
    setVolumeLoading(true);
    try {
      const kwTexts = keywords.map(kw => kw.text);
      const res = await api.post('/ads/keyword-planner/', { keywords: kwTexts });
      const volumes = {};
      (res.data.results || []).forEach(r => {
        volumes[r.text.toLowerCase()] = r;
      });
      setKeywordVolumes(volumes);
    } catch (err) {
      addNotification(err.response?.data?.error || t('campaigns.drawer.volumeError'), 'error');
    } finally {
      setVolumeLoading(false);
    }
  };

  const handleSuggestionApply = async (suggestion, force = false) => {
    setSuggestionActionLoading(suggestion.id);
    try {
      const res = await api.post(`/ads/suggestions/${suggestion.id}/apply/`, force ? { force: true } : {});
      // Zmiana przekracza limit bezpieczeństwa (np. skok budżetu > 30%). Nie udawaj,
      // że zastosowano - poinformuj i pozwól potwierdzić („zastosuj mimo to").
      if (res.data?.status === 'safety_warning') {
        setSuggestionSafetyModal({ suggestion, message: res.data.message });
        return;
      }
      addNotification(t('campaigns.drawer.suggestionApplied'), 'success');
      fetchCampaignData();
    } catch {
      addNotification(t('campaigns.drawer.suggestionApplyError'), 'error');
    } finally {
      setSuggestionActionLoading(null);
    }
  };

  const handleSuggestionDismiss = (suggestion) => setSuggestionDismissModal(suggestion);

  const confirmSuggestionDismiss = async ({ reason, remember }) => {
    const suggestion = suggestionDismissModal;
    if (!suggestion) return;
    setSuggestionDismissModal(null);
    setSuggestionActionLoading(suggestion.id);
    try {
      await api.post(`/ads/suggestions/${suggestion.id}/dismiss/`, { reason, remember });
      addNotification(t('campaigns.drawer.suggestionDismissed'), 'success');
      fetchCampaignData();
    } catch {
      addNotification(t('campaigns.drawer.suggestionDismissError'), 'error');
    } finally {
      setSuggestionActionLoading(null);
    }
  };

  // Ad group actions
  const handleToggleAdGroupStatus = async (group) => {
    const newStatus = group.status === 'active' ? 'paused' : 'active';
    setAdGroupActionLoading(group.id);
    try {
      await api.post(`/ads/campaigns/${campaign.id}/ad-groups/${group.id}/`, { status: newStatus });
      addNotification(
        newStatus === 'paused' ? t('campaigns.drawer.groupPaused', { name: group.name }) : t('campaigns.drawer.groupResumed', { name: group.name }),
        'success'
      );
      fetchCampaignData();
    } catch (err) {
      addNotification(err.response?.data?.error || t('campaigns.drawer.groupStatusError'), 'error');
    } finally {
      setAdGroupActionLoading(null);
    }
  };

  const handleSaveGroupBid = async (group) => {
    setAdGroupActionLoading(group.id);
    try {
      await api.post(`/ads/campaigns/${campaign.id}/ad-groups/${group.id}/`, { cpc_bid: editBidValue });
      addNotification(t('campaigns.drawer.groupBidUpdated', { name: group.name }), 'success');
      setEditingGroupBid(null);
      fetchCampaignData();
    } catch (err) {
      addNotification(err.response?.data?.error || t('campaigns.drawer.bidSaveError'), 'error');
    } finally {
      setAdGroupActionLoading(null);
    }
  };

  const handleGenerateAdForGroup = (group) => {
    setEditingAd({ adGroupId: group.id, data: group.ad_data });
    setActiveTab('ads');
    setShowAdForm(true);
  };

  const hasScheduler = campaignData?.scheduler_enabled || user?.organization?.scheduled_actions_enabled;
  const isMeta = campaign.platform === 'meta_ads';
  const campaignType = campaignData?.campaign_type || campaign.campaign_type || 'search';
  const isShopping = campaignType === 'shopping';
  const isPmax = campaignType === 'performance_max' || campaignType === 'pmax';
  const isUnsupportedType = campaign.platform === 'google_ads' && campaignType && campaignType !== 'search' && campaignType !== 'shopping' && !isPmax && campaignType !== '';
  // PMax nie ma słów kluczowych, grup reklam ani zwykłych reklam (zautomatyzowane
  // przez Google) - chowamy zakładki, które go nie dotyczą, żeby nie pokazywać
  // wszędzie tego samego/pustego.
  const tabs = [
    { id: 'overview', label: t('campaigns.tabs.overview'), icon: ChartBar },
    ...(isShopping ? [{ id: 'shopping', label: t('campaigns.tabs.products'), icon: ShoppingCart }] : []),
    ...(isPmax ? [{ id: 'asset-groups', label: t('campaigns.tabs.assetGroups'), icon: Rows }] : []),
    { id: 'health', label: t('campaigns.tabs.health'), icon: Heart },
    { id: 'conversions', label: t('campaigns.tabs.conversions'), icon: Target },
    ...(!isShopping && !isPmax ? [{ id: 'adgroups', label: isMeta ? t('campaigns.tabs.adSets') : t('campaigns.tabs.adGroups'), icon: Rows }] : []),
    ...(!isMeta && !isShopping && !isPmax ? [{ id: 'keywords', label: t('campaigns.tabs.keywords'), icon: Key }] : []),
    ...(!isShopping && !isPmax ? [{ id: 'ads', label: t('campaigns.tabs.ads'), icon: TextAa }] : []),
    ...(isMeta ? [{ id: 'targeting', label: t('campaigns.tabs.targeting'), icon: UsersThree }] : []),
    ...(!isShopping && !isPmax ? [{ id: 'creative-perf', label: t('campaigns.tabs.creativePerf'), icon: Sparkle }] : []),
    ...(!isMeta && !isShopping && !isPmax ? [{ id: 'search-terms', label: t('campaigns.tabs.searchTerms'), icon: MagnifyingGlass }] : []),
    ...(isMeta ? [{ id: 'leads', label: t('campaigns.tabs.leads'), icon: FileText }] : []),
    { id: 'suggestions', label: t('campaigns.tabs.suggestions'), icon: Lightbulb },
    { id: 'schedule', label: t('campaigns.tabs.schedule'), icon: CalendarBlank },
  ];
  // Jeśli aktywna zakładka została ukryta (np. deep-link tab=keywords dla PMax),
  // wróć do Przeglądu - inaczej treść byłaby pusta.
  useEffect(() => {
    if (tabs.length && !tabs.some(t => t.id === activeTab)) setActiveTab('overview');
  }, [activeTab, isPmax, isShopping, isMeta]); // eslint-disable-line

  const c = campaignData;
  // Collapse the header action buttons to icons-only when the drawer is narrow
  // (mobile, or a desktop drawer resized below the threshold) so they don't crowd.
  const compactActions = !isDesktop || (!drawerExpanded && drawerWidth < 860);
  // Meta lead-gen campaign: "conversions" are leads collected via the Instant Form.
  const isLeadGen = isMeta && (c?.campaign_goal === 'leads' || campaign.campaign_goal === 'leads');
  const conversionWord = isLeadGen ? t('campaigns.tabs.leads') : t('campaigns.metrics.conversions');

  // Collect all ads across ad groups for the Ads tab
  const allAds = adGroups
    .filter(ag => ag.ad_data && ag.ad_data.headlines && ag.ad_data.headlines.length > 0)
    .map(ag => ({ ...ag.ad_data, adGroupId: ag.id, adGroupName: ag.name }));

  return (
    <div className="fixed inset-0 z-40">
      <div
        className={`absolute inset-0 bg-black/20 transition-opacity duration-300 ${visible ? 'opacity-100' : 'opacity-0'}`}
        onClick={handleClose}
      />

      <div
        className={`fixed top-0 right-0 bottom-0 w-full ${drawerExpanded ? 'md:w-[calc(100%-60px)]' : ''} bg-white border-l border-gray-200 shadow-[-8px_0_30px_rgba(0,0,0,0.15)] ${resizing ? '' : 'transition-all duration-300'} ${
          visible ? 'translate-x-0' : 'translate-x-full'
        } flex flex-col`}
        style={(!drawerExpanded && isDesktop) ? {
          // Zawsze jawna szerokość na desktopie z gwarantowanym minimum - panel
          // nigdy nie otwiera się zwinięty do paska przy krawędzi (zgłoszenie YRKE).
          width: Math.max(DRAWER_MIN_W, Math.min(drawerWidth, Math.round((typeof window !== 'undefined' ? window.innerWidth : 1200) * 0.98))),
          minWidth: DRAWER_MIN_W,
        } : undefined}
      >
        {/* Resize handle (desktop, when not expanded) */}
        {!drawerExpanded && isDesktop && (
          <div
            onMouseDown={startResize}
            onDoubleClick={() => { setDrawerWidth(1000); localStorage.setItem('drawer_width', '1000'); }}
            title={t('campaigns.drawer.resizeHint')}
            className="hidden md:flex items-center justify-center absolute left-0 top-0 bottom-0 z-20 group"
            style={{ width: 16, cursor: 'col-resize' }}
          >
            {/* Full-height edge line, only visible on hover/drag */}
            <div className={`absolute left-0 top-0 bottom-0 w-[2px] transition-colors ${resizing ? 'bg-indigo-300' : 'bg-transparent group-hover:bg-indigo-200'}`} />
            {/* Subtle centered grip: faint at rest, clearer on hover/drag */}
            <div
              className={`relative z-10 flex items-center justify-center rounded transition-all ${resizing ? 'bg-indigo-100 text-indigo-500' : 'bg-transparent text-gray-300 group-hover:bg-gray-100 group-hover:text-gray-500'}`}
              style={{ width: 14, height: 34 }}
            >
              <DotsSixVertical size={12} />
            </div>
          </div>
        )}
        <div className="flex items-center justify-between px-3 md:px-6 h-[57px] border-b border-gray-200 gap-2">
          <div className="flex items-center gap-2 md:gap-3 min-w-0 flex-1">
            <Tooltip label={drawerExpanded ? t('campaigns.drawer.collapsePanel') : t('campaigns.drawer.expandPanel')} withArrow>
              <button
                onClick={toggleExpanded}
                className="hidden md:flex items-center justify-center w-7 h-7 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition shrink-0"
              >
                {drawerExpanded ? <ArrowsInSimple size={16} /> : <ArrowsOutSimple size={16} />}
              </button>
            </Tooltip>
            <div className="min-w-0">
              <div className="flex items-center gap-1.5">
                {c.is_managed && (
                  <Tooltip label={c.is_trial_active ? t('campaigns.drawer.managedTrialTooltip', { days: c.trial_days_remaining }) : t('campaigns.drawer.managedTooltip')} withArrow position="bottom">
                    <span className="inline-flex items-center justify-center shrink-0 text-indigo-600">
                      <Sparkle size={15} weight="fill" />
                    </span>
                  </Tooltip>
                )}
                <h2 className="text-sm font-semibold text-gray-900 truncate max-w-[120px] md:max-w-none">{c.name}</h2>
                {c.platform && c.platform !== 'google_ads' && (
                  <span className="inline-flex items-center text-[9px] font-medium px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 shrink-0">
                    {c.platform === 'meta_ads' ? 'Meta' : c.platform === 'microsoft_ads' ? 'Bing' : c.platform === 'tiktok_ads' ? 'TikTok' : c.platform.replace('_ads', '')}
                  </span>
                )}
              </div>
              {c.platform_campaign_id && !c.platform_campaign_id.startsWith('demo_') && !c.platform_campaign_id.startsWith('imported_') && (
                <p className="text-[10px] text-gray-400 font-mono">ID: {c.platform_campaign_id}</p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            {onToggleComparison && (
              <Tooltip
                label={inComparison ? t('campaigns.drawer.removeFromCompare') : comparisonFull ? t('campaigns.drawer.compareFull') : t('campaigns.drawer.addToCompare')}
                withArrow
              >
                <button
                  onClick={onToggleComparison}
                  disabled={comparisonFull}
                  className={`inline-flex items-center gap-1.5 text-xs font-medium border transition px-2.5 py-1.5 rounded-md ${
                    inComparison
                      ? 'text-indigo-700 bg-indigo-50 border-indigo-200 hover:bg-indigo-100'
                      : comparisonFull
                        ? 'text-gray-300 bg-white border-gray-100 cursor-not-allowed'
                        : 'text-gray-600 bg-white border-gray-200 hover:bg-gray-50 hover:text-gray-800'
                  }`}
                >
                  {inComparison ? <Check className="w-3.5 h-3.5" weight="bold" /> : <Plus className="w-3.5 h-3.5" weight="bold" />}
                  <span className={compactActions ? 'hidden' : 'inline'}>{inComparison ? t('campaigns.drawer.inCompare') : t('campaigns.drawer.compare')}</span>
                </button>
              </Tooltip>
            )}
            {onToggleComparison && comparisonCount >= 2 && (
              <Tooltip label={t('campaigns.drawer.openCompare')} withArrow>
                <button
                  onClick={onOpenComparison}
                  className="inline-flex items-center gap-1.5 text-xs font-medium text-indigo-700 bg-indigo-50 border border-indigo-200 hover:bg-indigo-100 transition px-2 py-1.5 rounded-md"
                >
                  <ChartBar className="w-3.5 h-3.5" weight="bold" />
                  <span className="inline">{comparisonCount}</span>
                </button>
              </Tooltip>
            )}
            {c.is_managed && (
              <Tooltip label={t('campaigns.drawer.exportReportPdf')} withArrow>
              <button
                onClick={() => setShowReport(true)}
                className="inline-flex items-center gap-1.5 text-xs font-medium text-indigo-700 bg-indigo-50 border border-indigo-200 hover:bg-indigo-100 transition px-2.5 py-1.5 rounded-md"
              >
                <FileText className="w-3.5 h-3.5" weight="bold" />
                <span className={compactActions ? 'hidden' : 'inline'}>{t('campaigns.drawer.report')}</span>
              </button>
              </Tooltip>
            )}
            {c.google_campaign_id && !c.google_campaign_id.startsWith('demo_') && (
              <Tooltip label={t('campaigns.drawer.openInGoogleAds')} withArrow>
              <a
                href={`https://ads.google.com/aw/campaigns?campaignId=${c.google_campaign_id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-600 bg-white border border-gray-200 hover:bg-gray-50 hover:text-gray-800 transition px-2.5 py-1.5 rounded-md"
              >
                <GoogleLogo className="w-3.5 h-3.5" weight="bold" />
                <span className={compactActions ? 'hidden' : 'inline'}>Google Ads</span>
                {!compactActions && <ArrowSquareOut className="w-3 h-3 opacity-60" />}
              </a>
              </Tooltip>
            )}
            {isMeta && c.platform_campaign_id && !c.platform_campaign_id.startsWith('demo_') && !c.platform_campaign_id.startsWith('imported_') && (
              <Tooltip label={t('campaigns.drawer.openInMetaAds')} withArrow>
              <a
                href={`https://adsmanager.facebook.com/adsmanager/manage/campaigns?${c.platform_account_id ? `act=${String(c.platform_account_id).replace(/^act_/, '')}&` : ''}selected_campaign_ids=${c.platform_campaign_id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-600 bg-white border border-gray-200 hover:bg-gray-50 hover:text-gray-800 transition px-2.5 py-1.5 rounded-md"
              >
                <MetaLogo className="w-3.5 h-3.5" weight="bold" />
                <span className={compactActions ? 'hidden' : 'inline'}>Ads Manager</span>
                {!compactActions && <ArrowSquareOut className="w-3 h-3 opacity-60" />}
              </a>
              </Tooltip>
            )}
            {!c.is_archived && c.status !== 'draft' && (
              <Tooltip label={t('campaigns.drawer.archiveCampaign')} withArrow>
              <button
                onClick={() => setShowArchiveModal(true)}
                className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-500 bg-white border border-gray-200 hover:bg-red-50 hover:text-red-600 hover:border-red-200 transition px-2.5 py-1.5 rounded-md"
              >
                <Lock className="w-3.5 h-3.5" />
                <span className={compactActions ? 'hidden' : 'inline'}>{t('campaigns.drawer.archive')}</span>
              </button>
              </Tooltip>
            )}
            <button onClick={handleClose} className="ml-0.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition p-1.5 rounded-md">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Trial status banner */}
        {c.is_trial_active && (() => {
          const days = c.trial_days_remaining;
          const isUrgent = days <= 1;
          const trialLabel = days <= 0 ? t('campaigns.drawer.trialLastDay') : days === 1 ? t('campaigns.drawer.trialExpiresTomorrow') : t('campaigns.drawer.trialDaysLeft', { days });
          return (
          <div className={`mx-4 mt-2 rounded-lg px-3.5 py-2.5 ${isUrgent ? 'bg-red-50 border border-red-200' : 'bg-amber-50 border border-amber-200'}`}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <Text size="xs" fw={600} c={isUrgent ? 'red.7' : 'yellow.8'}>
                  {trialLabel}
                </Text>
                <Text size="xs" c="dimmed" lh={1.5} mt={2}>
                  {isUrgent
                    ? t('campaigns.drawer.trialDescUrgent')
                    : (isMeta
                        ? t('campaigns.drawer.trialDescMeta')
                        : t('campaigns.drawer.trialDescGoogle'))}
                </Text>
              </div>
              <Button
                size="xs"
                color="brand"
                variant="filled"
                className="shrink-0"
                onClick={() => { window.dispatchEvent(new CustomEvent('droid-subscribe-campaign', { detail: { campaignId: campaign.id } })); }}
              >
                {t('campaigns.activateTrial.activateSubscription')}
              </Button>
            </div>
          </div>
          );
        })()}

        {/* Tabs when they fit the width, otherwise a dropdown. The tab bar is
            always rendered (hidden when overflowing) so it stays measurable. */}
        <div ref={tabsContainerRef} className="relative">
          <div style={tabsFit ? undefined : { position: 'absolute', visibility: 'hidden', pointerEvents: 'none', left: 0, right: 0, top: 0 }}>
            <Tabs value={activeTab} onChange={setActiveTab} color="brand" variant="default" style={{ borderBottom: '1px solid var(--mantine-color-gray-3)' }}>
              <Tabs.List ref={tabsListRef} px="sm" pt="xs" style={{ flexWrap: 'nowrap' }}>
                {tabs.map(tab => {
                  const Icon = tab.icon;
                  return (
                    <Tabs.Tab key={tab.id} value={tab.id} leftSection={<Icon size={14} weight={activeTab === tab.id ? 'fill' : 'regular'} color={activeTab === tab.id ? 'var(--mantine-color-brand-5)' : undefined} />} styles={{ tab: { fontSize: 14, padding: '8px 9px' } }}>{tab.label}</Tabs.Tab>
                  );
                })}
              </Tabs.List>
            </Tabs>
          </div>
          {!tabsFit && (
            <div style={{ borderBottom: '1px solid var(--mantine-color-gray-3)', padding: '8px 16px' }}>
              <Select
                value={activeTab}
                onChange={setActiveTab}
                data={tabs.map(t => ({ value: t.id, label: t.label }))}
                size="sm"
                leftSection={(() => { const t = tabs.find(t => t.id === activeTab); const I = t?.icon; return I ? <I size={14} weight="fill" color="var(--mantine-color-brand-5)" /> : null; })()}
              />
            </div>
          )}
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {dataLoading ? (
            <Flex align="center" justify="center" py="xl" gap="xs">
              <Loader size="sm" color="brand" />
              <Text size="sm" c="dimmed">{t('campaigns.drawer.loadingCampaign')}</Text>
            </Flex>
          ) : isUnsupportedType ? (
            <Stack align="center" gap="md" py="xl">
              <ThemeIcon variant="light" color="yellow" size="xl" radius="md">
                <Warning size={22} />
              </ThemeIcon>
              <Text size="sm" fw={600} ta="center">{t('campaigns.drawer.campaignTypeLabel', { type: campaignType === 'performance_max' ? 'Performance Max' : campaignType === 'display' ? 'Display' : campaignType === 'shopping' ? 'Shopping' : campaignType })}</Text>
              <Text size="xs" c="dimmed" ta="center" maw={360}>
                {t('campaigns.drawer.unsupportedDesc', { type: campaignType === 'performance_max' ? 'Performance Max' : campaignType })}
              </Text>
              <Badge color="yellow" variant="light" size="lg">{t('campaigns.drawer.previewOnly')}</Badge>
              <SimpleGrid cols={3} spacing="sm">
                {[
                  { label: t('campaigns.metrics.impressions'), value: (c.impressions || 0).toLocaleString('pl-PL') },
                  { label: t('campaigns.metrics.clicks'), value: (c.clicks || 0).toLocaleString('pl-PL') },
                  { label: t('campaigns.metrics.cost'), value: formatMoney(c.cost, c.currency, { decimals: 0 }) },
                  { label: t('campaigns.metrics.ctr'), value: `${c.ctr || 0}%` },
                  { label: t('campaigns.metrics.avgCpc'), value: formatMoney(parseFloat(c.cpc) || 0, c.currency) },
                  { label: t('campaigns.drawer.statBudget'), value: `${formatMoney(c.budget_amount, c.currency, { decimals: 0 })}/dzień` },
                ].map(s => (
                  <Paper key={s.label} p="sm" bg="gray.0" withBorder ta="center">
                    <Text size="xs" c="dimmed">{s.label}</Text>
                    <Text size="sm" fw={600} ff="monospace">{s.value}</Text>
                  </Paper>
                ))}
              </SimpleGrid>
            </Stack>
          ) : !c.is_managed ? (
            <Stack align="center" gap="md" py="xl">
              <ThemeIcon variant="light" color="gray" size="xl" radius="md">
                <Sparkle size={22} />
              </ThemeIcon>
              <Text size="sm" fw={500} ta="center">{t('campaigns.drawer.notManagedTitle')}</Text>
              <Text size="xs" c="dimmed" ta="center" maw={320}>
                {t('campaigns.drawer.notManagedDesc')}
              </Text>
              <SimpleGrid cols={3} spacing="sm">
                {[
                  { label: t('campaigns.metrics.impressions'), value: (c.impressions || 0).toLocaleString('pl-PL') },
                  { label: t('campaigns.metrics.clicks'), value: (c.clicks || 0).toLocaleString('pl-PL') },
                  { label: t('campaigns.metrics.cost'), value: formatMoney(c.cost, c.currency, { decimals: 0 }) },
                  { label: t('campaigns.metrics.ctr'), value: `${c.ctr || 0}%` },
                  { label: t('campaigns.metrics.avgCpc'), value: formatMoney(parseFloat(c.cpc) || (c.clicks > 0 ? parseFloat(c.cost) / c.clicks : 0), c.currency) },
                  { label: t('campaigns.drawer.statBudget'), value: `${formatMoney(c.budget_amount, c.currency, { decimals: 0 })}/dzień` },
                ].map(s => (
                  <Paper key={s.label} p="sm" bg="gray.0" withBorder ta="center">
                    <Text size="xs" c="dimmed">{s.label}</Text>
                    <Text size="sm" fw={600} ff="monospace">{s.value}</Text>
                  </Paper>
                ))}
              </SimpleGrid>
              <Button
                color="brand"
                size="md"
                leftSection={<Sparkle size={16} />}
                onClick={() => {
                  handleClose();
                  if (onCampaignUpdated) onCampaignUpdated();
                  // Trigger the toggle from parent
                  window.dispatchEvent(new CustomEvent('droid-enable-campaign', { detail: { campaignId: campaign.id } }));
                }}
              >
                {t('campaigns.activateTrial.title')}
              </Button>
            </Stack>
          ) : (
            <>
              {activeTab === 'asset-groups' && isPmax && (
                <PmaxPanel campaignId={campaign.id} currency={campaign.currency} />
              )}

              {activeTab === 'overview' && (
                <div className="space-y-4">
                  {/* Campaign status banner */}
                  {c.status === 'paused' && (
                    <Alert color="brand" icon={<Pause size={16} />} radius="md">
                      <Group justify="space-between" wrap="nowrap">
                        <div>
                          <Text size="xs" fw={500} c="brand.7">{t('campaigns.drawer.pausedBannerTitle')}</Text>
                          <Text size="xs" c="brand.5">{t('campaigns.drawer.pausedBannerDesc')}</Text>
                        </div>
                        <Button size="xs" color="green" leftSection={<Play size={12} />} onClick={handleToggleCampaignStatus} disabled={saving} style={{ flexShrink: 0 }}>
                          {t('campaigns.drawer.start')}
                        </Button>
                      </Group>
                    </Alert>
                  )}

                  {/* Mini chart */}
                  <CampaignMiniChart campaignId={campaign.id} onDataLoaded={setChartDays} activeMetrics={selectedMetrics} period={overviewPeriod} setPeriod={setOverviewPeriod} events={recentActions} currency={campaign.currency} />

                  {/* Stats grid */}
                  <Text size="xs" c="dimmed">
                    {selectedMetrics.length > 0
                      ? t('campaigns.drawer.metricsSelected', { count: selectedMetrics.length })
                      : t('campaigns.drawer.metricsHint')}
                  </Text>
                  <SimpleGrid cols={3} spacing="sm">
                    {(() => {
                    // Period-scoped values when available (respect the selector
                    // above), falling back to the campaign's 30-day rolling totals.
                    const pm = periodMetrics;
                    const mImpr = pm ? (pm.impressions || 0) : (c.impressions || 0);
                    const mClicks = pm ? (pm.clicks || 0) : (c.clicks || 0);
                    const mCost = pm ? parseFloat(pm.cost || 0) : parseFloat(c.cost || 0);
                    const mConv = pm ? parseFloat(pm.conversions || 0) : (parseFloat(c.conversions) || 0);
                    const mConvVal = pm ? parseFloat(pm.conversions_value || 0) : (parseFloat(c.conversions_value) || 0);
                    const mRoas = mCost > 0 ? (mConvVal / mCost) : 0;
                    const mCtr = mImpr > 0 ? (mClicks / mImpr * 100) : (pm ? 0 : (parseFloat(c.ctr) || 0));
                    const mCpc = mClicks > 0 ? (mCost / mClicks) : 0;
                    // Koszt/lead musi dzielić przez TĘ SAMĄ liczbę leadów, co karta „Leady"
                    // (realne zgłoszenia z formularza), a nie metrykę „konwersje" (pixel+lead).
                    const convDenom = (isLeadGen && typeof c.lead_count === 'number') ? c.lead_count : mConv;
                    const mCpa = convDenom > 0 ? (mCost / convDenom) : 0;
                    const target = parseFloat(c.target_cpa);
                    return [
                      { label: t('campaigns.metrics.impressions'), value: mImpr.toLocaleString('pl-PL'), key: 'impressions', color: '#8b5cf6' },
                      { label: t('campaigns.metrics.clicks'), value: mClicks.toLocaleString('pl-PL'), key: 'clicks', color: '#3b82f6' },
                      { label: t('campaigns.metrics.ctr'), value: `${mCtr.toFixed(1)}%`, key: 'ctr', color: '#f59e0b' },
                      { label: t('campaigns.metrics.cost'), value: formatMoney(mCost, c.currency), key: 'cost', color: '#1D4ED8' },
                      // Dla Meta lead gen pokazujemy realną liczbę leadów z formularza
                      // (= kolumna „Leady" w Menedżerze Meta), a nie metrykę „konwersje",
                      // która u Meta sumuje też zdarzenia pixela (np. CompleteRegistration).
                      { label: conversionWord, value: (isLeadGen && typeof c.lead_count === 'number') ? c.lead_count : Math.round(mConv), key: 'conversions', color: '#10b981' },
                      { label: t('campaigns.drawer.statConvValue'), value: formatMoney(mConvVal, c.currency), key: 'conversions_value', color: '#0ea5e9' },
                      { label: t('campaigns.drawer.statRoas'), value: mRoas > 0 ? `${mRoas.toFixed(2)}x` : '-', key: 'roas', color: '#7c3aed' },
                      { label: t('campaigns.metrics.avgCpc'), value: formatMoney(mCpc, c.currency), key: 'cpc', color: '#ef4444' },
                      { label: isLeadGen ? t('campaigns.drawer.statCostPerLead') : t('campaigns.drawer.statCostPerConv'), value: mCpa > 0 ? `${formatMoney(mCpa, c.currency)}${target > 0 ? ` / ${formatMoney(target, c.currency, { decimals: 0 })}` : ''}` : '-', key: 'cpa', color: target > 0 && mCpa > target ? '#ef4444' : '#10b981' },
                      { label: t('campaigns.drawer.statDailyBudget'), value: formatMoney(c.budget_amount, c.currency), key: 'budget_amount', color: '#3b82f6' },
                      { label: t('campaigns.drawer.statBiddingStrategy'), value: {manual_cpc:'Manual CPC',enhanced_cpc:'Enhanced CPC',maximize_conversions:t('campaigns.drawer.strategyMaxConv'),maximize_clicks:t('campaigns.drawer.strategyMaxClicks'),target_cpa:'Target CPA',target_roas:'Target ROAS',lowest_cost:t('campaigns.drawer.strategyLowestCost'),cost_cap:t('campaigns.drawer.strategyCostCap'),bid_cap:t('campaigns.drawer.strategyBidCap')}[c.bidding_strategy] || c.bidding_strategy || (isMeta ? t('campaigns.drawer.strategyLowestCost') : 'Manual CPC') },
                    ].map(stat => {
                      const isSelected = stat.key && selectedMetrics.includes(stat.key);
                      const selectable = stat.key && MINI_CHART_METRICS[stat.key];
                      const order = isSelected ? selectedMetrics.indexOf(stat.key) + 1 : null;
                      return (
                        <Paper
                          key={stat.label} p="sm" withBorder
                          onClick={() => selectable && toggleMetric(stat.key)}
                          style={{
                            cursor: selectable ? 'pointer' : 'default',
                            background: isSelected ? `${stat.color}10` : 'var(--mantine-color-gray-0)',
                            borderColor: isSelected ? stat.color : undefined,
                            transition: 'all 0.15s',
                          }}
                        >
                          <Group gap={6} wrap="nowrap" align="center">
                            {isSelected && (
                              <span style={{ width: 16, height: 16, borderRadius: 4, background: stat.color, color: '#fff', fontSize: 10, fontWeight: 700, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{order}</span>
                            )}
                            <Text size="xs" c={isSelected ? stat.color : 'dimmed'} fw={isSelected ? 600 : 400}>{stat.label}</Text>
                          </Group>
                          <Group justify="space-between" align="flex-end" wrap="nowrap">
                            <Text size="sm" fw={600}>{stat.value}</Text>
                            {stat.key && chartDays.length > 2 && (
                              <Box style={{ width: 60, height: 24, flexShrink: 0 }}>
                                <ResponsiveContainer width="100%" height="100%">
                                  <AreaChart data={chartDays}>
                                    <Area type="monotone" dataKey={stat.key} stroke={stat.color} fill={stat.color} fillOpacity={isSelected ? 0.3 : 0.15} strokeWidth={isSelected ? 2 : 1.5} dot={false} />
                                  </AreaChart>
                                </ResponsiveContainer>
                              </Box>
                            )}
                          </Group>
                        </Paper>
                      );
                    })})()}
                  </SimpleGrid>

                  {!isMeta && c.google_campaign_id && !String(c.google_campaign_id).startsWith('demo_') && (
                    <CampaignLocationEditor campaignId={campaign.id} platform={c.platform} />
                  )}

                  {isMeta && c.platform_campaign_id && (
                    <FacebookPageControl campaign={c} addNotification={addNotification} />
                  )}

                  {/* Conversion setup prompt (not for Meta leads: leads arrive via the form) */}
                  {parseFloat(c.conversions) === 0 && c.clicks > 0 && c.is_managed && !isLeadGen && (
                    <Alert color="blue" variant="light" icon={<Target size={16} />}>
                      <Group justify="space-between" align="center" wrap="nowrap">
                        <Text size="xs">{t('campaigns.drawer.noConvPrompt')}</Text>
                        <Button size="xs" color="brand" variant="filled" onClick={() => setShowConversionSetup(true)} style={{ flexShrink: 0 }}>
                          {t('campaigns.drawer.configure')}
                        </Button>
                      </Group>
                    </Alert>
                  )}

                  {/* Pending suggestions banner */}
                  {suggestions.filter(s => s.status === 'pending').length > 0 && (
                    <Paper withBorder p="sm" radius="md" style={{ borderColor: 'var(--mantine-color-brand-3)', backgroundColor: 'var(--mantine-color-brand-0)' }}>
                      <Group justify="space-between" wrap="nowrap">
                        <Group gap="xs" wrap="nowrap">
                          <Lightbulb size={16} className="text-indigo-600" style={{ flexShrink: 0 }} />
                          <div>
                            <Text size="xs" fw={600} c="brand">
                              {suggestions.filter(s => s.status === 'pending').length === 1 ? t('campaigns.drawer.pendingSuggestionsOne', { count: suggestions.filter(s => s.status === 'pending').length }) : t('campaigns.drawer.pendingSuggestionsMany', { count: suggestions.filter(s => s.status === 'pending').length })}
                            </Text>
                            <Text size="xs" c="dimmed">
                              {suggestions.filter(s => s.status === 'pending').slice(0, 2).map(s => s.title).join(', ')}
                            </Text>
                          </div>
                        </Group>
                        <Button size="compact-xs" variant="light" color="brand" onClick={() => setActiveTab('suggestions')}>
                          {t('campaigns.drawer.view')}
                        </Button>
                      </Group>
                    </Paper>
                  )}

                  {/* Actions */}
                  <Group gap="xs">
                    <Button variant="default" size="xs" leftSection={<Gear size={12} />} onClick={() => setEditMode(true)}>
                      {t('campaigns.drawer.campaignSettings')}
                    </Button>
                    <Button
                      variant={c.status === 'active' ? 'subtle' : 'filled'}
                      size="xs"
                      color={c.status === 'active' ? 'gray' : 'green'}
                      leftSection={c.status === 'active' ? <Pause size={12} /> : <Play size={12} />}
                      onClick={handleToggleCampaignStatus}
                      disabled={saving}
                    >
                      {c.status === 'active' ? t('campaigns.drawer.pauseCampaign') : t('campaigns.drawer.resumeCampaign')}
                    </Button>
                  </Group>

                  {/* Settings modal */}
                  <Modal
                    opened={editMode}
                    onClose={() => { setEditMode(false); setEditName(c.name); setEditBudget(c.budget_amount || ''); }}
                    title={<Group gap="xs"><Gear size={18} className="text-indigo-600" /><Text fw={600}>{t('campaigns.drawer.campaignSettings')}</Text></Group>}
                    size="xl"
                    centered
                    styles={{ body: { padding: 0, display: 'flex', flexDirection: 'column', maxHeight: '80vh' } }}
                  >
                    <div style={{ overflowY: 'auto', padding: 'var(--mantine-spacing-md)', flex: 1, minHeight: 0 }}>
                    <Stack gap="sm">
                      <TextInput
                        label={t('campaigns.settings.name')}
                        value={editName}
                        onChange={e => setEditName(e.target.value)}
                        size="sm"
                        styles={{ label: { fontSize: 12, fontWeight: 500 } }}
                      />
                      <SimpleGrid cols={2} spacing="sm">
                        <TextInput
                          label={t('campaigns.settings.dailyBudget', { cur: currencySymbol(campaign.currency) })}
                          type="number"
                          step="0.01"
                          min="0"
                          value={editBudget}
                          onChange={e => setEditBudget(e.target.value)}
                          size="sm"
                          styles={{ label: { fontSize: 12, fontWeight: 500 } }}
                        />
                        <TextInput
                          label={t('campaigns.settings.targetCpa', { cur: currencySymbol(campaign.currency) })}
                          type="number"
                          step="1"
                          min="0"
                          placeholder={t('campaigns.settings.targetCpaPlaceholder')}
                          value={editTargetCpa}
                          onChange={e => setEditTargetCpa(e.target.value)}
                          size="sm"
                          styles={{ label: { fontSize: 12, fontWeight: 500 } }}
                        />
                        {!isMeta && (
                        <TextInput
                          label={t('campaigns.settings.maxCpc', { cur: currencySymbol(campaign.currency) })}
                          description={t('campaigns.settings.maxCpcDesc')}
                          type="number"
                          step="0.5"
                          min="0"
                          placeholder={t('campaigns.settings.maxCpcPlaceholder')}
                          value={editMaxCpc}
                          onChange={e => setEditMaxCpc(e.target.value)}
                          size="sm"
                          styles={{ label: { fontSize: 12, fontWeight: 500 } }}
                        />
                        )}
                      </SimpleGrid>
                      {/* Keyword strategy and Google bid strategies apply to Google Ads only. */}
                      {!isMeta && (
                      <Select
                        label={t('campaigns.settings.keywordStrategy')}
                        data={[
                          { value: 'standard', label: t('campaigns.settings.kwStandard'), description: t('campaigns.settings.kwStandardDesc') },
                          { value: 'budget', label: t('campaigns.settings.kwBudget'), description: t('campaigns.settings.kwBudgetDesc') },
                        ]}
                        value={editKeywordStrategy}
                        onChange={setEditKeywordStrategy}
                        size="sm"
                        mb="sm"
                        styles={{ label: { fontSize: 12, fontWeight: 500 } }}
                      />
                      )}
                      <Select
                        label={t('campaigns.settings.optimizationFocus')}
                        data={[
                          { value: 'conversions', label: t('campaigns.metrics.conversions'), description: t('campaigns.settings.focusConversionsDesc') },
                          { value: 'ctr', label: t('campaigns.settings.focusCtr'), description: t('campaigns.settings.focusCtrDesc') },
                        ]}
                        value={editOptimizationFocus}
                        onChange={setEditOptimizationFocus}
                        size="sm"
                        mb="sm"
                        styles={{ label: { fontSize: 12, fontWeight: 500 } }}
                        renderOption={({ option, checked }) => (
                          <Box>
                            <Text size="sm" fw={checked ? 600 : 400}>{option.label}</Text>
                            <Text size="xs" c="dimmed" lh={1.3}>{option.description}</Text>
                          </Box>
                        )}
                      />
                      {editOptimizationFocus === 'ctr' && (
                        <Alert color="blue" variant="light" p="xs" mb="sm">
                          <Text size="xs" c="dimmed">{t('campaigns.settings.ctrModeNote')}</Text>
                        </Alert>
                      )}
                      {!isMeta && (<>
                      <Select
                        label={t('campaigns.settings.biddingStrategy')}
                        data={[
                          { value: 'manual_cpc', label: 'Manual CPC', description: t('campaigns.settings.stratManualCpcDesc') },
                          { value: 'maximize_conversions', label: 'Maximize Conversions', description: t('campaigns.settings.stratMaxConvDesc') },
                          { value: 'maximize_clicks', label: 'Maximize Clicks', description: t('campaigns.settings.stratMaxClicksDesc') },
                          { value: 'target_cpa', label: 'Target CPA', description: t('campaigns.settings.stratTargetCpaDesc') },
                          { value: 'target_roas', label: 'Target ROAS', description: t('campaigns.settings.stratTargetRoasDesc') },
                        ].filter(o => {
                          // Tylko strategie dopuszczane przez Google dla typu kampanii
                          // (backend: UpdateBiddingStrategyView.ALLOWED_BY_TYPE).
                          const allowed = STRATEGIES_BY_TYPE[campaignData.campaign_type];
                          return !allowed || allowed.includes(o.value) || o.value === editStrategy;
                        })}
                        value={editStrategy}
                        onChange={setEditStrategy}
                        size="sm"
                        styles={{ label: { fontSize: 12, fontWeight: 500 } }}
                        renderOption={({ option, checked }) => (
                          <Box>
                            <Text size="sm" fw={checked ? 600 : 400}>{option.label}</Text>
                            <Text size="xs" c="dimmed" lh={1.3}>{option.description}</Text>
                          </Box>
                        )}
                      />
                      {STRATEGY_HINTS[editStrategy] && (
                        <Alert color="blue" variant="light" p="xs">
                          <Text size="xs" c="dimmed">{t(STRATEGY_HINTS[editStrategy])}</Text>
                        </Alert>
                      )}
                      {(editStrategy === 'target_cpa' || editStrategy === 'target_roas') && (
                        <TextInput
                          label={editStrategy === 'target_cpa' ? t('campaigns.settings.targetCpaValue', { cur: currencySymbol(campaign.currency) }) : t('campaigns.settings.targetRoasValue')}
                          type="number"
                          step={editStrategy === 'target_roas' ? '10' : '0.01'}
                          min="0"
                          value={editTargetValue}
                          onChange={e => setEditTargetValue(e.target.value)}
                          size="sm"
                          styles={{ label: { fontSize: 12, fontWeight: 500 } }}
                        />
                      )}
                      </>)}

                      <Text size="xs" fw={600} c="dimmed" tt="uppercase" mt="xs" style={{ letterSpacing: '0.05em' }}>{t('campaigns.settings.automationSection')}</Text>
                      <SimpleGrid cols={2} spacing="sm">
                        <div>
                          <Select
                            label={t('campaigns.settings.autoOptimizations')}
                            data-droid-setting="auto_apply"
                            data-droid-value={editAutoApply}
                            data={[
                              { value: 'inherit', label: t('campaigns.settings.fromAccount') },
                              { value: 'on', label: t('campaigns.settings.enabled') },
                              { value: 'off', label: t('campaigns.settings.disabled') },
                            ]}
                            value={editAutoApply}
                            onChange={setEditAutoApply}
                            size="sm"
                            styles={{ label: { fontSize: 12, fontWeight: 500 } }}
                          />
                          <Text size="xs" c="dimmed" mt={4}>{isMeta ? t('campaigns.settings.autoOptDescMeta') : t('campaigns.settings.autoOptDescGoogle')}</Text>
                        </div>
                        <div>
                          <Select
                            label={t('campaigns.settings.highRisk')}
                            data-droid-setting="auto_apply_high_risk"
                            data-droid-value={editAutoApplyHR}
                            data={[
                              { value: 'inherit', label: t('campaigns.settings.fromAccount') },
                              { value: 'on', label: t('campaigns.settings.enabled') },
                              { value: 'off', label: t('campaigns.settings.disabled') },
                            ]}
                            value={editAutoApplyHR}
                            onChange={setEditAutoApplyHR}
                            size="sm"
                            styles={{ label: { fontSize: 12, fontWeight: 500 } }}
                          />
                          <Text size="xs" c="dimmed" mt={4}>{t('campaigns.settings.highRiskDesc')}</Text>
                        </div>
                      </SimpleGrid>

                      <Text size="xs" fw={600} c="dimmed" tt="uppercase" mt="xs" style={{ letterSpacing: '0.05em' }}>{t('campaigns.settings.emailSection')}</Text>
                      <SimpleGrid cols={2} spacing="sm">
                        <div>
                          <Select
                            label={t('campaigns.settings.criticalIssues')}
                            data-droid-setting="email_critical"
                            data-droid-value={editAlertsCrit}
                            data={[
                              { value: 'inherit', label: t('campaigns.settings.fromAccount') },
                              { value: 'on', label: t('campaigns.settings.enabled') },
                              { value: 'off', label: t('campaigns.settings.disabled') },
                            ]}
                            value={editAlertsCrit}
                            onChange={setEditAlertsCrit}
                            size="sm"
                            styles={{ label: { fontSize: 12, fontWeight: 500 } }}
                          />
                          <Text size="xs" c="dimmed" mt={4}>{t('campaigns.settings.criticalDesc')}</Text>
                        </div>
                        <div>
                          <Select
                            label={t('campaigns.settings.warnings')}
                            data-droid-setting="email_warning"
                            data-droid-value={editAlertsWarn}
                            data={[
                              { value: 'inherit', label: t('campaigns.settings.fromAccount') },
                              { value: 'on', label: t('campaigns.settings.enabled') },
                              { value: 'off', label: t('campaigns.settings.disabled') },
                            ]}
                            value={editAlertsWarn}
                            onChange={setEditAlertsWarn}
                            size="sm"
                            styles={{ label: { fontSize: 12, fontWeight: 500 } }}
                          />
                          <Text size="xs" c="dimmed" mt={4}>{t('campaigns.settings.warningsDesc')}</Text>
                        </div>
                      </SimpleGrid>

                    </Stack>
                    </div>
                    <Group justify="flex-end" gap="sm" style={{ borderTop: '1px solid var(--mantine-color-gray-2)', background: 'var(--mantine-color-body)', padding: 'var(--mantine-spacing-sm) var(--mantine-spacing-md)', flexShrink: 0 }}>
                      <Button variant="default" size="sm" onClick={() => { setEditMode(false); setEditName(c.name); setEditBudget(c.budget_amount || ''); }}>
                        {t('campaigns.common.cancel')}
                      </Button>
                      <Button color="brand" size="sm" leftSection={saving ? <CircleNotch size={12} className="animate-spin" /> : <FloppyDisk size={12} />} onClick={handleSaveSettings} disabled={saving}>
                        {saving ? t('campaigns.common.saving') : t('campaigns.common.save')}
                      </Button>
                    </Group>
                  </Modal>

                  {/* Recent agent actions */}
                  <div>
                    <Group justify="space-between" mb="xs">
                      <Text size="sm" fw={600} c="dark">{t('campaigns.drawer.recentActions')}</Text>
                      {recentActions.length > 5 && (
                        <Button variant="subtle" size="compact-xs" color="brand" onClick={() => setActiveTab('activity')}>
                          {t('campaigns.drawer.showAll')}
                        </Button>
                      )}
                    </Group>
                    {recentActions.length > 0 ? (
                      <Stack gap={0}>
                        {recentActions.slice(0, 5).map((a, i) => (
                          <div key={a.id || i} className="py-2 border-b border-gray-100 last:border-0">
                            <Text size="sm" lh={1.4} mb={2}>{a.description || a.action}</Text>
                            <Text size="xs" c="dimmed">{a.date}</Text>
                          </div>
                        ))}
                      </Stack>
                    ) : (
                      <Text size="sm" c="dimmed">{t('campaigns.drawer.noActions')}</Text>
                    )}
                  </div>
                </div>
              )}

              {activeTab === 'shopping' && isShopping && (
                <ShoppingDashboard campaignId={campaign.id} currency={campaign.currency} />
              )}

              {activeTab === 'health' && (
                <HealthAlertsPanel campaignId={campaign.id} onSuggestedKeywords={setSuggestedKeywords} />
              )}

              {activeTab === 'conversions' && c.platform === 'meta_ads' && (
                <MetaConversionsPanel campaignId={campaign.id} notify={addNotification} currency={campaign.currency} />
              )}

              {activeTab === 'conversions' && c.platform !== 'meta_ads' && (
                <ConversionsTab campaignId={campaign.id} onSetup={() => setShowConversionSetup(true)} />
              )}

              {activeTab === 'adgroups' && (
                <div>
                  {adGroups.length > 0 ? (
                    <div className="space-y-1">
                      {adGroups.map((group) => {
                        const isExpanded = expandedGroup === group.id;
                        const isEditingBid = editingGroupBid === group.id;
                        const isLoading = adGroupActionLoading === group.id;
                        return (
                          <div key={group.id} className="border border-gray-100 rounded-md">
                            {/* Row header */}
                            <div
                              className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-gray-50 transition"
                              onClick={() => setExpandedGroup(isExpanded ? null : group.id)}
                            >
                              {isExpanded ? <CaretDown className="w-3.5 h-3.5 text-gray-400 shrink-0" /> : <CaretRight className="w-3.5 h-3.5 text-gray-400 shrink-0" />}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <p className="text-sm text-gray-900 font-medium truncate">{group.name}</p>
                                  <span className="inline-flex items-center gap-1 text-xs">
                                    <span className={`w-1.5 h-1.5 rounded-full ${group.status === 'active' ? 'bg-green-500' : 'bg-yellow-500'}`} />
                                    {group.status === 'active' ? t('campaigns.adGroups.statusActive') : t('campaigns.adGroups.statusPaused')}
                                  </span>
                                </div>
                                <div className="flex items-center gap-3 mt-0.5">
                                  {!isMeta && <span className="text-[11px] text-gray-400">{t('campaigns.adGroups.maxCpc', { bid: group.cpc_bid, cur: currencySymbol(campaign.currency) })}</span>}
                                  <span className="text-[11px] text-gray-400">{t('campaigns.adGroups.clicks', { count: group.clicks || 0 })}</span>
                                  <span className="text-[11px] text-gray-400">{t('campaigns.adGroups.impressions', { count: (group.impressions || 0).toLocaleString('pl-PL') })}</span>
                                  <span className="text-[11px] text-gray-400">{t('campaigns.adGroups.conversions', { count: Math.round(parseFloat(group.conversions) || 0) })}</span>
                                </div>
                              </div>
                              <div className="flex items-center gap-1 shrink-0" onClick={e => e.stopPropagation()}>
                                {isLoading ? (
                                  <CircleNotch className="w-3.5 h-3.5 animate-spin text-gray-400" />
                                ) : (
                                  <button
                                    onClick={() => handleToggleAdGroupStatus(group)}
                                    className="p-1 rounded hover:bg-gray-100 transition"
                                    title={group.status === 'active' ? t('campaigns.keywords.pause') : t('campaigns.keywords.resume')}
                                  >
                                    {group.status === 'active' ? <Pause className="w-3.5 h-3.5 text-yellow-600" /> : <Play className="w-3.5 h-3.5 text-green-600" />}
                                  </button>
                                )}
                              </div>
                            </div>

                            {/* Expanded details */}
                            {isExpanded && (
                              <div className="px-3 pb-3 pt-1 border-t border-gray-100 space-y-3">
                                {/* Meta: the Reklamy tab is the single source of truth for
                                    creatives (live from Meta); a local snapshot here drifted
                                    from reality after edits and multi-ad publish. */}
                                {isMeta && (
                                  <div className="bg-gray-50 rounded-md p-2.5">
                                    <Text size="xs" c="dimmed">
                                      {group.ad_data?.ad_ids?.length
                                        ? t('campaigns.adGroups.metaAdSetHas', { count: group.ad_data.ad_ids.length })
                                        : ''}
                                      {t('campaigns.adGroups.metaCreativeNote')}
                                    </Text>
                                    <Text size="xs" c="dimmed" mt={4}>{t('campaigns.adGroups.cboNote')}</Text>
                                    <Button size="compact-xs" variant="light" color="brand" mt={6} onClick={() => setActiveTab('ads')}>{t('campaigns.adGroups.openAds')}</Button>
                                  </div>
                                )}

                                {/* Google: Inline CPC edit + RSA + keywords */}
                                {!isMeta && <>
                                <div className="flex items-center gap-2">
                                  <span className="text-xs text-gray-500">{t('campaigns.adGroups.cpcBid')}</span>
                                  {isEditingBid ? (
                                    <>
                                      <input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        value={editBidValue}
                                        onChange={e => setEditBidValue(e.target.value)}
                                        className="w-24 px-2 py-0.5 text-sm border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500"
                                        autoFocus
                                      />
                                      <span className="text-xs text-gray-400">{currencySymbol(campaign.currency)}</span>
                                      <button
                                        onClick={() => handleSaveGroupBid(group)}
                                        disabled={isLoading}
                                        className="px-2 py-0.5 rounded text-xs bg-[#1D4ED8] text-white hover:bg-[#1e40af] disabled:opacity-50 transition"
                                      >
                                        {isLoading ? <CircleNotch className="w-3 h-3 animate-spin" /> : t('campaigns.common.save')}
                                      </button>
                                      <button
                                        onClick={() => setEditingGroupBid(null)}
                                        className="px-2 py-0.5 rounded text-xs border border-gray-200 text-gray-600 hover:bg-gray-50 transition"
                                      >
                                        {t('campaigns.common.cancel')}
                                      </button>
                                    </>
                                  ) : (
                                    <>
                                      <span className="text-sm font-medium text-gray-900">{formatMoney(group.cpc_bid, campaign.currency)}</span>
                                      <button
                                        onClick={() => { setEditingGroupBid(group.id); setEditBidValue(group.cpc_bid); }}
                                        className="p-0.5 rounded hover:bg-gray-100 transition"
                                        title={t('campaigns.adGroups.editBid')}
                                      >
                                        <PencilSimple className="w-3 h-3 text-gray-400" />
                                      </button>
                                    </>
                                  )}
                                </div>

                                {/* Google: Ad data preview */}
                                {group.ad_data && group.ad_data.headlines && group.ad_data.headlines.length > 0 ? (
                                  <div className="bg-white border border-gray-100 rounded-md p-2.5">
                                    <p className="text-[10px] text-gray-400 uppercase tracking-wide mb-1.5">{t('campaigns.adGroups.rsaAd')}</p>
                                    <div className="space-y-1">
                                      {group.ad_data.headlines.slice(0, 3).map((h, i) => (
                                        <p key={i} className="text-sm text-indigo-700 font-medium">{h}</p>
                                      ))}
                                      {group.ad_data.headlines.length > 3 && (
                                        <p className="text-[11px] text-gray-400">{t('campaigns.adGroups.moreHeadlines', { count: group.ad_data.headlines.length - 3 })}</p>
                                      )}
                                    </div>
                                    {group.ad_data.descriptions && (
                                      <div className="mt-1.5">
                                        {group.ad_data.descriptions.slice(0, 2).map((d, i) => (
                                          <p key={i} className="text-xs text-gray-600">{d}</p>
                                        ))}
                                      </div>
                                    )}
                                    {group.ad_data.final_url && (
                                      <p className="text-[11px] text-green-700 mt-1">{group.ad_data.final_url}</p>
                                    )}
                                  </div>
                                ) : (
                                  <p className="text-xs text-gray-400">{t('campaigns.adGroups.noAdInGroup')}</p>
                                )}

                                {/* Google: Keywords list */}
                                {group.keywords && group.keywords.length > 0 && (
                                  <div>
                                    <p className="text-[10px] text-gray-400 uppercase tracking-wide mb-1">{t('campaigns.adGroups.keywordsCount', { count: group.keywords.length })}</p>
                                    <div className="flex flex-wrap gap-1">
                                      {group.keywords.map((kw) => (
                                        <span key={kw.id} className={`inline-flex items-center text-xs px-2 py-0.5 rounded-full border ${kw.status === 'active' ? 'bg-gray-50 border-gray-200 text-gray-700' : 'bg-yellow-50 border-yellow-200 text-yellow-700 opacity-60'}`}>
                                          {kw.text}
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                )}
                                </>}

                                {/* Generate ad button (Google only) */}
                                {!isMeta && (
                                <button
                                  onClick={() => handleGenerateAdForGroup(group)}
                                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-indigo-50 border border-indigo-200 text-xs text-indigo-700 hover:bg-indigo-100 transition"
                                >
                                  <Sparkle className="w-3 h-3" />
                                  {group.ad_data ? t('campaigns.adGroups.editAd') : t('campaigns.adGroups.generateAd')}
                                </button>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <p className="text-sm text-gray-700 mb-1">{t('campaigns.adGroups.emptyTitle')}</p>
                      <p className="text-xs text-gray-400">{t('campaigns.adGroups.emptyDesc')}</p>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'keywords' && (
                <KeywordsTab
                  keywords={keywords}
                  negativeKeywords={negativeKeywords}
                  adGroups={adGroups}
                  campaignId={campaign.id}
                  keywordActionLoading={keywordActionLoading}
                  handleKeywordAction={handleKeywordAction}
                  addNotification={addNotification}
                  suggestedKeywords={suggestedKeywords}
                  onSuggestedKeywordsChange={setSuggestedKeywords}
                  onKeywordsChanged={fetchCampaignData}
                  currency={campaign.currency}
                />
              )}

              {activeTab === 'search-terms' && (
                <SearchTermsTab
                  campaignId={campaign.id}
                  googleCampaignId={campaign.google_campaign_id}
                  addNotification={addNotification}
                  onExcluded={fetchCampaignData}
                  keywords={keywords}
                  currency={campaign.currency}
                />
              )}

              {activeTab === 'ads' && c.platform === 'meta_ads' && (
                <div className="space-y-4">
                  <MetaAdSetBudgets adGroups={adGroups} campaignId={campaign.id} onUpdated={fetchCampaignData} currency={campaign.currency} />
                  <MetaAdEditor campaignId={campaign.id} onUpdated={fetchCampaignData} currency={campaign.currency} />
                </div>
              )}

              {activeTab === 'targeting' && c.platform === 'meta_ads' && (
                <MetaTargetingPanel campaignId={campaign.id} campaignGoal={c.campaign_goal || campaign.campaign_goal} />
              )}

              {activeTab === 'ads' && c.platform !== 'meta_ads' && (
                <div className="space-y-4">
                  {/* Create/edit ad form */}
                  {showAdForm ? (
                    <AdForm
                      adGroups={adGroups}
                      campaignId={campaign.id}
                      initialData={editingAd?.data || null}
                      initialAdGroupId={editingAd?.adGroupId || null}
                      onSaved={() => {
                        setShowAdForm(false);
                        setEditingAd(null);
                        setAdsRefreshKey(k => k + 1);
                        fetchCampaignData();
                      }}
                      onCancel={() => { setShowAdForm(false); setEditingAd(null); }}
                    />
                  ) : (
                    <Button size="xs" color="brand" leftSection={<Plus size={12} />} onClick={() => { setEditingAd(null); setShowAdForm(true); }}>
                      {t('campaigns.adsTab.createNew')}
                    </Button>
                  )}

                  <AdPerformanceTable campaignId={campaign.id} refreshKey={adsRefreshKey} />

                  {/* Existing ads list */}
                  {allAds.length > 0 ? (
                    <div className="space-y-2">
                      {allAds.map((ad, idx) => (
                        <div key={idx} className="p-3 border border-gray-100 rounded-md">
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1.5">
                                <FileText className="w-3.5 h-3.5 text-indigo-500 shrink-0" />
                                <p className="text-xs text-gray-500">{t('campaigns.adsTab.group')} <span className="font-medium text-gray-700">{ad.adGroupName}</span></p>
                              </div>
                              <div className="space-y-0.5">
                                {ad.headlines.map((h, i) => (
                                  <p key={i} className="text-sm text-indigo-700 font-medium">{h}</p>
                                ))}
                              </div>
                              <div className="mt-1.5 space-y-0.5">
                                {ad.descriptions.map((d, i) => (
                                  <p key={i} className="text-xs text-gray-600">{d}</p>
                                ))}
                              </div>
                              {ad.final_url && (
                                <p className="text-[11px] text-green-700 mt-1.5">{ad.final_url}</p>
                              )}
                            </div>
                            <button
                              onClick={() => {
                                setEditingAd({ adGroupId: ad.adGroupId, data: { headlines: ad.headlines, descriptions: ad.descriptions, final_url: ad.final_url } });
                                setShowAdForm(true);
                              }}
                              className="p-1 rounded hover:bg-gray-100 transition shrink-0"
                              title={t('campaigns.adGroups.editAd')}
                            >
                              <PencilSimple className="w-3.5 h-3.5 text-gray-400" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : !showAdForm && (
                    <div className="text-center py-8">
                      <p className="text-sm text-gray-700 mb-1">{t('campaigns.adsTab.emptyTitle')}</p>
                      <p className="text-xs text-gray-400">{t('campaigns.adsTab.emptyDesc')}</p>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'leads' && isMeta && (
                <LeadsTab campaignId={campaign.id} />
              )}

              {activeTab === 'creative-perf' && (
                <CreativePerformance campaignId={campaign.id} currency={campaign.currency} />
              )}

              {activeTab === 'suggestions' && (
                <SuggestionsTab
                  suggestions={suggestions}
                  campaign={c}
                  keywords={keywords}
                  adGroups={adGroups}
                  suggestionActionLoading={suggestionActionLoading}
                  handleSuggestionApply={handleSuggestionApply}
                  handleSuggestionDismiss={handleSuggestionDismiss}
                  onOpenTab={setActiveTab}
                />
              )}
              {activeTab === 'schedule' && (
                hasScheduler ? (
                  <Stack gap="md">
                    <ScheduleTab campaignId={c.id} campaignName={c.name} supportsCpc={!isMeta && campaignType === 'search' && ['manual_cpc', 'enhanced_cpc'].includes(c.bidding_strategy || 'manual_cpc')} currency={c.currency} />
                    {!isMeta && campaignType === 'search' && <AdScheduleSection campaignId={c.id} />}
                  </Stack>
                ) : (
                  <Stack gap="md">
                  <Paper withBorder p="xl" radius="md" ta="center">
                    <ThemeIcon variant="light" color="gray" size="xl" radius="md" mx="auto" mb="sm">
                      <Lock size={24} />
                    </ThemeIcon>
                    <Text size="sm" fw={500} mb={4}>{t('campaigns.scheduleLocked.title')}</Text>
                    <Text size="xs" c="dimmed" maw={360} mx="auto" mb="md">
                      {t('campaigns.scheduleLocked.desc')}
                    </Text>
                    <Button
                      component="a"
                      href="/kontakt"
                      target="_blank"
                      variant="light"
                      color="brand"
                      size="xs"
                    >
                      {t('campaigns.scheduleLocked.contactUs')}
                    </Button>
                  </Paper>
                  {!isMeta && campaignType === 'search' && <AdScheduleSection campaignId={c.id} />}
                  </Stack>
                )
              )}
            </>
          )}
        </div>
      </div>
      {/* Report modal */}
      {showArchiveModal && (
        <Modal opened onClose={() => setShowArchiveModal(false)} title={t('campaigns.archive.title')} size="sm" centered>
          <Text size="sm" c="dimmed" mb="md">
            {t('campaigns.archive.msgBefore')} <Text span fw={600}>"{c.name}"</Text> {t('campaigns.archive.msgAfter')}
          </Text>
          <Text size="xs" c="dimmed" mb="md">
            {t('campaigns.archive.note')}
          </Text>
          <Group justify="flex-end" gap="xs">
            <Button variant="default" size="sm" onClick={() => setShowArchiveModal(false)}>{t('campaigns.common.cancel')}</Button>
            <Button color="red" size="sm" loading={archiving} onClick={async () => {
              setArchiving(true);
              try {
                await api.post(`/ads/campaigns/${campaign.id}/archive/`, { action: 'archive' });
                addNotification(t('campaigns.archive.archived'), 'success');
                setShowArchiveModal(false);
                onClose();
                if (onCampaignUpdated) onCampaignUpdated();
              } catch { addNotification(t('campaigns.archive.archiveError'), 'error'); }
              setArchiving(false);
            }}>{t('campaigns.drawer.archive')}</Button>
          </Group>
        </Modal>
      )}
      {showReport && (
        <Modal opened onClose={() => setShowReport(false)} title={t('campaigns.report.title')} size="md">
          {user?.organization?.is_agency && user?.organization?.agency_name && (
            <Paper withBorder p="sm" radius="md" mb="md" style={{ background: 'var(--mantine-color-brand-0)' }}>
              <Group gap="xs">
                {user.organization.agency_logo && <img src={user.organization.agency_logo} alt="" style={{ maxHeight: 24 }} />}
                <Text size="xs" fw={500} c="brand">{t('campaigns.report.whiteLabel', { agency: user.organization.agency_name })}</Text>
              </Group>
            </Paper>
          )}
          <Text size="sm" mb="md">{t('campaigns.report.campaign')} <strong>{campaign.name}</strong></Text>
          <Select
            label={t('campaigns.report.period')}
            data={[
              { value: 'this_week', label: t('campaigns.report.thisWeek') },
              { value: 'last_week', label: t('campaigns.report.lastWeek') },
              { value: 'this_month', label: t('campaigns.report.thisMonth') },
              { value: 'last_month', label: t('campaigns.report.lastMonth') },
              { value: 'last_30', label: t('campaigns.period.last30') },
            ]}
            value={reportPeriod}
            onChange={setReportPeriod}
            size="sm"
            mb="md"
          />
          <Group gap="xs">
            <Button
              color="brand"
              size="sm"
              loading={reportLoading}
              onClick={() => {
                setReportLoading(true);
                const token = localStorage.getItem('token');
                fetch(`${process.env.REACT_APP_API_URL || '/api'}/accounts/report/campaign/?campaign_id=${campaign.id}&period=${reportPeriod}&export_format=pdf`, {
                  headers: { Authorization: `Token ${token}` },
                })
                  .then(r => r.blob())
                  .then(blob => { const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = `raport-${campaign.name}.pdf`; a.click(); })
                  .finally(() => setReportLoading(false));
              }}
            >
              {t('campaigns.report.downloadPdf')}
            </Button>
            <Button
              variant="default"
              size="sm"
              loading={reportLoading}
              onClick={() => {
                setReportLoading(true);
                const token = localStorage.getItem('token');
                fetch(`${process.env.REACT_APP_API_URL || '/api'}/accounts/report/campaign/?campaign_id=${campaign.id}&period=${reportPeriod}&export_format=xlsx`, {
                  headers: { Authorization: `Token ${token}` },
                })
                  .then(r => r.blob())
                  .then(blob => { const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = `raport-${campaign.name}.xlsx`; a.click(); })
                  .finally(() => setReportLoading(false));
              }}
            >
              {t('campaigns.report.downloadExcel')}
            </Button>
          </Group>
        </Modal>
      )}
      {/* Conversion setup */}
      <ConversionSetup
        opened={showConversionSetup}
        onClose={() => setShowConversionSetup(false)}
        onCreated={fetchCampaignData}
      />
      <DismissReasonModal
        suggestion={suggestionDismissModal}
        onClose={() => setSuggestionDismissModal(null)}
        onConfirm={confirmSuggestionDismiss}
      />
      {/* Limit bezpieczeństwa: zmiana przekracza próg (np. budżet +50% > 30%).
          Informujemy użytkownika i pozwalamy zastosować mimo to, jeśli tego chce. */}
      <Modal
        opened={!!suggestionSafetyModal}
        onClose={() => setSuggestionSafetyModal(null)}
        title={t('campaigns.safety.title')}
        centered
        overlayProps={{ backgroundOpacity: 0.3, blur: 4 }}
      >
        {suggestionSafetyModal && (
          <Stack gap="md">
            <Alert color="yellow" icon={<WarningCircle size={16} />}>
              <Text size="sm">{suggestionSafetyModal.message}</Text>
            </Alert>
            <Paper bg="gray.0" p="sm" radius="sm">
              <Text size="xs" c="dimmed" mb={2}>{t('campaigns.safety.suggestion')}</Text>
              <Text size="sm" fw={500}>{suggestionSafetyModal.suggestion.title}</Text>
            </Paper>
            <Text size="xs" c="dimmed">
              {t('campaigns.safety.desc')}
            </Text>
            <Group justify="flex-end" gap="xs">
              <Button variant="default" size="sm" onClick={() => setSuggestionSafetyModal(null)}>
                {t('campaigns.common.cancel')}
              </Button>
              <Button color="brand" size="sm" onClick={() => {
                const s = suggestionSafetyModal.suggestion;
                setSuggestionSafetyModal(null);
                handleSuggestionApply(s, true);
              }}>
                {t('campaigns.safety.applyAnyway')}
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>
    </div>
  );
}

const AD_SCHEDULE_DAYS = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'];

function formatAdWindow(w) {
  const pad = (m) => String(m).padStart(2, '0');
  return `${w.start_hour}:${pad(w.start_minute || 0)}-${w.end_hour}:${pad(w.end_minute || 0)}`;
}

function AdScheduleSection({ campaignId }) {
  const { t } = useTranslation();
  const { addNotification } = useNotifications();
  const [data, setData] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [removing, setRemoving] = useState(false);

  const load = useCallback(() => {
    api.get(`/ads/campaigns/${campaignId}/ad-schedule/`)
      .then(res => setData(res.data))
      .catch(() => setData({ supported: false, schedules: [] }));
  }, [campaignId]);

  useEffect(() => { load(); }, [load]);

  if (!data || !data.supported) return null;

  // Grupujemy dni o identycznych oknach: „Codziennie: 6:00-24:00” zamiast 7 wierszy.
  const byDay = {};
  (data.schedules || []).forEach(w => {
    (byDay[w.day_of_week] = byDay[w.day_of_week] || []).push(formatAdWindow(w));
  });
  const groups = [];
  AD_SCHEDULE_DAYS.forEach(d => {
    if (!byDay[d]) return;
    const key = byDay[d].join(', ');
    const g = groups.find(x => x.key === key);
    if (g) g.days.push(d); else groups.push({ key, days: [d] });
  });
  const dayLabel = (days) => (days.length === 7
    ? t('campaigns.schedule.adSchedule.everyDay')
    : days.map(d => t(`campaigns.schedule.adSchedule.days.${d}`)).join(', '));

  const remove = async () => {
    setRemoving(true);
    try {
      const res = await api.delete(`/ads/campaigns/${campaignId}/ad-schedule/`);
      addNotification(res.data.message || t('campaigns.schedule.adSchedule.removed'), 'success');
      setConfirming(false);
      load();
    } catch (err) {
      addNotification(err.response?.data?.error || t('campaigns.schedule.adSchedule.removeError'), 'error');
    } finally {
      setRemoving(false);
    }
  };

  return (
    <Paper withBorder p="sm" radius="md">
      <Group justify="space-between" mb={4}>
        <Text size="sm" fw={600}>{t('campaigns.schedule.adSchedule.title')}</Text>
        {groups.length > 0 && (
          confirming ? (
            <Group gap={4}>
              <Button size="compact-xs" color="red" loading={removing} onClick={remove}>{t('campaigns.schedule.adSchedule.confirmRemove')}</Button>
              <Button size="compact-xs" variant="subtle" color="gray" onClick={() => setConfirming(false)}>{t('campaigns.common.cancel')}</Button>
            </Group>
          ) : (
            <Button size="compact-xs" variant="subtle" color="red" onClick={() => setConfirming(true)}>{t('campaigns.schedule.adSchedule.remove')}</Button>
          )
        )}
      </Group>
      {data.error ? (
        <Text size="xs" c="red">{data.error}</Text>
      ) : groups.length === 0 ? (
        <Text size="xs" c="dimmed">{t('campaigns.schedule.adSchedule.allDay')}</Text>
      ) : (
        <Stack gap={2}>
          {groups.map(g => (
            <Text key={g.key + g.days.join()} size="xs"><Text span c="dimmed">{dayLabel(g.days)}:</Text> {g.key}</Text>
          ))}
          <Text size="xs" c="dimmed" mt={2}>{t('campaigns.schedule.adSchedule.hint')}</Text>
        </Stack>
      )}
    </Paper>
  );
}

function ScheduleTab({ campaignId, campaignName, supportsCpc = false, currency = 'PLN' }) {
  const { t } = useTranslation();
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingAction, setEditingAction] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showPast, setShowPast] = useState(false);
  const [cancellingId, setCancellingId] = useState(null);
  const { addNotification } = useNotifications();

  // Form state
  const [actionType, setActionType] = useState('');
  const [scheduledDateTime, setScheduledDateTime] = useState(null);
  const [budgetAmount, setBudgetAmount] = useState('');
  const [budgetPercent, setBudgetPercent] = useState(false);
  const [notes, setNotes] = useState('');
  const [recurrence, setRecurrence] = useState('none');
  const [recurrenceEndDate, setRecurrenceEndDate] = useState(null);

  const loadActions = useCallback(() => {
    const show = showPast ? 'all' : 'upcoming';
    api.get(`/ads/scheduled-actions/?campaign_id=${campaignId}&show=${show}`)
      .then(res => setActions(res.data))
      .catch(() => setActions([]))
      .finally(() => setLoading(false));
  }, [campaignId, showPast]);

  useEffect(() => { loadActions(); }, [loadActions]);

  const resetForm = () => {
    setActionType(''); setScheduledDateTime(null); setBudgetAmount(''); setNotes('');
    setRecurrence('none'); setRecurrenceEndDate(null); setEditingAction(null);
  };

  const startEdit = (a) => {
    setActionType(a.action_type);
    setScheduledDateTime(new Date(a.scheduled_at));
    setBudgetAmount(a.params?.amount || '');
    setBudgetPercent(!!a.params?.percent);
    setNotes(a.notes || '');
    setRecurrence(a.recurrence || 'none');
    setRecurrenceEndDate(a.recurrence_end_at ? new Date(a.recurrence_end_at) : null);
    setEditingAction(a.id);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!actionType || !scheduledDateTime) return;
    setSaving(true);
    const dt = scheduledDateTime.toISOString();
    const params = {};
    if (isBudgetAction || isCpcAction) {
      params.amount = parseFloat(budgetAmount) || 0;
      if (budgetPercent) params.percent = true;
    }
    try {
      if (editingAction) {
        const payload = { action_type: actionType, scheduled_at: dt, params, notes, recurrence };
        if (recurrenceEndDate) payload.recurrence_end_at = recurrenceEndDate.toISOString();
        else payload.recurrence_end_at = '';
        await api.patch(`/ads/scheduled-actions/${editingAction}/edit/`, payload);
        addNotification(t('campaigns.schedule.actionUpdated'), 'success');
      } else {
        const payload = { campaign_id: campaignId, action_type: actionType, scheduled_at: dt, params, notes, recurrence };
        if (recurrenceEndDate) payload.recurrence_end_at = recurrenceEndDate.toISOString();
        const res = await api.post('/ads/scheduled-actions/', payload);
        addNotification(res.data.message, 'success');
      }
      setShowForm(false);
      resetForm();
      loadActions();
    } catch (err) {
      addNotification(err.response?.data?.error || t('campaigns.schedule.saveError'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = async (id) => {
    setCancellingId(id);
    try {
      await api.post(`/ads/scheduled-actions/${id}/cancel/`);
      addNotification(t('campaigns.schedule.actionCancelled'), 'info');
      loadActions();
    } catch {
      addNotification(t('campaigns.schedule.cancelError'), 'error');
    } finally {
      setCancellingId(null);
    }
  };

  const isBudgetAction = ['set_budget', 'increase_budget', 'decrease_budget'].includes(actionType);
  const isCpcAction = ['set_cpc', 'increase_cpc', 'decrease_cpc'].includes(actionType);
  const isAmountAction = isBudgetAction || isCpcAction;

  const statusColors = { pending: 'blue', executed: 'green', failed: 'red', cancelled: 'gray' };
  const statusLabels = { pending: t('campaigns.schedule.statusPending'), executed: t('campaigns.schedule.statusExecuted'), failed: t('campaigns.common.error'), cancelled: t('campaigns.schedule.statusCancelled') };

  if (loading) return <Flex align="center" justify="center" py="xl"><Loader size="sm" color="brand" /></Flex>;

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Text size="sm" fw={600}>{t('campaigns.schedule.scheduledActions')}</Text>
        <Group gap="xs">
          <Button variant={showPast ? 'light' : 'subtle'} color="gray" size="compact-xs" onClick={() => setShowPast(!showPast)}>
            {showPast ? t('campaigns.schedule.onlyUpcoming') : t('campaigns.schedule.showHistory')}
          </Button>
          <Button size="compact-sm" color="brand" leftSection={<Plus size={12} />} onClick={() => { resetForm(); setShowForm(!showForm); }}>
            {t('campaigns.schedule.schedule')}
          </Button>
        </Group>
      </Group>

      {showForm && (
        <Paper withBorder p="md" radius="md">
          <form onSubmit={handleSubmit}>
            <Stack gap="sm">
              <Select
                label={t('campaigns.schedule.action')}
                placeholder={t('campaigns.schedule.selectAction')}
                data={[
                  { value: 'pause_campaign', label: t('campaigns.drawer.pauseCampaign') },
                  { value: 'enable_campaign', label: t('campaigns.drawer.resumeCampaign') },
                  { group: t('campaigns.schedule.budgetGroup'), items: [
                    { value: 'set_budget', label: t('campaigns.schedule.setBudget') },
                    { value: 'increase_budget', label: t('campaigns.schedule.increaseBudget') },
                    { value: 'decrease_budget', label: t('campaigns.schedule.decreaseBudget') },
                  ]},
                  ...(supportsCpc ? [{ group: t('campaigns.schedule.cpcGroup'), items: [
                    { value: 'set_cpc', label: t('campaigns.schedule.setCpc') },
                    { value: 'increase_cpc', label: t('campaigns.schedule.increaseCpc') },
                    { value: 'decrease_cpc', label: t('campaigns.schedule.decreaseCpc') },
                  ]}] : []),
                ]}
                value={actionType}
                onChange={setActionType}
                size="sm"
                required
              />
              <DateTimePicker
                label={t('campaigns.schedule.dateTime')}
                placeholder={t('campaigns.schedule.selectDate')}
                value={scheduledDateTime}
                onChange={setScheduledDateTime}
                size="sm"
                locale="pl"
                minDate={new Date()}
                valueFormat="DD.MM.YYYY HH:mm"
                required
              />
              {isAmountAction && (
                <Group grow>
                  <TextInput
                    label={actionType === 'set_budget' ? t('campaigns.schedule.newBudget', { cur: currencySymbol(currency) }) : actionType === 'set_cpc' ? t('campaigns.schedule.newCpc', { cur: currencySymbol(currency) }) : (budgetPercent ? t('campaigns.schedule.amountPct') : t('campaigns.schedule.amountZl', { cur: currencySymbol(currency) }))}
                    type="number"
                    value={budgetAmount}
                    onChange={e => setBudgetAmount(e.target.value)}
                    size="sm"
                    required
                  />
                  {actionType !== 'set_budget' && actionType !== 'set_cpc' && (
                    <Select
                      label={t('campaigns.schedule.unit')}
                      data={[{ value: 'zl', label: currencySymbol(currency) }, { value: 'pct', label: '%' }]}
                      value={budgetPercent ? 'pct' : 'zl'}
                      onChange={v => setBudgetPercent(v === 'pct')}
                      size="sm"
                    />
                  )}
                </Group>
              )}
              <Group grow>
                <Select
                  label={t('campaigns.schedule.recurrence')}
                  data={[
                    { value: 'none', label: t('campaigns.schedule.recNone') },
                    { value: 'daily', label: t('campaigns.schedule.recDaily') },
                    { value: 'weekly', label: t('campaigns.schedule.recWeekly') },
                    { value: 'monthly', label: t('campaigns.schedule.recMonthly') },
                  ]}
                  value={recurrence}
                  onChange={setRecurrence}
                  size="sm"
                />
                {recurrence !== 'none' && (
                  <DateTimePicker
                    label={t('campaigns.schedule.recurUntil')}
                    placeholder={t('campaigns.schedule.noLimit')}
                    value={recurrenceEndDate}
                    onChange={setRecurrenceEndDate}
                    size="sm"
                    locale="pl"
                    minDate={scheduledDateTime || new Date()}
                    valueFormat="DD.MM.YYYY"
                    clearable
                  />
                )}
              </Group>
              <TextInput
                label={t('campaigns.schedule.note')}
                value={notes}
                onChange={e => setNotes(e.target.value)}
                size="sm"
                placeholder={t('campaigns.schedule.notePlaceholder')}
              />
              <Group gap="xs">
                <Button type="submit" size="sm" color="brand" loading={saving}>{editingAction ? t('campaigns.schedule.saveChanges') : t('campaigns.schedule.scheduleAction')}</Button>
                <Button variant="subtle" color="gray" size="sm" onClick={() => { setShowForm(false); resetForm(); }}>{t('campaigns.common.cancel')}</Button>
              </Group>
            </Stack>
          </form>
        </Paper>
      )}

      {actions.length > 0 ? (
        <Stack gap="xs">
          {actions.map(a => (
            <Paper key={a.id} withBorder p="sm" radius="md">
              <Group justify="space-between" mb={4}>
                <Group gap="xs">
                  <Badge size="xs" variant="light" color={statusColors[a.status]}>{statusLabels[a.status]}</Badge>
                  <Text size="sm" fw={500}>{a.action_label}</Text>
                  {a.recurrence && a.recurrence !== 'none' && (
                    <Badge size="xs" variant="light" color="blue">{a.recurrence_label}</Badge>
                  )}
                </Group>
                {a.status === 'pending' && (
                  <Group gap={4}>
                    <Button variant="subtle" color="brand" size="compact-xs" onClick={() => startEdit(a)}>
                      {t('campaigns.schedule.edit')}
                    </Button>
                    <Button variant="subtle" color="red" size="compact-xs" loading={cancellingId === a.id} onClick={() => handleCancel(a.id)}>
                      {t('campaigns.common.cancel')}
                    </Button>
                  </Group>
                )}
              </Group>
              <Group gap="lg">
                <Text size="xs" c="dimmed">
                  {new Date(a.scheduled_at).toLocaleString('pl-PL', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                </Text>
                {a.params?.amount && (
                  <Text size="xs" ff="monospace" c="dimmed">
                    {a.params.percent ? `${a.params.amount}%` : `${a.params.amount} ${currencySymbol(currency)}`}
                  </Text>
                )}
              </Group>
              {a.notes && <Text size="xs" c="dimmed" mt={2}>{a.notes}</Text>}
              {a.result?.old_budget != null && (
                <Text size="xs" c="green.6" mt={2}>
                  {t('campaigns.schedule.budgetResult', { old: a.result.old_budget, new: a.result.new_budget, cur: currencySymbol(currency) })}
                </Text>
              )}
              {a.result?.old_cpc != null && (
                <Text size="xs" c="green.6" mt={2}>
                  {t('campaigns.schedule.cpcResult', { old: a.result.old_cpc, new: a.result.new_cpc, cur: currencySymbol(currency), groups: a.result.ad_groups_updated || 0 })}
                </Text>
              )}
              {(a.result?.error || a.result?.reason) && (
                <Text size="xs" c={a.status === 'cancelled' ? 'dimmed' : 'red'} mt={2}>{t('campaigns.schedule.errorResult', { error: a.result.error || a.result.reason })}</Text>
              )}
            </Paper>
          ))}
        </Stack>
      ) : (
        <Paper withBorder p="lg" radius="md" ta="center">
          <Text size="sm" c="dimmed">{t('campaigns.schedule.emptyDesc')}</Text>
        </Paper>
      )}
    </Stack>
  );
}

function VideoThumbnailPicker({ campaignId, ad, video, onUpdated }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [second, setSecond] = useState(1);
  const [frameUrl, setFrameUrl] = useState('');
  const [frameLoading, setFrameLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const { addNotification } = useNotifications();
  const duration = Math.max(1, Math.floor(video?.duration_seconds || 0));

  const loadFrame = async (sec) => {
    if (!video) return;
    setFrameLoading(true);
    try {
      const res = await api.get(`/ads/drafts/${campaignId}/videos/${video.id}/frame/?second=${sec}`, { responseType: 'blob' });
      setFrameUrl(prev => { if (prev) URL.revokeObjectURL(prev); return URL.createObjectURL(res.data); });
    } catch {
      addNotification(t('campaigns.video.frameError'), 'error');
    } finally { setFrameLoading(false); }
  };

  const saveThumbnail = async (payload) => {
    setSaving(true);
    try {
      const res = await api.post(`/ads/campaigns/${campaignId}/meta-creative/thumbnail/`, payload,
        payload instanceof FormData ? { headers: { 'Content-Type': 'multipart/form-data' } } : undefined);
      addNotification(t('campaigns.video.thumbnailUpdated'), 'success');
      setOpen(false);
      if (onUpdated) onUpdated(res.data.creative_id);
    } catch (err) {
      addNotification(err.response?.data?.error || t('campaigns.video.thumbnailError'), 'error');
    } finally { setSaving(false); }
  };

  const handleUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('image', file);
    fd.append('ad_id', ad.ad_id);
    fd.append('creative_id', ad.creative_id);
    saveThumbnail(fd);
    e.target.value = '';
  };

  return (
    <div className="border-t border-gray-100 px-3 py-2">
      <button onClick={() => { setOpen(!open); if (!open && video && !frameUrl) loadFrame(second); }}
        className="text-xs text-indigo-600 hover:text-indigo-700 font-medium">
        {open ? t('campaigns.video.hidePicker') : t('campaigns.video.changeThumbnail')}
      </button>
      {open && (
        <div className="mt-2 space-y-2.5">
          {video ? (
            <div>
              <div className="flex items-center gap-2">
                <input type="range" min={0} max={duration} step={0.5} value={second}
                  onChange={e => setSecond(parseFloat(e.target.value))}
                  onMouseUp={() => loadFrame(second)} onTouchEnd={() => loadFrame(second)}
                  className="flex-1" />
                <span className="text-xs text-gray-500 w-12 text-right">{second.toFixed(1)} s</span>
              </div>
              <div className="mt-1.5 flex items-start gap-2">
                <div className="w-24 h-40 bg-gray-100 rounded overflow-hidden flex items-center justify-center shrink-0">
                  {frameLoading ? <CircleNotch size={14} className="animate-spin text-gray-400" />
                    : frameUrl ? <img src={frameUrl} alt="" className="w-full h-full object-cover" />
                    : <span className="text-[10px] text-gray-400 px-1 text-center">{t('campaigns.video.moveSlider')}</span>}
                </div>
                <Button size="compact-xs" color="brand" variant="light" loading={saving} disabled={!frameUrl}
                  onClick={() => saveThumbnail({ ad_id: ad.ad_id, creative_id: ad.creative_id, video_pk: video.id, second })}>
                  {t('campaigns.video.setFrame')}
                </Button>
              </div>
            </div>
          ) : (
            <p className="text-[11px] text-gray-400">{t('campaigns.video.noVideoFile')}</p>
          )}
          <label className="inline-flex items-center gap-1.5 text-xs text-indigo-600 hover:text-indigo-700 font-medium cursor-pointer">
            <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={handleUpload} disabled={saving} />
            <Upload size={12} /> {t('campaigns.video.uploadImage')}
          </label>
        </div>
      )}
    </div>
  );
}

function MetaTargetingPanel({ campaignId, campaignGoal }) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});
  const [audienceSize, setAudienceSize] = useState(null);
  const [sizeLoading, setSizeLoading] = useState(false);
  const [abLoading, setAbLoading] = useState(false);
  const { addNotification } = useNotifications();
  const isLeadGoal = campaignGoal === 'leads';

  const createBroadAB = async () => {
    setAbLoading(true);
    try {
      const res = await api.post(`/ads/campaigns/${campaignId}/meta-ab-broad/`);
      const note = res.data.budget_mode === 'split'
        ? t('campaigns.metaTargeting.abNoteSplit')
        : t('campaigns.metaTargeting.abNoteOwn');
      addNotification(t('campaigns.metaTargeting.abCreated', { note }), 'success');
    } catch (err) {
      addNotification(err.response?.data?.error || t('campaigns.metaTargeting.abError'), 'error');
    } finally { setAbLoading(false); }
  };

  const fetchSize = async (t) => {
    setSizeLoading(true);
    try {
      const res = await api.post('/ads/meta/audience-size/', {
        targeting: {
          age_min: t.age_min || 18,
          age_max: t.age_max || 65,
          genders: t.genders || ['all'],
          interests: (t.interests || []).map(i => ({ id: i.id, name: i.name })),
          countries: (t.countries || []).length ? t.countries : ['PL'],
          cities: t.cities || [],
          excluded_countries: t.excluded_countries || [],
          excluded_cities: t.excluded_cities || [],
          excluded_interests: t.excluded_interests || [],
        },
      });
      setAudienceSize(res.data.estimate);
    } catch { setAudienceSize(null); } finally { setSizeLoading(false); }
  };

  useEffect(() => {
    api.get(`/ads/campaigns/${campaignId}/meta-targeting/`)
      .then(res => { setData(res.data); setForm(res.data); fetchSize(res.data); })
      .catch(err => setError(err.response?.data?.error || t('campaigns.metaTargeting.fetchError')))
      .finally(() => setLoading(false));
  }, [campaignId]); // eslint-disable-line

  // Recompute the estimate while editing (debounced).
  useEffect(() => {
    if (!editing) return;
    const t = setTimeout(() => fetchSize(form), 700);
    return () => clearTimeout(t);
  }, [editing, form.age_min, form.age_max, form.genders, form.interests, form.countries, form.cities, form.excluded_countries, form.excluded_cities, form.excluded_interests]); // eslint-disable-line

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.post(`/ads/campaigns/${campaignId}/meta-targeting/`, {
        age_min: form.age_min,
        age_max: form.age_max,
        genders: form.genders,
        interests: form.interests,
        countries: form.countries,
        cities: form.cities,
        excluded_countries: form.excluded_countries,
        excluded_cities: form.excluded_cities,
        excluded_interests: form.excluded_interests,
      });
      addNotification(t('campaigns.metaTargeting.saved'), 'success');
      setData({ ...data, ...form });
      setEditing(false);
    } catch (err) {
      addNotification(err.response?.data?.error || t('campaigns.metaTargeting.saveError'), 'error');
    } finally { setSaving(false); }
  };

  if (loading) return <Flex align="center" justify="center" py="xl"><Loader size="sm" color="brand" /></Flex>;
  if (error) return <Text size="sm" c="dimmed" ta="center" py="md">{error}</Text>;
  if (!data) return null;

  const GENDER_LABELS = { all: t('campaigns.metaTargeting.genderAll'), male: t('campaigns.metaTargeting.genderMale'), female: t('campaigns.metaTargeting.genderFemale') };
  const genderValue = (form.genders || ['all']).length === 2 ? 'all' : (form.genders || ['all'])[0];
  const fmtSize = (n) => n >= 1e6 ? `${(n / 1e6).toFixed(1).replace('.', ',')} ${t('campaigns.metaTargeting.mln')}` : n >= 1e3 ? `${Math.round(n / 1e3)} ${t('campaigns.metaTargeting.thousand')}` : String(n);
  const view = editing ? form : data;

  const geoChips = (countries, cities, onRemoveCountry, onRemoveCity) => (
    <div className="flex flex-wrap gap-1.5">
      {(countries || []).map(c => (
        <span key={c} className="inline-flex items-center gap-1 bg-gray-100 text-gray-700 text-xs rounded-full px-2.5 py-1">
          <Globe size={11} /> {c}
          {onRemoveCountry && <button onClick={() => onRemoveCountry(c)} className="text-gray-400 hover:text-gray-700"><X size={11} /></button>}
        </span>
      ))}
      {(cities || []).map(c => (
        <span key={c.key} className="inline-flex items-center gap-1 bg-gray-100 text-gray-700 text-xs rounded-full px-2.5 py-1">
          {c.name}{c.radius ? ` +${c.radius} km` : ''}
          {onRemoveCity && <button onClick={() => onRemoveCity(c.key)} className="text-gray-400 hover:text-gray-700"><X size={11} /></button>}
        </span>
      ))}
    </div>
  );

  return (
    <Paper withBorder p="md" radius="md" style={{ maxWidth: 680 }}>
      <Group justify="space-between" mb="sm">
        <div>
          <Text size="sm" fw={600}>{t('campaigns.tabs.targeting')}</Text>
          <Text size="xs" c="dimmed">{t('campaigns.metaTargeting.adsetDesc', { name: data.adset_name })}</Text>
        </div>
        {!editing ? (
          <Button size="compact-xs" variant="light" color="brand" leftSection={<PencilSimple size={12} />} onClick={() => { setForm(data); setEditing(true); }}>{t('campaigns.metaTargeting.edit')}</Button>
        ) : (
          <Group gap="xs">
            <Button size="compact-xs" variant="subtle" color="gray" onClick={() => { setEditing(false); setForm(data); fetchSize(data); }}>{t('campaigns.common.cancel')}</Button>
            <Button size="compact-xs" color="brand" loading={saving} onClick={handleSave}>{t('campaigns.metaTargeting.saveInMeta')}</Button>
          </Group>
        )}
      </Group>

      <div className="flex items-center gap-2 bg-indigo-50/60 border border-indigo-100 rounded-lg px-3 py-2 mb-3">
        <UsersThree size={18} className="text-indigo-500 shrink-0" />
        <div>
          <Text size="xs" c="dimmed">{t('campaigns.metaTargeting.estimatedSize')}</Text>
          {sizeLoading ? (
            <Loader size={14} color="brand" />
          ) : audienceSize ? (
            <Text size="sm" fw={600}>{t('campaigns.metaTargeting.peopleRange', { lower: fmtSize(audienceSize.users_lower_bound || 0), upper: fmtSize(audienceSize.users_upper_bound || 0) })}</Text>
          ) : (
            <Text size="sm" c="dimmed">{t('campaigns.metaTargeting.noData')}</Text>
          )}
        </div>
      </div>

      {/* Rekomendacja targetowania zależna od celu kampanii (lead gen vs ruch). */}
      <Alert color={isLeadGoal ? 'teal' : 'blue'} variant="light" p="xs" mb="md" icon={<Lightbulb size={16} />}
             title={isLeadGoal ? t('campaigns.metaTargeting.leadTitle') : t('campaigns.metaTargeting.trafficTitle')}
             styles={{ message: { fontSize: 12 }, title: { fontSize: 13 } }}>
        {isLeadGoal ? (
          <>{t('campaigns.metaTargeting.leadBody1')} <b>{t('campaigns.metaTargeting.leadBodyBold')}</b>{t('campaigns.metaTargeting.leadBody2')}</>
        ) : (
          <>{t('campaigns.metaTargeting.trafficBody1')} <b>{t('campaigns.metaTargeting.trafficBodyBold')}</b> {t('campaigns.metaTargeting.trafficBody2')}</>
        )}
        {!isLeadGoal && (
          <div style={{ marginTop: 8 }}>
            <Button size="compact-xs" variant="white" color="blue" loading={abLoading} onClick={createBroadAB}>
              {t('campaigns.metaTargeting.abButton')}
            </Button>
            <Text size="10px" c="dimmed" mt={4}>{t('campaigns.metaTargeting.abHint')}</Text>
          </div>
        )}
      </Alert>

      <Stack gap="md">
        <div>
          <Text size="xs" fw={500} c="dimmed" mb={4}>{t('campaigns.metaTargeting.location')}</Text>
          {geoChips(view.countries, view.cities,
            editing ? (c) => setForm({ ...form, countries: form.countries.filter(x => x !== c) }) : null,
            editing ? (k) => setForm({ ...form, cities: form.cities.filter(x => x.key !== k) }) : null)}
          {editing && (
            <MetaGeoSearchInput placeholder={t('campaigns.metaTargeting.addLocation')} onPick={(loc) => {
              if (loc.type === 'country') {
                const code = loc.key.toUpperCase();
                if (!(form.countries || []).includes(code)) setForm({ ...form, countries: [...(form.countries || []), code] });
              } else {
                if (!(form.cities || []).some(c => c.key === loc.key)) setForm({ ...form, cities: [...(form.cities || []), { key: loc.key, name: loc.name }] });
              }
            }} />
          )}
        </div>

        <div>
          <Text size="xs" fw={500} c="dimmed" mb={4}>{t('campaigns.metaTargeting.excludedLocations')}</Text>
          {((view.excluded_countries || []).length + (view.excluded_cities || []).length) > 0 ? (
            geoChips(view.excluded_countries, view.excluded_cities,
              editing ? (c) => setForm({ ...form, excluded_countries: form.excluded_countries.filter(x => x !== c) }) : null,
              editing ? (k) => setForm({ ...form, excluded_cities: form.excluded_cities.filter(x => x.key !== k) }) : null)
          ) : !editing && <Text size="sm" c="dimmed">{t('campaigns.metaTargeting.none')}</Text>}
          {editing && (
            <MetaGeoSearchInput placeholder={t('campaigns.metaTargeting.excludeLocation')} onPick={(loc) => {
              if (loc.type === 'country') {
                const code = loc.key.toUpperCase();
                if (!(form.excluded_countries || []).includes(code)) setForm({ ...form, excluded_countries: [...(form.excluded_countries || []), code] });
              } else {
                if (!(form.excluded_cities || []).some(c => c.key === loc.key)) setForm({ ...form, excluded_cities: [...(form.excluded_cities || []), { key: loc.key, name: loc.name }] });
              }
            }} />
          )}
        </div>

        <div>
          <Text size="xs" fw={500} c="dimmed" mb={4}>{t('campaigns.metaTargeting.age')}</Text>
          {editing ? (
            <Group gap="xs">
              <input type="number" min={18} max={65} value={form.age_min || 18}
                onChange={e => setForm({ ...form, age_min: parseInt(e.target.value) || 18 })}
                className="w-20 border border-gray-200 rounded px-2 py-1 text-sm" />
              <Text size="xs" c="dimmed">{t('campaigns.metaTargeting.to')}</Text>
              <input type="number" min={18} max={65} value={form.age_max || 65}
                onChange={e => setForm({ ...form, age_max: parseInt(e.target.value) || 65 })}
                className="w-20 border border-gray-200 rounded px-2 py-1 text-sm" />
            </Group>
          ) : (
            <Text size="sm">{t('campaigns.metaTargeting.ageRange', { min: data.age_min, max: data.age_max })}</Text>
          )}
        </div>

        <div>
          <Text size="xs" fw={500} c="dimmed" mb={4}>{t('campaigns.metaTargeting.gender')}</Text>
          {editing ? (
            <select value={genderValue}
              onChange={e => setForm({ ...form, genders: e.target.value === 'all' ? ['all'] : [e.target.value] })}
              className="border border-gray-200 rounded px-2 py-1 text-sm bg-white">
              {Object.entries(GENDER_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          ) : (
            <Text size="sm">{(data.genders || ['all']).map(g => GENDER_LABELS[g] || g).join(', ')}</Text>
          )}
        </div>

        <div>
          <Text size="xs" fw={500} c="dimmed" mb={4}>{t('campaigns.metaTargeting.interests')}</Text>
          <div className="flex flex-wrap gap-1.5">
            {(view.interests || []).map(i => (
              <span key={i.id} className="inline-flex items-center gap-1 bg-indigo-50 text-indigo-700 text-xs rounded-full px-2.5 py-1">
                {i.name}
                {editing && (
                  <button onClick={() => setForm({ ...form, interests: form.interests.filter(x => x.id !== i.id) })}
                    className="text-indigo-400 hover:text-indigo-700"><X size={11} /></button>
                )}
              </span>
            ))}
            {(view.interests || []).length === 0 && !editing && (
              <Text size="sm" c="dimmed">{t('campaigns.metaTargeting.noInterests')}</Text>
            )}
          </div>
          {editing && (
            <MetaInterestSearchInput placeholder={t('campaigns.metaTargeting.searchInterests')}
              exclude={(form.interests || []).map(i => i.id)}
              onPick={(i) => setForm({ ...form, interests: [...(form.interests || []), { id: i.id, name: i.name }] })} />
          )}
        </div>

        <div>
          <Text size="xs" fw={500} c="dimmed" mb={4}>{t('campaigns.metaTargeting.excludedInterests')}</Text>
          <div className="flex flex-wrap gap-1.5">
            {(view.excluded_interests || []).map(i => (
              <span key={i.id} className="inline-flex items-center gap-1 bg-red-50 text-red-700 text-xs rounded-full px-2.5 py-1">
                {i.name}
                {editing && (
                  <button onClick={() => setForm({ ...form, excluded_interests: form.excluded_interests.filter(x => x.id !== i.id) })}
                    className="text-red-400 hover:text-red-700"><X size={11} /></button>
                )}
              </span>
            ))}
            {(view.excluded_interests || []).length === 0 && !editing && <Text size="sm" c="dimmed">{t('campaigns.metaTargeting.none')}</Text>}
          </div>
          {editing && (
            <MetaInterestSearchInput placeholder={t('campaigns.metaTargeting.excludeInterests')}
              exclude={[...(form.interests || []), ...(form.excluded_interests || [])].map(i => i.id)}
              onPick={(i) => setForm({ ...form, excluded_interests: [...(form.excluded_interests || []), { id: i.id, name: i.name }] })} />
          )}
        </div>
      </Stack>
      <Text size="xs" c="dimmed" mt="md">{t('campaigns.metaTargeting.changesNote')}</Text>
    </Paper>
  );
}

function MetaInterestSearchInput({ placeholder, exclude, onPick }) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);

  const search = async (q) => {
    setQuery(q);
    if (q.length < 2) { setResults([]); return; }
    setSearching(true);
    try {
      const res = await api.get(`/ads/meta/interests/search/?q=${encodeURIComponent(q)}`);
      const skip = new Set(exclude || []);
      setResults((res.data.interests || []).filter(i => !skip.has(i.id)).slice(0, 6));
    } catch {} finally { setSearching(false); }
  };

  return (
    <div className="mt-2 relative">
      <input value={query} onChange={e => search(e.target.value)} placeholder={placeholder}
        className="w-full border border-gray-200 rounded px-2.5 py-1.5 text-sm" />
      {searching && <CircleNotch size={13} className="animate-spin text-gray-400 absolute right-2.5 top-2.5" />}
      {results.length > 0 && (
        <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
          {results.map(i => (
            <button key={i.id} onClick={() => { onPick(i); setQuery(''); setResults([]); }}
              className="w-full text-left px-3 py-1.5 text-sm hover:bg-indigo-50">
              {i.name}
              {i.audience_size_upper_bound ? <span className="text-[10px] text-gray-400 ml-2">~{(i.audience_size_upper_bound / 1e6).toFixed(1)} {t('campaigns.metaTargeting.mln')}</span> : null}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function MetaGeoSearchInput({ placeholder, onPick }) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);

  const TYPE_LABELS = { country: t('campaigns.metaTargeting.geoCountry'), city: t('campaigns.metaTargeting.geoCity'), region: t('campaigns.metaTargeting.geoRegion') };

  const search = async (q) => {
    setQuery(q);
    if (q.length < 2) { setResults([]); return; }
    setSearching(true);
    try {
      const res = await api.get(`/ads/meta/geo/search/?q=${encodeURIComponent(q)}`);
      setResults((res.data.locations || []).slice(0, 8));
    } catch {} finally { setSearching(false); }
  };

  return (
    <div className="mt-2 relative">
      <input value={query} onChange={e => search(e.target.value)} placeholder={placeholder}
        className="w-full border border-gray-200 rounded px-2.5 py-1.5 text-sm" />
      {searching && <CircleNotch size={13} className="animate-spin text-gray-400 absolute right-2.5 top-2.5" />}
      {results.length > 0 && (
        <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
          {results.map(loc => (
            <button key={`${loc.type}:${loc.key}`} onClick={() => { onPick(loc); setQuery(''); setResults([]); }}
              className="w-full text-left px-3 py-1.5 text-sm hover:bg-indigo-50">
              {loc.name}
              <span className="text-[10px] text-gray-400 ml-2">
                {TYPE_LABELS[loc.type] || loc.type}{loc.region ? `, ${loc.region}` : ''}{loc.country_code ? `, ${loc.country_code}` : ''}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// Draft status display helper
// Budżety zestawów reklam (Meta ABO): edycja dziennego budżetu każdego zestawu.
function MetaAdSetBudgets({ adGroups, campaignId, onUpdated, currency = 'PLN' }) {
  const { t } = useTranslation();
  const { addNotification } = useNotifications();
  const [edits, setEdits] = useState({});
  const [savingId, setSavingId] = useState(null);
  if (!adGroups || adGroups.length === 0) return null;

  const save = async (ag) => {
    const val = edits[ag.id];
    if (val === undefined || val === '' || parseFloat(val) === parseFloat(ag.daily_budget || 0)) return;
    setSavingId(ag.id);
    try {
      await api.post(`/ads/campaigns/${campaignId}/ad-groups/${ag.id}/`, { daily_budget: parseFloat(val) });
      addNotification(t('campaigns.adSetBudgets.saved'), 'success');
      setEdits(e => { const n = { ...e }; delete n[ag.id]; return n; });
      onUpdated?.();
    } catch (err) {
      addNotification(err.response?.data?.error || t('campaigns.adSetBudgets.error'), 'error');
    } finally {
      setSavingId(null);
    }
  };

  return (
    <Paper withBorder p="md" radius="md">
      <Text fw={600} size="sm">{t('campaigns.adSetBudgets.title')}</Text>
      <Text size="xs" c="dimmed" mb="sm">{t('campaigns.adSetBudgets.hint')}</Text>
      <Stack gap="xs">
        {adGroups.map(ag => {
          const cur = edits[ag.id] !== undefined ? edits[ag.id] : (parseFloat(ag.daily_budget) || 0);
          const dirty = edits[ag.id] !== undefined && parseFloat(edits[ag.id]) !== parseFloat(ag.daily_budget || 0);
          return (
            <Group key={ag.id} justify="space-between" wrap="nowrap">
              <Group gap="xs" wrap="nowrap" style={{ minWidth: 0 }}>
                <Badge size="xs" variant="light" color={ag.status === 'active' ? 'green' : 'gray'}>
                  {ag.status === 'active' ? t('campaigns.adSetBudgets.active') : t('campaigns.adSetBudgets.paused')}
                </Badge>
                <Text size="sm" truncate>{ag.name}</Text>
              </Group>
              <Group gap="xs" wrap="nowrap">
                <NumberInput
                  size="xs" w={130} min={0} step={1} hideControls
                  value={cur}
                  onChange={(v) => setEdits(e => ({ ...e, [ag.id]: v }))}
                  suffix={` ${t('campaigns.adSetBudgets.perDay', { cur: currencySymbol(currency) })}`}
                />
                <Button size="compact-xs" variant="light" disabled={!dirty} loading={savingId === ag.id} onClick={() => save(ag)}>
                  {t('campaigns.adSetBudgets.save')}
                </Button>
              </Group>
            </Group>
          );
        })}
      </Stack>
    </Paper>
  );
}

function MetaAdEditor({ campaignId, onUpdated, currency = 'PLN' }) {
  const { t } = useTranslation();
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [savingId, setSavingId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({});
  const { addNotification } = useNotifications();

  const [videos, setVideos] = useState([]);
  const [refreshing, setRefreshing] = useState(false);
  const loadAds = (refresh = false) => {
    if (refresh) setRefreshing(true);
    return api.get(`/ads/campaigns/${campaignId}/meta-creative/${refresh ? '?refresh=1' : ''}`)
      .then(res => setAds(res.data.ads || (res.data.ad_id ? [res.data] : [])))
      .catch(err => setError(err.response?.data?.error || t('campaigns.metaAd.fetchError')))
      .finally(() => setRefreshing(false));
  };
  useEffect(() => {
    Promise.all([
      loadAds(),
      api.get(`/ads/drafts/${campaignId}/videos/`).catch(() => ({ data: { videos: [] } })),
    ])
      .then(([, vidRes]) => { if (vidRes?.data) setVideos(vidRes.data.videos || []); })
      .finally(() => setLoading(false));
  }, [campaignId]); // eslint-disable-line

  const handleSave = async (ad) => {
    setSavingId(ad.ad_id);
    try {
      const res = await api.post(`/ads/campaigns/${campaignId}/meta-creative/`, {
        ad_id: ad.ad_id,
        creative_id: ad.creative_id,
        primary_text: form.primary_text,
        headline: form.headline,
        description: form.description,
      });
      addNotification(t('campaigns.metaAd.creativeUpdated'), 'success');
      setAds(prev => prev.map(a => a.ad_id === ad.ad_id
        ? { ...a, ...form, creative_id: res.data.creative_id || a.creative_id } : a));
      setEditingId(null);
      if (onUpdated) onUpdated();
    } catch (err) {
      addNotification(err.response?.data?.error || t('campaigns.metaAd.saveError'), 'error');
    } finally { setSavingId(null); }
  };

  if (loading) return <Flex align="center" justify="center" py="xl"><Loader size="sm" color="brand" /></Flex>;
  if (error) return <Text size="sm" c="dimmed" ta="center" py="md">{error}</Text>;
  if (ads.length === 0) return <Text size="sm" c="dimmed" ta="center" py="md">{t('campaigns.metaAd.noData')}</Text>;

  const CTA_LABELS = { LEARN_MORE: t('campaigns.metaAd.ctaLearnMore'), SHOP_NOW: t('campaigns.metaAd.ctaShopNow'), SIGN_UP: t('campaigns.metaAd.ctaSignUp'), CONTACT_US: t('campaigns.metaAd.ctaContact'), GET_OFFER: t('campaigns.metaAd.ctaGetOffer'), BOOK_TRAVEL: t('campaigns.metaAd.ctaBook') };
  const STATUS_LABELS = { ACTIVE: t('campaigns.metaAd.statusActive'), PAUSED: t('campaigns.metaAd.statusPaused'), PENDING_REVIEW: t('campaigns.metaAd.statusReview'), DISAPPROVED: t('campaigns.metaAd.statusDisapproved'), CAMPAIGN_PAUSED: t('campaigns.metaAd.statusCampaignPaused'), ADSET_PAUSED: t('campaigns.metaAd.statusAdsetPaused') };
  const adWord = ads.length === 1 ? t('campaigns.metaAd.adWordOne') : ads.length < 5 ? t('campaigns.metaAd.adWordFew') : t('campaigns.metaAd.adWordMany');

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Text size="xs" c="dimmed">{t('campaigns.metaAd.adsInSet', { count: ads.length, word: adWord })}</Text>
        <Button size="compact-xs" variant="subtle" color="brand" loading={refreshing}
          leftSection={<ArrowsClockwise size={12} />} onClick={() => loadAds(true)}>
          {t('campaigns.metaAd.refresh')}
        </Button>
      </Group>
      {ads.map(ad => {
        const editing = editingId === ad.ad_id;
        return (
          <Paper key={ad.ad_id} withBorder p="md" radius="md">
            <Group justify="space-between" mb="sm">
              <Group gap="xs">
                <Text size="sm" fw={600}>{ad.name || t('campaigns.metaAd.adDefault')}</Text>
                <span className={`text-[10px] font-medium rounded px-1.5 py-0.5 ${ad.type === 'video' ? 'bg-purple-50 text-purple-600' : 'bg-indigo-50 text-indigo-600'}`}>
                  {ad.type === 'video' ? t('campaigns.metaAd.video') : t('campaigns.metaAd.image')}
                </span>
                <span className="text-[10px] text-gray-400">{STATUS_LABELS[ad.effective_status] || ad.effective_status || ad.ad_status}</span>
              </Group>
              {!editing ? (
                <Button size="compact-xs" variant="light" color="brand" leftSection={<PencilSimple size={12} />}
                  onClick={() => { setEditingId(ad.ad_id); setForm({ primary_text: ad.primary_text, headline: ad.headline, description: ad.description }); }}>
                  {t('campaigns.metaAd.edit')}
                </Button>
              ) : (
                <Group gap="xs">
                  <Button size="compact-xs" variant="subtle" color="gray" onClick={() => setEditingId(null)}>{t('campaigns.common.cancel')}</Button>
                  <Button size="compact-xs" color="brand" loading={savingId === ad.ad_id} onClick={() => handleSave(ad)}>{t('campaigns.metaAd.saveToMeta')}</Button>
                </Group>
              )}
            </Group>

            {ad.metrics && (
              <div className="flex flex-wrap gap-x-4 gap-y-1 mb-3 bg-gray-50 rounded-md px-3 py-2">
                <span className="text-xs text-gray-600">{t('campaigns.metaAd.mImpr')} <b>{(ad.metrics.impressions || 0).toLocaleString('pl-PL')}</b></span>
                <span className="text-xs text-gray-600">{t('campaigns.metaAd.mClicks')} <b>{ad.metrics.clicks || 0}</b></span>
                <span className="text-xs text-gray-600">{t('campaigns.metaAd.mCtr')} <b>{ad.metrics.ctr || 0}%</b></span>
                <span className="text-xs text-gray-600">{t('campaigns.metaAd.mConversions')} <b className={ad.metrics.conversions > 0 ? 'text-green-700' : undefined}>{ad.metrics.conversions || 0}</b></span>
                <span className="text-xs text-gray-600">{t('campaigns.metaAd.mCost')} <b>{formatMoney(ad.metrics.cost || 0, currency)}</b></span>
                {ad.metrics.cost_per_result != null && (
                  <span className="text-xs text-gray-600">{t('campaigns.metaAd.mCostPerConv')} <b>{formatMoney(ad.metrics.cost_per_result, currency)}</b></span>
                )}
              </div>
            )}

            {editing ? (
              <Stack gap="sm">
                <div>
                  <Text size="xs" fw={500} mb={4}>{t('campaigns.metaAd.primaryText')}</Text>
                  <textarea value={form.primary_text || ''} onChange={e => setForm({ ...form, primary_text: e.target.value })}
                    rows={3} className="w-full border border-gray-200 rounded px-2.5 py-1.5 text-sm" />
                </div>
                <div>
                  <Text size="xs" fw={500} mb={4}>{t('campaigns.metaAd.headline')}</Text>
                  <input value={form.headline || ''} onChange={e => setForm({ ...form, headline: e.target.value })}
                    className="w-full border border-gray-200 rounded px-2.5 py-1.5 text-sm" maxLength={40} />
                </div>
                <div>
                  <Text size="xs" fw={500} mb={4}>{t('campaigns.metaAd.description')}</Text>
                  <input value={form.description || ''} onChange={e => setForm({ ...form, description: e.target.value })}
                    className="w-full border border-gray-200 rounded px-2.5 py-1.5 text-sm" maxLength={125} />
                </div>
              </Stack>
            ) : (
              <div className="border border-gray-200 rounded-lg overflow-hidden" style={{ maxWidth: 400 }}>
                <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-100">
                  <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center">
                    <span className="text-xs font-bold text-indigo-600">M</span>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-gray-900">{t('campaigns.metaAd.facebookPage')}</p>
                    <p className="text-[10px] text-gray-400">{t('campaigns.metaAd.sponsored')}</p>
                  </div>
                </div>
                <p className="px-3 py-2 text-xs text-gray-700 leading-relaxed">{ad.primary_text}</p>
                {ad.video_url ? (
                  <div>
                    <video controls playsInline preload="metadata" poster={ad.image_url || ad.thumbnail_url || undefined} className="w-full" style={{ maxHeight: 480, background: '#000' }}>
                      <source src={ad.video_url} type="video/mp4" />
                    </video>
                    <VideoThumbnailPicker campaignId={campaignId} ad={ad}
                      video={videos.find(v => v.meta_video_id === ad.video_id)}
                      onUpdated={(newCreativeId) => setAds(prev => prev.map(a => a.ad_id === ad.ad_id ? { ...a, creative_id: newCreativeId } : a))} />
                  </div>
                ) : (ad.image_url || ad.thumbnail_url) && <img src={ad.image_url || ad.thumbnail_url} alt="" className="w-full" />}
                <div className="px-3 py-2 border-t border-gray-100 flex items-center justify-between">
                  <div className="min-w-0 flex-1">
                    <p className="text-[10px] text-gray-400 uppercase truncate">{ad.link ? new URL(ad.link).hostname : ''}</p>
                    <p className="text-xs font-semibold text-gray-900 truncate">{ad.headline}</p>
                    <p className="text-[11px] text-gray-500 truncate">{ad.description}</p>
                  </div>
                  <span className="text-[10px] font-medium text-gray-500 border border-gray-300 rounded px-2 py-1 shrink-0 ml-2">
                    {CTA_LABELS[ad.cta_type] || ad.cta_type}
                  </span>
                </div>
              </div>
            )}
          </Paper>
        );
      })}
    </Stack>
  );
}

function DraftStatusBadge({ campaign, onRetry, onDelete }) {
  const { t } = useTranslation();
  const ds = campaign.draft_status;

  if (ds === 'analyzing') {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs text-indigo-600">
        <CircleNotch className="w-3 h-3 animate-spin" />
        {t('campaigns.draftStatus.analyzing')}
      </span>
    );
  }
  if (ds === 'ready') {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs text-indigo-600 font-medium cursor-pointer hover:text-indigo-700">
        <span className="w-1.5 h-1.5 rounded-full bg-indigo-500" />
        {t('campaigns.draftStatus.readyDraft')}
      </span>
    );
  }
  if (ds === 'publishing') {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs text-amber-600">
        <CircleNotch className="w-3 h-3 animate-spin" />
        {t('campaigns.draftStatus.publishing')}
      </span>
    );
  }
  if (ds === 'failed') {
    const d = campaign.ai_draft_data || {};
    const hasData = d.keywords?.length > 0 || d.headlines?.length > 0 || d.audience;
    const errorMsg = campaign.draft_error || d.error_message || '';
    return (
      <span className="inline-flex items-center gap-2 text-xs text-red-500">
        <WarningCircle className="w-3 h-3 shrink-0" />
        <span className="min-w-0">
          {hasData ? (
            <span className="text-red-500 cursor-pointer hover:underline">{t('campaigns.draftStatus.publishError')}</span>
          ) : (
            t('campaigns.draftStatus.analysisError')
          )}
          {errorMsg && (
            <span className="block text-[10px] text-red-400 truncate max-w-[250px]" title={errorMsg}>{errorMsg}</span>
          )}
        </span>
        {onRetry && (
          <button
            onClick={(e) => { e.stopPropagation(); onRetry(campaign); }}
            className="p-0.5 rounded hover:bg-indigo-50 text-indigo-600 hover:text-indigo-700 transition shrink-0"
            title={hasData ? t('campaigns.draftStatus.retryPublish') : t('campaigns.draftStatus.retryAnalysis')}
          >
            <ArrowCounterClockwise className="w-3.5 h-3.5" />
          </button>
        )}
        {onDelete && (
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(campaign); }}
            className="p-0.5 rounded hover:bg-red-50 text-red-400 hover:text-red-600 transition shrink-0"
            title={t('campaigns.draftStatus.deleteDraft')}
          >
            <Trash className="w-3.5 h-3.5" />
          </button>
        )}
      </span>
    );
  }

  return null;
}

// Health Status Indicator

function HealthStatusDot({ campaignId, health: healthProp }) {
  const { t } = useTranslation();
  const [healthState, setHealth] = useState(null);

  // When the parent passes a batched health summary, use it directly and skip
  // the per-campaign request. Only fall back to fetching when no prop is given.
  const usePropHealth = healthProp !== undefined;

  useEffect(() => {
    if (usePropHealth || !campaignId) return;
    api.get(`/ads/campaigns/${campaignId}/health/`)
      .then(res => {
        if (res.data && res.data.overall_status) {
          setHealth(res.data);
        }
      })
      .catch(() => {});
  }, [campaignId, usePropHealth]);

  const health = usePropHealth ? healthProp : healthState;

  if (!health || !health.overall_status || health.status === 'no_data') return null;

  const statusConfig = {
    healthy: { color: 'bg-green-500', label: t('campaigns.health.statusHealthy') },
    warning: { color: 'bg-yellow-500', label: t('campaigns.health.statusWarning') },
    critical: { color: 'bg-red-500', label: t('campaigns.health.statusCritical') },
  };

  const config = statusConfig[health.overall_status] || statusConfig.healthy;

  return (
    <Tooltip label={t('campaigns.health.tooltip', { label: config.label, score: health.score })} position="top" withArrow>
      <span className="inline-flex items-center">
        <span className={`w-2.5 h-2.5 rounded-full ${config.color}`} />
      </span>
    </Tooltip>
  );
}

function HealthAlertsPanel({ campaignId, onSuggestedKeywords }) {
  const { t } = useTranslation();
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(false);
  const [runLoading, setRunLoading] = useState(false);

  const fetchHealth = useCallback(() => {
    if (!campaignId) return;
    setLoading(true);
    api.get(`/ads/campaigns/${campaignId}/health/`)
      .then(res => {
        if (res.data && res.data.issues) {
          setHealth(res.data);
        } else if (res.data && res.data.status === 'no_data') {
          // Auto-run health check if no data exists
          setRunLoading(true);
          api.post(`/ads/campaigns/${campaignId}/health/run/`)
            .then(runRes => { if (runRes.data) setHealth(runRes.data); })
            .catch(() => {})
            .finally(() => setRunLoading(false));
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [campaignId]);

  useEffect(() => { fetchHealth(); }, [fetchHealth]);

  const handleRunCheck = () => {
    setRunLoading(true);
    api.post(`/ads/campaigns/${campaignId}/health/run/`)
      .then(res => {
        if (res.data) setHealth(res.data);
      })
      .catch(() => {})
      .finally(() => setRunLoading(false));
  };

  const [expandingKeywords, setExpandingKeywords] = useState(false);
  const [suggestedKeywords, setSuggestedKeywords] = useState(null);

  const handleExpandKeywords = async () => {
    setExpandingKeywords(true);
    try {
      const dataRes = await api.get(`/ads/campaigns/${campaignId}/data/`);
      const campaign = dataRes.data.campaign || {};
      const currentKws = (dataRes.data.keywords || []).map(k => k.text);

      const res = await api.post('/ai/suggest-keywords/', {
        description: `${campaign.name} ${campaign.source_url || ''}`,
        current_keywords: currentKws,
      });

      const kws = res.data.keywords || [];
      setSuggestedKeywords(kws);
      // Pass suggestions up to the drawer so KeywordsTab can show them
      if (onSuggestedKeywords) onSuggestedKeywords(kws);
    } catch {
      setSuggestedKeywords([]);
    } finally {
      setExpandingKeywords(false);
    }
  };

  const severityConfig = {
    critical: { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700', icon: 'text-red-500', label: t('campaigns.health.statusCritical') },
    warning: { bg: 'bg-yellow-50', border: 'border-yellow-200', text: 'text-yellow-700', icon: 'text-yellow-500', label: t('campaigns.health.statusWarning') },
    info: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700', icon: 'text-blue-500', label: t('campaigns.health.severityInfo') },
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-xs text-gray-500">{t('campaigns.health.title')}</p>
        <button
          onClick={handleRunCheck}
          disabled={runLoading}
          className="text-xs text-indigo-600 hover:text-indigo-700 disabled:opacity-50 transition"
        >
          {runLoading ? t('campaigns.health.checking') : t('campaigns.health.checkNow')}
        </button>
      </div>

      {loading && !health && (
        <div className="flex items-center gap-2 text-xs text-gray-400">
          <CircleNotch className="w-3 h-3 animate-spin" />
          {t('campaigns.common.loading')}
        </div>
      )}

      {health && health.issues && health.issues.length === 0 && (
        <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-md">
          <ShieldCheck className="w-4 h-4 text-green-500" />
          <span className="text-sm text-green-700">{t('campaigns.health.healthy')}</span>
        </div>
      )}

      {health && health.issues && health.issues.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center gap-2 mb-1">
            {health.overall_status === 'critical' ? (
              <ShieldWarning className="w-4 h-4 text-red-500" />
            ) : health.overall_status === 'warning' ? (
              <Shield className="w-4 h-4 text-yellow-500" />
            ) : (
              <ShieldCheck className="w-4 h-4 text-green-500" />
            )}
            <span className="text-xs font-medium text-gray-700">
              {t('campaigns.health.score', { score: health.score })}
            </span>
          </div>
          {health.issues.map((issue, idx) => {
            const cfg = severityConfig[issue.severity] || severityConfig.info;
            return (
              <div key={idx} className={`p-3 rounded-md border ${cfg.bg} ${cfg.border}`}>
                <div className="flex items-start gap-2">
                  <WarningCircle className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${cfg.icon}`} />
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium ${cfg.text}`}>{issue.title}</p>
                    <p className="text-xs text-gray-600 mt-1">{issue.description}</p>
                    <p className="text-xs text-gray-500 mt-1.5 italic">{issue.recommendation}</p>
                    {issue.auto_fixable && issue.auto_fix_action === 'expand_keywords' ? (
                      <button
                        onClick={handleExpandKeywords}
                        disabled={expandingKeywords}
                        className="inline-flex items-center gap-1 mt-2 text-xs px-2.5 py-1 bg-[#1D4ED8] text-white rounded-md hover:bg-[#1e40af] transition disabled:opacity-50"
                      >
                        {expandingKeywords ? (
                          <><CircleNotch className="w-3 h-3 animate-spin" /> {t('campaigns.adForm.generating')}</>
                        ) : (
                          t('campaigns.health.suggestKeywords')
                        )}
                      </button>
                    ) : issue.auto_fixable ? (
                      <span className="inline-block mt-1.5 text-[10px] px-1.5 py-0.5 bg-indigo-100 text-indigo-700 rounded">
                        {t('campaigns.health.autoFixable')}
                      </span>
                    ) : null}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {(!health || health.status === 'no_data') && !loading && (
        <div className="text-xs text-gray-400">
          {t('campaigns.health.noData')}
        </div>
      )}

      {/* Suggested keywords from AI */}
      {suggestedKeywords && suggestedKeywords.length > 0 && (
        <div className="border border-indigo-200 rounded-lg p-3 mt-3">
          <div className="flex items-center gap-2">
            <Sparkle className="w-4 h-4 text-indigo-500" />
            <p className="text-xs font-medium text-gray-900">{t('campaigns.health.generatedKeywords', { count: suggestedKeywords.length })}</p>
          </div>
          <p className="text-[11px] text-gray-500 mt-1.5">
            {t('campaigns.health.generatedHint')}
          </p>
        </div>
      )}
    </div>
  );
}

// Konfigurowalne kolumny tabeli kampanii. Nazwa (pierwsza) i Droid (ostatnia)
// są stałe; poniższe można włączać/wyłączać i zmieniać kolejność (zapis w
// localStorage). `width` to udział w wyblakłym wierszu nakładki (szkice/niezarządzane).
export const CAMPAIGN_COLUMN_DEFS = {
  status: {
    labelKey: 'campaigns.columns.status', align: 'center', sortKey: null, width: '11%',
    cell: (c, t) => (
      <Box ta="center">
        <Box mx="auto" mb={3} style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: c.status === 'active' ? 'var(--mantine-color-green-5)' : 'var(--mantine-color-yellow-5)' }} />
        <Text size="xs" c="dimmed">{c.status === 'active' ? t('campaigns.columns.statusActive') : t('campaigns.columns.statusPaused')}</Text>
      </Box>
    ),
    group: (a) => <Text size="xs" c="dimmed">{a.activeCount}/{a.total}</Text>,
    faded: (c, t) => c.status === 'active' ? t('campaigns.columns.statusActive') : t('campaigns.columns.statusPaused'),
  },
  budget: {
    labelKey: 'campaigns.columns.budget', align: 'right', sortKey: 'budget', width: '13%',
    cell: (c) => <Text size="sm">{`${formatMoney(c.budget_amount, c.currency, { decimals: 0 })}/dz.`}</Text>,
    group: (a) => <Text size="sm" fw={500} c="dimmed">{`${formatMoney(a.totalBudget, a.currency, { decimals: 0 })}/dz.`}</Text>,
    faded: (c) => `${formatMoney(c.budget_amount, c.currency, { decimals: 0 })}/dz.`,
  },
  cost: {
    labelKey: 'campaigns.columns.cost', align: 'right', sortKey: 'cost', width: '14%',
    cell: (c) => <Text size="sm" fw={500}>{formatMoney(c.cost, c.currency)}</Text>,
    group: (a) => <Text size="sm" fw={600} c="dimmed">{formatMoney(a.totalCost, a.currency)}</Text>,
    faded: (c) => formatMoney(c.cost, c.currency),
  },
  impressions: {
    labelKey: 'campaigns.columns.impressions', align: 'right', sortKey: 'impressions', width: '13%',
    cell: (c) => <Text size="sm">{(c.impressions || 0).toLocaleString('pl-PL')}</Text>,
    group: (a) => <Text size="sm" c="dimmed">{a.totalImpressions.toLocaleString('pl-PL')}</Text>,
    faded: (c) => (c.impressions || 0).toLocaleString('pl-PL'),
  },
  clicks: {
    labelKey: 'campaigns.columns.clicks', align: 'right', sortKey: 'clicks', width: '13%',
    cell: (c) => <Text size="sm">{(c.clicks || 0).toLocaleString('pl-PL')}</Text>,
    group: (a) => <Text size="sm" c="dimmed">{a.totalClicks.toLocaleString('pl-PL')}</Text>,
    faded: (c) => (c.clicks || 0).toLocaleString('pl-PL'),
  },
  ctr: {
    labelKey: 'campaigns.columns.ctr', align: 'right', sortKey: 'ctr', width: '10%',
    cell: (c) => {
      const ctr = c.clicks > 0 && c.impressions > 0 ? (c.clicks / c.impressions * 100) : parseFloat(c.ctr || 0);
      return <Text size="sm" fw={500} c={ctr >= 5 ? 'green.7' : ctr < 2 ? 'red.6' : undefined}>{`${ctr.toFixed(1).replace('.', ',')}%`}</Text>;
    },
    group: (a) => <Text size="sm" c="dimmed">{a.avgCtr.toFixed(1).replace('.', ',')}%</Text>,
    faded: (c) => `${parseFloat(c.ctr || 0).toFixed(1)}%`,
  },
  cpc: {
    labelKey: 'campaigns.columns.cpc', align: 'right', sortKey: 'cpc', width: '11%',
    cell: (c) => {
      const cpc = c.clicks > 0 ? parseFloat(c.cost || 0) / c.clicks : parseFloat(c.cpc || 0);
      return <Text size="sm">{cpc > 0 ? formatMoney(cpc, c.currency) : '-'}</Text>;
    },
    group: (a) => <Text size="sm" c="dimmed">{a.totalClicks > 0 ? formatMoney(a.totalCost / a.totalClicks, a.currency) : '-'}</Text>,
    faded: (c) => { const v = c.clicks > 0 ? parseFloat(c.cost || 0) / c.clicks : parseFloat(c.cpc || 0); return v > 0 ? formatMoney(v, c.currency) : '-'; },
  },
  conversions: {
    labelKey: 'campaigns.columns.conversions', align: 'right', sortKey: 'conversions', width: '13%',
    // Dla kampanii Meta lead gen pokazujemy realną liczbę leadów (lead_count =
    // rekordy MetaLead), tak samo jak karta „Leady" w szczegółach. Metryka
    // `conversions` z insightów bywa rozjechana (inny sync, double-count w
    // dziennych metrykach), przez co lista i szczegóły pokazywały różne liczby.
    cell: (c) => {
      const isLeads = c.platform === 'meta_ads' && c.campaign_goal === 'leads' && c.lead_count != null;
      return <Text size="sm" fw={600}>{isLeads ? c.lead_count : Math.round(parseFloat(c.conversions) || 0)}</Text>;
    },
    group: (a) => <Text size="sm" fw={600} c="dimmed">{Math.round(a.totalConversions)}</Text>,
    faded: (c) => {
      const isLeads = c.platform === 'meta_ads' && c.campaign_goal === 'leads' && c.lead_count != null;
      return isLeads ? c.lead_count : Math.round(parseFloat(c.conversions) || 0);
    },
  },
  cost_per_conversion: {
    // Koszt/konwersję = koszt / konwersje (dla Meta lead gen: koszt / liczba leadów,
    // spójnie z kolumną „Konwersje"). Liczony w locie, więc podąża za okresem.
    labelKey: 'campaigns.columns.costPerConversion', align: 'right', sortKey: null, width: '14%',
    cell: (c) => {
      const isLeads = c.platform === 'meta_ads' && c.campaign_goal === 'leads' && c.lead_count != null;
      const conv = isLeads ? (c.lead_count || 0) : (parseFloat(c.conversions) || 0);
      const v = conv > 0 ? parseFloat(c.cost || 0) / conv : 0;
      return <Text size="sm">{v > 0 ? formatMoney(v, c.currency) : '-'}</Text>;
    },
    group: (a) => <Text size="sm" c="dimmed">{a.totalConversions > 0 ? formatMoney(a.totalCost / a.totalConversions, a.currency) : '-'}</Text>,
    faded: (c) => { const conv = parseFloat(c.conversions) || 0; const v = conv > 0 ? parseFloat(c.cost || 0) / conv : 0; return v > 0 ? formatMoney(v, c.currency) : '-'; },
  },
  cost_per_lead: {
    // Koszt zapytania ofertowego (akcje konwersji Google z ostatnich 30 dni).
    // Tylko kampanie Google; Meta ma własną kolumnę leadów w „Konwersje".
    labelKey: 'campaigns.columns.costPerLead', align: 'right', sortKey: null, width: '14%',
    cell: (c, t) => {
      if (c.platform === 'meta_ads') return <Text size="sm" c="dimmed">{'\u2013'}</Text>;
      if (c.cost_per_lead == null) {
        return (
          <Tooltip label={t('campaigns.columns.noLeads30d')} withArrow>
            <Text size="sm" c="dimmed" span>{'\u2013'}</Text>
          </Tooltip>
        );
      }
      // Gdy lista pokazuje inny okres niż 30 dni, koszt zapytania zostaje 30-dniowy:
      // przygaszamy go i wyjaśniamy w podpowiedzi, żeby nie mieszać okresów.
      if (c.cpl_period_mismatch) {
        return (
          <Tooltip label={t('campaigns.columns.costPerLeadAlways30d')} withArrow>
            <Text size="sm" c="dimmed" span>{formatMoney(c.cost_per_lead, c.currency)}</Text>
          </Tooltip>
        );
      }
      return <Text size="sm" fw={500}>{formatMoney(c.cost_per_lead, c.currency)}</Text>;
    },
    group: () => <Text size="sm" c="dimmed">{'\u2013'}</Text>,
    faded: (c) => (c.cost_per_lead != null ? formatMoney(c.cost_per_lead, c.currency) : '\u2013'),
  },
  conversions_value: {
    // Wartość konwersji (przychód) za wybrany okres. Google raportuje ją na poziomie
    // kampanii dla Search/Shopping/PMax; kampanie bez śledzenia wartości pokazują 0.
    labelKey: 'campaigns.columns.conversionsValue', align: 'right', sortKey: 'conversions_value', width: '14%',
    cell: (c) => {
      const v = parseFloat(c.conversions_value || 0);
      return <Text size="sm" fw={500}>{v > 0 ? formatMoney(v, c.currency) : '-'}</Text>;
    },
    group: (a) => <Text size="sm" fw={600} c="dimmed">{formatMoney(a.totalConversionsValue || 0, a.currency)}</Text>,
    faded: (c) => { const v = parseFloat(c.conversions_value || 0); return v > 0 ? formatMoney(v, c.currency) : '-'; },
  },
  roas: {
    // Wartość konw./koszt (ROAS) = wartość konwersji / koszt. Liczona w locie, więc
    // podąża za okresem tak samo jak jej składniki (koszt i wartość konwersji).
    labelKey: 'campaigns.columns.roas', align: 'right', sortKey: 'roas', width: '13%',
    cell: (c) => {
      const cost = parseFloat(c.cost || 0);
      const r = cost > 0 ? parseFloat(c.conversions_value || 0) / cost : 0;
      return <Text size="sm" fw={500} c={r >= 4 ? 'green.7' : undefined}>{r > 0 ? `${r.toFixed(2).replace('.', ',')}x` : '-'}</Text>;
    },
    group: (a) => {
      const r = a.totalCost > 0 ? (a.totalConversionsValue || 0) / a.totalCost : 0;
      return <Text size="sm" fw={600} c="dimmed">{r > 0 ? `${r.toFixed(2).replace('.', ',')}x` : '-'}</Text>;
    },
    faded: (c) => { const cost = parseFloat(c.cost || 0); const r = cost > 0 ? parseFloat(c.conversions_value || 0) / cost : 0; return r > 0 ? `${r.toFixed(2)}x` : '-'; },
  },
};
// Wszystkie dostępne kolumny w naturalnej kolejności (do panelu wyboru).
export const CAMPAIGN_COLUMNS_ALL = ['status', 'budget', 'cost', 'impressions', 'clicks', 'ctr', 'cpc', 'conversions', 'cost_per_conversion', 'cost_per_lead', 'conversions_value', 'roas'];
// Domyślnie widoczne kolumny (jak dotychczas, bez Wyświetleń i CPC).
export const CAMPAIGN_COLUMNS_DEFAULT = ['status', 'budget', 'cost', 'clicks', 'ctr', 'conversions', 'cost_per_lead'];
const CAMPAIGN_COLUMNS_LS_KEY = 'campaigns_columns_v1';

const CAMPAIGN_COLUMNS_CPL_MARKER = 'campaigns_columns_cpl_added';

// Jednorazowa migracja zapisanej listy: dodaje „Koszt zapytania" po „Konwersje".
// Marker w localStorage sprawia, że po ręcznym usunięciu kolumny nie wraca.
export function migrateCplColumn(storage, valid) {
  try {
    if (storage.getItem(CAMPAIGN_COLUMNS_CPL_MARKER)) return valid;
    let next = valid;
    if (!valid.includes('cost_per_lead')) {
      const idx = valid.indexOf('conversions');
      next = idx >= 0
        ? [...valid.slice(0, idx + 1), 'cost_per_lead', ...valid.slice(idx + 1)]
        : [...valid, 'cost_per_lead'];
      storage.setItem(CAMPAIGN_COLUMNS_LS_KEY, JSON.stringify(next));
    }
    storage.setItem(CAMPAIGN_COLUMNS_CPL_MARKER, '1');
    return next;
  } catch (e) { return valid; }
}

function loadCampaignColumns() {
  try {
    const raw = JSON.parse(localStorage.getItem(CAMPAIGN_COLUMNS_LS_KEY) || 'null');
    if (Array.isArray(raw)) {
      const valid = raw.filter(k => CAMPAIGN_COLUMN_DEFS[k]);
      if (valid.length) return migrateCplColumn(localStorage, valid);
    }
  } catch (e) { /* ignore */ }
  return CAMPAIGN_COLUMNS_DEFAULT;
}

// Panel wyboru i kolejności kolumn tabeli kampanii.
function ColumnPicker({ visibleColumns, updateColumns }) {
  const { t } = useTranslation();
  const [opened, setOpened] = useState(false);
  const visibleSet = new Set(visibleColumns);
  // Kolejność na liście: najpierw widoczne (w wybranej kolejności), potem reszta.
  const ordered = [...visibleColumns, ...CAMPAIGN_COLUMNS_ALL.filter(k => !visibleSet.has(k))];

  const toggle = (key) => {
    if (visibleSet.has(key)) {
      updateColumns(visibleColumns.filter(k => k !== key));
    } else {
      updateColumns([...visibleColumns, key]);
    }
  };
  const move = (key, dir) => {
    const idx = visibleColumns.indexOf(key);
    if (idx < 0) return;
    const to = idx + dir;
    if (to < 0 || to >= visibleColumns.length) return;
    const next = [...visibleColumns];
    [next[idx], next[to]] = [next[to], next[idx]];
    updateColumns(next);
  };
  const isDefault = visibleColumns.length === CAMPAIGN_COLUMNS_DEFAULT.length
    && visibleColumns.every((k, i) => k === CAMPAIGN_COLUMNS_DEFAULT[i]);

  return (
    <Popover opened={opened} onChange={setOpened} position="bottom-end" shadow="md" withArrow width={280}>
      <Popover.Target>
        <Button variant="default" size="sm" leftSection={<Columns size={14} />} onClick={() => setOpened(o => !o)}>
          {t('campaigns.columns.button')}
        </Button>
      </Popover.Target>
      <Popover.Dropdown p="xs">
        <Group justify="space-between" mb={6} px={4}>
          <Text size="xs" fw={600} c="dimmed">{t('campaigns.columns.columnsAndOrder')}</Text>
          {!isDefault && (
            <Anchor component="button" type="button" size="xs" onClick={() => updateColumns(CAMPAIGN_COLUMNS_DEFAULT)}>
              {t('campaigns.columns.default')}
            </Anchor>
          )}
        </Group>
        <Stack gap={2}>
          {ordered.map((key) => {
            const def = CAMPAIGN_COLUMN_DEFS[key];
            const shown = visibleSet.has(key);
            const vIdx = visibleColumns.indexOf(key);
            return (
              <Group key={key} gap={4} wrap="nowrap" justify="space-between"
                     style={{ padding: '2px 4px', borderRadius: 4 }}>
                <Checkbox
                  size="xs"
                  checked={shown}
                  onChange={() => toggle(key)}
                  label={t(def.labelKey)}
                  styles={{ label: { paddingLeft: 6, fontSize: 13 }, input: { cursor: 'pointer' } }}
                />
                {shown && (
                  <Group gap={0} wrap="nowrap">
                    <ActionIcon variant="subtle" color="gray" size="sm" disabled={vIdx <= 0}
                                onClick={() => move(key, -1)} aria-label={t('campaigns.columns.moveUp')}>
                      <CaretUp size={13} weight="bold" />
                    </ActionIcon>
                    <ActionIcon variant="subtle" color="gray" size="sm" disabled={vIdx >= visibleColumns.length - 1}
                                onClick={() => move(key, 1)} aria-label={t('campaigns.columns.moveDown')}>
                      <CaretDown size={13} weight="bold" />
                    </ActionIcon>
                  </Group>
                )}
              </Group>
            );
          })}
        </Stack>
        <Divider my={6} />
        <Text size="10px" c="dimmed" px={4}>{t('campaigns.columns.alwaysVisible')}</Text>
      </Popover.Dropdown>
    </Popover>
  );
}

// Metryki do pokazania/ukrycia na wykresach porównania (jak karty w szczegółach).
const COMPARE_CHART_METRICS = ['impressions', 'clicks', 'cost', 'conversions', 'ctr', 'cpc'];

// Modal porównania kampanii: do 5 kampanii obok siebie, wspólny okres i wspólny
// zestaw metryk na wykresach, podstawowe liczby per kampania, eksport do Excel/PDF.
function ComparisonModal({ campaigns, onClose, onRemove, onClear }) {
  const { t } = useTranslation();
  const [period, setPeriod] = useState('30');
  const [activeMetrics, setActiveMetrics] = useState(['cost']);
  const [periodMetrics, setPeriodMetrics] = useState({});

  useEffect(() => {
    api.get(`/ads/campaigns/period-metrics/?period=${period}`)
      .then(res => setPeriodMetrics(res.data?.metrics || {}))
      .catch(() => setPeriodMetrics({}));
  }, [period]);

  const toggleMetric = (m) => setActiveMetrics(prev =>
    prev.includes(m) ? prev.filter(x => x !== m) : (prev.length >= 4 ? prev : [...prev, m]));

  const ids = campaigns.map(c => c.id).join(',');

  const cardFor = (c) => {
    const m = periodMetrics[c.id] || {};
    const impressions = m.impressions || 0;
    const clicks = m.clicks || 0;
    const cost = m.cost || 0;
    const conversions = m.conversions || 0;
    const ctr = impressions > 0 ? (clicks / impressions * 100) : 0;
    const cpc = clicks > 0 ? (cost / clicks) : 0;
    return [
      [t('campaigns.metrics.impressions'), impressions.toLocaleString('pl-PL')],
      [t('campaigns.metrics.clicks'), clicks.toLocaleString('pl-PL')],
      [t('campaigns.metrics.ctr'), `${ctr.toFixed(1).replace('.', ',')}%`],
      [t('campaigns.metrics.cost'), formatMoney(cost, c.currency)],
      [t('campaigns.metrics.avgCpc'), cpc > 0 ? formatMoney(cpc, c.currency) : '-'],
      [t('campaigns.metrics.conversions'), Math.round(conversions)],
    ];
  };

  return (
    <Modal opened onClose={onClose} size={1200} radius="md" padding="lg"
      title={<Text fw={700} size="lg">{t('campaigns.compare.title', { count: campaigns.length })}</Text>}
      styles={{ content: { maxWidth: '96vw' } }}>
      <Group justify="space-between" mb="sm" wrap="wrap" gap="sm">
        <Group gap="xs" wrap="nowrap">
          <Text size="xs" c="dimmed">{t('campaigns.compare.period')}</Text>
          <SegmentedControl value={period} onChange={setPeriod} size="xs" data={[
            { value: '7', label: '7d' }, { value: '14', label: '14d' },
            { value: '30', label: '30d' }, { value: 'month', label: t('campaigns.chart.thisMonth') }]} />
        </Group>
        <ExportMenu baseUrl="/ads/campaigns/compare/export/" filename="porownanie-kampanii"
          filters={`ids=${ids}&period=${period}`} />
      </Group>
      <Group gap={6} mb="md" wrap="wrap">
        <Text size="xs" c="dimmed">{t('campaigns.compare.chartMetrics')}</Text>
        {COMPARE_CHART_METRICS.map(m => (
          <Chip key={m} size="xs" checked={activeMetrics.includes(m)} onChange={() => toggleMetric(m)}>
            {t(MINI_CHART_METRICS[m].labelKey)}
          </Chip>
        ))}
      </Group>
      <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
        {campaigns.map(c => (
          <Paper key={c.id} withBorder p="sm" radius="md">
            <Group justify="space-between" mb="xs" wrap="nowrap">
              <Box style={{ flex: 1, minWidth: 0 }}>
                <Text size="sm" fw={600} truncate>{c.name}</Text>
                <Text size="10px" c="dimmed">{c.status === 'active' ? t('campaigns.columns.statusActive') : t('campaigns.columns.statusPaused')}</Text>
              </Box>
              <Tooltip label={t('campaigns.drawer.removeFromCompare')} withArrow>
                <ActionIcon variant="subtle" color="gray" size="sm" onClick={() => onRemove(c.id)}><X size={14} /></ActionIcon>
              </Tooltip>
            </Group>
            <SimpleGrid cols={3} spacing={8} mb="sm">
              {cardFor(c).map(([l, v]) => (
                <Box key={l}>
                  <Text size="10px" c="dimmed">{l}</Text>
                  <Text size="sm" fw={600}>{v}</Text>
                </Box>
              ))}
            </SimpleGrid>
            <CampaignMiniChart campaignId={c.id} period={period} setPeriod={setPeriod}
              activeMetrics={activeMetrics} hideControls syncId="campaign-comparison" currency={c.currency} />
          </Paper>
        ))}
      </SimpleGrid>
      <Group justify="flex-end" mt="md">
        <Button variant="subtle" color="gray" size="xs" onClick={onClear}>{t('campaigns.compare.clear')}</Button>
      </Group>
    </Modal>
  );
}

// Main Component

export default function Campaigns() {
  const { t } = useTranslation();
  const { user, refreshUser } = useAuth();
  const { addNotification } = useNotifications();
  const [campaigns, setCampaigns] = useState([]);
  // Symbol dominującej (wg kosztu) waluty kont na liście, np. dla etykiet filtra budżetu.
  const listCurSym = (() => {
    const spend = {};
    campaigns.forEach((c) => { const cur = c.currency || 'PLN'; spend[cur] = (spend[cur] || 0) + parseFloat(c.cost || 0); });
    const keys = Object.keys(spend);
    return currencySymbol(keys.length ? keys.reduce((a, b) => (spend[a] >= spend[b] ? a : b)) : 'PLN');
  })();
  const [healthMap, setHealthMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  // Restore saved filters from localStorage on mount (sync URL)
  const filtersRestoredRef = useRef(false);
  useEffect(() => {
    if (filtersRestoredRef.current) return;
    filtersRestoredRef.current = true;
    const filterKeys = ['status', 'managed', 'clicks', 'budget', 'client', 'platform'];
    if (filterKeys.some(k => searchParams.has(k))) return;
    try {
      const saved = JSON.parse(localStorage.getItem('droid_campaign_filters') || '{}');
      if (Object.keys(saved).length > 0) {
        const params = new URLSearchParams(searchParams);
        for (const [k, v] of Object.entries(saved)) {
          if (v) params.set(k, v);
        }
        setSearchParams(params, { replace: true });
      }
    } catch {}
  }, []); // eslint-disable-line
  const [sortField, setSortField] = useState(searchParams.get('sort') || null);
  const [sortDir, setSortDir] = useState(searchParams.get('dir') || 'asc');

  // Okres dla liczb na liście kampanii: 'all' = cały czas (domyślnie, jak dotąd),
  // inaczej liczba dni lub 'month'. Liczby per kampania bierzemy z period-metrics.
  const [listPeriod, setListPeriod] = useState('all');
  const [listPeriodMetrics, setListPeriodMetrics] = useState({});
  useEffect(() => {
    if (listPeriod === 'all') { setListPeriodMetrics({}); return; }
    let cancelled = false;
    api.get(`/ads/campaigns/period-metrics/?period=${listPeriod}`)
      .then(res => { if (!cancelled) setListPeriodMetrics(res.data.metrics || {}); })
      .catch(() => { if (!cancelled) setListPeriodMetrics({}); });
    return () => { cancelled = true; };
  }, [listPeriod]);

  // Porównanie kampanii: do 5 kampanii (id), zapamiętane w localStorage.
  const [comparisonIds, setComparisonIds] = useState(() => {
    try {
      const r = JSON.parse(localStorage.getItem('campaign_comparison') || '[]');
      return Array.isArray(r) ? r.slice(0, 5) : [];
    } catch (e) { return []; }
  });
  const [showComparison, setShowComparison] = useState(false);
  useEffect(() => {
    localStorage.setItem('campaign_comparison', JSON.stringify(comparisonIds));
  }, [comparisonIds]);
  const toggleComparison = (id) => setComparisonIds(prev =>
    prev.includes(id) ? prev.filter(x => x !== id) : (prev.length >= 5 ? prev : [...prev, id]));
  const removeFromComparison = (id) => setComparisonIds(prev => prev.filter(x => x !== id));
  // Porównanie tylko z kampanii bieżącego użytkownika/konta. Po zmianie konta lub
  // impersonacji id z localStorage należące do innego konta przestają tu pasować.
  const comparisonCampaigns = comparisonIds
    .map(id => campaigns.find(c => c.id === id))
    .filter(Boolean);
  // Samoczyszczenie schowka: po załadowaniu listy usuwamy nieaktualne id (żeby nie
  // wisiał przycisk porównania z pustym modalem). Pomijamy widok archiwum, gdzie
  // lista to tylko zarchiwizowane - nie chcemy wtedy gubić porównania aktywnych.
  useEffect(() => {
    if (loading || campaigns.length === 0) return;
    if (searchParams.get('status') === 'archived') return;
    setComparisonIds(prev => {
      const valid = prev.filter(id => campaigns.some(c => c.id === id));
      return valid.length === prev.length ? prev : valid;
    });
  }, [loading, campaigns, searchParams]);

  // Widoczne kolumny tabeli kampanii (konfigurowalne, zapamiętane w localStorage).
  const [visibleColumns, setVisibleColumns] = useState(loadCampaignColumns);
  const updateColumns = (next) => {
    const valid = next.filter(k => CAMPAIGN_COLUMN_DEFS[k]);
    const final = valid.length ? valid : CAMPAIGN_COLUMNS_DEFAULT;
    setVisibleColumns(final);
    try { localStorage.setItem(CAMPAIGN_COLUMNS_LS_KEY, JSON.stringify(final)); } catch (e) { /* ignore */ }
  };

  const filterStatus = searchParams.get('status') || null;
  const filterManaged = searchParams.get('managed') || null;
  const filterClicks = searchParams.get('clicks') || null;
  const filterBudget = searchParams.get('budget') || null;
  const filterClient = searchParams.get('client') || null;
  const filterPlatform = searchParams.get('platform') || null;
  const isAgency = user?.organization?.is_agency;

  const setFilter = (key, value) => {
    const params = new URLSearchParams(searchParams);
    if (value) {
      params.set(key, value);
    } else {
      params.delete(key);
    }
    // Keep campaign/draft/tab params
    setSearchParams(params, { replace: true });
    // Persist filters to localStorage (exclude 'archived' status - should never be default)
    const filterKeys = ['status', 'managed', 'clicks', 'budget', 'client', 'platform'];
    const toSave = {};
    filterKeys.forEach(k => {
      const v = k === key ? value : params.get(k);
      if (v && !(k === 'status' && v === 'archived')) toSave[k] = v;
    });
    localStorage.setItem('droid_campaign_filters', JSON.stringify(toSave));
  };
  const [selectedCampaign, setSelectedCampaignRaw] = useState(null);
  const [selectedDraft, setSelectedDraftRaw] = useState(null);

  const setSelectedCampaign = (c) => {
    setSelectedCampaignRaw(c);
    if (c) {
      setSearchParams({ campaign: c.id }, { replace: true });
    } else {
      setSearchParams({}, { replace: true });
    }
  };
  const setSelectedDraft = (c) => {
    setSelectedDraftRaw(c);
    if (c) {
      setSearchParams({ draft: c.id }, { replace: true });
    } else {
      setSearchParams({}, { replace: true });
    }
  };

  // Open campaign/draft from URL on load
  useEffect(() => {
    if (campaigns.length === 0) return;
    const campaignId = searchParams.get('campaign');
    const draftId = searchParams.get('draft');
    if (campaignId) {
      const c = campaigns.find(c => String(c.id) === campaignId);
      if (c) setSelectedCampaignRaw(c);
    } else if (draftId && !selectedDraft) {
      const c = campaigns.find(c => String(c.id) === draftId && c.status === 'draft');
      if (c) {
        // Fetch full draft data including ai_draft_data
        api.get(`/ads/drafts/${draftId}/status/`)
          .then(res => setSelectedDraftRaw(res.data))
          .catch(() => setSelectedDraftRaw(c));
      }
    }
  }, [campaigns, searchParams]); // eslint-disable-line
  const [trialUsed, setTrialUsed] = useState(false);
  // Trial dostępny, jeśli nieużyty LUB istnieje trial do przeniesienia z innej
  // (wstrzymanej) kampanii - backend to wylicza (trial_available).
  const [trialAvailable, setTrialAvailable] = useState(true);
  const [trialTransferDays, setTrialTransferDays] = useState(null);
  const [isFreeAccount, setIsFreeAccount] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [googleAccountName, setGoogleAccountName] = useState('');
  const [groups, setGroups] = useState([]);
  const [collapsedGroups, setCollapsedGroups] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem('droid_collapsed_groups') || '[]')); } catch { return new Set(); }
  });
  const [showCreateGroupModal, setShowCreateGroupModal] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [creatingGroup, setCreatingGroup] = useState(false);
  const [renamingGroup, setRenamingGroup] = useState(null);
  const [renameGroupName, setRenameGroupName] = useState('');
  const [savingGroupName, setSavingGroupName] = useState(false);
  // Usuniecie grupy rozwiazuje ja, ale NIE rusza kampanii (FK ma SET_NULL).
  // Bez potwierdzenia jedno klikniecie w ikone kosza rozbijalo cala strukture.
  const [deletingGroup, setDeletingGroup] = useState(null);
  const [movingCampaign, setMovingCampaign] = useState(null); // { campaignId, currentGroupId }
  const [rearrangeMode, setRearrangeMode] = useState(false);
  const reorderRef = useRef(null);
  const [actionLoading, setActionLoading] = useState(null);

  const [showConnectModal, setShowConnectModal] = useState(false);
  const [showTrialModal, setShowTrialModal] = useState(null);
  const [showSubscribeModal, setShowSubscribeModal] = useState(null);
  const [planLimitInfo, setPlanLimitInfo] = useState(null);
  const [planStatus, setPlanStatus] = useState(null);
  const [showDeactivateModal, setShowDeactivateModal] = useState(null);
  const [unsupportedTypeModal, setUnsupportedTypeModal] = useState(null);
  const [draftToDelete, setDraftToDelete] = useState(null);
  const [showCreateWizard, setShowCreateWizard] = useState(false);
  const [showConnectedModal, setShowConnectedModal] = useState(false);
  const [connectedCustomerId, setConnectedCustomerId] = useState('');
  const [showSelectAccountModal, setShowSelectAccountModal] = useState(false);
  const [showSetupHelp, setShowSetupHelp] = useState(false);

  const pollRef = useRef(null);

  const isGoogleConnected = !!user?.google_ads_connected || !!user?.google_ads_customer_id;
  const hasAnyPlatform = isGoogleConnected || (user?.organization?.platform_accounts || []).some(pa => pa.is_active);
  const isConnected = hasAnyPlatform;

  // Handle OAuth return
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('google_ads') === 'connected') {
      const shouldSelectAccount = params.get('select_account') === 'true';
      refreshUser().then((updatedUser) => {
        window.history.replaceState({}, '', '/app/campaigns');
        if (shouldSelectAccount) {
          setShowSelectAccountModal(true);
        } else {
          setConnectedCustomerId(updatedUser?.google_ads_customer_id || '');
          setShowConnectedModal(true);
        }
      });
      localStorage.setItem('droid_onboarding_complete', 'true');
    }

    // Handle payment return
    if (params.get('payment') === 'success') {
      const campaignId = params.get('campaign_id');
      // Pakiet i kwota netto z success_url - żeby potwierdzenie pokazało realną cenę
      // wykupionego pakietu (np. Micro 79 zł), a nie zaszytą wcześniej 299 zł.
      const amt = params.get('amount');
      setPaymentSuccessInfo({
        plan: params.get('plan') || null,
        amount: amt != null && amt !== '' ? Number(amt) : null,
        currency: params.get('currency') || 'PLN',
      });
      window.history.replaceState({}, '', '/app/campaigns');
      setShowPaymentSuccessModal(campaignId || true);
      fetchCampaigns();
    } else if (params.get('payment') === 'cancelled') {
      window.history.replaceState({}, '', '/app/campaigns');
      addNotification(t('campaigns.main.paymentCancelled'), 'warning');
    }
  }, [refreshUser, addNotification]);

  const [showPaymentSuccessModal, setShowPaymentSuccessModal] = useState(false);
  const [paymentSuccessInfo, setPaymentSuccessInfo] = useState(null);

  const fetchCampaigns = useCallback((forceArchived) => {
    // Read archived state from URL at call time, not from closure
    const isArchived = forceArchived !== undefined ? forceArchived : new URLSearchParams(window.location.search).get('status') === 'archived';
    const archivedParam = isArchived ? '?archived=1' : '';
    api.get(`/ads/my-campaigns/${archivedParam}`)
      .then(res => {
        setCampaigns(res.data.campaigns || []);
        setGroups(res.data.groups || []);
        setTrialUsed(res.data.trial_used || false);
        setTrialAvailable(res.data.trial_available ?? !res.data.trial_used);
        setTrialTransferDays(res.data.trial_transfer_days ?? null);
        setIsFreeAccount(res.data.is_free || false);
      })
      .catch(() => {
        // Don't clear campaigns on transient errors - keep showing stale data
      })
      .finally(() => {
        setLoading(false);
        setRefreshing(false);
      });
    // Batched health for all campaigns in one request (replaces per-campaign N+1)
    api.get('/ads/campaigns/health-summary/')
      .then(res => setHealthMap(res.data.health || {}))
      .catch(() => {});
    // Pakiet i wykorzystanie slotow (limity kampanii/kont)
    api.get('/ads/plan-status/')
      .then(res => setPlanStatus(res.data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    fetchCampaigns();
    // Get Google account name
    if (user?.google_ads_customer_id) {
      const pa = (user.organization?.platform_accounts || []).find(
        a => a.platform === 'google_ads' && a.account_id === user.google_ads_customer_id
      );
      if (pa && pa.account_name && pa.account_name !== `Google Ads ${pa.account_id}`) {
        setGoogleAccountName(pa.account_name);
      } else {
        // Name not cached yet - fetch from API (also persists the real name for next load)
        getGoogleAccounts().then(res => {
          const acct = (res.data.accounts || []).find(a => a.id === user.google_ads_customer_id);
          if (acct) setGoogleAccountName(acct.custom_name || acct.name || '');
        }).catch(() => {});
      }
    }
  }, [fetchCampaigns, user?.google_ads_customer_id, user?.organization?.platform_accounts]); // eslint-disable-line

  // Re-fetch when switching to/from archived filter
  const prevArchivedRef = useRef(filterStatus === 'archived');
  useEffect(() => {
    const isArchived = filterStatus === 'archived';
    if (isArchived !== prevArchivedRef.current) {
      prevArchivedRef.current = isArchived;
      fetchCampaigns(isArchived);
    }
  }, [filterStatus, fetchCampaigns]);

  // Listen for enable-campaign event from drawer
  useEffect(() => {
    const handler = (e) => {
      const camp = campaigns.find(c => c.id === e.detail.campaignId);
      if (camp) handleToggleManaged(camp);
    };
    window.addEventListener('droid-enable-campaign', handler);
    return () => window.removeEventListener('droid-enable-campaign', handler);
  }, [campaigns]);

  // Listen for subscribe event from drawer trial banner. setShowSubscribeModal
  // lives in this parent component, not in CampaignDrawer, so the banner button
  // cannot call it directly (powodowało ReferenceError, przycisk nie działał).
  useEffect(() => {
    const handler = (e) => setShowSubscribeModal(e.detail.campaignId);
    window.addEventListener('droid-subscribe-campaign', handler);
    return () => window.removeEventListener('droid-subscribe-campaign', handler);
  }, []);

  // Poll for draft campaigns in progress
  useEffect(() => {
    const hasInProgress = campaigns.some(
      c => c.status === 'draft' && (c.draft_status === 'analyzing' || c.draft_status === 'publishing')
    );

    if (hasInProgress) {
      pollRef.current = setInterval(() => {
        fetchCampaigns();
      }, 5000);
    } else if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }

    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
      }
    };
  }, [campaigns, fetchCampaigns]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('success') === '1') {
      addNotification(t('campaigns.main.paymentSuccess'), 'success');
      window.history.replaceState({}, '', window.location.pathname);
      fetchCampaigns();
    } else if (params.get('cancelled') === '1') {
      addNotification(t('campaigns.main.paymentCancelled'), 'warning');
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [addNotification, fetchCampaigns]);

  // Open the confirmation modal instead of deleting immediately.
  const requestDeleteDraft = (campaign) => setDraftToDelete(campaign);

  const confirmDeleteDraft = async () => {
    const campaign = draftToDelete;
    if (!campaign) return;
    setDraftToDelete(null);
    try {
      await api.delete(`/ads/drafts/${campaign.id}/delete/`);
      addNotification(t('campaigns.main.draftDeleted'), 'success');
      fetchCampaigns();
    } catch {
      addNotification(t('campaigns.main.draftDeleteError'), 'error');
    }
  };

  // Group operations
  const toggleGroupCollapsed = (groupId) => {
    setCollapsedGroups(prev => {
      const next = new Set(prev);
      if (next.has(groupId)) next.delete(groupId); else next.add(groupId);
      localStorage.setItem('droid_collapsed_groups', JSON.stringify([...next]));
      return next;
    });
  };

  const handleCreateGroup = async () => {
    if (!newGroupName.trim()) return;
    setCreatingGroup(true);
    try {
      await api.post('/ads/campaign-groups/', { name: newGroupName.trim() });
      setNewGroupName('');
      setShowCreateGroupModal(false);
      fetchCampaigns();
    } catch { addNotification(t('campaigns.main.groupCreateError'), 'error'); }
    setCreatingGroup(false);
  };

  const handleRenameGroup = async () => {
    const name = renameGroupName.trim();
    if (!name || !renamingGroup) return;
    setSavingGroupName(true);
    try {
      await api.patch(`/ads/campaign-groups/${renamingGroup.id}/`, { name });
      setRenamingGroup(null);
      setRenameGroupName('');
      fetchCampaigns();
      addNotification(t('campaigns.group.renamed'), 'success');
    } catch { addNotification(t('campaigns.group.renameError'), 'error'); }
    setSavingGroupName(false);
  };

  const handleDeleteGroup = async (groupId) => {
    try {
      await api.delete(`/ads/campaign-groups/${groupId}/`);
      setDeletingGroup(null);
      fetchCampaigns();
      addNotification(t('campaigns.main.groupDeleted'), 'success');
    } catch { addNotification(t('campaigns.group.deleteError'), 'error'); }
  };

  const handleMoveCampaign = async (campaignId, targetGroupId) => {
    try {
      const body = targetGroupId
        ? { groups: [{ id: targetGroupId, position: 0, campaign_ids: [campaignId] }], ungrouped: [] }
        : { groups: [], ungrouped: [campaignId] };
      await api.post('/ads/campaign-groups/reorder/', body);
      fetchCampaigns();
      setMovingCampaign(null);
    } catch {}
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      // Trigger backend sync for all platforms
      await api.post('/ads/sync/');
    } catch {}
    // Poll for updated data
    setTimeout(() => fetchCampaigns(), 3000);
    setTimeout(() => fetchCampaigns(), 8000);
    setTimeout(() => { fetchCampaigns(); setRefreshing(false); }, 15000);
  };

  const [showAgencyEnableModal, setShowAgencyEnableModal] = useState(null);
  const [showFilterDrawer, setShowFilterDrawer] = useState(false);
  const hasActiveFilters = !!(filterStatus || filterManaged || filterClicks || filterBudget || filterClient || filterPlatform);
  const clearAllFilters = () => {
    const params = new URLSearchParams();
    setSearchParams(params, { replace: true });
    setShowFilterDrawer(false);
    localStorage.removeItem('droid_campaign_filters');
  };

  const [agencySchedulerAddon, setAgencySchedulerAddon] = useState(false);

  const handleAgencyEnable = async () => {
    const campaignId = showAgencyEnableModal;
    setActionLoading(campaignId);
    try {
      const res = await api.post(`/ads/agency/campaigns/${campaignId}/toggle/`, { action: 'enable', include_scheduler: agencySchedulerAddon });
      addNotification(res.data.message || t('campaigns.main.managementEnabled'), 'success');
      fetchCampaigns();
    } catch (err) {
      addNotification(err.response?.data?.error || t('campaigns.main.managementEnableError'), 'error');
    } finally {
      setActionLoading(null);
      setShowAgencyEnableModal(null);
      setAgencySchedulerAddon(false);
    }
  };

  const [showAgencyDisableModal, setShowAgencyDisableModal] = useState(null);

  const handleAgencyDisable = async () => {
    const campaignId = showAgencyDisableModal;
    setActionLoading(campaignId);
    try {
      const res = await api.post(`/ads/agency/campaigns/${campaignId}/toggle/`, { action: 'disable' });
      addNotification(res.data.message || t('campaigns.main.managementPaused'), 'success');
      fetchCampaigns();
    } catch (err) {
      addNotification(err.response?.data?.error || t('campaigns.main.managementPauseError'), 'error');
    } finally {
      setActionLoading(null);
      setShowAgencyDisableModal(null);
    }
  };

  const handleToggleManaged = async (campaign) => {
    // Agency mode: use agency toggle endpoints
    if (isAgency) {
      if (campaign.is_managed) {
        setShowAgencyDisableModal(campaign.id);
      } else {
        setShowAgencyEnableModal(campaign.id);
      }
      return;
    }

    if (campaign.is_managed) {
      setShowDeactivateModal(campaign.id);
      return;
    }

    // If management was paused (previously enabled), show re-enable modal
    if (campaign.management_paused) {
      setShowSubscribeModal(campaign.id);
      return;
    }

    // Block unsupported campaign types
    const cType = campaign.campaign_type || 'search';
    // PMax jest wspierany (pmax_optimizer); blokujemy tylko typy bez zarządzania
    // (display/video/demand_gen/...). Search/Shopping/PMax można włączyć.
    if (campaign.platform === 'google_ads' && cType && !['search', 'shopping', 'performance_max', 'pmax', ''].includes(cType)) {
      setUnsupportedTypeModal(cType);
      return;
    }

    // Free accounts: show modal with free activation
    if (isFreeAccount) {
      setShowTrialModal(campaign.id);
      return;
    }

    // New campaign: trial if available (nieużyty LUB do przeniesienia z innej
    // wstrzymanej kampanii - backend liczy trial_available), inaczej subskrypcja.
    if (trialAvailable) {
      setShowTrialModal(campaign.id);
    } else {
      setShowSubscribeModal(campaign.id);
    }
  };

  const [trialError, setTrialError] = useState(null);

  const handleActivateTrial = async () => {
    const campaignId = showTrialModal;
    setActionLoading(campaignId);
    setTrialError(null);
    try {
      const res = await api.post('/ads/activate-trial/', { campaign_id: campaignId });
      trackMetaEvent('StartTrial');
      addNotification(res.data.message || t('campaigns.main.trialActivated'), 'success');
      setShowTrialModal(null);
      // Status „zarządzane" potrafi ustabilizować się chwilę po aktywacji -
      // dociągamy też z opóźnieniem, żeby panel sam się odświeżył bez F5.
      fetchCampaigns();
      setTimeout(() => fetchCampaigns(), 1500);
      setTimeout(() => fetchCampaigns(), 4000);
    } catch (err) {
      const msg = err.response?.data?.error || t('campaigns.main.trialError');
      setTrialError(msg);
    } finally {
      setActionLoading(null);
    }
  };

  const handleSubscribe = async (includeScheduler = false, plan = null, currency = null) => {
    const campaignId = showSubscribeModal;
    setActionLoading(campaignId);
    try {
      const res = await api.post('/ads/create-checkout/', { campaign_id: campaignId, include_scheduler: includeScheduler, ...(plan ? { plan } : {}), ...(currency ? { currency } : {}) });
      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      } else {
        addNotification(res.data.message || t('campaigns.main.managementEnabled'), 'success');
        fetchCampaigns();
        setTimeout(() => fetchCampaigns(), 1500);
        setTimeout(() => fetchCampaigns(), 4000);
        setActionLoading(null);
        setShowSubscribeModal(null);
      }
    } catch (err) {
      // Przekroczono limit pakietu -> pokaż modal upgrade zamiast błędu
      if (err.response?.status === 409 && err.response?.data?.code === 'plan_limit') {
        setShowSubscribeModal(null);
        setPlanLimitInfo({ ...err.response.data, campaignId });
        setActionLoading(null);
        return;
      }
      addNotification(err.response?.data?.error || t('campaigns.main.genericError'), 'error');
      setActionLoading(null);
      setShowSubscribeModal(null);
    }
  };

  const handleUpgradePlan = async () => {
    if (!planLimitInfo) return;
    setActionLoading(planLimitInfo.campaignId);
    try {
      const res = await api.post('/ads/change-plan/', {
        plan: planLimitInfo.suggested_plan,
        campaign_id: planLimitInfo.campaignId,
        ...(planStatus?.currency ? { currency: planStatus.currency } : {}),
      });
      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
        return;
      }
      addNotification(res.data.message || t('campaigns.main.planChanged'), 'success');
      setPlanLimitInfo(null);
      fetchCampaigns();
    } catch (err) {
      addNotification(err.response?.data?.error || t('campaigns.main.planChangeError'), 'error');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeactivate = async () => {
    const campaignId = showDeactivateModal;
    setActionLoading(campaignId);
    try {
      await api.post('/ads/deactivate/', { campaign_id: campaignId });
      addNotification(t('campaigns.main.managementDisabled'), 'success');
      fetchCampaigns();
    } catch (err) {
      addNotification(err.response?.data?.error || t('campaigns.main.genericError'), 'error');
    } finally {
      setActionLoading(null);
      setShowDeactivateModal(null);
    }
  };

  const openDraftReview = async (campaign) => {
    try {
      // Fetch full campaign data including ai_draft_data
      const res = await api.get(`/ads/drafts/${campaign.id}/status/`);
      setSelectedDraft(res.data);
    } catch {
      // Fallback to list data
      setSelectedDraft(campaign);
    }
  };

  const handleCampaignClick = (campaign) => {
    if (campaign.status === 'draft' && campaign.draft_status === 'ready') {
      openDraftReview(campaign);
    } else if (campaign.status === 'draft' && campaign.draft_status === 'failed') {
      // Check if it's a publish failure (has valid draft data) or analysis failure
      const d = campaign.ai_draft_data || {};
    const hasData = d.keywords?.length > 0 || d.headlines?.length > 0 || d.audience;
      if (hasData) {
        // Publish failure - open summary with retry
        openDraftReview(campaign);
      }
      // Analysis failure - handled by inline retry/delete buttons
      return;
    } else if (campaign.status !== 'draft') {
      setSelectedCampaign(campaign);
    }
  };

  const handleRetryDraft = async (campaign) => {
    // Publikacja nieudana = szkic ma treść (słowa/teksty). NIE opieramy się na samym
    // draft_error: szkic, któremu zginął komunikat, był brany za nieudaną analizę
    // i USUWANY (RedBlond, 2026-10-17).
    const d = campaign.ai_draft_data || {};
    const isPublishFailure = !!campaign.draft_error || d.keywords?.length > 0 || d.headlines?.length > 0 || !!d.audience;
    try {
      if (isPublishFailure) {
        // Otwórz szkic do edycji: popraw ustawienia i opublikuj ponownie.
        await api.post(`/ads/drafts/${campaign.id}/update/`, { reset_failed: true });
        addNotification(t('campaigns.main.draftRestored'), 'success');
        fetchCampaigns();
        openDraftReview({ ...campaign, draft_status: 'ready' });
      } else {
        // Analysis failure - delete and re-analyze
        await api.delete(`/ads/drafts/${campaign.id}/delete/`);
        await api.post('/ads/drafts/start/', { url: campaign.source_url, platform: campaign.platform || 'google_ads' });
        addNotification(t('campaigns.main.retryingAnalysis'), 'success');
        fetchCampaigns();
      }
    } catch (err) {
      addNotification(err.response?.data?.error || t('campaigns.main.retryError'), 'error');
    }
  };

  // Not connected state - still allow creating drafts
  if (loading) {
    return (
      <Flex align="center" justify="center" h={256}>
        <AnimatedLogo size={48} />
      </Flex>
    );
  }

  // Connected state
  return (
    <Box px="lg" py="md">
      <SEO title={t('campaigns.main.pageTitle')} path="/app/campaigns" noIndex />
      {/* Header */}
      <StickyHeader>
        <Group gap="sm">
          <Title order={3}>{t('campaigns.main.pageTitle')}</Title>
          {/* The Google Ads account picker now lives globally in the top bar
              (GoogleAccountSwitcher), so it is no longer shown per-page here. */}
        </Group>
        <Group gap="xs">
          <ExportMenu baseUrl="/ads/campaigns/export/" filename="droid-kampanie" />
          {isConnected && !rearrangeMode ? (
            <Group gap={0}>
              <Button color="brand" leftSection={<Plus size={14} />} onClick={() => setShowCreateWizard(true)} style={{ borderTopRightRadius: 0, borderBottomRightRadius: 0 }}>
                {t('campaigns.main.createCampaign')}
              </Button>
              <Menu shadow="md" position="bottom-end" withArrow>
                <Menu.Target>
                  <Button color="brand" style={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0, borderLeft: '1px solid rgba(255,255,255,0.3)', paddingLeft: 8, paddingRight: 8 }}>
                    <CaretDown size={14} />
                  </Button>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Item leftSection={<Rows size={14} />} onClick={() => setShowCreateGroupModal(true)}>
                    {t('campaigns.main.createGroup')}
                  </Menu.Item>
                  {(campaigns.length > 1 || groups.length > 0) && (
                    <Menu.Item leftSection={<ArrowsClockwise size={14} />} onClick={() => setRearrangeMode(true)}>
                      {t('campaigns.main.rearrange')}
                    </Menu.Item>
                  )}
                </Menu.Dropdown>
              </Menu>
            </Group>
          ) : isConnected && rearrangeMode ? (
            <Group gap="xs">
              <Button variant="default" onClick={() => setRearrangeMode(false)}>{t('campaigns.common.cancel')}</Button>
              <Button color="green" onClick={() => {
                // Trigger save via ref
                if (reorderRef.current) reorderRef.current.save();
              }}>
                {t('campaigns.main.saveOrder')}
              </Button>
            </Group>
          ) : (
            <Button color="brand" leftSection={<ArrowSquareOut size={14} />} onClick={() => setShowConnectModal(true)}>
              {t('campaigns.main.connectGoogle')}
            </Button>
          )}
          {isConnected && (
            <Tooltip label={t('campaigns.main.syncCampaigns')}>
              <ActionIcon variant="default" size="lg" onClick={handleRefresh} loading={refreshing}>
                <ArrowsClockwise size={16} />
              </ActionIcon>
            </Tooltip>
          )}
        </Group>
      </StickyHeader>

      {/* Filters - desktop */}
      {campaigns.length > 0 && (
        <Group gap="xs" mb="md" className="hidden md:flex">
          <Select placeholder={t('campaigns.filters.platform')} data={[{ value: 'google_ads', label: 'Google Ads' },{ value: 'meta_ads', label: 'Meta Ads' }]} value={filterPlatform} onChange={(v) => setFilter('platform', v)} clearable size="sm" style={{ width: 160 }} />
          <Select placeholder={t('campaigns.filters.status')} data={[{ value: 'active', label: t('campaigns.filters.statusActive') },{ value: 'paused', label: t('campaigns.filters.statusPaused') },{ value: 'draft', label: t('campaigns.filters.statusDraft') },{ value: 'archived', label: t('campaigns.filters.statusArchived') }]} value={filterStatus} onChange={(v) => setFilter('status', v)} clearable size="sm" style={{ width: 160 }} />
          <Select placeholder={t('campaigns.filters.management')} data={[{ value: 'managed', label: t('campaigns.filters.managed') },{ value: 'unmanaged', label: t('campaigns.filters.unmanaged') }]} value={filterManaged} onChange={(v) => setFilter('managed', v)} clearable size="sm" style={{ width: 220 }} />
          <Select placeholder={t('campaigns.filters.clicks')} data={[{ value: '0', label: t('campaigns.filters.clicks0') },{ value: '10', label: '> 10' },{ value: '50', label: '> 50' },{ value: '100', label: '> 100' },{ value: '500', label: '> 500' }]} value={filterClicks} onChange={(v) => setFilter('clicks', v)} clearable size="sm" style={{ width: 160 }} />
          <Select placeholder={t('campaigns.filters.budget')} data={[{ value: '10', label: `> 10 ${listCurSym}/dz.` },{ value: '20', label: `> 20 ${listCurSym}/dz.` },{ value: '50', label: `> 50 ${listCurSym}/dz.` },{ value: '100', label: `> 100 ${listCurSym}/dz.` }]} value={filterBudget} onChange={(v) => setFilter('budget', v)} clearable size="sm" style={{ width: 160 }} />
          {isAgency && (() => {
            const clientNames = [...new Set(campaigns.map(c => c.agency_client_name).filter(Boolean))].sort();
            return clientNames.length > 0 ? <Select placeholder={t('campaigns.filters.client')} data={clientNames.map(n => ({ value: n, label: n }))} value={filterClient} onChange={(v) => setFilter('client', v)} clearable size="sm" style={{ width: 180 }} /> : null;
          })()}
          <Group gap="xs" ml="auto" wrap="nowrap">
            <SegmentedControl
              value={listPeriod}
              onChange={setListPeriod}
              size="xs"
              data={[
                { value: 'today', label: t('campaigns.period.today') },
                { value: 'yesterday', label: t('campaigns.period.yesterday') },
                { value: '7', label: '7d' },
                { value: '14', label: '14d' },
                { value: 'all', label: t('campaigns.period.last30') },
                { value: 'month', label: t('campaigns.chart.thisMonth') },
                { value: '90', label: '90d' },
              ]}
            />
            <ColumnPicker visibleColumns={visibleColumns} updateColumns={updateColumns} />
          </Group>
        </Group>
      )}
      {/* Filters - mobile */}
      {campaigns.length > 0 && (
        <div className="md:hidden mb-3">
          <Button variant="default" size="sm" leftSection={<Funnel size={14} />} onClick={() => setShowFilterDrawer(true)}>
            {t('campaigns.filters.filtersButton')}{hasActiveFilters ? t('campaigns.filters.activeSuffix') : ''}
          </Button>
          <Drawer opened={showFilterDrawer} onClose={() => setShowFilterDrawer(false)} title={t('campaigns.filters.filtersButton')} position="bottom" size="auto" padding="md">
            <Stack gap="sm">
              <Select label={t('campaigns.filters.platform')} placeholder={t('campaigns.filters.all')} data={[{ value: 'google_ads', label: 'Google Ads' },{ value: 'meta_ads', label: 'Meta Ads' }]} value={filterPlatform} onChange={(v) => setFilter('platform', v)} clearable size="sm" />
              <Select label={t('campaigns.filters.status')} placeholder={t('campaigns.filters.all')} data={[{ value: 'active', label: t('campaigns.filters.statusActive') },{ value: 'paused', label: t('campaigns.filters.statusPaused') },{ value: 'draft', label: t('campaigns.filters.statusDraft') },{ value: 'archived', label: t('campaigns.filters.statusArchived') }]} value={filterStatus} onChange={(v) => setFilter('status', v)} clearable size="sm" />
              <Select label={t('campaigns.filters.management')} placeholder={t('campaigns.filters.all')} data={[{ value: 'managed', label: t('campaigns.filters.managed') },{ value: 'unmanaged', label: t('campaigns.filters.unmanaged') }]} value={filterManaged} onChange={(v) => setFilter('managed', v)} clearable size="sm" />
              <Select label={t('campaigns.filters.clicks')} placeholder={t('campaigns.filters.any')} data={[{ value: '0', label: t('campaigns.filters.clicks0') },{ value: '10', label: '> 10' },{ value: '50', label: '> 50' },{ value: '100', label: '> 100' },{ value: '500', label: '> 500' }]} value={filterClicks} onChange={(v) => setFilter('clicks', v)} clearable size="sm" />
              <Select label={t('campaigns.filters.budget')} placeholder={t('campaigns.filters.anyM')} data={[{ value: '10', label: `> 10 ${listCurSym}/dz.` },{ value: '20', label: `> 20 ${listCurSym}/dz.` },{ value: '50', label: `> 50 ${listCurSym}/dz.` },{ value: '100', label: `> 100 ${listCurSym}/dz.` }]} value={filterBudget} onChange={(v) => setFilter('budget', v)} clearable size="sm" />
              {isAgency && (() => {
                const clientNames = [...new Set(campaigns.map(c => c.agency_client_name).filter(Boolean))].sort();
                return clientNames.length > 0 ? <Select label={t('campaigns.filters.client')} placeholder={t('campaigns.filters.everyone')} data={clientNames.map(n => ({ value: n, label: n }))} value={filterClient} onChange={(v) => setFilter('client', v)} clearable size="sm" /> : null;
              })()}
              <Select label={t('campaigns.filters.numbersForPeriod')} data={[{ value: 'today', label: t('campaigns.period.today') },{ value: 'yesterday', label: t('campaigns.period.yesterday') },{ value: 'all', label: t('campaigns.period.last30') },{ value: '7', label: t('campaigns.filters.days7') },{ value: '14', label: t('campaigns.filters.days14') },{ value: 'month', label: t('campaigns.chart.thisMonth') },{ value: '90', label: t('campaigns.filters.days90') }]} value={listPeriod} onChange={(v) => setListPeriod(v || 'all')} size="sm" />
              <Group gap="xs" mt="xs">
                <Button size="sm" color="brand" onClick={() => setShowFilterDrawer(false)}>{t('campaigns.filters.apply')}</Button>
                {hasActiveFilters && <Button size="sm" variant="subtle" color="gray" onClick={clearAllFilters}>{t('campaigns.filters.clearFilters')}</Button>}
              </Group>
            </Stack>
          </Drawer>
        </div>
      )}

      {/* Syncing banner */}
      {syncing && (
        <Paper withBorder p="lg" radius="md" mb="md" style={{ textAlign: 'center' }}>
          <Flex align="center" justify="center" gap="sm" mb="xs">
            <Loader size="sm" color="brand" />
            <Text size="sm" fw={600}>{t('campaigns.main.syncingTitle')}</Text>
          </Flex>
          <Text size="xs" c="dimmed">{t('campaigns.main.syncingDesc')}</Text>
        </Paper>
      )}

      {/* Account warning banner */}
      {campaigns.length > 0 && campaigns.every(c => c.status === 'active' && c.impressions === 0) && (
        <Alert color="red" icon={<WarningCircle size={20} />} mb="md" radius="md">
          <Text size="sm" fw={500} c="red.8">{t('campaigns.main.verifyTitle')}</Text>
          <Text size="xs" c="red.6" mt={4}>
            {t('campaigns.main.verifyDesc')}
          </Text>
          <a
            href="https://ads.google.com/aw/accountsettings"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-xs text-red-700 font-medium mt-2 hover:text-red-800"
          >
            {t('campaigns.main.verifyLink')}
            <ArrowSquareOut className="w-3 h-3" />
          </a>
        </Alert>
      )}

      {/* Rearrange mode */}
      {rearrangeMode && (
        <CampaignReorder
          ref={reorderRef}
          campaigns={campaigns}
          groups={groups}
          onSave={() => { setRearrangeMode(false); fetchCampaigns(); }}
          onCancel={() => setRearrangeMode(false)}
          onError={(msg) => addNotification(msg, 'error')}
        />
      )}

      {/* Campaign Table */}
      {rearrangeMode ? null : (campaigns.length > 0 || groups.length > 0) ? (() => {
        const handleSort = (field) => {
          if (sortField === field) {
            setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
          } else {
            setSortField(field);
            setSortDir('desc');
          }
        };
        const SortIcon = ({ field }) => {
          if (sortField !== field) return null;
          return sortDir === 'asc' ? <CaretUp size={12} weight="bold" /> : <CaretDown size={12} weight="bold" />;
        };
        const thStyle = (field, align = 'right') => ({
          textAlign: align,
          cursor: 'pointer',
          userSelect: 'none',
        });
        // Konfigurowalne kolumny tabeli (kolejność + widoczność z localStorage).
        const displayColumns = visibleColumns.map(k => ({ key: k, ...CAMPAIGN_COLUMN_DEFS[k] }));
        const metricColCount = displayColumns.length;

        // Gdy wybrano okres inny niż „Cały czas", podmieniamy liczby kampanii na
        // wartości z period-metrics (CampaignDailyMetric). Kolumny, sortowanie,
        // filtry i sumy grup czytają te same pola, więc wszystko podąża za okresem.
        const displayCampaigns = listPeriod === 'all' ? campaigns : campaigns.map(c => {
          const m = listPeriodMetrics[c.id];
          const cost = m ? (m.cost || 0) : 0;
          const clicks = m ? (m.clicks || 0) : 0;
          const impressions = m ? (m.impressions || 0) : 0;
          const conversions = m ? (m.conversions || 0) : 0;
          const conversions_value = m ? (m.conversions_value || 0) : 0;
          const ctr = impressions > 0 ? +((clicks / impressions) * 100).toFixed(2) : 0;
          const cpc = clicks > 0 ? +(cost / clicks).toFixed(2) : 0;
          return { ...c, cost, clicks, impressions, conversions, conversions_value, ctr, cpc, cpl_period_mismatch: true };
        });

        const filtered = displayCampaigns.filter(c => {
          if (filterStatus && filterStatus !== 'archived' && c.status !== filterStatus) return false;
          if (filterManaged === 'managed' && !c.is_managed) return false;
          if (filterManaged === 'unmanaged' && c.is_managed) return false;
          if (filterClicks) {
            const min = parseInt(filterClicks);
            if (min === 0 && (c.clicks || 0) !== 0) return false;
            if (min > 0 && (c.clicks || 0) <= min) return false;
          }
          if (filterBudget && parseFloat(c.budget_amount || 0) <= parseFloat(filterBudget)) return false;
          if (filterClient && (c.agency_client_name || '') !== filterClient) return false;
          if (filterPlatform && (c.platform || 'google_ads') !== filterPlatform) return false;
          return true;
        });
        const sorted = [...filtered].sort((a, b) => {
          const isDraft = (c) => c.status === 'draft';
          // Drafts always last
          if (isDraft(a) && !isDraft(b)) return 1;
          if (!isDraft(a) && isDraft(b)) return -1;
          // Default sort: managed first, then alphabetically by name
          if (!sortField) {
            if (a.is_managed && !b.is_managed) return -1;
            if (!a.is_managed && b.is_managed) return 1;
            return (a.name || '').localeCompare(b.name || '', 'pl');
          }
          let va = 0, vb = 0;
          if (sortField === 'name') { va = a.name?.toLowerCase() || ''; vb = b.name?.toLowerCase() || ''; }
          else if (sortField === 'budget') { va = parseFloat(a.budget_amount || 0); vb = parseFloat(b.budget_amount || 0); }
          else if (sortField === 'cost') { va = parseFloat(a.cost || 0); vb = parseFloat(b.cost || 0); }
          else if (sortField === 'clicks') { va = a.clicks || 0; vb = b.clicks || 0; }
          else if (sortField === 'impressions') { va = a.impressions || 0; vb = b.impressions || 0; }
          else if (sortField === 'ctr') { va = parseFloat(a.ctr || 0); vb = parseFloat(b.ctr || 0); }
          else if (sortField === 'cpc') { va = a.clicks > 0 ? parseFloat(a.cost || 0) / a.clicks : parseFloat(a.cpc || 0); vb = b.clicks > 0 ? parseFloat(b.cost || 0) / b.clicks : parseFloat(b.cpc || 0); }
          else if (sortField === 'conversions') { va = parseFloat(a.conversions || 0); vb = parseFloat(b.conversions || 0); }
          else if (sortField === 'conversions_value') { va = parseFloat(a.conversions_value || 0); vb = parseFloat(b.conversions_value || 0); }
          else if (sortField === 'roas') { va = parseFloat(a.cost || 0) > 0 ? parseFloat(a.conversions_value || 0) / parseFloat(a.cost) : 0; vb = parseFloat(b.cost || 0) > 0 ? parseFloat(b.conversions_value || 0) / parseFloat(b.cost) : 0; }
          if (sortField === 'name') return sortDir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
          return sortDir === 'asc' ? va - vb : vb - va;
        });

        // Re-sort by groups: grouped campaigns come first (in group order), then ungrouped
        if (groups.length > 0 && !sortField) {
          const groupPos = {};
          groups.forEach((g, i) => { groupPos[g.id] = i; });
          sorted.sort((a, b) => {
            const aGroup = a.group ? groupPos[a.group] ?? 999 : 1000;
            const bGroup = b.group ? groupPos[b.group] ?? 999 : 1000;
            if (aGroup !== bGroup) return aGroup - bGroup;
            return (a.position || 0) - (b.position || 0);
          });
        }

        // Build display rows: interleave groups and ungrouped campaigns by position
        const displayRows = [];
        if (groups.length > 0 && !sortField) {
          // Build top-level items: groups and ungrouped campaigns share position namespace
          const topItems = [];
          const sortedGroups = [...groups].sort((a, b) => (a.position || 0) - (b.position || 0));
          for (const g of sortedGroups) {
            const groupCampaigns = sorted.filter(c => c.group === g.id);
            topItems.push({ type: 'group', group: g, campaigns: groupCampaigns, position: g.position || 0 });
          }
          const ungrouped = sorted.filter(c => !c.group);
          for (const c of ungrouped) {
            topItems.push({ type: 'ungrouped', campaign: c, position: c.position || 0 });
          }
          topItems.sort((a, b) => a.position - b.position);

          for (const topItem of topItems) {
            if (topItem.type === 'group') {
              displayRows.push({ type: 'group-header', group: topItem.group, campaigns: topItem.campaigns });
              if (!collapsedGroups.has(topItem.group.id)) {
                for (const c of topItem.campaigns) {
                  displayRows.push({ type: 'campaign', campaign: c, inGroup: true });
                }
              }
            } else {
              displayRows.push({ type: 'campaign', campaign: topItem.campaign, inGroup: false });
            }
          }
        } else {
          for (const c of sorted) {
            displayRows.push({ type: 'campaign', campaign: c });
          }
        }

        return (
        <>
        {/* Mobile cards */}
        <div className="md:hidden">
          <Stack gap="xs">
            {sorted.length === 0 && groups.length === 0 && (
              <Paper withBorder p="lg" radius="md" ta="center">
                <Text size="sm" fw={500} mb={4}>{t('campaigns.main.noMatchTitle')}</Text>
              </Paper>
            )}
            {sorted.map(campaign => {
              const isManaged = campaign.is_managed;
              const isDraft = campaign.status === 'draft';
              return (
                <Paper
                  key={campaign.id}
                  withBorder
                  p="sm"
                  radius="md"
                  onClick={() => handleCampaignClick(campaign)}
                  style={{
                    cursor: 'pointer',
                    borderLeft: isManaged ? '3px solid var(--mantine-color-brand-5)' : undefined,
                  }}
                >
                  <Group justify="space-between" mb={6} align="flex-start" wrap="nowrap">
                    <Group gap="xs" style={{ flex: 1, minWidth: 0 }} align="flex-start" wrap="nowrap">
                      {!isDraft && <HealthStatusDot campaignId={campaign.id} health={healthMap[campaign.id] || null} />}
                      <div style={{ minWidth: 0 }}>
                        <Text size="sm" fw={600} truncate>{campaign.name}</Text>
                        <Text size="10px" c="dimmed" truncate>{campaignTypeLabel(campaign, t)}</Text>
                      </div>
                    </Group>
                    {isDraft ? (
                      <DraftStatusBadge campaign={campaign} onRetry={handleRetryDraft} onDelete={requestDeleteDraft} />
                    ) : (
                      <Badge variant="dot" color={campaign.status === 'active' ? 'green' : 'yellow'} size="sm">
                        {campaign.status === 'active' ? t('campaigns.adGroups.statusActive') : t('campaigns.adGroups.statusPaused')}
                      </Badge>
                    )}
                  </Group>
                  {!isDraft && (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 4 }}>
                      <div>
                        <Text size="xs" c="dimmed">{t('campaigns.metrics.cost')}</Text>
                        <Text size="sm" ff="monospace" fw={500}>{formatMoney(campaign.cost, campaign.currency)}</Text>
                      </div>
                      <div>
                        <Text size="xs" c="dimmed">{t('campaigns.metrics.clicks')}</Text>
                        <Text size="sm" ff="monospace">{(campaign.clicks || 0).toLocaleString('pl-PL')}</Text>
                      </div>
                      <div>
                        <Text size="xs" c="dimmed">{t('campaigns.metrics.ctr')}</Text>
                        <Text size="sm" ff="monospace" c={parseFloat(campaign.ctr || 0) >= 5 ? 'green.7' : parseFloat(campaign.ctr || 0) < 2 ? 'red.6' : 'dimmed'}>{campaign.ctr}%</Text>
                      </div>
                    </div>
                  )}
                  {!isDraft && (
                    <Group justify="space-between" mt={6}>
                      <Group gap="xs">
                        <Text size="xs" c="dimmed">{t('campaigns.main.mobileBudget', { budget: parseFloat(campaign.budget_amount || 0).toLocaleString('pl-PL', { minimumFractionDigits: 0, maximumFractionDigits: 0 }), cur: currencySymbol(campaign.currency) })}</Text>
                        <Text size="xs" c="dimmed">{t('campaigns.main.mobileConv', { count: Math.round(parseFloat(campaign.conversions) || 0) })}</Text>
                      </Group>
                      <Tooltip label={isManaged ? t('campaigns.main.disableManaged') : t('campaigns.main.enableManaged')} position="left">
                        <div onClick={(e) => e.stopPropagation()}>
                          <Switch
                            checked={isManaged}
                            onChange={() => handleToggleManaged(campaign)}
                            color="brand"
                            size="sm"
                            styles={{ track: { cursor: 'pointer' } }}
                          />
                        </div>
                      </Tooltip>
                    </Group>
                  )}
                </Paper>
              );
            })}
          </Stack>
        </div>
        {/* Desktop table */}
        <Paper withBorder radius="md" style={{ overflow: 'hidden' }} className="hidden md:block">
          <EdgeScroll stickyLast>
          <Table highlightOnHover verticalSpacing="md" horizontalSpacing="lg" className="campaign-list-table" style={{ minWidth: 700, fontSize: 14 }}>
            <Table.Thead>
              <Table.Tr>
                <Table.Th onClick={() => handleSort('name')} style={thStyle('name', 'left')}>
                  <Group gap={4} wrap="nowrap">{t('campaigns.main.colName')} <SortIcon field="name" /></Group>
                </Table.Th>
                {displayColumns.map(col => (
                  col.sortKey ? (
                    <Table.Th key={col.key} onClick={() => handleSort(col.sortKey)} style={thStyle(col.sortKey, col.align)}>
                      <Group gap={4} wrap="nowrap" justify={col.align === 'right' ? 'flex-end' : col.align === 'center' ? 'center' : 'flex-start'}>{t(col.labelKey)} <SortIcon field={col.sortKey} /></Group>
                    </Table.Th>
                  ) : (
                    <Table.Th key={col.key} style={{ textAlign: col.align }}>{t(col.labelKey)}</Table.Th>
                  )
                ))}
                <Table.Th style={{ textAlign: 'center', width: 60 }}>Droid</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {displayRows.length === 0 && (
                <Table.Tr>
                  <Table.Td colSpan={metricColCount + 2} style={{ textAlign: 'center', padding: '40px 0' }}>
                    <Text size="sm" fw={500} mb={4}>{t('campaigns.main.noMatchTitle')}</Text>
                    <Text size="xs" c="dimmed">{t('campaigns.main.noMatchDesc')}</Text>
                  </Table.Td>
                </Table.Tr>
              )}
              {displayRows.map(row => {
                if (row.type === 'group-header') {
                  const g = row.group;
                  const isCollapsed = collapsedGroups.has(g.id);
                  const gc = row.campaigns;
                  const totalBudget = gc.reduce((s, c) => s + parseFloat(c.budget_amount || 0), 0);
                  const totalCost = gc.reduce((s, c) => s + parseFloat(c.cost || 0), 0);
                  const totalClicks = gc.reduce((s, c) => s + (c.clicks || 0), 0);
                  const totalImpressions = gc.reduce((s, c) => s + (c.impressions || 0), 0);
                  const totalConversions = gc.reduce((s, c) => s + parseFloat(c.conversions || 0), 0);
                  const totalConversionsValue = gc.reduce((s, c) => s + parseFloat(c.conversions_value || 0), 0);
                  const avgCtr = totalImpressions > 0 ? (totalClicks / totalImpressions * 100) : 0;
                  const managedCount = gc.filter(c => c.is_managed).length;
                  // Waluta grupy: dominująca (wg kosztu) waluta kont w grupie. Grupa z
                  // kontami w różnych walutach ma przybliżoną sumę (znane ograniczenie).
                  const groupCurrency = (() => {
                    const spend = {};
                    gc.forEach((c) => { const cur = c.currency || 'PLN'; spend[cur] = (spend[cur] || 0) + parseFloat(c.cost || 0); });
                    const keys = Object.keys(spend);
                    return keys.length ? keys.reduce((a, b) => (spend[a] >= spend[b] ? a : b)) : 'PLN';
                  })();
                  const groupAgg = {
                    total: gc.length, activeCount: gc.filter(c => c.status === 'active').length,
                    totalBudget, totalCost, totalClicks, totalImpressions, totalConversions, totalConversionsValue, avgCtr,
                    currency: groupCurrency,
                  };
                  return (
                    <Table.Tr key={`group:${g.id}`} style={{ backgroundColor: 'var(--mantine-color-gray-0)', cursor: 'pointer' }} onClick={() => toggleGroupCollapsed(g.id)}>
                      <Table.Td>
                        <Group gap="xs" wrap="nowrap">
                          {isCollapsed ? <CaretRight size={14} weight="bold" className="text-gray-400" /> : <CaretDown size={14} weight="bold" className="text-gray-400" />}
                          <Rows size={14} className="text-indigo-500" />
                          <Text size="sm" fw={600} c="indigo.7">{g.name}</Text>
                          <Badge size="xs" variant="light" color="indigo">{gc.length}</Badge>
                        </Group>
                      </Table.Td>
                      {displayColumns.map(col => (
                        <Table.Td key={col.key} style={{ textAlign: col.align }}>
                          {gc.length > 0 && col.group(groupAgg)}
                        </Table.Td>
                      ))}
                      <Table.Td style={{ textAlign: 'center' }}>
                        <Group gap={4} justify="center" wrap="nowrap">
                          {managedCount > 0 && <Text size="xs" c="dimmed">{managedCount}/{gc.length}</Text>}
                          <Tooltip label={t('campaigns.group.rename')}>
                            <ActionIcon variant="subtle" color="gray" size="xs" onClick={(e) => { e.stopPropagation(); setRenamingGroup(g); setRenameGroupName(g.name); }}>
                              <PencilSimple size={12} />
                            </ActionIcon>
                          </Tooltip>
                          <Tooltip label={t('campaigns.main.deleteGroup')}>
                            <ActionIcon variant="subtle" color="gray" size="xs" onClick={(e) => { e.stopPropagation(); setDeletingGroup({ ...g, count: gc.length }); }}>
                              <Trash size={12} />
                            </ActionIcon>
                          </Tooltip>
                        </Group>
                      </Table.Td>
                    </Table.Tr>
                  );
                }

                const campaign = row.campaign;
                const isManaged = campaign.is_managed;
                const isDraft = campaign.status === 'draft';
                const isFailed = isDraft && campaign.draft_status === 'failed';
                const showOverlay = isDraft || !isManaged;
                const errorMsg = campaign.draft_error || (campaign.ai_draft_data || {}).error_message || '';

                return (
                  <Table.Tr
                    key={campaign.id}
                    onClick={() => handleCampaignClick(campaign)}
                    style={{ cursor: isDraft && campaign.draft_status !== 'ready' && !isFailed ? 'default' : 'pointer' }}
                  >
                    {/* Name + platform */}
                    <Table.Td style={row.inGroup ? { paddingLeft: 40 } : undefined}>
                      <Group gap="xs" wrap="nowrap">
                        <Tooltip label={campaign.platform === 'meta_ads' ? 'Meta Ads' : 'Google Ads'} position="top">
                          {campaign.platform === 'meta_ads' ? (
                            <MetaLogo size={18} weight="bold" style={{ color: '#1877F2', flexShrink: 0 }} />
                          ) : (
                            <GoogleLogo size={18} weight="bold" style={{ color: '#4285F4', flexShrink: 0 }} />
                          )}
                        </Tooltip>
                        <div>
                          <Group gap={6} wrap="nowrap">
                            <Text size="sm" fw={500}>{campaign.name}</Text>
                            {!isDraft && campaign.is_managed && campaign.status === 'paused' && (
                              <Tooltip label={t('campaigns.main.pausedManagedTooltip')} multiline w={320} position="top">
                                <Badge size="xs" color="orange" variant="filled" style={{ cursor: 'help' }}>
                                  {t('campaigns.main.pausedManagedBadge')}
                                </Badge>
                              </Tooltip>
                            )}
                            {!isDraft && campaign.is_trial_active && (
                              <Badge size="xs" color={campaign.trial_days_remaining <= 1 ? 'red' : 'orange'} variant="light">
                                {t('campaigns.main.trialBadge', { count: campaign.trial_days_remaining, word: campaign.trial_days_remaining === 1 ? t('campaigns.main.dayOne') : t('campaigns.main.dayMany') })}
                              </Badge>
                            )}
                          </Group>
                          <Text size="11px" c="dimmed" mt={2} truncate maw={280}>
                            {campaignTypeLabel(campaign, t)}{campaign.source_url ? ` · ${campaign.source_url.replace(/^https?:\/\//, '')}` : ''}
                          </Text>
                        </div>
                      </Group>
                    </Table.Td>
                    {/* Metryki: albo realne wartości, albo nakładka (szkic/niezarządzane) */}
                    {showOverlay ? (
                      <>
                      <Table.Td colSpan={metricColCount} style={{ position: 'relative', padding: 0, background: 'transparent' }}>
                        <div style={{ position: 'relative', minHeight: 52 }}>
                        {/* Real data row - visible but faded */}
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', opacity: 0.1, pointerEvents: 'none', fontSize: 13, padding: '16px 20px' }}>
                          {displayColumns.map(col => (
                            <span key={col.key} style={{ width: col.width, textAlign: col.align }}>
                              {isDraft ? '-' : col.faded(campaign, t)}
                            </span>
                          ))}
                        </div>
                        {/* Overlay */}
                        {/* Overlay */}
                        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10 }}>
                          {isFailed ? (
                            <>
                              <WarningCircle size={15} className="text-red-400" />
                              <Tooltip label={errorMsg} multiline maw={400} withArrow disabled={!errorMsg}>
                                <Text size="xs" c="red.5" maw={280} truncate>{errorMsg || t('campaigns.draftStatus.publishError')}</Text>
                              </Tooltip>
                              <Button size="compact-xs" variant="light" color="red" leftSection={<ArrowCounterClockwise size={12} />} onClick={(e) => { e.stopPropagation(); handleRetryDraft(campaign); }}>{t('campaigns.main.retry')}</Button>
                              <ActionIcon variant="subtle" color="gray" size="sm" onClick={(e) => { e.stopPropagation(); requestDeleteDraft(campaign); }}><Trash size={14} /></ActionIcon>
                            </>
                          ) : isDraft ? (
                            campaign.draft_status === 'analyzing' || campaign.draft_status === 'publishing' ? (
                              <><CircleNotch size={15} className="animate-spin text-indigo-400" /><Text size="xs" c="dimmed">{campaign.draft_status === 'analyzing' ? t('campaigns.main.draftAnalyzing') : t('campaigns.main.draftPublishing')}</Text></>
                            ) : (
                              <>
                                <Sparkle size={15} weight="duotone" className="text-indigo-400" />
                                <Text size="xs" c="dimmed">{t('campaigns.main.draftReady')}</Text>
                                <Button size="compact-xs" variant="light" color="brand" onClick={(e) => { e.stopPropagation(); openDraftReview(campaign); }}>{t('campaigns.main.open')}</Button>
                                <ActionIcon variant="subtle" color="gray" size="sm" onClick={(e) => { e.stopPropagation(); requestDeleteDraft(campaign); }}><Trash size={14} /></ActionIcon>
                              </>
                            )
                          ) : (
                            <>
                              {campaign.platform === 'google_ads' && campaign.campaign_type && !['search', 'shopping', 'performance_max', 'pmax', ''].includes(campaign.campaign_type) ? (
                                <Text size="xs" c="dimmed">{t('campaigns.main.typePreviewOnly', { type: {display:'Display',video:'Video',demand_gen:'Demand Gen',smart:'Smart',local:'Local',discovery:'Discovery'}[campaign.campaign_type] || campaign.campaign_type })}</Text>
                              ) : (
                                <>
                                  <Text size="xs" c="dimmed">{t('campaigns.main.notManaged')}</Text>
                                  <Button size="compact-xs" variant="light" color="brand" onClick={(e) => { e.stopPropagation(); handleToggleManaged(campaign); }}>{t('campaigns.main.enableManagement')}</Button>
                                </>
                              )}
                            </>
                          )}
                        </div>
                        </div>
                      </Table.Td>
                      {/* Kolumna „Droid” zawsze osobno (przyklejona do prawej): przełącznik
                          tylko dla kampanii, które Droid może przejąć. */}
                      <Table.Td style={{ textAlign: 'center' }}>
                        {!isDraft && !(campaign.platform === 'google_ads' && campaign.campaign_type && !['search', 'shopping', 'performance_max', 'pmax', ''].includes(campaign.campaign_type)) && (
                          <Tooltip label={t('campaigns.main.enableManaged')} position="left">
                            <div onClick={(e) => e.stopPropagation()} style={{ display: 'inline-block' }}>
                              <Switch
                                checked={false}
                                onChange={() => handleToggleManaged(campaign)}
                                color="brand"
                                size="sm"
                                styles={{ track: { cursor: 'pointer' } }}
                              />
                            </div>
                          </Tooltip>
                        )}
                      </Table.Td>
                      </>
                    ) : (
                      <>
                        {displayColumns.map(col => (
                          <Table.Td key={col.key} style={{ textAlign: col.align }}>
                            {col.cell(campaign, t)}
                          </Table.Td>
                        ))}
                        <Table.Td style={{ textAlign: 'center' }}>
                          <Tooltip label={t('campaigns.main.disableManaged')} position="left">
                            <div onClick={(e) => e.stopPropagation()} style={{ display: 'inline-block' }}>
                              <Switch
                                checked
                                onChange={() => handleToggleManaged(campaign)}
                                color="brand"
                                size="sm"
                                styles={{ track: { cursor: 'pointer' } }}
                              />
                            </div>
                          </Tooltip>
                        </Table.Td>
                      </>
                    )}
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
          </EdgeScroll>
        </Paper>
        </>
        );
      })() : (
        <Paper withBorder radius="md" p="xl" style={{ textAlign: 'center' }}>
          {isConnected ? (
            <Stack align="center" gap="sm">
              <Text size="sm" fw={500}>{t('campaigns.main.emptyTitle')}</Text>
              <Text size="xs" c="dimmed">{t('campaigns.main.emptyDesc')}</Text>
              <Button color="brand" leftSection={<Plus size={14} />} onClick={() => setShowCreateWizard(true)} mt="xs">
                {t('campaigns.main.createCampaign')}
              </Button>
            </Stack>
          ) : (
            <Stack align="center" gap="sm">
              <Text size="sm" fw={500}>{t('campaigns.main.connectGoogle')}</Text>
              <Text size="xs" c="dimmed">{t('campaigns.main.connectDesc')}</Text>
              <Button color="brand" leftSection={<ArrowSquareOut size={14} />} onClick={() => setShowConnectModal(true)} mt="xs">
                {t('campaigns.main.connectGoogle')}
              </Button>
              <Button variant="subtle" color="gray" size="xs" onClick={() => setShowSetupHelp(true)} mt={4}>
                {t('campaigns.main.noAccountHelp')}
              </Button>
            </Stack>
          )}
        </Paper>
      )}

      {/* Campaign Drawer (for active campaigns) */}
      {selectedCampaign && (
        <CampaignDrawer
          campaign={selectedCampaign}
          onClose={() => setSelectedCampaign(null)}
          onCampaignUpdated={fetchCampaigns}
          inComparison={comparisonIds.includes(selectedCampaign.id)}
          comparisonFull={comparisonCampaigns.length >= 5 && !comparisonIds.includes(selectedCampaign.id)}
          comparisonCount={comparisonCampaigns.length}
          onToggleComparison={() => toggleComparison(selectedCampaign.id)}
          onOpenComparison={() => setShowComparison(true)}
        />
      )}

      {/* Pływający przycisk: otwórz porównanie, gdy w koszyku >= 2 kampanie */}
      {comparisonCampaigns.length >= 2 && !showComparison && (
        <div style={{ position: 'fixed', bottom: 20, left: '50%', transform: 'translateX(-50%)', zIndex: 250 }}>
          <Button color="brand" radius="xl" leftSection={<ChartBar size={16} weight="bold" />}
            onClick={() => setShowComparison(true)}>
            {t('campaigns.main.compareBtn', { count: comparisonCampaigns.length, word: comparisonCampaigns.length >= 5 ? t('campaigns.main.campWordMany') : t('campaigns.main.campWordFew') })}
          </Button>
        </div>
      )}

      {/* Modal porównania kampanii */}
      {showComparison && comparisonCampaigns.length > 0 && (
        <ComparisonModal
          campaigns={comparisonCampaigns}
          onClose={() => setShowComparison(false)}
          onRemove={removeFromComparison}
          onClear={() => { setComparisonIds([]); setShowComparison(false); }}
        />
      )}

      {/* Draft Review Drawer */}
      {selectedDraft && (
        <CampaignDraftReview
          campaign={selectedDraft}
          trialUsed={trialUsed && !trialAvailable}
          isFree={isFreeAccount}
          onClose={() => setSelectedDraft(null)}
          onUpdated={() => {
            fetchCampaigns();
            setSelectedDraft(null);
          }}
        />
      )}

      {/* Modals */}
      {showPaymentSuccessModal && (() => {
        // Realna cena wykupionego pakietu: kwota netto z success_url ma pierwszeństwo
        // (nie zależy od webhooka), potem cennik pakietu, na końcu bieżący plan org.
        const info = paymentSuccessInfo || {};
        const cur = info.currency || 'PLN';
        const catalogPlan = (planStatus?.plans || []).find(p => p.key === info.plan);
        const netto = info.amount != null ? info.amount
          : (catalogPlan?.prices?.[cur] != null ? catalogPlan.prices[cur]
            : (catalogPlan?.price != null ? catalogPlan.price
              : (planStatus?.plan_price != null ? planStatus.plan_price : null)));
        const label = catalogPlan?.label
          || (info.plan === 'pla' ? 'PLA' : (planStatus?.plan_label || null));
        return (
        <ModalWrapper onClose={() => setShowPaymentSuccessModal(false)}>
          <Stack align="center" gap="sm">
            <ThemeIcon size={48} radius="xl" variant="light" color="green">
              <CheckCircle size={24} />
            </ThemeIcon>
            <Title order={4}>{t('campaigns.paymentSuccess.title')}</Title>
            <Text size="xs" c="dimmed" ta="center">
              {t('campaigns.paymentSuccess.desc')}
            </Text>
            {netto != null && (
              <Paper bg="gray.0" withBorder p="sm" w="100%">
                <Group justify="space-between"><Text size="xs" c="dimmed">{t('campaigns.paymentSuccess.plan')}</Text><Text size="xs" fw={500}>{label ? `${label}: ` : ''}{t('campaigns.paymentSuccess.netMonthly', { amount: formatMoney(netto, cur) })}</Text></Group>
                {cur === 'PLN' ? (
                  <>
                    <Group justify="space-between" mt={6}><Text size="xs" c="dimmed">{t('campaigns.paymentSuccess.vat')}</Text><Text size="xs">{formatMoney(netto * 0.23, cur)}</Text></Group>
                    <Group justify="space-between" mt={6} pt={6} style={{ borderTop: '1px solid var(--mantine-color-gray-3)' }}><Text size="xs" fw={500}>{t('campaigns.paymentSuccess.totalGross')}</Text><Text size="xs" fw={600}>{formatMoney(netto * 1.23, cur)}</Text></Group>
                  </>
                ) : (
                  <Text size="xs" c="dimmed" mt={6}>{t('campaigns.paymentSuccess.vatReverseCharge')}</Text>
                )}
              </Paper>
            )}
            <Button color="brand" fullWidth onClick={() => setShowPaymentSuccessModal(false)}>
              {t('campaigns.paymentSuccess.goToCampaigns')}
            </Button>
          </Stack>
        </ModalWrapper>
        );
      })()}
      {showConnectedModal && (
        <ModalWrapper onClose={() => setShowConnectedModal(false)}>
          <Stack align="center" gap="sm">
            <ThemeIcon size={40} radius="xl" variant="light" color="green">
              <CheckCircle size={20} />
            </ThemeIcon>
            <Title order={4}>{t('campaigns.connected.title')}</Title>
            <Text size="xs" c="dimmed" ta="center">
              {t('campaigns.connected.descBefore')} <Text span ff="monospace" fw={500} c="dark">{connectedCustomerId}</Text> {t('campaigns.connected.descAfter')}
            </Text>
            <Paper bg="gray.0" withBorder p="sm" w="100%" style={{ textAlign: 'left' }}>
              <Text size="xs" c="dimmed" mb="xs">{t('campaigns.connected.whatNext')}</Text>
              <Stack gap={6}>
                <Group gap="xs" wrap="nowrap" align="flex-start">
                  <Box style={{ width: 4, height: 4, borderRadius: '50%', background: 'var(--mantine-color-brand-5)', marginTop: 6, flexShrink: 0 }} />
                  <Text size="xs" c="dimmed">{t('campaigns.connected.bullet1')}</Text>
                </Group>
                <Group gap="xs" wrap="nowrap" align="flex-start">
                  <Box style={{ width: 4, height: 4, borderRadius: '50%', background: 'var(--mantine-color-brand-5)', marginTop: 6, flexShrink: 0 }} />
                  <Text size="xs" c="dimmed">{t('campaigns.connected.bullet2')}</Text>
                </Group>
              </Stack>
            </Paper>
            <Button color="brand" fullWidth onClick={() => setShowConnectedModal(false)}>
              {t('campaigns.connected.gotIt')}
            </Button>
          </Stack>
        </ModalWrapper>
      )}
      {showConnectModal && (
        <ConnectGoogleAdsModal onClose={() => setShowConnectModal(false)} />
      )}
      {showTrialModal && (
        <ActivateTrialModal
          onClose={() => { setShowTrialModal(null); setTrialError(null); }}
          onConfirm={handleActivateTrial}
          loading={!!actionLoading}
          // Nie sama flaga trial_used: klient, który zwolnił trial na innej
          // kampanii, ma go do przeniesienia (backend liczy to w trial_available).
          // Wcześniej modal pokazywał „Trial został już wykorzystany" mimo
          // ważnego triala do przeniesienia (zgłoszenie adwokata).
          trialUsed={trialUsed && !trialAvailable}
          transferDays={trialUsed ? trialTransferDays : null}
          isFree={isFreeAccount}
          error={trialError}
          activationPlan={planForCampaign(campaigns.find(c => c.id === showTrialModal), planStatus)}
          planChoices={(planStatus?.plans || []).length}
          currency={planStatus?.currency || 'PLN'}
          onSubscribe={() => {
            setShowTrialModal(null);
            setTrialError(null);
            setShowSubscribeModal(showTrialModal);
          }}
        />
      )}
      {showSubscribeModal && (
        <SubscribeModal
          onClose={() => setShowSubscribeModal(null)}
          onConfirm={handleSubscribe}
          loading={!!actionLoading}
          isReactivation={!!(campaigns.find(c => c.id === showSubscribeModal)?.management_paused)}
          addonsIncluded={!!user?.organization?.agency_addons_included}
          planStatus={planStatus}
          campaign={campaigns.find(c => c.id === showSubscribeModal)}
        />
      )}
      {planLimitInfo && (
        <PlanUpgradeModal
          info={planLimitInfo}
          onClose={() => setPlanLimitInfo(null)}
          onUpgrade={handleUpgradePlan}
          loading={!!actionLoading}
        />
      )}
      {showAgencyEnableModal && (
        <Modal
          opened
          onClose={() => setShowAgencyEnableModal(null)}
          title={<Text size="sm" fw={600}>{t('campaigns.agency.enableTitle')}</Text>}
          centered
          size="sm"
        >
          <Text size="sm" c="dimmed" mb="md">
            {t('campaigns.agency.enableDesc')}
          </Text>
          <Paper withBorder p="sm" mb="md" style={{ borderColor: agencySchedulerAddon ? 'var(--mantine-color-brand-4)' : undefined, cursor: 'pointer' }} onClick={() => setAgencySchedulerAddon(!agencySchedulerAddon)}>
            <Group justify="space-between">
              <Box>
                <Group gap="xs" mb={2}>
                  <Text size="sm" fw={500}>{t('campaigns.subscribe.schedulerModule')}</Text>
                  <Badge size="xs" variant="light" color="brand">{t('campaigns.subscribe.optionBadge')}</Badge>
                </Group>
                <Text size="xs" c="dimmed">{t('campaigns.agency.schedulerDesc')}</Text>
              </Box>
              {!user?.organization?.agency_addons_included && (
                <Box ta="right">
                  <Text size="sm" fw={600}>+100 zł</Text>
                  <Text size="xs" c="dimmed">{t('campaigns.subscribe.netPerMonth')}</Text>
                </Box>
              )}
              {user?.organization?.agency_addons_included && (
                <Badge size="xs" variant="light" color="green">{t('campaigns.subscribe.included')}</Badge>
              )}
            </Group>
            <div onClick={(e) => e.stopPropagation()}>
              <Switch
                checked={agencySchedulerAddon}
                onChange={() => setAgencySchedulerAddon(!agencySchedulerAddon)}
                color="brand"
                size="sm"
                mt="xs"
                label={agencySchedulerAddon ? t('campaigns.subscribe.added') : t('campaigns.subscribe.addModule')}
                styles={{ track: { cursor: 'pointer' }, label: { cursor: 'pointer', fontSize: 12 } }}
              />
            </div>
          </Paper>
          <Group justify="flex-end" gap="xs">
            <Button variant="outline" color="gray" size="sm" onClick={() => { setShowAgencyEnableModal(null); setAgencySchedulerAddon(false); }}>
              {t('campaigns.common.cancel')}
            </Button>
            <Button color="brand" size="sm" onClick={handleAgencyEnable} loading={!!actionLoading}>
              {t('campaigns.subscribe.activateManagement')}
            </Button>
          </Group>
        </Modal>
      )}
      {showAgencyDisableModal && (
        <Modal
          opened
          onClose={() => setShowAgencyDisableModal(null)}
          title={<Text size="sm" fw={600}>{t('campaigns.agency.disableTitle')}</Text>}
          centered
          size="sm"
        >
          <Text size="sm" c="dimmed" mb="md">
            {t('campaigns.agency.disableDesc')}
          </Text>
          <Text size="xs" c="dimmed" mb="md">
            {t('campaigns.agency.disableNote')}
          </Text>
          <Group justify="flex-end" gap="xs">
            <Button variant="outline" color="gray" size="sm" onClick={() => setShowAgencyDisableModal(null)}>
              {t('campaigns.common.cancel')}
            </Button>
            <Button color="red" size="sm" onClick={handleAgencyDisable} loading={!!actionLoading}>
              {t('campaigns.agency.disableConfirm')}
            </Button>
          </Group>
        </Modal>
      )}
      {unsupportedTypeModal && (
        <Modal opened onClose={() => setUnsupportedTypeModal(null)} title={<Group gap="xs"><Warning size={18} className="text-amber-500" /><Text fw={600}>{t('campaigns.unsupported.title')}</Text></Group>} centered size="sm">
          <Text size="sm" c="dimmed" mb="md">
            {t('campaigns.unsupported.descBefore')} <Text span fw={600}>{unsupportedTypeModal === 'performance_max' ? 'Performance Max' : unsupportedTypeModal === 'display' ? 'Display' : unsupportedTypeModal === 'shopping' ? 'Shopping' : unsupportedTypeModal}</Text> {t('campaigns.unsupported.descAfter')}
          </Text>
          <Text size="sm" c="dimmed" mb="md">
            {t('campaigns.unsupported.desc2')}
          </Text>
          <Text size="xs" c="dimmed" mb="lg">
            {t('campaigns.unsupported.desc3')}
          </Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setUnsupportedTypeModal(null)}>{t('campaigns.unsupported.understand')}</Button>
          </Group>
        </Modal>
      )}
      {draftToDelete && (
        <Modal
          opened
          onClose={() => setDraftToDelete(null)}
          title={<Group gap="xs"><Trash size={18} className="text-red-500" /><Text fw={600}>{t('campaigns.deleteDraft.title')}</Text></Group>}
          centered
          size="sm"
        >
          <Text size="sm" c="dimmed" mb="md">
            {t('campaigns.deleteDraft.confirmBefore')} <Text span fw={600}>{draftToDelete.name || t('campaigns.deleteDraft.campaignFallback')}</Text>{t('campaigns.deleteDraft.confirmAfter')}
          </Text>
          <Group justify="flex-end" gap="xs">
            <Button variant="default" onClick={() => setDraftToDelete(null)}>{t('campaigns.common.cancel')}</Button>
            <Button color="red" leftSection={<Trash size={14} />} onClick={confirmDeleteDraft}>{t('campaigns.deleteDraft.deleteBtn')}</Button>
          </Group>
        </Modal>
      )}
      {/* Create group modal */}
      {showCreateGroupModal && (
        <Modal opened onClose={() => setShowCreateGroupModal(false)} title={t('campaigns.main.createGroup')} size="sm" centered>
          <TextInput
            label={t('campaigns.group.nameLabel')}
            placeholder={t('campaigns.group.namePlaceholder')}
            value={newGroupName}
            onChange={e => setNewGroupName(e.target.value)}
            mb="md"
            autoFocus
            onKeyDown={e => e.key === 'Enter' && handleCreateGroup()}
          />
          <Group justify="flex-end" gap="xs">
            <Button variant="default" onClick={() => setShowCreateGroupModal(false)}>{t('campaigns.common.cancel')}</Button>
            <Button color="brand" loading={creatingGroup} onClick={handleCreateGroup} disabled={!newGroupName.trim()}>{t('campaigns.group.create')}</Button>
          </Group>
        </Modal>
      )}
      {/* Rename group modal */}
      {renamingGroup && (
        <Modal opened onClose={() => setRenamingGroup(null)} title={t('campaigns.group.renameTitle')} size="sm" centered>
          <TextInput
            label={t('campaigns.group.nameLabel')}
            value={renameGroupName}
            onChange={e => setRenameGroupName(e.target.value)}
            mb="md"
            autoFocus
            onKeyDown={e => e.key === 'Enter' && handleRenameGroup()}
          />
          <Group justify="flex-end" gap="xs">
            <Button variant="default" onClick={() => setRenamingGroup(null)}>{t('campaigns.common.cancel')}</Button>
            <Button color="brand" loading={savingGroupName} onClick={handleRenameGroup} disabled={!renameGroupName.trim()}>{t('campaigns.group.renameSave')}</Button>
          </Group>
        </Modal>
      )}
      {/* Delete group confirmation */}
      {deletingGroup && (
        <Modal opened onClose={() => setDeletingGroup(null)} title={t('campaigns.group.deleteTitle', { name: deletingGroup.name })} size="sm" centered>
          <Text size="sm" mb="xs">{t('campaigns.group.deleteBody')}</Text>
          <Text size="xs" c="dimmed" mb="md">{t('campaigns.group.deleteBodyCount', { count: deletingGroup.count })}</Text>
          <Group justify="flex-end" gap="xs">
            <Button variant="default" onClick={() => setDeletingGroup(null)}>{t('campaigns.common.cancel')}</Button>
            <Button color="red" onClick={() => handleDeleteGroup(deletingGroup.id)}>{t('campaigns.group.deleteConfirm')}</Button>
          </Group>
        </Modal>
      )}
      {/* Move campaign to group modal */}
      {movingCampaign && (
        <Modal opened onClose={() => setMovingCampaign(null)} title={t('campaigns.group.moveToGroup')} size="sm" centered>
          <Stack gap="xs">
            {groups.map(g => (
              <Paper key={g.id} withBorder p="sm" radius="md"
                style={{ cursor: 'pointer', borderColor: movingCampaign.currentGroupId === g.id ? 'var(--mantine-color-brand-3)' : undefined, backgroundColor: movingCampaign.currentGroupId === g.id ? 'var(--mantine-color-brand-0)' : undefined }}
                onClick={() => handleMoveCampaign(movingCampaign.campaignId, g.id)}
              >
                <Text size="sm" fw={500}>{g.name}</Text>
                <Text size="xs" c="dimmed">{t('campaigns.selectAccount.campaignCount', { count: g.campaign_count })}</Text>
              </Paper>
            ))}
            <Paper withBorder p="sm" radius="md" style={{ cursor: 'pointer' }} onClick={() => handleMoveCampaign(movingCampaign.campaignId, null)}>
              <Text size="sm" fw={500}>{t('campaigns.group.noGroup')}</Text>
              <Text size="xs" c="dimmed">{t('campaigns.group.removeFromGroup')}</Text>
            </Paper>
          </Stack>
        </Modal>
      )}
      {showDeactivateModal && (
        <ConfirmDeactivateModal
          onClose={() => setShowDeactivateModal(null)}
          onConfirm={handleDeactivate}
          loading={!!actionLoading}
        />
      )}
      {showCreateWizard && (
        <CreateCampaignWizard
          onClose={() => setShowCreateWizard(false)}
          onCreated={() => fetchCampaigns()}
          user={user}
        />
      )}
      {showSelectAccountModal && (
        <SelectAccountModal
          onClose={() => setShowSelectAccountModal(false)}
          onRenamed={(accountId, newName) => {
            if (accountId === user?.google_ads_customer_id) setGoogleAccountName(newName);
          }}
          onSelected={(account, needsSync) => {
            setShowSelectAccountModal(false);
            setGoogleAccountName(account.custom_name || account.name || '');
            refreshUser();
            if (needsSync) {
              // New account - need to sync from API
              setCampaigns([]);
              setSyncing(true);
              setTimeout(() => fetchCampaigns(), 2000);
              setTimeout(() => fetchCampaigns(), 5000);
              setTimeout(() => fetchCampaigns(), 10000);
              setTimeout(() => fetchCampaigns(), 16000);
              setTimeout(() => { fetchCampaigns(); setSyncing(false); }, 22000);
            } else {
              // Already synced - just re-fetch local data
              fetchCampaigns();
              addNotification(t('campaigns.main.switchedAccount', { name: account.custom_name || account.name || account.id }), 'success');
            }
          }}
        />
      )}
      {showSetupHelp && (
        <Modal opened onClose={() => setShowSetupHelp(false)} title={t('campaigns.setup.title')} size="lg" centered>
          <Stack gap="md">
            <Alert color="blue" icon={<Lightbulb size={16} />}>
              <Text size="sm">{t('campaigns.setup.intro')}</Text>
              <Anchor href="/poradniki/google-ads-setup" target="_blank" size="sm" fw={500} mt={4} style={{ display: 'block' }}>
                {t('campaigns.setup.fullGuide')}
              </Anchor>
            </Alert>

            <Paper withBorder p="md" radius="md">
              <Text size="sm" fw={600} mb="sm">{t('campaigns.setup.step1Title')}</Text>
              <Stack gap="xs">
                <Text size="sm" c="dimmed" lh={1.5}>{t('campaigns.setup.step1a')} <a href="https://ads.google.com" target="_blank" rel="noopener noreferrer" className="text-indigo-600 underline">ads.google.com</a> {t('campaigns.setup.step1aAfter')}</Text>
                <Text size="sm" c="dimmed" lh={1.5}>{t('campaigns.setup.step1b')}</Text>
                <Text size="sm" c="dimmed" lh={1.5}>{t('campaigns.setup.step1c')}</Text>
              </Stack>
            </Paper>

            <Paper withBorder p="md" radius="md">
              <Text size="sm" fw={600} mb="sm">{t('campaigns.setup.step2Title')}</Text>
              <Stack gap="xs">
                <Text size="sm" c="dimmed" lh={1.5}>{t('campaigns.setup.step2a')}</Text>
                <Text size="sm" c="dimmed" lh={1.5}>{t('campaigns.setup.step2b')}</Text>
                <Text size="sm" c="dimmed" lh={1.5}>{t('campaigns.setup.step2c')}</Text>
              </Stack>
            </Paper>

            <Paper withBorder p="md" radius="md">
              <Text size="sm" fw={600} mb="sm">{t('campaigns.setup.step3Title')}</Text>
              <Stack gap="xs">
                <Text size="sm" c="dimmed" lh={1.5}>{t('campaigns.setup.step3a')}</Text>
                <Text size="sm" c="dimmed" lh={1.5}>{t('campaigns.setup.step3b')}</Text>
              </Stack>
            </Paper>

            <Paper withBorder p="md" radius="md">
              <Text size="sm" fw={600} mb="sm">{t('campaigns.setup.step4Title')}</Text>
              <Stack gap="xs">
                <Text size="sm" c="dimmed" lh={1.5}>{t('campaigns.setup.step4a')}</Text>
                <Text size="sm" c="dimmed" lh={1.5}>{t('campaigns.setup.step4b')}</Text>
              </Stack>
            </Paper>

            <Paper withBorder p="md" radius="md" style={{ background: 'var(--mantine-color-brand-0)' }}>
              <Text size="sm" fw={600} mb="xs">{t('campaigns.setup.helpTitle')}</Text>
              <Text size="sm" c="dimmed" lh={1.5}>{t('campaigns.setup.helpBefore')} <a href="mailto:kontakt@droid.pl" className="text-indigo-600 underline">kontakt@droid.pl</a>{t('campaigns.setup.helpAfter')}</Text>
            </Paper>

            <Group justify="flex-end">
              <Button variant="default" onClick={() => setShowSetupHelp(false)}>{t('campaigns.common.close')}</Button>
              <Button color="brand" leftSection={<ArrowSquareOut size={14} />} onClick={() => { setShowSetupHelp(false); setShowConnectModal(true); }}>
                {t('campaigns.setup.haveAccount')}
              </Button>
            </Group>
          </Stack>
        </Modal>
      )}
    </Box>
  );
}
